import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { TransporeonQuotesPanel } from "../components/TransporeonQuotesPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { SmartLocationInput } from "../components/SmartLocationInput";
import BLInstructionsRequestModal from "../components/BLInstructionsRequestModal";
import PFAssignmentBlock from "../components/PFAssignmentBlock";
import { CarrierSelect } from "../components/CarrierSelect";
import { EquipmentSelect, FALLBACK_EQUIPMENT } from "../components/EquipmentSelect";
import { EMAIL_TEMPLATES, TEMPLATE_CATEGORIES, fillTemplate } from "../constants/emailTemplates";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "../components/ui/select";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  FolderOpen,
  Mail,
  Upload,
  Search,
  Plus,
  FileText,
  User,
  Calendar,
  Building,
  Building2,
  X,
  ChevronLeft,
  ChevronRight,
  Send,
  MapPin,
  Paperclip,
  Clock,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Filter,
  BarChart3,
  Inbox,
  ArrowRight,
  Download,
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Package,
  Edit,
  Save,
  Layers,
  GitBranch,
  Ship,
  Users,
  Target,
  Truck,
  ClipboardCheck,
  Weight,
  Loader2,
  Radar,
  ToggleLeft,
  ListTodo,
  Bell,
  Anchor,
  Navigation,
  ExternalLink,
  Star,
  Lock,
  Unlock,
  GripHorizontal,
  FileUp,
  Trash2,
  Check,
  RefreshCw,
  Warehouse,
  Boxes,
  CalendarClock,
  Leaf,
  Plane,
  BellOff,
  BellRing,
  ShieldCheck,
  PackagePlus,
  PackageOpen,
} from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { AwbTracker } from "../components/AwbTracker";
import { ContactAutocomplete } from "../components/ContactAutocomplete";
import { PFMilestones } from "../components/PFMilestones";
import { PFComments } from "../components/PFComments";
import { InspectionReportsPanel } from "../components/InspectionReportsPanel";
import { Switch } from "../components/ui/switch";
import { API_URL, API_BASE_URL } from "../config/api";
import { PantheonCompanySelector } from "../components/PantheonCompanySelector";


// DVH (vhodna dispozicija) quick toggle — closes the Koper DVH
// reminder loop the moment staff flip the Pending/Done switch, enter
// a DVH number, OR upload the DVH.pdf in Documents. Renders inside
// the PF Details tab so the user never has to leave the PF view.
function CODTracker({ folder, isCustomerView, onUpdate }) {
  const token = localStorage.getItem("token");
  const cod = folder.cod || {};
  const [status, setStatus] = useState(cod.status || folder.cod_status || "none");
  const [newDest, setNewDest] = useState(cod.new_destination || "");
  const [reason, setReason] = useState(cod.reason || "");
  const [cc, setCc] = useState("");
  const [notify, setNotify] = useState(false);
  const [saving, setSaving] = useState(false);

  const STATUSES = [
    { key: "pending", label: "Pending", cls: "bg-amber-100 text-amber-700 border-amber-300" },
    { key: "approved", label: "Approved", cls: "bg-green-100 text-green-700 border-green-300" },
    { key: "denied", label: "Denied", cls: "bg-red-100 text-red-700 border-red-300" },
  ];

  const save = async (newStatus) => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/position-folders/${folder.id}/cod-status`,
        {
          status: newStatus,
          new_destination: newDest,
          reason,
          notify_customer: notify,
          cc_emails: cc ? cc.split(",").map((e) => e.trim()).filter(Boolean) : [],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(newStatus);
      toast.success(`COD marked ${newStatus}${res.data.notified ? " · customer notified" : ""}`);
      onUpdate && onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update COD status");
    } finally {
      setSaving(false);
    }
  };

  const current = STATUSES.find((s) => s.key === status);

  return (
    <div className="p-4 border rounded-lg bg-white" data-testid="cod-tracker">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold flex items-center gap-2">
          <MapPin className="h-4 w-4 text-indigo-600" /> Change of Destination (COD)
        </h4>
        {status && status !== "none" && (
          <span className={`text-xs px-2 py-0.5 rounded-full border ${current?.cls || ""}`}>
            {current?.label || status}
          </span>
        )}
      </div>
      {isCustomerView ? (
        <p className="text-sm text-slate-500">
          Status: {status === "none" ? "No change of destination requested" : current?.label || status}
          {cod.new_destination ? ` · New POD: ${cod.new_destination}` : ""}
        </p>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">New destination (POD)</Label>
              <Input value={newDest} onChange={(e) => setNewDest(e.target.value)} placeholder="e.g. Rijeka, HR" data-testid="cod-new-dest" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">CC emails (comma-separated)</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="ops@..., mgr@..." data-testid="cod-cc" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Reason / note</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cod-reason" />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="cod-notify" />
            Notify customer by email on status change
          </label>
          <div className="flex gap-2">
            {STATUSES.map((s) => (
              <Button key={s.key} size="sm" variant={status === s.key ? "default" : "outline"} disabled={saving}
                onClick={() => save(s.key)} data-testid={`cod-set-${s.key}`}>
                {s.label}
              </Button>
            ))}
          </div>
          {cod.updated_at && (
            <p className="text-xs text-slate-400">Last updated by {cod.updated_by || "-"} · {new Date(cod.updated_at).toLocaleString()}</p>
          )}
        </div>
      )}
    </div>
  );
}

function DvhQuickToggle({ folder, token, onUpdate }) {
  const [done, setDone] = useState(folder.dvh_done === true);
  const [num, setNum] = useState(folder.dvh_number || "");
  const [saving, setSaving] = useState(false);

  // Reset local state if the modal is reopened on a different folder
  useEffect(() => {
    setDone(folder.dvh_done === true);
    setNum(folder.dvh_number || "");
  }, [folder.id, folder.dvh_done, folder.dvh_number]);

  const dirty = (folder.dvh_done === true) !== done || (folder.dvh_number || "") !== (num || "").trim();

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { dvh_done: done };
      const trimmed = (num || "").trim();
      // Send dvh_number even when empty so staff can clear it
      payload.dvh_number = trimmed;
      await axios.put(
        `${API_URL}/position-folders/${folder.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        done ? "DVH marked as Done — reminders stopped" : "DVH marked as Pending"
      );
      if (typeof onUpdate === "function") onUpdate();
    } catch (err) {
      console.error("Save DVH error:", err);
      toast.error(err.response?.data?.detail || "Failed to save DVH");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white rounded-xl p-4 border shadow-sm"
      data-testid="pf-detail-dvh-block"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2 text-slate-900">
          <FileText className="w-4 h-4 text-teal-600" />
          DVH — Vhodna dispozicija
        </h3>
        <button
          type="button"
          role="switch"
          aria-checked={done}
          data-testid="pf-detail-dvh-toggle"
          onClick={() => setDone(v => !v)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            done ? "bg-emerald-500" : "bg-slate-300"
          }`}
          title={done ? "Marked as Done — toggle to Pending" : "Marked as Pending — toggle to Done"}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              done ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <div className="text-xs text-slate-500 mb-3">
        {done ? (
          <span className="text-emerald-700 font-medium">✅ Done — no reminders will fire.</span>
        ) : (
          <span className="text-amber-700">⏳ Pending — staff will be nudged every 2h after a Koper outbound by rail/truck until any of: <b>toggle Done</b>, <b>DVH number</b>, <b>DVH.pdf</b> uploaded.</span>
        )}
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label htmlFor={`dvh-num-${folder.id}`} className="text-xs">DVH number <span className="text-slate-400">(optional)</span></Label>
          <Input
            id={`dvh-num-${folder.id}`}
            data-testid="pf-detail-dvh-number"
            type="text"
            placeholder="e.g. DVH-2026-04-001234"
            value={num}
            onChange={(e) => setNum(e.target.value)}
            className="mt-1 text-sm"
          />
        </div>
        <Button
          size="sm"
          onClick={save}
          disabled={saving || !dirty}
          className="bg-teal-600 hover:bg-teal-700 text-white"
          data-testid="pf-detail-dvh-save"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <p className="text-[11px] text-slate-400 mt-2">
        Tip: dropping a DVH-tagged PDF in the <b>Documents</b> tab also closes the loop automatically.
      </p>
    </div>
  );
}


// DIZ (izhodna dispozicija) quick toggle — export-side mirror of the
// DVH toggle. Closes the Koper DIZ reminder loop the moment staff flip
// the Pending/Done switch, enter a DIZ number, OR upload the DIZ.pdf
// in Documents. Renders inside the PF Details tab for export PFs.
function DizQuickToggle({ folder, token, onUpdate }) {
  const [done, setDone] = useState(folder.diz_done === true);
  const [num, setNum] = useState(folder.diz_number || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDone(folder.diz_done === true);
    setNum(folder.diz_number || "");
  }, [folder.id, folder.diz_done, folder.diz_number]);

  const dirty = (folder.diz_done === true) !== done || (folder.diz_number || "") !== (num || "").trim();

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { diz_done: done };
      const trimmed = (num || "").trim();
      payload.diz_number = trimmed;
      await axios.put(
        `${API_URL}/position-folders/${folder.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        done ? "DIZ marked as Done — reminders stopped" : "DIZ marked as Pending"
      );
      if (typeof onUpdate === "function") onUpdate();
    } catch (err) {
      console.error("Save DIZ error:", err);
      toast.error(err.response?.data?.detail || "Failed to save DIZ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white rounded-xl p-4 border shadow-sm"
      data-testid="pf-detail-diz-block"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2 text-slate-900">
          <FileText className="w-4 h-4 text-indigo-600" />
          DIZ — Izhodna dispozicija
        </h3>
        <button
          type="button"
          role="switch"
          aria-checked={done}
          data-testid="pf-detail-diz-toggle"
          onClick={() => setDone(v => !v)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            done ? "bg-emerald-500" : "bg-slate-300"
          }`}
          title={done ? "Marked as Done — toggle to Pending" : "Marked as Pending — toggle to Done"}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              done ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <div className="text-xs text-slate-500 mb-3">
        {done ? (
          <span className="text-emerald-700 font-medium">✅ Done — no reminders will fire.</span>
        ) : (
          <span className="text-amber-700">⏳ Pending — staff will be nudged every 2h after a Koper IN/F (export) until any of: <b>toggle Done</b>, <b>DIZ number</b>, <b>DIZ.pdf</b> uploaded.</span>
        )}
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label htmlFor={`diz-num-${folder.id}`} className="text-xs">DIZ number <span className="text-slate-400">(optional)</span></Label>
          <Input
            id={`diz-num-${folder.id}`}
            data-testid="pf-detail-diz-number"
            type="text"
            placeholder="e.g. DIZ-2026-04-001234"
            value={num}
            onChange={(e) => setNum(e.target.value)}
            className="mt-1 text-sm"
          />
        </div>
        <Button
          size="sm"
          onClick={save}
          disabled={saving || !dirty}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="pf-detail-diz-save"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <p className="text-[11px] text-slate-400 mt-2">
        Tip: dropping a DIZ-tagged PDF in the <b>Documents</b> tab also closes the loop automatically.
      </p>
    </div>
  );
}


// PF Tasks Section Component
function PFTasksSection({ folder, token, onUpdate }) {
  const [tasks, setTasks] = useState(folder.tasks || []);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [defaultTasks, setDefaultTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTaskLabel, setNewTaskLabel] = useState("");
  const [newTaskDate, setNewTaskDate] = useState("");
  const [newTaskAssignee, setNewTaskAssignee] = useState("");
  const [newTaskComment, setNewTaskComment] = useState("");
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    fetchTasksAndTemplates();
  }, [folder.id]);

  useEffect(() => {
    // Load staff list once for the assignee dropdown
    axios.get(`${API_URL}/users/staff`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStaffList(r.data || []))
      .catch(() => {});
  }, [token]);

  const fetchTasksAndTemplates = async () => {
    setLoading(true);
    try {
      const [tasksRes, templatesRes] = await Promise.all([
        axios.get(`${API_URL}/position-folders/${folder.id}/tasks`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/pf-tasks/templates?direction=${folder.direction || 'import'}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      
      setTasks(tasksRes.data.tasks || []);
      setDefaultTasks(tasksRes.data.defaults || templatesRes.data.defaults || []);
      setTaskTemplates(templatesRes.data.templates || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const toggleTask = async (taskId) => {
    try {
      await axios.put(
        `${API_URL}/position-folders/${folder.id}/tasks/${taskId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTasks(tasks.map(t => 
        t.id === taskId ? { ...t, completed: !t.completed } : t
      ));
      onUpdate();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const addTask = async (label, description = null, dueDate = null, assignedTo = null, comment = null) => {
    if (!label.trim()) return;

    const newTask = {
      id: `task_${Date.now()}`,
      label: label.trim(),
      description,
      due_date: dueDate || null,
      assigned_to: (assignedTo || "").trim() || null,
      comment: (comment || "").trim() || null,
      completed: false,
    };

    const updatedTasks = [...tasks, newTask];
    setTasks(updatedTasks);
    setNewTaskLabel("");
    setNewTaskDate("");
    setNewTaskAssignee("");
    setNewTaskComment("");

    try {
      await axios.put(
        `${API_URL}/position-folders/${folder.id}/tasks`,
        { tasks: updatedTasks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate();
    } catch (error) {
      toast.error("Failed to add task");
      setTasks(tasks); // Revert
    }
  };

  const updateTaskDate = async (taskId, dueDate) => {
    const updatedTasks = tasks.map(t =>
      t.id === taskId ? { ...t, due_date: dueDate || null } : t
    );
    setTasks(updatedTasks);
    try {
      await axios.put(
        `${API_URL}/position-folders/${folder.id}/tasks`,
        { tasks: updatedTasks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate();
    } catch (error) {
      toast.error("Failed to update task date");
      setTasks(tasks);
    }
  };

  const updateTaskAssignment = async (taskId, assignedTo) => {
    const updatedTasks = tasks.map(t =>
      t.id === taskId ? { ...t, assigned_to: assignedTo || null } : t
    );
    setTasks(updatedTasks);
    try {
      await axios.put(
        `${API_URL}/position-folders/${folder.id}/tasks`,
        { tasks: updatedTasks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate();
    } catch (error) {
      toast.error("Failed to update task assignee");
      setTasks(tasks);
    }
  };

  const updateTaskComment = async (taskId, comment) => {
    // Inline note (e.g. truck registration number) on a PF task in the
    // tasks-tab UI. Persists via PATCH so a single edit doesn't churn the
    // whole tasks array (the bulk PUT path is reserved for reorder/assign).
    const previous = tasks;
    const updatedTasks = tasks.map(t =>
      t.id === taskId ? { ...t, comment: comment || "" } : t
    );
    setTasks(updatedTasks);
    try {
      await axios.patch(
        `${API_URL}/position-folders/${folder.id}/tasks/${taskId}`,
        { comment: comment || "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate();
    } catch (error) {
      toast.error("Failed to save comment");
      setTasks(previous);
    }
  };

  const removeTask = async (taskId) => {
    const updatedTasks = tasks.filter(t => t.id !== taskId);
    setTasks(updatedTasks);
    
    try {
      await axios.put(
        `${API_URL}/position-folders/${folder.id}/tasks`,
        { tasks: updatedTasks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate();
    } catch (error) {
      toast.error("Failed to remove task");
      setTasks(tasks); // Revert
    }
  };

  const addDefaultTasks = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/position-folders/${folder.id}/tasks/add-defaults`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTasks(res.data.tasks);
      toast.success(`Added ${res.data.added} default tasks`);
      onUpdate();
    } catch (error) {
      toast.error("Failed to add default tasks");
    }
  };

  const saveAsTemplate = async (label) => {
    try {
      await axios.post(
        `${API_URL}/pf-tasks/templates`,
        { label, direction: folder.direction },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Task saved as template");
      fetchTasksAndTemplates();
    } catch (error) {
      toast.error("Failed to save template");
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder tasks
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = tasks.findIndex(t => t.id === active.id);
      const newIndex = tasks.findIndex(t => t.id === over.id);
      
      const newTasks = arrayMove(tasks, oldIndex, newIndex);
      setTasks(newTasks);
      
      // Save to backend
      try {
        await axios.put(
          `${API_URL}/position-folders/${folder.id}/tasks/reorder`,
          { task_ids: newTasks.map(t => t.id) },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        onUpdate();
      } catch (error) {
        // Revert on error
        setTasks(tasks);
        toast.error("Failed to reorder tasks");
      }
    }
  };

  // Sortable Task Item Component
  const SortableTaskItem = ({ task, onToggle, onRemove, onSaveTemplate, isTemplate }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: task.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 1000 : 1,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
          task.completed 
            ? "bg-green-50 border-green-200" 
            : isDragging
              ? "bg-blue-50 border-blue-300 shadow-lg"
              : "bg-white border-slate-200 hover:border-slate-300"
        }`}
      >
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 mr-2 text-slate-400 hover:text-slate-600"
          title="Drag to reorder"
        >
          <GripHorizontal className="h-4 w-4" />
        </div>
        
        <div className="flex items-center gap-3 flex-1">
          <button
            onClick={onToggle}
            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
              task.completed
                ? "bg-green-500 border-green-500 text-white"
                : "border-slate-300 hover:border-blue-400"
            }`}
          >
            {task.completed && <CheckCircle className="h-4 w-4" />}
          </button>
          <div className="flex-1">
            <span className={`font-medium ${task.completed ? "line-through text-slate-500" : "text-slate-900"}`}>
              {task.label}
            </span>
            {task.description && (
              <p className="text-xs text-slate-500 mt-0.5">{task.description}</p>
            )}
            {task.completed && task.completed_by && (
              <p className="text-xs text-green-600 mt-0.5">
                Completed by {task.completed_by.split("@")[0]}
              </p>
            )}
            {/* Short free-text note (TRK reg number, triage comment, etc.) */}
            <input
              type="text"
              placeholder="+ note (eg. TRK reg. nr)"
              defaultValue={task.comment || ""}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v !== (task.comment || "")) updateTaskComment(task.id, v);
              }}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              data-testid={`task-comment-${task.id}`}
              className="mt-1 text-xs w-full px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:bg-white"
            />
          </div>
          {/* Inline due date picker */}
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5 text-slate-400" />
            <input
              type="date"
              value={task.due_date || ""}
              onChange={(e) => updateTaskDate(task.id, e.target.value)}
              data-testid={`task-due-date-${task.id}`}
              className={`text-xs px-2 py-1 rounded border bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                task.due_date && !task.completed && new Date(task.due_date) < new Date(new Date().toDateString())
                  ? "border-red-300 text-red-700"
                  : "border-slate-200 text-slate-700"
              }`}
              title="Due date"
            />
          </div>
          {/* Inline assignee dropdown */}
          <div className="flex items-center gap-1 ml-2">
            <User className="h-3.5 w-3.5 text-slate-400" />
            <select
              value={task.assigned_to || ""}
              onChange={(e) => updateTaskAssignment(task.id, e.target.value)}
              data-testid={`task-assignee-${task.id}`}
              className="text-xs px-2 py-1 rounded border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400 max-w-[160px]"
              title="Assign to staff member"
            >
              <option value="">Unassigned</option>
              {staffList.map(s => (
                <option key={s.email} value={s.email}>
                  {s.full_name || s.name || s.email.split("@")[0]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isTemplate && (
            <button
              onClick={onSaveTemplate}
              className="text-slate-400 hover:text-blue-600 p-1"
              title="Save as template"
            >
              <Save className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onRemove}
            className="text-slate-400 hover:text-red-600 p-1"
            title="Remove task"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  const completedCount = tasks.filter(t => t.completed).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-blue-600" />
            Tasks Checklist
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {completedCount} of {tasks.length} completed • {folder.direction || 'import'} workflow
          </p>
        </div>
        {tasks.length === 0 && defaultTasks.length > 0 && (
          <Button onClick={addDefaultTasks} size="sm" className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-1" />
            Add Default Tasks
          </Button>
        )}
      </div>

      {/* Progress Bar */}
      {tasks.length > 0 && (
        <div className="w-full bg-slate-200 rounded-full h-2">
          <div 
            className="bg-green-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(completedCount / tasks.length) * 100}%` }}
          />
        </div>
      )}

      {/* Task List with Drag & Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {tasks.map((task) => (
              <SortableTaskItem
                key={task.id}
                task={task}
                onToggle={() => toggleTask(task.id)}
                onRemove={() => removeTask(task.id)}
                onSaveTemplate={() => saveAsTemplate(task.label)}
                isTemplate={!!taskTemplates.find(t => t.label === task.label)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add New Task */}
      <div className="border rounded-lg p-4 bg-slate-50">
        <Label className="text-sm font-medium text-slate-700 mb-2 block">Add Task</Label>
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            placeholder="Enter task name..."
            value={newTaskLabel}
            onChange={(e) => setNewTaskLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTask(newTaskLabel, null, newTaskDate, newTaskAssignee, newTaskComment)}
            data-testid="new-task-name-input"
            className="flex-1 min-w-[220px]"
          />
          <input
            type="date"
            value={newTaskDate}
            onChange={(e) => setNewTaskDate(e.target.value)}
            data-testid="new-task-due-date-input"
            title="Due date (optional)"
            className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <select
            value={newTaskAssignee}
            onChange={(e) => setNewTaskAssignee(e.target.value)}
            data-testid="new-task-assignee-input"
            title="Delegate to staff member (optional)"
            className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400 max-w-[170px]"
          >
            <option value="">Unassigned</option>
            {staffList.map(s => (
              <option key={s.email} value={s.email}>
                {s.full_name || s.name || s.email.split("@")[0]}
              </option>
            ))}
          </select>
          <Button onClick={() => addTask(newTaskLabel, null, newTaskDate, newTaskAssignee, newTaskComment)} disabled={!newTaskLabel.trim()} data-testid="add-task-btn">
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <Input
          placeholder="+ note (eg. TRK reg. nr, special instruction)"
          value={newTaskComment}
          onChange={(e) => setNewTaskComment(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addTask(newTaskLabel, null, newTaskDate, newTaskAssignee, newTaskComment)}
          data-testid="new-task-comment-input"
          className="mt-2 text-sm"
        />

        {/* Quick Add from Templates/Defaults */}
        {(taskTemplates.length > 0 || defaultTasks.length > 0) && (
          <div className="mt-3">
            <Label className="text-xs text-slate-500 mb-2 block">Quick Add:</Label>
            <div className="flex flex-wrap gap-2">
              {[...new Set([
                ...defaultTasks.map(t => t.label),
                ...taskTemplates.map(t => t.label)
              ])].filter(label => !tasks.find(t => t.label === label)).slice(0, 8).map((label) => (
                <button
                  key={label}
                  onClick={() => addTask(label)}
                  className="px-3 py-1 text-sm bg-white border rounded-full hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  + {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {tasks.length === 0 && (
        <div className="text-center py-8 bg-slate-50 rounded-lg">
          <ListTodo className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No tasks yet</p>
          <p className="text-sm text-slate-400 mt-1">
            Add default tasks or create custom ones
          </p>
        </div>
      )}
    </div>
  );
}

const STATUS_COLORS = {
  "Odprto": "bg-green-100 text-green-800",
  "Preneseno v TLM": "bg-blue-100 text-blue-800",
  "Zaključeno": "bg-gray-100 text-gray-800",
};

const STATUS_LABELS = {
  "Odprto": "Open",
  "Preneseno v TLM": "Transferred to TLM",
  "Zaključeno": "Closed",
};

export default function PositionFoldersPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("orders");
  const [drafts, setDrafts] = useState([]);
  const [folders, setFolders] = useState([]);
  // 2026-05-16 — per-PF CO2 badge map { pf_id: co2_kg|null }
  const [co2Map, setCo2Map] = useState({});
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // desc = newest first, asc = oldest first
  const [sortBy, setSortBy] = useState("pf_number"); // pf_number or date
  const [stats, setStats] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [showMyPFsOnly, setShowMyPFsOnly] = useState(false); // Toggle for My PFs / All PFs
  const [viewMode, setViewMode] = useState("working"); // "working" or "pending" (Freightlinks queue)
  const [pendingPFs, setPendingPFs] = useState([]); // Freightlinks queue items
  const [pendingLoading, setPendingLoading] = useState(false);
  const [showAddToMasterModal, setShowAddToMasterModal] = useState(false);
  const [selectedPendingItem, setSelectedPendingItem] = useState(null);
  const [selectedPendingItems, setSelectedPendingItems] = useState([]); // Multi-select for bulk linking
  const [masterPFsList, setMasterPFsList] = useState([]);
  const [loadingMasterPFs, setLoadingMasterPFs] = useState(false);
  const [creatingFromPending, setCreatingFromPending] = useState(false);
  const [masterPfSearch, setMasterPfSearch] = useState(""); // Search filter for Master PF modal;
  const [pendingToDelete, setPendingToDelete] = useState(null); // Item pending deletion confirmation
  const [deletingPending, setDeletingPending] = useState(false); // Loading state for delete
  const [pfToDelete, setPfToDelete] = useState(null); // PF pending deletion (admin only)
  const [deletingPf, setDeletingPf] = useState(false); // Loading state for PF delete
  const [purgeFolders, setPurgeFolders] = useState([]); // Soft-deleted PFs in purge folder
  const [restoringPf, setRestoringPf] = useState(null); // PF ID being restored
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // Preview of duplicates before import
  const [importMode, setImportMode] = useState("skip"); // "fresh", "update", "skip"
  const [previewingImport, setPreviewingImport] = useState(false);
  const [editingPfNumber, setEditingPfNumber] = useState(null); // PF ID being edited
  const [newPfNumber, setNewPfNumber] = useState(""); // New PF number value
  const [savingPfNumber, setSavingPfNumber] = useState(false);
  const [expandedMasterPFs, setExpandedMasterPFs] = useState({}); // Track which master PFs are expanded
  const [loadingSubOrders, setLoadingSubOrders] = useState({}); // Track loading state for sub-orders
  const [subOrdersCache, setSubOrdersCache] = useState({}); // Cache fetched sub-orders
  const [subOrdersError, setSubOrdersError] = useState({}); // Track fetch errors for sub-orders
  const [createPFMode, setCreatePFMode] = useState("manual"); // "manual" | "email" | "excel" | "maersk"
  const [maerskTemplatePf, setMaerskTemplatePf] = useState("PF-000763-26"); // Template PF to clone for Maersk bookings
  const [maerskFile, setMaerskFile] = useState(null);
  const [maerskImporting, setMaerskImporting] = useState(false);
  const [maerskResult, setMaerskResult] = useState(null);
  const [myTasks, setMyTasks] = useState([]); // To-dos and activities
  const [feedStaffList, setFeedStaffList] = useState([]); // Staff list for inline feed reassign
  const [feedScope, setFeedScope] = useState(() => localStorage.getItem("tasksFeedScope") || "mine"); // "mine" | "all"
  const [etaKoperArrivals, setEtaKoperArrivals] = useState([]); // ETA Koper next 10 days
  const [splitPosition, setSplitPosition] = useState(() => {
    const saved = localStorage.getItem('pf_splitPosition');
    return saved ? parseFloat(saved) : 45;
  });
  const [etaSplitPosition, setEtaSplitPosition] = useState(() => {
    const saved = localStorage.getItem('pf_etaSplitPosition');
    return saved ? parseFloat(saved) : 70;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingEta, setIsDraggingEta] = useState(false);
  const [isPanelLocked, setIsPanelLocked] = useState(() => {
    return localStorage.getItem('pf_panelLocked') === 'true';
  });
  
  // Save panel positions to localStorage
  useEffect(() => {
    localStorage.setItem('pf_splitPosition', splitPosition.toString());
  }, [splitPosition]);
  
  useEffect(() => {
    localStorage.setItem('pf_etaSplitPosition', etaSplitPosition.toString());
  }, [etaSplitPosition]);
  
  useEffect(() => {
    localStorage.setItem('pf_panelLocked', isPanelLocked.toString());
  }, [isPanelLocked]);

  // Handle drag resize for main split (PFs vs Tasks+ETA)
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || isPanelLocked) return;
      const container = document.getElementById('pf-split-container');
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const newPosition = ((e.clientY - rect.top) / rect.height) * 100;
      setSplitPosition(Math.min(Math.max(newPosition, 10), 95)); // Clamp between 10% and 95%
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    if (isDragging && !isPanelLocked) {
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isPanelLocked]);

  // Handle drag resize for ETA split (Tasks vs ETA)
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingEta || isPanelLocked) return;
      const container = document.getElementById('tasks-eta-container');
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const newPosition = ((e.clientY - rect.top) / rect.height) * 100;
      setEtaSplitPosition(Math.min(Math.max(newPosition, 20), 90)); // Clamp between 20% and 90%
    };

    const handleMouseUp = () => {
      setIsDraggingEta(false);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    if (isDraggingEta && !isPanelLocked) {
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingEta, isPanelLocked]);
  
  // Modals
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreatePFModal, setShowCreatePFModal] = useState(false);
  const [showExportLclModal, setShowExportLclModal] = useState(false);
  const [editingCargoSubPf, setEditingCargoSubPf] = useState(null); // sub-PF object being cargo-edited from master row
  const [newPFData, setNewPFData] = useState({
    customer_name: "",
    customer_email: "",
    description: "",
    shipping_line: "",
    service_type: "FCL",
    direction: "import", // import, export, trucking
    containers: "",
    mbl_number: "",
    hbl_numbers: "",
    origin_port: "",
    destination_port: "",
    eta: "",
    etd: "",
    vessel_name: "",
    voyage: "",
    seal_number: "",
    booking_number: "",
    bl_structure: "",
    remarks: "",
    is_master: false,
    shipper: "",
    consignee: "",
    // Air freight specific fields
    airline_name: "",
    flight_number: "",
    air_cargo_weight: "",
    // CRM linking
    crm_company_id: null,
    crm_group_id: null,
    // Tracking options
    enable_tracking: true,
    track_containers: true,
    track_mbl: false,
    track_hbl: false,
    notification_emails: "",
    // Tracking sources
    tracking_fcl: true,
    tracking_lcl: false,
    tracking_port: true,
    tracking_metrans: false,
    // Trucking-only details (collected when service_type === "TRUCKING")
    trucking_details: {
      trucking_company: "",
      truck_plate: "",
      trailer_plate: "",
      driver_name: "",
      driver_phone: "",
      dispatcher_name: "",
      dispatcher_phone: "",
      dispatcher_email: "",
      notes: "",
    },
  });
  const [creatingPF, setCreatingPF] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicatePFs, setDuplicatePFs] = useState([]);
  const [mergeFolder, setMergeFolder] = useState(null);
  const [mergeOtherId, setMergeOtherId] = useState("");
  const [mergeKeepId, setMergeKeepId] = useState("");
  const [mergeReason, setMergeReason] = useState("");
  const [merging, setMerging] = useState(false);
  const [parsingEmail, setParsingEmail] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  
  // CRM Contact confirmation modal
  const [showContactModal, setShowContactModal] = useState(false);
  const [pendingContact, setPendingContact] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  
  // CRM Company/Group selection for PF creation
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyGroups, setCompanyGroups] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [creditCheck, setCreditCheck] = useState(null);
  const [creditChecking, setCreditChecking] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  
  // Add-existing-PFs-to-master modal state
  const [addPFsMaster, setAddPFsMaster] = useState(null);
  const [addPFsSearch, setAddPFsSearch] = useState("");
  const [addPFsAvailable, setAddPFsAvailable] = useState([]);
  const [addPFsSelected, setAddPFsSelected] = useState([]);
  const [addPFsLoading, setAddPFsLoading] = useState(false);
  const [attachingPFs, setAttachingPFs] = useState(false);
  const [togglingConsol, setTogglingConsol] = useState({});

  const fetchAvailablePFs = async (search = "") => {
    setAddPFsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/position-folders/available-for-lcl`, {
        headers: { Authorization: `Bearer ${token}` },
        params: search ? { search } : {},
      });
      setAddPFsAvailable(res.data.items || []);
    } catch (e) {
      toast.error("Failed to load available PFs");
    } finally {
      setAddPFsLoading(false);
    }
  };

  const openAddPFsModal = (master) => {
    setAddPFsMaster(master);
    setAddPFsSelected([]);
    setAddPFsSearch("");
    fetchAvailablePFs("");
  };

  const handleAttachPFs = async () => {
    if (!addPFsMaster || addPFsSelected.length === 0) return;
    setAttachingPFs(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/position-folders/${addPFsMaster.id}/attach-sub-pfs`,
        { sub_pf_ids: addPFsSelected },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || "PFs added to consolidation");
      // Refresh cached sub-orders + folder list so the count updates
      setSubOrdersCache((prev) => ({ ...prev, [addPFsMaster.id]: undefined }));
      if (expandedMasterPFs[addPFsMaster.id]) {
        const so = await axios.get(`${API_URL}/position-folders/${addPFsMaster.id}/sub-orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubOrdersCache((prev) => ({ ...prev, [addPFsMaster.id]: so.data.sub_orders || so.data || [] }));
      }
      setAddPFsMaster(null);
      fetchFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add PFs");
    } finally {
      setAttachingPFs(false);
    }
  };

  const handleToggleConsolidation = async (master) => {
    const reopen = !!master.lcl_consolidation_closed;
    setTogglingConsol((p) => ({ ...p, [master.id]: true }));
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/position-folders/${master.id}/close-consolidation`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, params: { reopen } }
      );
      toast.success(res.data.message);
      fetchFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update consolidation");
    } finally {
      setTogglingConsol((p) => ({ ...p, [master.id]: false }));
    }
  };

  // Sub-order modal state
  const [showSubOrderModal, setShowSubOrderModal] = useState(false);
  const [subOrderParent, setSubOrderParent] = useState(null);
  const [subOrderData, setSubOrderData] = useState({
    customer_name: "",
    customer_email: "",
    description: "",
    hbl_number: "",
    shipper: "",
    consignee: "",
    commodity: "",
    packages: "",
    weight: "",
    volume: "",
    remarks: "",
    pantheon_id: "",
    pantheon_name: "",
  });
  const [subOrderPantheonCompany, setSubOrderPantheonCompany] = useState(null);
  const [creatingSub, setCreatingSub] = useState(false);
  const [subOrders, setSubOrders] = useState([]);
  
  // Customer email auto-suggest state
  const [suggestedEmails, setSuggestedEmails] = useState([]);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  
  const token = localStorage.getItem("token");

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/email/drafts?section=position_folder&status=draft&page=${page}&per_page=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDrafts(response.data.drafts);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error("Failed to load email drafts");
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  // Lookup customer emails from previous PFs
  const lookupCustomerEmails = useCallback(async (customerName) => {
    if (!customerName || customerName.length < 3) {
      setSuggestedEmails([]);
      setShowEmailSuggestions(false);
      return;
    }
    
    try {
      const response = await axios.get(
        `${API_URL}/position-folders/customer-emails?customer_name=${encodeURIComponent(customerName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const allEmails = [...new Set([
        ...response.data.emails,
        ...response.data.notification_emails
      ])];
      
      if (allEmails.length > 0) {
        setSuggestedEmails(allEmails);
        setShowEmailSuggestions(true);
      } else {
        setSuggestedEmails([]);
        setShowEmailSuggestions(false);
      }
    } catch (error) {
      console.error("Failed to lookup customer emails:", error);
    }
  }, [token]);

  // Debounce customer name changes to lookup emails
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (newPFData.customer_name && !newPFData.customer_email) {
        lookupCustomerEmails(newPFData.customer_name);
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [newPFData.customer_name, newPFData.customer_email, lookupCustomerEmails]);

  // Credit / SOA check: when a Pantheon company is selected on the Create-PF
  // form, hit the finance credit check endpoint so we can surface a warning
  // banner about any overdue invoices BEFORE the PF is opened.
  useEffect(() => {
    const companyId = selectedCompany?.type !== "group" ? selectedCompany?.id : null;
    if (!companyId) {
      setCreditCheck(null);
      return;
    }
    let cancelled = false;
    setCreditChecking(true);
    setCreditCheck(null);
    axios
      .post(
        `${API_URL}/finance/credit-check/${companyId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((r) => { if (!cancelled) setCreditCheck(r.data); })
      .catch(() => { if (!cancelled) setCreditCheck(null); })
      .finally(() => { if (!cancelled) setCreditChecking(false); });
    return () => { cancelled = true; };
  }, [selectedCompany, token]);


  // Fetch CRM company options for PF creation
  const fetchCompanyOptions = useCallback(async (query = "") => {
    setLoadingCompanies(true);
    try {
      const response = await axios.get(
        `${API_URL}/crm/company-groups/pf-selection/options?q=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanyOptions(response.data || []);
    } catch (error) {
      console.error("Failed to fetch company options:", error);
    } finally {
      setLoadingCompanies(false);
    }
  }, [token]);

  // Fetch company groups
  const fetchCompanyGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/crm/company-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyGroups(response.data || []);
    } catch (error) {
      console.error("Failed to fetch company groups:", error);
    }
  }, [token]);

  // Load company options when modal opens
  useEffect(() => {
    if (showCreatePFModal) {
      fetchCompanyOptions("");
      fetchCompanyGroups();
    }
  }, [showCreatePFModal, fetchCompanyOptions, fetchCompanyGroups]);

  // Debounce company search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (companySearchQuery) {
        fetchCompanyOptions(companySearchQuery);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [companySearchQuery, fetchCompanyOptions]);

  const handleDeleteDraft = async (e, draftId) => {
    e.stopPropagation(); // Prevent opening the draft detail
    if (!window.confirm("Are you sure you want to delete this email?")) return;
    
    try {
      await axios.delete(`${API_URL}/email/draft/${draftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Email deleted successfully");
      // Remove from local state
      setDrafts(prev => prev.filter(d => d.id !== draftId));
    } catch (error) {
      toast.error("Failed to delete email");
    }
  };

  const fetchFolders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20, sort_order: sortOrder, sort_by: sortBy });
      if (searchQuery) params.append("search", searchQuery);
      // Tab-driven default status filter. The dropdown `statusFilter`
      // overrides this when the user picks a specific status.
      //   orders  → only Open (Odprto)
      //   archive → closed-side statuses (Zaključeno + Preneseno v TLM)
      //   all     → no status filter (everything except soft-deleted)
      let effectiveStatus = statusFilter;
      if (!effectiveStatus) {
        if (activeTab === "orders") effectiveStatus = "Odprto";
        else if (activeTab === "archive") effectiveStatus = "Zaključeno,Preneseno v TLM";
        // "all" → leave empty
      }
      if (effectiveStatus) params.append("status", effectiveStatus);
      if (showMyPFsOnly) params.append("assignee", user?.email || "");
      
      const response = await axios.get(
        `${API_URL}/position-folders?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFolders(response.data.folders);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error("Failed to load position folders");
    } finally {
      setLoading(false);
    }
  }, [token, page, searchQuery, statusFilter, sortOrder, sortBy, showMyPFsOnly, user, activeTab]);

  // 2026-05-16 — bulk CO2 fetch for the visible PF rows (badge next to status pill)
  useEffect(() => {
    if (!folders || folders.length === 0) { setCo2Map({}); return; }
    let cancelled = false;
    const ids = folders.map(f => f.id).filter(Boolean);
    if (ids.length === 0) return;
    (async () => {
      try {
        const r = await axios.post(
          `${API_URL}/position-folders/co2-batch`,
          { pf_ids: ids },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setCo2Map(r.data?.results || {});
      } catch {
        if (!cancelled) setCo2Map({});
      }
    })();
    return () => { cancelled = true; };
  }, [folders, token]);

  // Fetch purge folder (soft-deleted PFs)
  const fetchPurgeFolders = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(
        `${API_URL}/position-folders/purge`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPurgeFolders(response.data.folders || []);
    } catch (error) {
      console.error("Failed to load purge folder:", error);
    }
  }, [token, isAdmin]);

  // Restore PF from purge
  const handleRestorePF = async (pf) => {
    const pfNumber = pf.pf_number || pf.id;
    setRestoringPf(pfNumber);
    try {
      await axios.post(
        `${API_URL}/position-folders/${pfNumber}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Restored ${pfNumber}`);
      fetchPurgeFolders();
      fetchFolders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore PF");
    } finally {
      setRestoringPf(null);
    }
  };

  // Permanently delete from purge
  const handlePermanentDelete = async (pf) => {
    const pfNumber = pf.pf_number || pf.id;
    if (!window.confirm(`Permanently delete ${pfNumber}? This cannot be undone!`)) return;
    
    setDeletingPf(true);
    try {
      await axios.delete(
        `${API_URL}/position-folders/${pfNumber}?confirm=true&permanent=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Permanently deleted ${pfNumber}`);
      fetchPurgeFolders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete PF");
    } finally {
      setDeletingPf(false);
    }
  };

  // Refresh all data
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchFolders(),
        fetchStats(),
        fetchPendingPFs(),
        fetchDrafts(),
        fetchEtaKoperOverview(),
      ]);
      toast.success("Data refreshed");
    } catch (error) {
      toast.error("Failed to refresh data");
    } finally {
      setRefreshing(false);
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/position-folders/stats/summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats");
    }
  }, [token]);

  // Fetch user's tasks/to-dos from PFs
  const fetchMyTasks = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/position-folders/my-tasks?scope=${feedScope}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMyTasks(response.data.tasks || []);
    } catch (error) {
      // Silently fail - tasks panel is optional
      console.log("Tasks endpoint not available");
    }
  }, [token, feedScope]);

  // Persist scope choice
  useEffect(() => {
    localStorage.setItem("tasksFeedScope", feedScope);
  }, [feedScope]);

  // Re-fetch when scope toggles
  useEffect(() => {
    if (token) fetchMyTasks();
  }, [feedScope, token, fetchMyTasks]);

  // Load staff list once for the inline feed reassign dropdown
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/users/staff`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setFeedStaffList(r.data || []))
      .catch(() => {});
  }, [token]);

  // Reassign a feed task inline (PATCH a single PF task field)
  const reassignFeedTask = async (task, newAssignee) => {
    if (!task?.pf_id || !task?.id) {
      toast.error("Cannot reassign: missing task IDs");
      return;
    }
    // Optimistic update
    setMyTasks(prev => prev.map(t => t.id === task.id ? { ...t, assigned_to: newAssignee || null } : t));
    try {
      await axios.patch(
        `${API_URL}/position-folders/${task.pf_id}/tasks/${task.id}`,
        { assigned_to: newAssignee || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newAssignee ? `Reassigned to ${newAssignee.split('@')[0]}` : "Unassigned");
      fetchMyTasks();
    } catch (e) {
      toast.error("Failed to reassign task");
      fetchMyTasks();
    }
  };

  // Update a feed task's due date inline (PATCH)
  const updateFeedTaskDate = async (task, newDate) => {
    if (!task?.pf_id || !task?.id) {
      toast.error("Cannot update: missing task IDs");
      return;
    }
    setMyTasks(prev => prev.map(t => t.id === task.id ? { ...t, due_date: newDate || null } : t));
    try {
      await axios.patch(
        `${API_URL}/position-folders/${task.pf_id}/tasks/${task.id}`,
        { due_date: newDate || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newDate ? `Due date updated` : "Due date cleared");
      fetchMyTasks();
    } catch (e) {
      toast.error("Failed to update due date");
      fetchMyTasks();
    }
  };

  // Update a feed task's comment/note inline (PATCH)
  const updateFeedTaskComment = async (task, newComment) => {
    if (!task?.pf_id || !task?.id) return;
    setMyTasks(prev => prev.map(t => t.id === task.id ? { ...t, comment: newComment } : t));
    try {
      await axios.patch(
        `${API_URL}/position-folders/${task.pf_id}/tasks/${task.id}`,
        { comment: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      toast.error("Failed to save comment");
      fetchMyTasks();
    }
  };

  // Delete a single PF task from the feed (e.g. test rows the user wants gone).
  const deleteFeedTask = async (task) => {
    if (!task?.pf_id || !task?.id) return;
    if (!window.confirm(`Delete task "${task.title || task.label || task.description || ''}"?`)) return;
    const prev = myTasks;
    setMyTasks(prev.filter(t => t.id !== task.id));
    try {
      await axios.delete(
        `${API_URL}/position-folders/${task.pf_id}/tasks/${task.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Task deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete task");
      setMyTasks(prev); // rollback
    }
  };

  // Approve/Dismiss admin tasks (e.g. PF deletion requests) right from the feed.
  const approveAdminTask = async (task) => {
    if (!task?.id) return;
    const pfLabel = task.pf_number ? ` (${task.pf_number})` : "";
    if (!window.confirm(`Approve "${task.title}"${pfLabel}? This will recycle the PF and notify ${task.requested_by || 'the requester'}.`)) return;
    try {
      const resp = await axios.post(
        `${API_URL}/admin-tasks/${task.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const r = resp.data?.resolution || "approved";
      const subs = resp.data?.sub_orders_deleted || 0;
      toast.success(`Approved — ${task.pf_number} ${r === "approved" ? `recycled${subs ? ` (+${subs} sub-orders)` : ""}` : r.replace(/_/g, " ")}`);
      setMyTasks(prev => prev.filter(t => t.id !== task.id));
      fetchFolders?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve task");
    }
  };

  // Delegate (reassign) an admin task to a colleague.
  const delegateAdminTask = async (task, newAssignee) => {
    if (!task?.id) return;
    const assignee = (newAssignee || "").trim() || null;
    const prev = myTasks;
    setMyTasks(prev.map(t => t.id === task.id ? { ...t, assigned_to: assignee } : t));
    try {
      await axios.patch(
        `${API_URL}/admin-tasks/${task.id}`,
        { assigned_to: assignee },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(assignee ? `Delegated to ${assignee.split('@')[0]}` : "Unassigned");
      fetchMyTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delegate task");
      setMyTasks(prev);
    }
  };

  // Update the free-text note (e.g., TRK reg number) on an admin task.
  const updateAdminTaskNote = async (task, newNote) => {
    if (!task?.id) return;
    setMyTasks(prev => prev.map(t => t.id === task.id ? { ...t, note: newNote } : t));
    try {
      await axios.patch(
        `${API_URL}/admin-tasks/${task.id}`,
        { note: newNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      toast.error("Failed to save note");
      fetchMyTasks();
    }
  };

  const dismissAdminTask = async (task) => {
    if (!task?.id) return;
    const reason = window.prompt(`Dismiss "${task.title}". Reason for the requester (optional):`);
    if (reason === null) return; // cancelled
    try {
      await axios.post(
        `${API_URL}/admin-tasks/${task.id}/dismiss`,
        { reason: (reason || "").trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Dismissed — ${task.requested_by || "requester"} notified`);
      setMyTasks(prev => prev.filter(t => t.id !== task.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to dismiss task");
    }
  };

  // Open the PF detail panel for a task in the feed. Falls back to fetching
  // the folder by id when it's not in the currently loaded folders list.
  const openFeedTaskPF = async (task) => {
    try {
      if (task.type === 'email_draft') {
        navigate(`/email-repository?email=${task.email_id}`);
        return;
      }
      if (task.transport_order_id || task.source === 'transport_order') {
        navigate(`/transport-orders?highlight=${task.transport_order_id || task.order_number}`);
        return;
      }
      if (!task.pf_number && !task.pf_id) return;
      let folder = folders.find(f => f.pf_number === task.pf_number || f.id === task.pf_id);
      if (!folder && task.pf_id) {
        try {
          const resp = await axios.get(
            `${API_URL}/position-folders/${task.pf_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          folder = resp.data;
        } catch {
          // fall through to search navigation
        }
      }
      if (folder) {
        await handleSelectFolder(folder);
        // Bring the detail panel into view
        setTimeout(() => {
          const el = document.querySelector('[data-testid="pf-detail-panel"]')
            || document.querySelector('#pf-detail-panel');
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else if (task.pf_number) {
        navigate(`/position-folders?search=${task.pf_number}`);
      }
    } catch (e) {
      console.error("openFeedTaskPF failed", e);
    }
  };


  // Fetch ETA Koper arrivals for next 10 days
  const fetchEtaKoperOverview = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/position-folders/eta-koper-overview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEtaKoperArrivals(response.data.by_date || []);
    } catch (error) {
      // Silently fail - ETA panel is optional
      console.log("ETA Koper endpoint not available");
    }
  }, [token]);

  // Fetch Pending PFs (Freightlinks queue)
  const fetchPendingPFs = useCallback(async () => {
    setPendingLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/lcl-status/queue`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingPFs(response.data.items || []);
    } catch (error) {
      console.log("Freightlinks queue not available");
      setPendingPFs([]);
    } finally {
      setPendingLoading(false);
    }
  }, [token]);

  const fetchAssignees = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/position-folders/assignees`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAssignees(response.data);
    } catch (error) {
      console.error("Failed to load assignees");
    }
  }, [token]);

  useEffect(() => {
    fetchAssignees();
    fetchStats();
    fetchMyTasks();
    fetchEtaKoperOverview();
    fetchPendingPFs();
  }, [fetchAssignees, fetchStats, fetchMyTasks, fetchEtaKoperOverview, fetchPendingPFs]);

  // Fetch Master PFs for the Add to Master modal
  const fetchMasterPFs = useCallback(async () => {
    setLoadingMasterPFs(true);
    try {
      const response = await axios.get(
        `${API_URL}/position-folders?is_master=true&status=Odprto&per_page=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMasterPFsList(response.data.folders || []);
    } catch (error) {
      console.error("Failed to load master PFs");
      setMasterPFsList([]);
    } finally {
      setLoadingMasterPFs(false);
    }
  }, [token]);

  // Handler: Create PF from pending Freightlinks item
  const handleCreatePFFromPending = async (pendingItem) => {
    const queueId = pendingItem.id || pendingItem._id;
    if (!queueId) {
      toast.error("Invalid queue item ID");
      return;
    }
    
    setCreatingFromPending(true);
    try {
      const response = await axios.post(
        `${API_URL}/lcl-status/queue/${queueId}/create-pf`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Created PF: ${response.data.pf_number}`);
      // Refresh both lists
      fetchPendingPFs();
      fetchFolders();
      fetchStats();
      // Switch to working view to see the new PF
      setViewMode("working");
    } catch (error) {
      console.error("Failed to create PF from pending:", error);
      toast.error(error.response?.data?.detail || "Failed to create Position Folder");
    } finally {
      setCreatingFromPending(false);
    }
  };

  // Handler: Delete pending Freightlinks item (with confirmation)
  const handleDeletePending = async () => {
    if (!pendingToDelete) return;
    
    const queueId = pendingToDelete.id || pendingToDelete._id;
    if (!queueId) {
      toast.error("Invalid queue item ID");
      return;
    }
    
    setDeletingPending(true);
    try {
      await axios.delete(
        `${API_URL}/lcl-status/queue/${queueId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Deleted pending shipment: ${pendingToDelete.hbl_number || queueId}`);
      // Refresh the list
      fetchPendingPFs();
      // Clear the confirmation state
      setPendingToDelete(null);
    } catch (error) {
      console.error("Failed to delete pending:", error);
      toast.error(error.response?.data?.detail || "Failed to delete pending shipment");
    } finally {
      setDeletingPending(false);
    }
  };

  // Handler: Delete Position Folder (Admin only - Soft Delete)
  const handleDeletePF = async () => {
    if (!pfToDelete || !isAdmin) return;
    
    const pfNumber = pfToDelete.pf_number || pfToDelete.id;
    
    setDeletingPf(true);
    try {
      const response = await axios.delete(
        `${API_URL}/position-folders/${pfNumber}?confirm=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show different message for soft delete vs permanent delete
      if (response.data.deletion_type === "soft") {
        toast.success(`Moved ${pfNumber} to purge folder. Will be permanently deleted on ${new Date(response.data.purge_date).toLocaleDateString()}`);
      } else {
        toast.success(`Permanently deleted ${pfNumber} and ${response.data.sub_orders_deleted || 0} sub-orders`);
      }
      // Close dialog first, then refresh
      setPfToDelete(null);
      setSelectedFolder(null);
      fetchFolders();
      // Refresh purge folder count
      fetchPurgeFolders();
    } catch (error) {
      console.error("Failed to delete PF:", error);
      toast.error(error.response?.data?.detail || "Failed to delete Position Folder");
    } finally {
      setDeletingPf(false);
    }
  };

  // Handler: Recycle Position Folder - clear data and make number available for reuse
  const [recyclingPf, setRecyclingPf] = useState(false);
  
  const handleRecyclePF = async () => {
    if (!pfToDelete) return;
    
    const pfNumber = pfToDelete.pf_number || pfToDelete.id;
    
    setRecyclingPf(true);
    try {
      const response = await axios.post(
        `${API_URL}/position-folders/${pfNumber}/recycle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      // Close dialog first, then refresh
      setPfToDelete(null);
      setSelectedFolder(null);
      fetchFolders();
    } catch (error) {
      console.error("Failed to recycle PF:", error);
      toast.error(error.response?.data?.detail || "Failed to recycle Position Folder");
    } finally {
      setRecyclingPf(false);
    }
  };

  // Handler: Request Deletion (for non-admin staff)
  const [deletionReason, setDeletionReason] = useState("");
  const [requestingDeletion, setRequestingDeletion] = useState(false);
  
  const handleRequestDeletion = async () => {
    if (!pfToDelete) return;
    
    const pfNumber = pfToDelete.pf_number || pfToDelete.id;
    
    setRequestingDeletion(true);
    try {
      const response = await axios.post(
        `${API_URL}/position-folders/${pfNumber}/request-deletion`,
        { reason: deletionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Deletion request for ${pfNumber} sent to administrator`);
      setPfToDelete(null);
      setDeletionReason("");
    } catch (error) {
      console.error("Failed to request deletion:", error);
      // If PF not found, close the modal and refresh the list
      if (error.response?.status === 404) {
        toast.error(`${pfNumber} no longer exists. Refreshing list...`);
        setPfToDelete(null);
        setDeletionReason("");
        fetchFolders();
      } else {
        toast.error(error.response?.data?.detail || "Failed to send deletion request");
      }
    } finally {
      setRequestingDeletion(false);
    }
  };

  // Handler: Open Add to Master modal (single or bulk)
  const handleOpenAddToMasterModal = (pendingItem = null) => {
    if (pendingItem) {
      // Single item mode
      setSelectedPendingItem(pendingItem);
    }
    // Clear search when opening modal
    setMasterPfSearch("");
    fetchMasterPFs();
    setShowAddToMasterModal(true);
  };

  // Handler: Toggle selection of a pending item for bulk operations
  const handleTogglePendingSelection = (item) => {
    const itemId = item.id || item._id;
    setSelectedPendingItems(prev => {
      if (prev.some(i => (i.id || i._id) === itemId)) {
        return prev.filter(i => (i.id || i._id) !== itemId);
      }
      return [...prev, item];
    });
  };

  // Handler: Select all pending items
  const handleSelectAllPending = () => {
    if (selectedPendingItems.length === pendingPFs.length) {
      setSelectedPendingItems([]);
    } else {
      setSelectedPendingItems([...pendingPFs]);
    }
  };

  // Handler: Link pending item to an existing Master PF (single or bulk)
  const handleLinkToMasterPF = async (masterPfId) => {
    // Check if we're doing bulk linking
    if (selectedPendingItems.length > 0) {
      await handleBulkLinkToMasterPF(masterPfId);
      return;
    }
    
    // Single item linking
    if (!selectedPendingItem) return;
    
    const queueId = selectedPendingItem.id || selectedPendingItem._id;
    if (!queueId) {
      toast.error("Invalid queue item ID");
      return;
    }
    
    setCreatingFromPending(true);
    try {
      const response = await axios.post(
        `${API_URL}/lcl-status/queue/${queueId}/link`,
        { pf_id: masterPfId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Linked to PF: ${response.data.pf_number}`);
      setShowAddToMasterModal(false);
      setSelectedPendingItem(null);
      setMasterPfSearch("");
      // Refresh lists
      fetchPendingPFs();
      fetchFolders();
    } catch (error) {
      console.error("Failed to link to Master PF:", error);
      toast.error(error.response?.data?.detail || "Failed to link to Master PF");
    } finally {
      setCreatingFromPending(false);
    }
  };

  // Handler: Bulk link multiple pending items to a Master PF
  const handleBulkLinkToMasterPF = async (masterPfId) => {
    if (selectedPendingItems.length === 0) {
      toast.error("No items selected");
      return;
    }
    
    const queueIds = selectedPendingItems.map(item => item.id || item._id).filter(Boolean);
    if (queueIds.length === 0) {
      toast.error("Invalid queue item IDs");
      return;
    }
    
    setCreatingFromPending(true);
    try {
      const response = await axios.post(
        `${API_URL}/lcl-status/queue/bulk-link`,
        { queue_ids: queueIds, pf_id: masterPfId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Linked ${response.data.linked_count} shipment(s) to PF: ${response.data.pf_number}`);
      
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} shipment(s) failed to link`);
      }
      
      setShowAddToMasterModal(false);
      setSelectedPendingItem(null);
      setSelectedPendingItems([]);
      setMasterPfSearch("");
      // Refresh lists
      fetchPendingPFs();
      fetchFolders();
    } catch (error) {
      console.error("Failed to bulk link to Master PF:", error);
      toast.error(error.response?.data?.detail || "Failed to link shipments to Master PF");
    } finally {
      setCreatingFromPending(false);
    }
  };

  // Handler: Import Maersk booking Excel — clone a template PF per row
  const handleImportMaersk = async () => {
    if (!maerskTemplatePf.trim()) { toast.error("Enter the template PF number to clone"); return; }
    if (!maerskFile) { toast.error("Upload the Maersk shipments Excel file"); return; }
    setMaerskImporting(true);
    setMaerskResult(null);
    try {
      const formData = new FormData();
      formData.append("file", maerskFile);
      formData.append("template_pf_number", maerskTemplatePf.trim());
      const response = await axios.post(
        `${API_URL}/position-folders/import-maersk-booking`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      const jobId = response.data.job_id;
      const total = response.data.total;
      toast.success(`Creating ${total} Position Folder(s)…`);
      // Poll status until completed
      const poll = async () => {
        try {
          const st = await axios.get(
            `${API_URL}/position-folders/import-maersk-booking/status/${jobId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setMaerskResult(st.data);
          if (st.data.status === "completed") {
            toast.success(`Done — created ${st.data.created_count} PF(s)${st.data.error_count ? `, ${st.data.error_count} failed` : ""}`);
            fetchFolders();
            fetchStats();
            setMaerskImporting(false);
          } else {
            setTimeout(poll, 2500);
          }
        } catch (e) {
          setMaerskImporting(false);
          toast.error("Lost track of the import job");
        }
      };
      setTimeout(poll, 2000);
    } catch (error) {
      console.error("Maersk import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import Maersk bookings");
      setMaerskImporting(false);
    }
  };


  // Handler: Import Excel file (Eurasia format)
  const handleImportExcel = async () => {
    if (!importFile) {
      toast.error("Please select a file to import");
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      
      const response = await axios.post(
        `${API_URL}/position-folders/import-excel`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      toast.success(response.data.message || `Imported ${response.data.total_sub_pfs} sub-PFs`);
      setShowCreatePFModal(false);
      setCreatePFMode("manual");
      setImportFile(null);
      // Refresh folders list
      fetchFolders();
      fetchStats();
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import Excel file");
    } finally {
      setImporting(false);
    }
  };

  // Preview import to check for duplicates
  const handleImportPreview = async (file) => {
    if (!file) return;
    
    setPreviewingImport(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(
        `${API_URL}/import/position-folders/preview`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      setImportPreview(response.data);
    } catch (error) {
      console.error("Preview error:", error);
      toast.error(error.response?.data?.detail || "Failed to preview import file");
    } finally {
      setPreviewingImport(false);
    }
  };

  // Import with selected mode
  const handleImportWithMode = async () => {
    if (!importFile) {
      toast.error("Please select a file to import");
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      
      const response = await axios.post(
        `${API_URL}/import/position-folders?import_mode=${importMode}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      const result = response.data;
      toast.success(
        `Import complete! New: ${result.imported || 0}, Updated: ${result.updated || 0}, Skipped: ${result.skipped || 0}`
      );
      setShowImportModal(false);
      setImportFile(null);
      setImportPreview(null);
      fetchFolders();
      fetchStats();
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import file");
    } finally {
      setImporting(false);
    }
  };

  // Update PF number with customer notification
  const handleUpdatePfNumber = async (pfId) => {
    if (!newPfNumber.trim()) {
      toast.error("Please enter a new PF number");
      return;
    }
    
    setSavingPfNumber(true);
    try {
      const response = await axios.patch(
        `${API_URL}/position-folders/${pfId}/number`,
        { new_pf_number: newPfNumber.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        response.data.customer_notified 
          ? `PF number updated to ${response.data.new_pf_number}. Customer notified at ${response.data.customer_email}`
          : `PF number updated to ${response.data.new_pf_number}`
      );
      
      // Update local state
      setFolders(prev => prev.map(f => 
        f.id === pfId ? { ...f, pf_number: response.data.new_pf_number } : f
      ));
      
      // Update selected folder if it's the same
      if (selectedFolder?.id === pfId) {
        setSelectedFolder(prev => ({ ...prev, pf_number: response.data.new_pf_number }));
      }
      
      setEditingPfNumber(null);
      setNewPfNumber("");
    } catch (error) {
      console.error("Update PF number error:", error);
      toast.error(error.response?.data?.detail || "Failed to update PF number");
    } finally {
      setSavingPfNumber(false);
    }
  };

  useEffect(() => {
    if (activeTab === "drafts") {
      fetchDrafts();
    } else if (activeTab === "orders" || activeTab === "archive" || activeTab === "all") {
      if (viewMode === "working") {
        fetchFolders();
      } else {
        fetchPendingPFs();
      }
    }
  }, [activeTab, fetchDrafts, fetchFolders, fetchPendingPFs, viewMode]);

  const handleUploadEml = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(
        `${API_URL}/email/upload-eml`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Email uploaded! Routed to: ${response.data.target_section}`);
      setShowUploadModal(false);
      fetchDrafts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload email");
    }
  };

  // Robust file extractor for drop events.
  // Chrome on Windows sometimes exposes Outlook drags via dataTransfer.items
  // (kind:"file", getAsFile() returns a virtual .msg) even when
  // dataTransfer.files is empty — so we try BOTH paths before giving up.
  // Returns { file, source, types } so callers can show a useful diagnostic.
  const extractDroppedFile = (e) => {
    const dt = e.dataTransfer;
    if (!dt) return { file: null, source: null, types: [] };

    // 1) Standard path
    if (dt.files && dt.files.length > 0) {
      return { file: dt.files[0], source: "dataTransfer.files", types: [] };
    }

    // 2) DataTransferItemList fallback — Chrome/Edge often exposes Outlook
    //    virtual files here when files[] is empty.
    if (dt.items && dt.items.length > 0) {
      for (let i = 0; i < dt.items.length; i++) {
        const item = dt.items[i];
        if (item && item.kind === "file" && typeof item.getAsFile === "function") {
          const f = item.getAsFile();
          if (f) {
            return { file: f, source: "dataTransfer.items.getAsFile", types: [] };
          }
        }
      }
    }

    // 3) Nothing usable — collect MIME types for diagnostics
    let types = [];
    try { types = dt.types ? Array.from(dt.types) : []; } catch (_) {}
    return { file: null, source: null, types };
  };

  // Parse email file for PF data
  const handleEmailDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    // Try the standard files[] first, then fall back to items[].getAsFile().
    let file = null;
    let diagnosticTypes = [];
    if (e.target?.files && e.target.files.length > 0) {
      // File picker / input change event
      file = e.target.files[0];
    } else {
      const ex = extractDroppedFile(e);
      file = ex.file;
      diagnosticTypes = ex.types;
    }

    if (!file) {
      // Outlook didn't serialise the .msg bytes — only metadata. Read what
      // IT DID give us so we can show the user exactly what's missing.
      const dt = e.dataTransfer;
      let uriList = "";
      let plainText = "";
      try { uriList = (dt?.getData && dt.getData("text/uri-list")) || ""; } catch (_) {}
      try { plainText = (dt?.getData && dt.getData("text/plain")) || ""; } catch (_) {}

      const uri = (uriList || plainText || "").trim();
      const isOwaUri = uri.includes("outlook.office.com") || uri.includes("outlook.live.com") || uri.startsWith("outlook:");

      let msg;
      if (isOwaUri) {
        // Diagnosis: user is on New Outlook for Windows / Outlook on the
        // Web. Microsoft only exposes a deep-link, not the .msg bytes.
        msg = "Looks like you're on \"New Outlook\" — it doesn't share the email file when dragging (only a deep-link).\n\nFix: in Outlook, toggle OFF \"Try the new Outlook\" (top-right corner) to switch back to Classic Outlook, then drag-drop will work again.\n\nWorkaround: drag the email to your desktop first, then drop the .msg file here.";
      } else {
        const uriShort = uri ? uri.substring(0, 120) + (uri.length > 120 ? "…" : "") : "(empty)";
        msg = `Outlook didn't share the .msg file — only metadata.\nURI: ${uriShort}\n\nWorkaround: drag the email to your desktop first, then drop the .msg file here.`;
      }
      toast.error(msg, { duration: 15000 });
      // Also expose to window for copy-paste debugging
      try { window.__lastOutlookDrop = { uriList, plainText, types: diagnosticTypes }; } catch (_) {}
      return;
    }
    const filename = file.name.toLowerCase();

    if (!filename.endsWith('.msg') && !filename.endsWith('.eml') && !filename.endsWith('.pdf')) {
      toast.error("Please drop a .msg, .eml or .pdf file");
      return;
    }

    // Reset input value so picking the same file twice still triggers onChange
    if (e.target && 'value' in e.target) {
      try { e.target.value = ""; } catch (_) {}
    }

    setParsingEmail(true);

    // Clear any data left over from a PREVIOUS parsed email BEFORE we start,
    // so a failed/partial parse never leaves stale values on screen.
    setNewPFData(prev => ({
      ...prev,
      customer_name: "",
      customer_email: "",
      description: "",
      shipping_line: "",
      containers: "",
      mbl_number: "",
      hbl_numbers: "",
      origin_port: "",
      destination_port: "",
      eta: "",
      remarks: "",
      shipper: "",
      consignee: "",
      notification_emails: "",
      track_containers: false,
      track_mbl: false,
      track_hbl: false,
    }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/position-folders/parse-email`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      if (response.data.success) {
        const data = response.data.extracted_data;
        
        // Pre-populate the form with extracted data
        setNewPFData(prev => ({
          ...prev,
          customer_name: data.customer_name || prev.customer_name,
          customer_email: data.customer_email || prev.customer_email,
          description: data.description || prev.description,
          shipping_line: data.shipping_line || prev.shipping_line,
          service_type: data.service_type || prev.service_type,
          containers: (data.containers || []).join(', ') || prev.containers,
          mbl_number: data.mbl_number || prev.mbl_number,
          hbl_numbers: (data.hbl_numbers || []).join(', ') || prev.hbl_numbers,
          origin_port: data.origin_port || prev.origin_port,
          destination_port: data.destination_port || prev.destination_port,
          eta: data.eta || prev.eta,
          remarks: data.remarks || prev.remarks,
          shipper: data.shipper || prev.shipper,
          consignee: data.consignee || prev.consignee,
          // Auto-enable tracking for items found
          track_containers: data.containers && data.containers.length > 0,
          track_mbl: !!data.mbl_number,
          track_hbl: data.hbl_numbers && data.hbl_numbers.length > 0,
          // Set notification emails from TO and CC
          notification_emails: (data.notification_emails || []).join(', ') || prev.notification_emails,
        }));
        
        const notifyCount = data.notification_emails?.length || 0;
        toast.success(`Email parsed! Found: ${data.containers?.length || 0} containers, MBL: ${data.mbl_number || 'N/A'}, HBL: ${data.hbl_numbers?.length || 0}`);
        if (notifyCount > 0) {
          toast.info(`${notifyCount} email(s) added for tracking notifications (from TO/CC)`);
        }
        
        // Show attachments info
        if (response.data.attachments && response.data.attachments.length > 0) {
          toast.info(`Email has ${response.data.attachments.length} attachment(s)`);
        }
        
        // Check if contact needs to be created
        if (response.data.suggested_contact?.needs_confirmation) {
          setPendingContact({
            ...response.data.suggested_contact,
            suggested_company: response.data.crm_contact?.suggested_company,
            suggested_company_id: response.data.crm_contact?.suggested_company_id,
          });
          setShowContactModal(true);
        } else if (response.data.crm_contact?.exists) {
          toast.info(`Contact found: ${response.data.crm_contact.contact_name}`);
        }
      } else {
        toast.error("Failed to extract data from email");
      }
    } catch (error) {
      console.error("Email parsing error:", error);
      toast.error(error.response?.data?.detail || "Failed to parse email");
    } finally {
      setParsingEmail(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  // Save confirmed CRM contact
  const saveConfirmedContact = async () => {
    if (!pendingContact?.email) {
      toast.error("No contact to save");
      return;
    }
    
    setSavingContact(true);
    try {
      await axios.post(
        `${API_URL}/crm/contacts`,
        {
          name: pendingContact.name,
          email: pendingContact.email,
          company_name: pendingContact.company_name,
          phone: pendingContact.phone,
          status: "active",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Contact "${pendingContact.name}" added to CRM`);
      setShowContactModal(false);
      setPendingContact(null);
    } catch (error) {
      console.error("Failed to save contact:", error);
      toast.error(error.response?.data?.detail || "Failed to save contact");
    } finally {
      setSavingContact(false);
    }
  };

  const openMergeModal = (folder) => {
    const dupes = folder.possible_duplicate_of || [];
    setMergeFolder(folder);
    setMergeOtherId(dupes[0]?.id || "");
    setMergeKeepId(folder.id); // default: keep the current PF live
    setMergeReason("Duplicate shipment — merged");
  };

  const confirmMerge = async () => {
    if (!mergeFolder || !mergeOtherId) { toast.error("Pick the PF to merge with"); return; }
    const keep_id = mergeKeepId;
    const drop_id = keep_id === mergeFolder.id ? mergeOtherId : mergeFolder.id;
    setMerging(true);
    try {
      const res = await axios.post(
        `${API_URL}/position-folders/merge`,
        { keep_id, drop_id, reason: mergeReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || "Merge queued for approval");
      setMergeFolder(null);
      fetchFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to merge");
    } finally {
      setMerging(false);
    }
  };

  const unmarkDuplicate = async (folder) => {
    if (!window.confirm(`Mark ${folder.pf_number} as NOT a duplicate and remove the warning?`)) return;
    try {
      await axios.post(
        `${API_URL}/position-folders/${folder.id}/unmark-duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Duplicate warning removed");
      fetchFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove warning");
    }
  };


  const createNewPF = async (skipDupCheck = false, dupDecision = null) => {
    // Validation: Require Pantheon company selection OR customer name for legacy support
    if (!selectedCompany && !newPFData.customer_name) {
      toast.error("Please select a company from Pantheon list");
      return;
    }
    if (!newPFData.description) {
      toast.error("Please fill in shipment description");
      return;
    }
    // Validation: Require at least one recipient email so automated milestone /
    // arrival-notice / delivery emails actually have somewhere to go.
    const ce = (newPFData.customer_email || "").trim();
    const notify = (newPFData.notification_emails || "")
      .split(',').map(e => e.trim()).filter(e => e);
    const looksValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
    const hasRecipient = looksValid(ce) || notify.some(looksValid);
    if (!hasRecipient) {
      toast.error("Customer Email is required (or add at least one Notification Email). Otherwise milestone & arrival emails will silently drop.");
      return;
    }

    // Duplicate PF guard: warn if an OPEN PF already uses the same shipment refs
    if (!skipDupCheck) {
      const idPayload = {
        mbl_number: (newPFData.mbl_number || "").trim(),
        booking_number: (newPFData.booking_number || "").trim(),
        hbl_numbers: newPFData.hbl_numbers.split(',').map(h => h.trim()).filter(Boolean),
        containers: newPFData.containers.split(',').map(c => c.trim()).filter(Boolean),
      };
      if (idPayload.mbl_number || idPayload.booking_number || idPayload.hbl_numbers.length || idPayload.containers.length) {
        try {
          const dupRes = await axios.post(
            `${API_URL}/position-folders/check-duplicate`,
            idPayload,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (dupRes.data && dupRes.data.count > 0) {
            setDuplicatePFs(dupRes.data.duplicates);
            setShowDuplicateWarning(true);
            return; // stop — user must acknowledge before opening a duplicate
          }
        } catch (e) {
          // Non-blocking: if the check fails, allow creation to proceed
        }
      }
    }
    
    setCreatingPF(true);
    try {
      const payload = {
        customer_name: newPFData.customer_name,
        customer_email: newPFData.customer_email,
        description: newPFData.description,
        shipping_line: newPFData.shipping_line,
        service_type: newPFData.service_type,
        direction: newPFData.direction,
        containers: newPFData.containers.split(',').map(c => c.trim()).filter(c => c),
        mbl_number: newPFData.mbl_number,
        hbl_numbers: newPFData.hbl_numbers.split(',').map(h => h.trim()).filter(h => h),
        origin_port: newPFData.origin_port,
        destination_port: newPFData.destination_port,
        eta: newPFData.eta,
        etd: newPFData.etd,
        vessel_name: newPFData.vessel_name,
        voyage: newPFData.voyage,
        seal_number: newPFData.seal_number,
        booking_number: newPFData.booking_number,
        bl_structure: newPFData.bl_structure || null,
        remarks: newPFData.remarks,
        status: "Odprto",
        is_master: newPFData.is_master,
        // Duplicate decision from the warning dialog:
        // "not_duplicate" → do NOT flag; "unsure" → flag as possible duplicate.
        duplicate_ack: dupDecision,
        shipper: newPFData.shipper,
        consignee: newPFData.consignee,
        // Tracking options
        enable_tracking: newPFData.enable_tracking,
        track_containers: newPFData.track_containers,
        track_mbl: newPFData.track_mbl,
        track_hbl: newPFData.track_hbl,
        notification_emails: newPFData.notification_emails.split(',').map(e => e.trim()).filter(e => e),
        // Tracking sources
        tracking_sources: {
          fcl: newPFData.tracking_fcl,
          lcl: newPFData.tracking_lcl,
          port: newPFData.tracking_port,
          metrans: newPFData.tracking_metrans,
          hbl: newPFData.track_hbl,
        },
        // Trucking-specific contacts — only sent when service_type is
        // TRUCKING so non-trucking PFs don't get an empty placeholder.
        ...(newPFData.service_type === "TRUCKING" ? { trucking_details: newPFData.trucking_details } : {}),
      };
      
      const response = await axios.post(
        `${API_URL}/position-folders`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Created Position Folder ${response.data.pf_number}`);
      
      // Show tracking info if enabled
      if (newPFData.enable_tracking && response.data.tracking_added) {
        toast.info(`Added ${response.data.tracking_added} items to tracking watchlist`);
      }
      
      // Show customer notification status
      if (response.data.customer_notified) {
        toast.success("Customer notification email sent!");
      } else if (newPFData.customer_email) {
        toast.warning("Customer notification email could not be sent");
      }
      
      setShowCreatePFModal(false);
      setNewPFData({
        customer_name: "",
        customer_email: "",
        description: "",
        shipping_line: "",
        service_type: "FCL",
        direction: "import",
        containers: "",
        mbl_number: "",
        hbl_numbers: "",
        origin_port: "",
        destination_port: "",
        eta: "",
        etd: "",
        vessel_name: "",
        voyage: "",
        seal_number: "",
        booking_number: "",
        bl_structure: "",
        remarks: "",
        is_master: false,
        shipper: "",
        consignee: "",
        // Air freight specific fields
        airline_name: "",
        flight_number: "",
        air_cargo_weight: "",
        // CRM linking
        crm_company_id: null,
        crm_group_id: null,
        enable_tracking: true,
        track_containers: true,
        track_mbl: false,
        track_hbl: false,
        notification_emails: "",
        tracking_fcl: true,
        tracking_lcl: false,
        tracking_port: true,
        tracking_metrans: false,
        trucking_details: {
          trucking_company: "",
          truck_plate: "",
          trailer_plate: "",
          driver_name: "",
          driver_phone: "",
          dispatcher_name: "",
          dispatcher_phone: "",
          dispatcher_email: "",
          notes: "",
        },
      });
      setActiveTab("orders");
      fetchFolders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create Position Folder");
    } finally {
      setCreatingPF(false);
    }
  };

  // Fetch sub-orders for a master PF
  const fetchSubOrders = async (pfId) => {
    try {
      const response = await axios.get(
        `${API_URL}/position-folders/${pfId}/sub-orders`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubOrders(response.data.sub_orders || []);
    } catch (error) {
      console.error("Failed to fetch sub-orders:", error);
      setSubOrders([]);
    }
  };

  // Fetch + cache sub-orders for a master PF (used by expand and Retry).
  const loadSubOrders = async (pfId) => {
    setLoadingSubOrders(prev => ({ ...prev, [pfId]: true }));
    setSubOrdersError(prev => ({ ...prev, [pfId]: false }));
    try {
      const response = await axios.get(
        `${API_URL}/position-folders/${pfId}/sub-orders`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubOrdersCache(prev => ({ ...prev, [pfId]: response.data.sub_orders || [] }));
    } catch (error) {
      console.error("Failed to fetch sub-orders:", error);
      // Do NOT poison the cache with an empty array on error — mark it so we retry.
      setSubOrdersError(prev => ({ ...prev, [pfId]: true }));
    } finally {
      setLoadingSubOrders(prev => ({ ...prev, [pfId]: false }));
    }
  };

  // Toggle expand/collapse for master PF to show sub-orders
  const toggleMasterPFExpansion = async (folder) => {
    const pfId = folder.id;
    const isExpanded = expandedMasterPFs[pfId];

    if (isExpanded) {
      setExpandedMasterPFs(prev => ({ ...prev, [pfId]: false }));
    } else {
      setExpandedMasterPFs(prev => ({ ...prev, [pfId]: true }));
      // Refetch when we have no cached result yet OR the previous attempt errored.
      if (!subOrdersCache[pfId] || subOrdersError[pfId]) {
        await loadSubOrders(pfId);
      }
    }
  };

  // Manual "Send to CargoCFS" trigger for an existing PF
  const [sendingToCargoCFS, setSendingToCargoCFS] = useState({});
  const handleSendToCargoCFS = async (e, folder) => {
    e.stopPropagation();
    if (!window.confirm(`Send a CargoCFS pre-alert for ${folder.pf_number}? This will notify the Dekani warehouse to expect this shipment.`)) {
      return;
    }
    setSendingToCargoCFS(prev => ({ ...prev, [folder.id]: true }));
    try {
      const res = await axios.post(
        `${API_URL}/position-folders/${folder.id}/send-to-cargocfs`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const r = res.data || {};
      if (r.ok) {
        toast.success(`✅ ${r.message}`);
      } else if (r.queued) {
        toast(`⏳ ${r.message}`, { icon: "📥" });
      } else {
        toast.error(r.message || "CargoCFS pre-alert failed");
      }
      fetchFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send to CargoCFS");
    } finally {
      setSendingToCargoCFS(prev => ({ ...prev, [folder.id]: false }));
    }
  };

  // Open sub-order creation modal
  const openSubOrderModal = (folder) => {
    setSubOrderParent(folder);
    setSubOrderData({
      customer_name: "",
      customer_email: "",
      description: "",
      hbl_number: "",
      shipper: "",
      consignee: "",
      commodity: "",
      packages: "",
      weight: "",
      volume: "",
      remarks: "",
      pantheon_id: "",
      pantheon_name: "",
    });
    setSubOrderPantheonCompany(null);
    setShowSubOrderModal(true);
  };

  // Create a sub-order
  const createSubOrder = async () => {
    if (!subOrderData.shipper || !subOrderData.consignee) {
      toast.error("Please fill in shipper and consignee");
      return;
    }
    
    setCreatingSub(true);
    try {
      const response = await axios.post(
        `${API_URL}/position-folders/${subOrderParent.id}/sub-orders`,
        subOrderData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Created sub-order ${response.data.pf_number}`);
      setShowSubOrderModal(false);
      // Refresh the folder to show updated sub_order_count
      fetchFolders();
      // If viewing folder detail, refresh sub-orders
      if (selectedFolder?.id === subOrderParent.id) {
        fetchSubOrders(subOrderParent.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create sub-order");
    } finally {
      setCreatingSub(false);
    }
  };

  // When selecting a folder, also fetch its sub-orders
  const handleSelectFolder = async (folder) => {
    setSelectedFolder(folder);
    if (folder.is_master || folder.sub_order_count > 0) {
      await fetchSubOrders(folder.id);
    } else {
      setSubOrders([]);
    }
  };

  // Inline toggle workflow mode for PFs in list view
  const [togglingWorkflowMode, setTogglingWorkflowMode] = useState({});
  const [togglingTrackingFinished, setTogglingTrackingFinished] = useState({});
  
  const togglePFWorkflowMode = async (e, folder) => {
    e.stopPropagation(); // Prevent opening the folder details
    const pfId = folder.id;
    const currentMode = folder.workflow_mode || "auto";
    const newMode = currentMode === "auto" ? "pending" : "auto";
    
    setTogglingWorkflowMode(prev => ({ ...prev, [pfId]: true }));
    try {
      const response = await axios.put(
        `${API_URL}/position-folders/${pfId}/workflow-mode`,
        { mode: newMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the folder in local state
      setFolders(prev => prev.map(f => 
        f.id === pfId ? { ...f, workflow_mode: newMode } : f
      ));
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change workflow mode");
    } finally {
      setTogglingWorkflowMode(prev => ({ ...prev, [pfId]: false }));
    }
  };

  const togglePFTrackingFinished = async (e, folder) => {
    e.stopPropagation();
    const pfId = folder.id;
    const newFinished = !folder.tracking_finished;
    
    setTogglingTrackingFinished(prev => ({ ...prev, [pfId]: true }));
    try {
      const response = await axios.put(
        `${API_URL}/position-folders/${pfId}/tracking-finished`,
        { finished: newFinished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setFolders(prev => prev.map(f => 
        f.id === pfId ? { ...f, tracking_finished: newFinished } : f
      ));
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle tracking");
    } finally {
      setTogglingTrackingFinished(prev => ({ ...prev, [pfId]: false }));
    }
  };

  const syncTransbook = async () => {
    try {
      // First refresh Transbook data
      await axios.post(
        `${API_URL}/transbook/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Then sync to drafts
      const response = await axios.post(
        `${API_URL}/transbook/sync-to-drafts`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.drafts_created > 0) {
        toast.success(`Synced ${response.data.drafts_created} new orders from Transbook`);
        fetchDrafts();
      } else {
        toast.info("No new orders from Transbook");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync Transbook");
    }
  };

  const handleConvertToPF = async (draftId, customer, assignee, description, notificationEmails) => {
    const formData = new FormData();
    formData.append("customer", customer);
    formData.append("assignee", assignee);
    if (description) formData.append("description", description);
    if (notificationEmails?.length) formData.append("notification_emails", JSON.stringify(notificationEmails));
    
    try {
      const response = await axios.post(
        `${API_URL}/position-folders/convert-from-draft/${draftId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Order created: ${response.data.pf_number}`);
      setSelectedDraft(null);
      fetchDrafts();
      fetchFolders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create order");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 sm:py-8 pt-20 sm:pt-24">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-2">
                <FolderOpen className="h-6 w-6 sm:h-8 sm:w-8" />
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">Position Folders</h1>
              </div>
              <p className="text-sm sm:text-base text-slate-300">
                Manage customer orders and shipment requests
              </p>
            </div>
            <div className="flex gap-2 sm:gap-3">
              <Button
                onClick={handleRefresh}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                disabled={refreshing}
                data-testid="refresh-pf-btn"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline ml-2">Refresh</span>
              </Button>
              <Button
                onClick={() => setShowCreatePFModal(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white flex-1 sm:flex-none"
                data-testid="create-new-pf-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden xs:inline">Create</span> New PF
              </Button>
              <Button
                onClick={() => setShowExportLclModal(true)}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                title="Group several existing export PFs into one master LCL container"
                data-testid="build-export-lcl-btn"
              >
                <Boxes className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Build Export LCL</span>
              </Button>
            </div>
          </div>

          {/* Stats - Compact for admin/CEO only */}
          {stats && isAdmin && (
            <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-4 bg-white/10 rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm">
              <div className="flex items-center gap-2">
                <span className="text-slate-300">Total:</span>
                <span className="font-bold">{stats.total}</span>
              </div>
              {stats.by_status?.map((s) => (
                <div key={s._id} className="flex items-center gap-2">
                  <span className="text-slate-300">{STATUS_LABELS[s._id] || s._id}:</span>
                  <span className="font-bold">{s.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tabs - Horizontal scroll on mobile */}
        <div className="flex gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 scrollbar-hide">
          {[
            { id: "drafts", label: "Email Drafts", icon: Inbox, count: drafts.length },
            { id: "orders", label: "Open Orders", icon: FolderOpen },
            { id: "archive", label: "Archive", icon: FileText },
            { id: "all", label: "All PFs", icon: FolderOpen },
            ...(isAdmin ? [{ id: "purge", label: "Purge", icon: Trash2, adminOnly: true }] : []),
          ].map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              onClick={() => {
                setActiveTab(tab.id);
                setPage(1);
                // Reset the status dropdown when switching tabs so the
                // tab-driven default takes effect (orders → Odprto,
                // archive → closed-side statuses, all → no filter).
                setStatusFilter("");
                if (tab.id === "purge") fetchPurgeFolders();
              }}
              className={`flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${tab.adminOnly ? 'border-red-200 text-red-600 hover:bg-red-50' : ''}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              {tab.count !== undefined && (
                <span className="ml-1 px-2 py-0.5 bg-primary/20 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
              {purgeFolders.length > 0 && tab.id === "purge" && (
                <span className="ml-1 px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs">
                  {purgeFolders.length}
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Search & Filters */}
        {activeTab !== "drafts" && (
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by PF#, Customer, Container#, BL#, HBL, MBL..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchFolders()}
              />
            </div>
            {/* My PFs / All PFs Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-white">
              <button
                onClick={() => { setShowMyPFsOnly(false); setPage(1); }}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  !showMyPFsOnly 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="all-pfs-btn"
              >
                All PFs
              </button>
              <button
                onClick={() => { setShowMyPFsOnly(true); setPage(1); }}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  showMyPFsOnly 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="my-pfs-btn"
              >
                My PFs
              </button>
            </div>
            {/* Working / Pending (Freightlinks) Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-white">
              <button
                onClick={() => { setViewMode("working"); setPage(1); }}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === "working" 
                    ? 'bg-green-600 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="working-pfs-btn"
              >
                Working
              </button>
              <button
                onClick={() => { setViewMode("pending"); setPage(1); }}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                  viewMode === "pending" 
                    ? 'bg-orange-500 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="pending-pfs-btn"
              >
                Pending
                {pendingPFs.length > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    viewMode === "pending" ? 'bg-white/20' : 'bg-orange-100 text-orange-700'
                  }`}>
                    {pendingPFs.length}
                  </span>
                )}
              </button>
            </div>
            <select
              className="border rounded-lg px-4 py-2"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Statuses</option>
              <option value="Odprto">Open (Odprto)</option>
              <option value="Preneseno v TLM">Transferred (TLM)</option>
              <option value="Zaključeno">Closed (Zaključeno)</option>
            </select>
            <select
              className="border rounded-lg px-4 py-2"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
            >
              <option value="pf_number">Sort by PF Number</option>
              <option value="date">Sort by Date</option>
            </select>
            <Button 
              variant="outline"
              onClick={() => {
                setSortOrder(sortOrder === "desc" ? "asc" : "desc");
                setPage(1);
              }}
              className="flex items-center gap-2"
              data-testid="sort-date-btn"
            >
              {sortOrder === "desc" ? (
                <>
                  <ArrowDown className="h-4 w-4" />
                  High to Low
                </>
              ) : (
                <>
                  <ArrowUp className="h-4 w-4" />
                  Low to High
                </>
              )}
            </Button>
            <Button onClick={fetchFolders} data-testid="search-btn">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading...</p>
          </div>
        ) : activeTab === "drafts" ? (
          <div className="space-y-4">
            {drafts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border">
                <Inbox className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No email drafts</h3>
                <p className="text-muted-foreground">
                  Emails sent to roket@europacific.com with (PF) in subject will appear here
                </p>
                <Button 
                  className="mt-4" 
                  variant="outline"
                  onClick={() => setShowUploadModal(true)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload .eml file
                </Button>
              </div>
            ) : (
              drafts.map((draft) => (
                <motion.div
                  key={draft.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedDraft(draft)}
                  data-testid={`draft-${draft.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Mail className="h-4 w-4 text-blue-500" />
                        <span className="text-sm text-muted-foreground">{draft.from_email}</span>
                        {draft.trigger && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                            {draft.trigger}
                          </span>
                        )}
                      </div>
                      <h3 className="font-medium">{draft.subject}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {draft.body?.slice(0, 200)}...
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">
                        {new Date(draft.received_at).toLocaleString()}
                      </div>
                      {draft.attachments?.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <Paperclip className="h-3 w-3" />
                          {draft.attachments.length} file(s)
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 mt-3">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => handleDeleteDraft(e, draft.id)}
                      data-testid={`delete-draft-${draft.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <Button size="sm" data-testid={`convert-draft-${draft.id}`}>
                      <ArrowRight className="h-4 w-4 mr-1" />
                      Convert to Order
                    </Button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        ) : activeTab === "purge" ? (
          /* PURGE FOLDER - Soft-deleted PFs awaiting permanent deletion */
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <div>
                  <h3 className="font-medium text-red-800">Purge Folder</h3>
                  <p className="text-sm text-red-600">
                    Position Folders here will be permanently deleted after 30 days. 
                    You can restore them or delete immediately.
                  </p>
                </div>
              </div>
            </div>
            
            {purgeFolders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border">
                <Trash2 className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium">Purge folder is empty</h3>
                <p className="text-muted-foreground">
                  Deleted Position Folders will appear here for 30 days
                </p>
              </div>
            ) : (
              purgeFolders.map((folder) => (
                <motion.div
                  key={folder.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl border border-red-200 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-bold text-lg text-red-700 line-through">
                          {folder.pf_number}
                        </span>
                        <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-700">
                          Deleted
                        </span>
                        {folder.sub_orders_count > 0 && (
                          <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">
                            +{folder.sub_orders_count} sub-orders
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{folder.customer_name || folder.customer}</span>
                        <span>Deleted by: {folder.deleted_by?.split('@')[0]}</span>
                        <span>
                          {folder.days_until_purge !== undefined 
                            ? `${folder.days_until_purge} days until permanent deletion`
                            : `Purge: ${new Date(folder.purge_date).toLocaleDateString()}`
                          }
                        </span>
                      </div>
                      {folder.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                          {folder.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestorePF(folder)}
                        disabled={restoringPf === folder.pf_number}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        {restoringPf === folder.pf_number ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Restore
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePermanentDelete(folder)}
                        disabled={deletingPf}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete Forever
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        ) : (
          <div id="pf-split-container" className="flex flex-col" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
            {/* ============ SPLIT CONTAINER FOR PFs AND TASKS ============ */}
            {/* PFs List - Top Pane */}
            <div style={{ height: `${splitPosition}%`, overflow: 'auto' }} className="space-y-4">
            
            {/* PENDING PFs (Freightlinks Queue) */}
            {viewMode === "pending" ? (
              pendingLoading ? (
                <div className="text-center py-12 bg-white rounded-xl border">
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-orange-500 mb-4" />
                  <p className="text-muted-foreground">Loading pending shipments...</p>
                </div>
              ) : pendingPFs.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border">
                  <Clock className="h-12 w-12 mx-auto text-orange-300 mb-4" />
                  <h3 className="text-lg font-medium">No Pending Shipments</h3>
                  <p className="text-muted-foreground">
                    Shipments from Freightlinks will appear here for review
                  </p>
                </div>
              ) : (
                <>
                  {/* Header with select all and bulk action */}
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-orange-800">
                        <strong>{pendingPFs.length} pending shipment(s)</strong> from Freightlinks waiting for review. 
                        These can be converted to PFs or consolidated into a Master PF.
                      </p>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-orange-700 hover:text-orange-900">
                          <input
                            type="checkbox"
                            checked={selectedPendingItems.length === pendingPFs.length && pendingPFs.length > 0}
                            onChange={handleSelectAllPending}
                            className="w-4 h-4 rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                          />
                          Select All
                        </label>
                      </div>
                    </div>
                    {/* Bulk action bar - shows when items are selected */}
                    {selectedPendingItems.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-orange-200 flex items-center justify-between">
                        <span className="text-sm font-medium text-orange-800">
                          {selectedPendingItems.length} shipment(s) selected
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-white border-purple-300 text-purple-700 hover:bg-purple-50"
                          onClick={() => handleOpenAddToMasterModal()}
                          disabled={creatingFromPending}
                          data-testid="bulk-add-to-master-btn"
                        >
                          <Layers className="h-3 w-3 mr-1" />
                          Link Selected to Master
                        </Button>
                      </div>
                    )}
                  </div>
                  {pendingPFs.map((item) => {
                    const itemId = item.id || item._id;
                    const isSelected = selectedPendingItems.some(i => (i.id || i._id) === itemId);
                    return (
                    <motion.div
                      key={itemId || item.queue_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`bg-white rounded-xl border p-4 hover:shadow-md transition-shadow ${
                        isSelected ? 'border-purple-400 ring-2 ring-purple-200' : 'border-orange-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        {/* Checkbox for multi-select */}
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleTogglePendingSelection(item)}
                            className="mt-1 w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                            data-testid={`select-pending-${itemId}`}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-700 font-medium">
                                PENDING
                              </span>
                              <span className="font-mono font-bold text-lg">
                                {item.hbl_number || item.data?.hbl_number || "No HBL"}
                              </span>
                              {item.data?.mbl_number && (
                                <span className="text-xs text-slate-500">
                                  MBL: {item.data.mbl_number}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-slate-500">Shipper:</span>
                                <span>{item.data?.shipper?.name || item.shipper_name || "-"}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-slate-500">Consignee:</span>
                                <span>{item.data?.consignee?.name || item.consignee_name || "-"}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-slate-500">Route:</span>
                                <span>
                                  {item.data?.origin_port || "-"} → {item.data?.destination_port || "-"}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-slate-500">Cargo:</span>
                                <span>
                                  {item.data?.cargo?.packages || "-"} {item.data?.cargo?.package_type || "pkgs"}, 
                                  {item.data?.cargo?.weight_kg || "-"} kg
                                </span>
                              </div>
                              {item.data?.vessel_name && (
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-500">Vessel:</span>
                                  <span>{item.data.vessel_name} {item.data.voyage_number || ""}</span>
                                </div>
                              )}
                              {item.data?.eta_date && (
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-500">ETA:</span>
                                  <span>{item.data.eta_date}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                              <Clock className="h-3 w-3" />
                              Received: {new Date(item.created_at || item.received_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <Button
                            size="sm"
                            disabled={creatingFromPending}
                            onClick={() => handleCreatePFFromPending(item)}
                            data-testid={`create-pf-from-pending-${itemId}`}
                          >
                            {creatingFromPending ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <Plus className="h-3 w-3 mr-1" />
                            )}
                            Create PF
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={creatingFromPending}
                            onClick={() => handleOpenAddToMasterModal(item)}
                            data-testid={`add-to-master-${itemId}`}
                          >
                            <Layers className="h-3 w-3 mr-1" />
                            Add to Master
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setPendingToDelete(item)}
                            data-testid={`delete-pending-${itemId}`}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                    );
                  })}
                </>
              )
            ) : (
            /* WORKING PFs (Regular folders) */
            folders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border">
                <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No position folders</h3>
                <p className="text-muted-foreground">
                  Convert email drafts to create position folders
                </p>
              </div>
            ) : (
              folders.map((folder) => (
                <div key={folder.id}>
                  {/* Main Folder Card */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-white rounded-xl border p-4 hover:shadow-md transition-shadow ${
                      folder.is_sub_order ? 'ml-8 border-l-4 border-l-amber-400' : ''
                    }`}
                    data-testid={`folder-${folder.pf_number}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 cursor-pointer" onClick={() => handleSelectFolder(folder)}>
                        <div className="flex items-center gap-2 mb-1">
                          {/* Expand/Collapse button for Master PFs */}
                          {(folder.is_master || folder.sub_order_count > 0) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMasterPFExpansion(folder);
                              }}
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                              data-testid={`expand-${folder.pf_number}`}
                            >
                              {loadingSubOrders[folder.id] ? (
                                <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                              ) : expandedMasterPFs[folder.id] ? (
                                <ChevronRight className="h-4 w-4 text-purple-600 rotate-90 transition-transform" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-purple-600 transition-transform" />
                              )}
                            </button>
                          )}
                          <span className="font-mono font-bold text-lg">{folder.pf_number}</span>
                          {/* COD pending red dot — needs to be checked with carrier */}
                          {((folder.cod_status || folder.cod?.status) === "pending") && (
                            <span
                              className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 border border-red-300 flex items-center gap-1 animate-pulse"
                              title="Change of Destination pending — check with carrier"
                              data-testid={`pf-cod-pending-${folder.pf_number}`}
                            >
                              <span className="h-2 w-2 rounded-full bg-red-500" />
                              COD pending
                            </span>
                          )}
                          {/* Possible duplicate — shares a shipment ref with another open PF */}
                          {folder.is_possible_duplicate && (folder.possible_duplicate_of || []).length > 0 && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 border border-red-300"
                              title={`Duplicate of ${(folder.possible_duplicate_of || []).map((d) => d.pf_number).join(", ")} — ${(folder.possible_duplicate_of[0]?.matched_on || []).join(", ")}`}
                              data-testid={`pf-duplicate-badge-${folder.pf_number}`}
                            >
                              <AlertTriangle className="h-3 w-3" />
                              <span>Duplicate of</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); const o = folder.possible_duplicate_of[0]; if (o?.pf_number) window.open(`/position-folders?search=${encodeURIComponent(o.pf_number)}`, "_blank"); }}
                                className="font-semibold underline hover:text-red-900"
                                data-testid={`pf-duplicate-link-${folder.pf_number}`}
                              >
                                {folder.possible_duplicate_of[0].pf_number}{folder.possible_duplicate_of.length > 1 ? ` +${folder.possible_duplicate_of.length - 1}` : ""}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); openMergeModal(folder); }}
                                className="ml-1 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold text-white hover:bg-red-700"
                                data-testid={`pf-merge-btn-${folder.pf_number}`}
                              >
                                Merge
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); unmarkDuplicate(folder); }}
                                className="ml-1 rounded bg-white/70 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:bg-white"
                                data-testid={`pf-unmark-dup-btn-${folder.pf_number}`}
                                title="Not a duplicate — remove this warning"
                              >
                                ✕ Not duplicate
                              </button>
                            </span>
                          )}

                          <span className={`px-2 py-0.5 text-xs rounded ${STATUS_COLORS[folder.status]}`}>
                            {STATUS_LABELS[folder.status] || folder.status}
                          </span>
                          {/* 2026-05-16 — CO2 footprint badge */}
                          {co2Map[folder.id] != null && co2Map[folder.id] > 0 && (
                            <span
                              className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-800 flex items-center gap-1"
                              title="Estimated CO₂e for this shipment (GLEC v3.0)"
                              data-testid={`pf-co2-badge-${folder.id}`}
                            >
                              <Leaf className="h-3 w-3" />
                              {co2Map[folder.id] >= 1000
                                ? `${(co2Map[folder.id] / 1000).toFixed(1)} t CO₂e`
                                : `${Math.round(co2Map[folder.id])} kg CO₂e`}
                            </span>
                          )}
                          {/* Master PF / LCL indicator */}
                          {(folder.is_master || folder.sub_order_count > 0) && (
                            <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-800 flex items-center gap-1">
                              <Layers className="h-3 w-3" />
                              Master ({folder.sub_order_count || 0} sub)
                            </span>
                          )}
                          {/* Workflow mode indicator for master PFs */}
                          {(folder.is_master || folder.sub_order_count > 0) && folder.workflow_mode === "pending" && (
                            <span className="px-2 py-0.5 text-xs rounded bg-amber-200 text-amber-800 flex items-center gap-1" title="Automated emails paused">
                              <Clock className="h-3 w-3" />
                              Pending
                            </span>
                          )}
                          {folder.is_sub_order && (
                            <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800 flex items-center gap-1">
                              <GitBranch className="h-3 w-3" />
                              Sub-order
                            </span>
                          )}
                          {folder.service_type === "LCL" && (
                            <span className="px-2 py-0.5 text-xs rounded bg-cyan-100 text-cyan-800">
                              LCL
                            </span>
                          )}
                        {/* Direction indicator */}
                        {folder.direction && (
                          <span className={`px-2 py-0.5 text-xs rounded flex items-center gap-1 ${
                            folder.direction === "import" 
                              ? "bg-blue-100 text-blue-700" 
                              : folder.direction === "export"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-purple-100 text-purple-700"
                          }`}>
                            {folder.direction === "import" ? <ArrowDown className="h-3 w-3" /> : 
                             folder.direction === "export" ? <ArrowUp className="h-3 w-3" /> : 
                             <ArrowRight className="h-3 w-3" />}
                            {folder.direction === "cross_trade" ? "Cross Trade" : 
                             folder.direction.charAt(0).toUpperCase() + folder.direction.slice(1)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Building2 className="h-4 w-4" />
                          {folder.customer}
                        </span>
                        {folder.shipper && (
                          <span className="flex items-center gap-1 text-green-600">
                            <ArrowRight className="h-3 w-3" />
                            {folder.shipper}
                          </span>
                        )}
                        {folder.consignee && (
                          <span className="flex items-center gap-1 text-blue-600">
                            → {folder.consignee}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {folder.assignee?.split("@")[0]}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {folder.document_date}
                        </span>
                      </div>
                      {/* Document Indicators */}
                      {folder.doc_count > 0 && (
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          {folder.doc_summary?.BL > 0 && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded font-medium" title="Bill of Lading">
                              <FileText className="h-3 w-3" />
                              BL {folder.doc_summary.BL > 1 && `(${folder.doc_summary.BL})`}
                            </span>
                          )}
                          {folder.doc_summary?.Invoice > 0 && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-100 text-green-800 text-xs rounded font-medium" title="Invoice">
                              INV {folder.doc_summary.Invoice > 1 && `(${folder.doc_summary.Invoice})`}
                            </span>
                          )}
                          {folder.doc_summary?.PackingList > 0 && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-xs rounded font-medium" title="Packing List">
                              PL {folder.doc_summary.PackingList > 1 && `(${folder.doc_summary.PackingList})`}
                            </span>
                          )}
                          {folder.doc_summary?.CustomsDoc > 0 && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-purple-100 text-purple-800 text-xs rounded font-medium" title="Customs Document">
                              CUS {folder.doc_summary.CustomsDoc > 1 && `(${folder.doc_summary.CustomsDoc})`}
                            </span>
                          )}
                          {folder.doc_summary?.Delivery > 0 && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-cyan-100 text-cyan-800 text-xs rounded font-medium" title="Delivery Note">
                              DEL {folder.doc_summary.Delivery > 1 && `(${folder.doc_summary.Delivery})`}
                            </span>
                          )}
                          {folder.doc_summary?.Other > 0 && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 text-gray-800 text-xs rounded font-medium" title="Other Documents">
                              +{folder.doc_summary.Other}
                            </span>
                          )}
                        </div>
                      )}
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-1">
                        {folder.description}
                      </p>
                    </div>
                    <div className="text-right flex flex-col gap-2">
                      {/* Workflow Mode Toggle for ALL PFs */}
                      <div 
                        className="flex items-center justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className={`text-xs ${folder.workflow_mode === 'pending' ? 'text-amber-600 font-medium' : 'text-slate-400'}`}>
                          {folder.workflow_mode === 'pending' ? 'Pending' : ''}
                        </span>
                        <button
                          onClick={(e) => togglePFWorkflowMode(e, folder)}
                          disabled={togglingWorkflowMode[folder.id]}
                          className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 ${
                            folder.workflow_mode === 'pending' ? 'bg-amber-400' : 'bg-green-500'
                          } ${togglingWorkflowMode[folder.id] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
                          data-testid={`workflow-toggle-${folder.pf_number}`}
                          title={folder.workflow_mode === 'pending' 
                            ? 'Click to enable automated emails' 
                            : 'Click to pause automated emails'}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                              folder.workflow_mode === 'pending' ? 'translate-x-0.5' : 'translate-x-5'
                            }`}
                          />
                          {togglingWorkflowMode[folder.id] && (
                            <Loader2 className="absolute inset-0 m-auto h-3 w-3 animate-spin text-white" />
                          )}
                        </button>
                        <span className={`text-xs ${folder.workflow_mode !== 'pending' ? 'text-green-600 font-medium' : 'text-slate-400'}`}>
                          {folder.workflow_mode !== 'pending' ? 'Auto' : ''}
                        </span>
                      </div>
                      {/* Tracking Finished Toggle */}
                      <div 
                        className="flex items-center justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={(e) => togglePFTrackingFinished(e, folder)}
                          disabled={togglingTrackingFinished[folder.id]}
                          className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-1 ${
                            folder.tracking_finished ? 'bg-red-500' : 'bg-slate-300'
                          } ${togglingTrackingFinished[folder.id] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
                          data-testid={`tracking-finished-toggle-${folder.pf_number}`}
                          title={folder.tracking_finished 
                            ? 'Tracking stopped - click to re-enable' 
                            : 'Click to stop all tracking'}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                              folder.tracking_finished ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                          />
                          {togglingTrackingFinished[folder.id] && (
                            <Loader2 className="absolute inset-0 m-auto h-3 w-3 animate-spin text-white" />
                          )}
                        </button>
                        <span className={`text-xs ${folder.tracking_finished ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
                          {folder.tracking_finished ? 'Finished' : ''}
                        </span>
                      </div>
                      {folder.charged_amount > 0 && (
                        <div className="font-medium text-green-600">
                          €{folder.charged_amount.toLocaleString()}
                        </div>
                      )}
                      {folder.attachments?.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Paperclip className="h-3 w-3" />
                          {folder.attachments.length}
                        </div>
                      )}
                      {/* Add Sub-Order button for LCL/Master PFs */}
                      {(folder.service_type === "LCL" || folder.is_master || !folder.is_sub_order) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-purple-600 hover:bg-purple-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            openSubOrderModal(folder);
                          }}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Sub-order
                        </Button>
                      )}
                      {/* Add existing PFs to an open consolidation master + close/reopen */}
                      {folder.is_master && (
                        <>
                          {!folder.lcl_consolidation_closed && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-teal-700 hover:bg-teal-50"
                              onClick={(e) => { e.stopPropagation(); openAddPFsModal(folder); }}
                              data-testid={`add-pfs-to-master-${folder.pf_number}`}
                              title="Add more existing PFs to this consolidation"
                            >
                              <PackagePlus className="h-3 w-3 mr-1" />
                              Add PFs
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className={folder.lcl_consolidation_closed ? "text-amber-600 hover:bg-amber-50" : "text-slate-500 hover:bg-slate-100"}
                            onClick={(e) => { e.stopPropagation(); handleToggleConsolidation(folder); }}
                            disabled={togglingConsol[folder.id]}
                            data-testid={`toggle-consolidation-${folder.pf_number}`}
                            title={folder.lcl_consolidation_closed ? "Reopen consolidation to add more PFs" : "Close consolidation (no more PFs)"}
                          >
                            {togglingConsol[folder.id] ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : folder.lcl_consolidation_closed ? (
                              <Unlock className="h-3 w-3 mr-1" />
                            ) : (
                              <Lock className="h-3 w-3 mr-1" />
                            )}
                            {folder.lcl_consolidation_closed ? "Reopen" : "Close consol"}
                          </Button>
                        </>
                      )}
                      {/* Send to CargoCFS — manual cross-app trigger */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`${
                          folder.cargocfs_pre_alert_status === "sent"
                            ? "text-green-600 hover:bg-green-50"
                            : folder.cargocfs_pre_alert_status === "queued"
                            ? "text-amber-600 hover:bg-amber-50"
                            : "text-blue-600 hover:bg-blue-50"
                        }`}
                        onClick={(e) => handleSendToCargoCFS(e, folder)}
                        disabled={sendingToCargoCFS[folder.id]}
                        data-testid={`send-to-cargocfs-${folder.pf_number}`}
                        title={
                          folder.cargocfs_pre_alert_status === "sent"
                            ? `CargoCFS pre-alert sent ${folder.cargocfs_pre_alert_at ? new Date(folder.cargocfs_pre_alert_at).toLocaleString() : ""}`
                            : folder.cargocfs_pre_alert_status === "queued"
                            ? "Pre-alert queued for retry — click to refire"
                            : "Send pre-alert to CargoCFS warehouse (Dekani)"
                        }
                      >
                        {sendingToCargoCFS[folder.id] ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : folder.cargocfs_pre_alert_status === "sent" ? (
                          <Check className="h-3 w-3 mr-1" />
                        ) : (
                          <Warehouse className="h-3 w-3 mr-1" />
                        )}
                        {folder.cargocfs_pre_alert_status === "sent" ? "CFS ✓" : "Send to CFS"}
                      </Button>
                    </div>
                  </div>
                </motion.div>
                
                {/* Expandable Sub-Orders Section */}
                {(folder.is_master || folder.sub_order_count > 0) && expandedMasterPFs[folder.id] && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="ml-8 border-l-2 border-purple-200 pl-4 space-y-2 py-2"
                  >
                    {loadingSubOrders[folder.id] ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading sub-orders...
                      </div>
                    ) : subOrdersError[folder.id] ? (
                      <div className="flex items-center gap-3 text-sm py-2" data-testid={`sub-orders-error-${folder.pf_number}`}>
                        <span className="text-red-600">Couldn't load sub-orders.</span>
                        <button
                          type="button"
                          onClick={() => loadSubOrders(folder.id)}
                          className="text-xs px-2 py-1 bg-white border border-red-300 rounded hover:bg-red-50 flex items-center gap-1 text-red-700 font-medium"
                          data-testid={`retry-sub-orders-${folder.pf_number}`}
                        >
                          <RefreshCw className="h-3 w-3" /> Retry
                        </button>
                      </div>
                    ) : subOrdersCache[folder.id]?.length > 0 ? (
                      subOrdersCache[folder.id].map((subOrder) => {
                        const cps = subOrder.cargo_pieces_summary || null;
                        const isExportLclSub = subOrder.lcl_master_pf_number || (folder.is_master && folder.direction === 'export' && (folder.service_type === 'LCL' || folder.lcl_container_type));
                        return (
                        <div
                          key={subOrder.id}
                          className="bg-amber-50 border border-amber-200 rounded-lg p-3 hover:bg-amber-100 transition-colors"
                          data-testid={`sub-folder-${subOrder.pf_number}`}
                        >
                          <div className="flex items-center justify-between cursor-pointer" onClick={() => handleSelectFolder(subOrder)}>
                            <div className="flex items-center gap-2">
                              <GitBranch className="h-4 w-4 text-amber-600" />
                              <span className="font-mono font-bold text-purple-700">{subOrder.pf_number}</span>
                              {subOrder.hbl_number && (
                                <span className="font-mono text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                  HBL: {subOrder.hbl_number}
                                </span>
                              )}
                              <span className={`px-2 py-0.5 text-xs rounded ${STATUS_COLORS[subOrder.status]}`}>
                                {STATUS_LABELS[subOrder.status] || subOrder.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              {(subOrder.gross_weight_kg || subOrder.gross_weight) && (
                                <span className="text-xs">{subOrder.gross_weight_kg || subOrder.gross_weight} kg</span>
                              )}
                              {(subOrder.volume_cbm || subOrder.volume) && (
                                <span className="text-xs">{subOrder.volume_cbm || subOrder.volume} CBM</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              {subOrder.shipper && <span>{subOrder.shipper}</span>}
                              {subOrder.shipper && subOrder.consignee && <span> → </span>}
                              {subOrder.consignee && <span>{subOrder.consignee}</span>}
                            </div>
                            {subOrder.customer && (
                              <div className="text-sm text-muted-foreground flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {subOrder.customer}
                              </div>
                            )}
                          </div>
                          {isExportLclSub && (
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-amber-200">
                              {cps && cps.total_pieces > 0 ? (
                                <div className="flex items-center gap-2 text-xs text-amber-900">
                                  <Package className="h-3 w-3" />
                                  <span className="font-medium">{cps.total_pieces}</span> pieces ·
                                  <span className="font-medium">{cps.total_weight_kg}</span> kg ·
                                  <span className="font-medium">{cps.total_volume_m3}</span> m³
                                </div>
                              ) : (
                                <div className="text-xs text-amber-700/80 italic">No cargo pieces yet</div>
                              )}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setEditingCargoSubPf(subOrder); }}
                                className="text-xs px-2 py-1 bg-white border border-amber-300 rounded hover:bg-amber-100 flex items-center gap-1 text-amber-900 font-medium"
                                data-testid={`edit-sub-cargo-${subOrder.pf_number}`}
                              >
                                <Edit className="h-3 w-3" /> {cps && cps.total_pieces > 0 ? "Edit cargo" : "Add cargo"}
                              </button>
                            </div>
                          )}
                        </div>
                        );
                      })
                    ) : (
                      <div className="text-sm text-muted-foreground py-2">
                        No sub-orders found
                      </div>
                    )}
                  </motion.div>
                )}
                </div>
              ))
            )
            )}
            </div>
            {/* End PFs List */}

            {/* ============ RESIZABLE SPLIT - DRAGGABLE DIVIDER WITH LOCK ============ */}
            <div 
              className={`relative py-1 select-none flex-shrink-0 ${isPanelLocked ? 'cursor-default' : 'cursor-row-resize'}`}
            >
              <div className="flex items-center justify-center gap-2">
                {/* Lock Button */}
                <button
                  onClick={() => setIsPanelLocked(!isPanelLocked)}
                  className={`px-2 py-1 rounded-lg transition-colors flex items-center gap-1 text-xs font-medium ${isPanelLocked ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                  title={isPanelLocked ? 'Unlock panel (allow resize)' : 'Lock panel position'}
                >
                  {isPanelLocked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                  {isPanelLocked ? 'Locked' : 'Lock'}
                </button>
                
                {/* Drag Handle */}
                <div 
                  className={`h-2 flex-1 max-w-md ${isDragging ? 'bg-blue-400' : isPanelLocked ? 'bg-slate-100' : 'bg-slate-200 hover:bg-slate-300'} rounded-full flex items-center justify-center transition-colors`}
                  onMouseDown={(e) => {
                    if (isPanelLocked) return;
                    setIsDragging(true);
                    e.preventDefault();
                  }}
                >
                  <GripHorizontal className={`h-4 w-4 ${isPanelLocked ? 'text-slate-300' : 'text-slate-400'}`} />
                </div>
                
                {/* Spacer for symmetry */}
                <div className="w-16"></div>
              </div>
            </div>

            {/* ============ TASKS + ETA CONTAINER (SPLIT) ============ */}
            <div id="tasks-eta-container" className="flex-1 flex flex-col overflow-hidden" style={{ minHeight: '250px' }}>
              
              {/* ============ TO-DOS & ACTIVITIES PANEL ============ */}
              <div 
                className="bg-white rounded-xl border shadow-sm overflow-hidden flex-shrink-0"
                style={{ height: `${etaSplitPosition}%`, minHeight: '120px' }}
              >
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 text-white flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <ListTodo className="h-5 w-5" />
                  Tasks & Activities
                </h3>
                <div className="flex items-center gap-2">
                  <div className="bg-white/20 rounded flex" role="group" data-testid="tasks-scope-toggle">
                    <button
                      onClick={() => setFeedScope("mine")}
                      className={`px-2.5 py-0.5 text-xs rounded transition-colors ${feedScope === "mine" ? "bg-white text-amber-700 font-semibold" : "text-white hover:bg-white/10"}`}
                      data-testid="tasks-scope-mine"
                    >Mine</button>
                    <button
                      onClick={() => setFeedScope("all")}
                      className={`px-2.5 py-0.5 text-xs rounded transition-colors ${feedScope === "all" ? "bg-white text-amber-700 font-semibold" : "text-white hover:bg-white/10"}`}
                      data-testid="tasks-scope-all"
                    >All</button>
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-1 rounded">
                    {myTasks.filter(t => !t.completed).length} pending
                  </span>
                </div>
              </div>
              
              {/* Split Content - Today | Next 5 Days */}
              <div className="flex h-[calc(100%-40px)]">
                {/* TODAY Column */}
                <div className="flex-1 border-r overflow-hidden flex flex-col">
                  <div className="bg-red-50 px-3 py-2 border-b flex items-center justify-between">
                    <h4 className="font-semibold text-red-700 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Today
                    </h4>
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                      {myTasks.filter(t => {
                        if (!t.due_date) return true; // No date = today
                        const d = new Date(t.due_date);
                        const today = new Date();
                        return d.toDateString() === today.toDateString();
                      }).length}
                    </span>
                  </div>
                <div className="flex-1 overflow-y-auto">
                  {myTasks.filter(t => {
                    if (!t.due_date) return true;
                    const d = new Date(t.due_date);
                    const today = new Date();
                    return d.toDateString() === today.toDateString();
                  }).length === 0 ? (
                    <div className="p-4 text-center text-slate-400 text-sm">
                      <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-300" />
                      All caught up for today!
                    </div>
                  ) : (
                    myTasks.filter(t => {
                      if (!t.due_date) return true;
                      const d = new Date(t.due_date);
                      const today = new Date();
                      return d.toDateString() === today.toDateString();
                    }).map((task, idx) => (
                      <div 
                        key={task.id || idx} 
                        className="p-2 border-b hover:bg-amber-50 flex items-start gap-2 cursor-pointer transition-colors"
                        style={task.type === "inspection" && task.color ? { borderLeftWidth: '3px', borderLeftColor: task.color } : {}}
                        onClick={() => openFeedTaskPF(task)}
                        title={task.type === 'email_draft' ? `Click to convert email to PF` : `Click to open ${task.pf_number || task.order_number || 'details'}`}
                      >
                        <input
                          type="checkbox"
                          checked={task.completed || false}
                          onChange={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 mt-0.5 rounded border-slate-300"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${task.completed ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                            {task.type === "inspection" && (
                              <span 
                                className="inline-block w-2 h-2 rounded-full mr-1.5"
                                style={{ backgroundColor: task.color || '#dc2626' }}
                              ></span>
                            )}
                            {task.type === "email_draft" && (
                              <span className="inline-block w-2 h-2 rounded-full mr-1.5 bg-purple-500"></span>
                            )}
                            {task.title || task.description}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {task.type === 'email_draft' 
                              ? `${task.sender_name || task.sender_email}${task.has_attachments ? ' • 📎' : ''}`
                              : `${task.pf_number || task.order_number} • ${task.customer}`
                            }
                            {task.type === "inspection" && task.assigned_to_name && ` • ${task.assigned_to_name}`}
                          </p>
                          {/* Inline short comment / note — PF tasks only */}
                          {task.pf_id && task.id && task.type !== 'inspection' && task.type !== 'email_draft' && task.type !== 'admin_task' && (
                            <input
                              type="text"
                              placeholder="+ note (eg. TRK reg. nr)"
                              defaultValue={task.comment || ""}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={(e) => {
                                const v = e.target.value;
                                if (v !== (task.comment || "")) updateFeedTaskComment(task, v);
                              }}
                              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                              data-testid={`feed-task-comment-${task.id}`}
                              className="mt-1 text-xs w-full px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white"
                            />
                          )}
                          {/* Inline note for admin tasks (delegated assignee / TRK reg / triage note) */}
                          {task.type === 'admin_task' && task.id && (
                            <input
                              type="text"
                              placeholder="+ note (eg. TRK reg. nr, triage comment)"
                              defaultValue={task.note || ""}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v !== (task.note || "")) updateAdminTaskNote(task, v);
                              }}
                              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                              data-testid={`feed-task-admin-note-${task.id}`}
                              className="mt-1 text-xs w-full px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-red-400 focus:bg-white"
                            />
                          )}
                        </div>
                        {task.priority === "high" && (
                          <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded">!</span>
                        )}
                        {task.type === "email_draft" && (
                          <span className="text-xs bg-purple-500 text-white px-1.5 py-0.5 rounded">Email</span>
                        )}
                        {task.type === "inspection" && (
                          <span 
                            className="text-xs px-1.5 py-0.5 rounded text-white"
                            style={{ backgroundColor: task.color || '#dc2626' }}
                          >
                            {task.due_date ? new Date(task.due_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        )}
                        {/* Inline due date — PF tasks only */}
                        {task.pf_id && task.id && task.type !== 'inspection' && task.type !== 'email_draft' && task.type !== 'admin_task' && (
                          <input
                            type="date"
                            value={task.due_date ? String(task.due_date).slice(0, 10) : ""}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); updateFeedTaskDate(task, e.target.value); }}
                            data-testid={`feed-task-due-date-${task.id}`}
                            className="text-xs px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-amber-400"
                            title="Due date"
                          />
                        )}
                        {/* Inline assignee — PF tasks only */}
                        {task.pf_id && task.id && task.type !== 'inspection' && task.type !== 'email_draft' && task.type !== 'admin_task' && (
                          <select
                            value={task.assigned_to || ""}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); reassignFeedTask(task, e.target.value); }}
                            data-testid={`feed-task-assignee-${task.id}`}
                            className="text-xs px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-amber-400 max-w-[100px]"
                            title="Reassign task"
                          >
                            <option value="">Unassigned</option>
                            {feedStaffList.map(s => (
                              <option key={s.email} value={s.email}>
                                {(s.full_name || s.name || s.email.split("@")[0]).split(" ")[0]}
                              </option>
                            ))}
                          </select>
                        )}
                        {/* Delete task button — only for embedded PF tasks */}
                        {task.pf_id && task.id && task.type !== 'inspection' && task.type !== 'email_draft' && task.type !== 'admin_task' && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); deleteFeedTask(task); }}
                            data-testid={`feed-task-delete-${task.id}`}
                            title="Delete task"
                            className="p-0.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {/* Admin task: delegate (assignee) + note */}
                        {task.type === 'admin_task' && task.id && (
                          <select
                            value={task.assigned_to || ""}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); delegateAdminTask(task, e.target.value); }}
                            data-testid={`feed-task-admin-assignee-${task.id}`}
                            className="text-xs px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-400 max-w-[110px]"
                            title="Delegate to colleague"
                          >
                            <option value="">Unassigned</option>
                            {feedStaffList.map(s => (
                              <option key={s.email} value={s.email}>
                                {(s.full_name || s.name || s.email.split("@")[0]).split(" ")[0]}
                              </option>
                            ))}
                          </select>
                        )}
                        {task.type === 'admin_task' && (
                          <div className="flex items-center gap-1" data-testid={`feed-task-admin-actions-${task.id}`}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); approveAdminTask(task); }}
                              data-testid={`feed-task-approve-${task.id}`}
                              title={task.admin_type === 'pf_deletion_request' ? 'Approve & recycle PF' : 'Approve'}
                              className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-600 hover:text-white font-medium whitespace-nowrap transition-colors"
                            >Approve</button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); dismissAdminTask(task); }}
                              data-testid={`feed-task-dismiss-${task.id}`}
                              title="Dismiss & notify requester"
                              className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 hover:bg-red-600 hover:text-white font-medium whitespace-nowrap transition-colors"
                            >Dismiss</button>
                          </div>
                        )}
                        <ExternalLink className="h-3 w-3 text-slate-400" />
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* NEXT 5 WORKING DAYS Column */}
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="bg-blue-50 px-3 py-2 border-b flex items-center justify-between">
                  <h4 className="font-semibold text-blue-700 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Next 5 Working Days
                  </h4>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {myTasks.filter(t => {
                      if (!t.due_date) return false;
                      const d = new Date(t.due_date);
                      const today = new Date();
                      const fiveDaysLater = new Date(today);
                      fiveDaysLater.setDate(today.getDate() + 7); // ~5 working days
                      return d > today && d <= fiveDaysLater;
                    }).length}
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {myTasks.filter(t => {
                    if (!t.due_date) return false;
                    const d = new Date(t.due_date);
                    const today = new Date();
                    const fiveDaysLater = new Date(today);
                    fiveDaysLater.setDate(today.getDate() + 7);
                    return d > today && d <= fiveDaysLater;
                  }).length === 0 ? (
                    <div className="p-4 text-center text-slate-400 text-sm">
                      <Calendar className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      No upcoming tasks
                    </div>
                  ) : (
                    myTasks.filter(t => {
                      if (!t.due_date) return false;
                      const d = new Date(t.due_date);
                      const today = new Date();
                      const fiveDaysLater = new Date(today);
                      fiveDaysLater.setDate(today.getDate() + 7);
                      return d > today && d <= fiveDaysLater;
                    }).map((task, idx) => (
                      <div 
                        key={task.id || idx} 
                        className="p-2 border-b hover:bg-blue-50 flex items-start gap-2 cursor-pointer transition-colors"
                        style={task.type === "inspection" && task.color ? { borderLeftWidth: '3px', borderLeftColor: task.color } : (task.type === "email_draft" ? { borderLeftWidth: '3px', borderLeftColor: '#8b5cf6' } : {})}
                        onClick={() => openFeedTaskPF(task)}
                        title={task.type === 'email_draft' ? `Click to convert email to PF` : `Click to open ${task.pf_number || task.order_number || 'details'}`}
                      >
                        <input
                          type="checkbox"
                          checked={task.completed || false}
                          onChange={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 mt-0.5 rounded border-slate-300"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${task.completed ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                            {task.type === "inspection" && (
                              <span 
                                className="inline-block w-2 h-2 rounded-full mr-1.5"
                                style={{ backgroundColor: task.color || '#dc2626' }}
                              ></span>
                            )}
                            {task.type === "email_draft" && (
                              <span className="inline-block w-2 h-2 rounded-full mr-1.5 bg-purple-500"></span>
                            )}
                            {task.title || task.description}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {task.type === 'email_draft' 
                              ? `${task.sender_name || task.sender_email}${task.has_attachments ? ' • 📎' : ''}`
                              : `${task.pf_number || task.order_number} • ${task.customer}`
                            }
                          </p>
                          {/* Inline short comment / note — PF tasks only */}
                          {task.pf_id && task.id && task.type !== 'inspection' && task.type !== 'email_draft' && task.type !== 'admin_task' && (
                            <input
                              type="text"
                              placeholder="+ note (eg. TRK reg. nr)"
                              defaultValue={task.comment || ""}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={(e) => {
                                const v = e.target.value;
                                if (v !== (task.comment || "")) updateFeedTaskComment(task, v);
                              }}
                              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                              data-testid={`feed-task-comment-${task.id}`}
                              className="mt-1 text-xs w-full px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:bg-white"
                            />
                          )}
                          {/* Inline note for admin tasks (delegated assignee / TRK reg / triage note) */}
                          {task.type === 'admin_task' && task.id && (
                            <input
                              type="text"
                              placeholder="+ note (eg. TRK reg. nr, triage comment)"
                              defaultValue={task.note || ""}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v !== (task.note || "")) updateAdminTaskNote(task, v);
                              }}
                              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                              data-testid={`feed-task-admin-note-${task.id}`}
                              className="mt-1 text-xs w-full px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-red-400 focus:bg-white"
                            />
                          )}
                        </div>
                        <span className={`text-xs px-1.5 py-0.5 rounded whitespace-nowrap ${
                          task.type === "inspection" && task.color 
                            ? 'text-white' 
                            : task.type === "email_draft"
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                        style={task.type === "inspection" && task.color ? { backgroundColor: task.color } : {}}
                        >
                          {task.type === "email_draft" ? 'Email' : new Date(task.due_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' })}
                          {task.type === "inspection" && ` ${new Date(task.due_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`}
                        </span>
                        {/* Inline due date — PF tasks only */}
                        {task.pf_id && task.id && task.type !== 'inspection' && task.type !== 'email_draft' && task.type !== 'admin_task' && (
                          <input
                            type="date"
                            value={task.due_date ? String(task.due_date).slice(0, 10) : ""}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); updateFeedTaskDate(task, e.target.value); }}
                            data-testid={`feed-task-due-date-${task.id}`}
                            className="text-xs px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            title="Due date"
                          />
                        )}
                        {/* Inline assignee — PF tasks only */}
                        {task.pf_id && task.id && task.type !== 'inspection' && task.type !== 'email_draft' && task.type !== 'admin_task' && (
                          <select
                            value={task.assigned_to || ""}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); reassignFeedTask(task, e.target.value); }}
                            data-testid={`feed-task-assignee-${task.id}`}
                            className="text-xs px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400 max-w-[100px]"
                            title="Reassign task"
                          >
                            <option value="">Unassigned</option>
                            {feedStaffList.map(s => (
                              <option key={s.email} value={s.email}>
                                {(s.full_name || s.name || s.email.split("@")[0]).split(" ")[0]}
                              </option>
                            ))}
                          </select>
                        )}
                        {/* Delete task button — only for embedded PF tasks */}
                        {task.pf_id && task.id && task.type !== 'inspection' && task.type !== 'email_draft' && task.type !== 'admin_task' && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); deleteFeedTask(task); }}
                            data-testid={`feed-task-delete-${task.id}`}
                            title="Delete task"
                            className="p-0.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {/* Admin task: delegate (assignee) + note */}
                        {task.type === 'admin_task' && task.id && (
                          <select
                            value={task.assigned_to || ""}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); delegateAdminTask(task, e.target.value); }}
                            data-testid={`feed-task-admin-assignee-${task.id}`}
                            className="text-xs px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-400 max-w-[110px]"
                            title="Delegate to colleague"
                          >
                            <option value="">Unassigned</option>
                            {feedStaffList.map(s => (
                              <option key={s.email} value={s.email}>
                                {(s.full_name || s.name || s.email.split("@")[0]).split(" ")[0]}
                              </option>
                            ))}
                          </select>
                        )}
                        {task.type === 'admin_task' && (
                          <div className="flex items-center gap-1" data-testid={`feed-task-admin-actions-${task.id}`}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); approveAdminTask(task); }}
                              data-testid={`feed-task-approve-${task.id}`}
                              title={task.admin_type === 'pf_deletion_request' ? 'Approve & recycle PF' : 'Approve'}
                              className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-600 hover:text-white font-medium whitespace-nowrap transition-colors"
                            >Approve</button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); dismissAdminTask(task); }}
                              data-testid={`feed-task-dismiss-${task.id}`}
                              title="Dismiss & notify requester"
                              className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 hover:bg-red-600 hover:text-white font-medium whitespace-nowrap transition-colors"
                            >Dismiss</button>
                          </div>
                        )}
                        <ExternalLink className="h-3 w-3 text-slate-400" />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* ============ END TO-DOS PANEL ============ */}
          
          {/* ============ ETA SPLIT DIVIDER ============ */}
          <div 
            className={`relative py-1 select-none flex-shrink-0 ${isPanelLocked ? 'cursor-default' : 'cursor-row-resize'}`}
            onMouseDown={(e) => {
              if (isPanelLocked) return;
              setIsDraggingEta(true);
              e.preventDefault();
            }}
          >
            <div className={`h-2 ${isDraggingEta ? 'bg-cyan-400' : isPanelLocked ? 'bg-slate-100' : 'bg-slate-200 hover:bg-slate-300'} rounded-full flex items-center justify-center transition-colors`}>
              <div className={`w-10 h-1 ${isPanelLocked ? 'bg-slate-200' : 'bg-slate-400'} rounded-full`}></div>
            </div>
          </div>

          {/* ============ ETA KOPER OVERVIEW PANEL ============ */}
          <div 
            className="bg-white rounded-xl border shadow-sm overflow-hidden flex-1" 
            data-testid="eta-koper-panel"
            style={{ minHeight: '80px' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-2 text-white flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Anchor className="h-5 w-5" />
                ETA Koper - Next 10 Days
              </h3>
              <span className="text-xs bg-white/20 px-2 py-1 rounded">
                {etaKoperArrivals.reduce((sum, d) => sum + d.count, 0)} arrivals
              </span>
            </div>
            
            {/* Content - Horizontal scrollable post-it style */}
            <div className="p-3 overflow-x-auto overflow-y-auto">
              {etaKoperArrivals.length === 0 ? (
                <div className="p-4 text-center text-slate-400 text-sm">
                  <Ship className="h-6 w-6 mx-auto mb-1 text-slate-300" />
                  No arrivals expected
                </div>
              ) : (
                <div className="flex gap-3 flex-wrap">
                  {etaKoperArrivals.flatMap((dateGroup) => {
                    const dateObj = new Date(dateGroup.date);
                    const isToday = dateObj.toDateString() === new Date().toDateString();
                    const isTomorrow = dateObj.toDateString() === new Date(Date.now() + 86400000).toDateString();
                    
                    // Create a post-it for each arrival
                    return dateGroup.arrivals.map((arrival, idx) => (
                      <div 
                        key={arrival.id || `${dateGroup.date}-${idx}`}
                        className={`flex-shrink-0 w-44 rounded-lg border-2 cursor-pointer transition-shadow hover:shadow-md ${
                          isToday 
                            ? 'border-blue-400 bg-blue-50' 
                            : isTomorrow 
                            ? 'border-amber-400 bg-amber-50' 
                            : 'border-slate-200 bg-slate-50'
                        }`}
                        onClick={() => handleSelectFolder(arrival)}
                      >
                        {/* Date badge */}
                        <div className={`px-2 py-1 text-xs font-semibold flex items-center justify-between ${
                          isToday 
                            ? 'bg-blue-500 text-white rounded-t-md' 
                            : isTomorrow 
                            ? 'bg-amber-500 text-white rounded-t-md' 
                            : 'bg-slate-300 text-slate-700 rounded-t-md'
                        }`}>
                          <span>{isToday ? 'TODAY' : isTomorrow ? 'TOMORROW' : dateObj.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase()}</span>
                          <span>{dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                        </div>
                        
                        {/* Content */}
                        <div className="p-2">
                          <div className="font-mono text-xs font-bold text-blue-600 mb-1">
                            {arrival.pf_number}
                          </div>
                          <div className="text-xs text-slate-700 truncate font-medium">
                            {arrival.customer || arrival.customer_name}
                          </div>
                          {arrival.vessel_name && (
                            <div className="text-xs text-slate-500 truncate flex items-center gap-1 mt-1">
                              <Ship className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{arrival.vessel_name}</span>
                            </div>
                          )}
                          {arrival.containers && arrival.containers.length > 0 && (
                            <div className="text-xs text-slate-400 mt-0.5">
                              {arrival.containers.length} container{arrival.containers.length > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    ));
                  })}
                </div>
              )}
            </div>
          </div>
          {/* ============ END ETA KOPER PANEL ============ */}
          
          </div>
          {/* ============ END TASKS + ETA CONTAINER ============ */}
          {/* ============ END MAIN SPLIT CONTAINER ============ */}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-4 py-2">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <UploadEmlModal
            onClose={() => setShowUploadModal(false)}
            onUpload={handleUploadEml}
          />
        )}

        {/* Create New PF Modal */}
        {showCreatePFModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <Plus className="h-5 w-5" />
                      Create New Position Folder
                    </h2>
                    <p className="text-emerald-100 text-sm mt-1">
                      Manually create a new PF without email import
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowCreatePFModal(false)}
                    className="text-white hover:bg-white/20"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div 
                className="p-6 space-y-5 max-h-[80vh] overflow-y-auto"
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const file = e.dataTransfer.files?.[0];
                  if (file) {
                    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
                      setCreatePFMode("excel");
                      setImportFile(file);
                    } else if (file.name.endsWith('.msg') || file.name.endsWith('.eml') || file.name.endsWith('.pdf')) {
                      setCreatePFMode("email");
                      // Trigger email handler
                      handleEmailDrop({ dataTransfer: e.dataTransfer, preventDefault: () => {} });
                    }
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {/* Import Options Tabs */}
                <div className="flex gap-2 border-b pb-3">
                  <button
                    onClick={() => setCreatePFMode("manual")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      createPFMode === "manual" || !createPFMode
                        ? "bg-emerald-100 text-emerald-700"
                        : "text-slate-500 hover:bg-slate-100"
                    }`}
                  >
                    <Plus className="h-4 w-4 inline mr-1" />
                    Manual Entry
                  </button>
                  <button
                    onClick={() => setCreatePFMode("email")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      createPFMode === "email"
                        ? "bg-blue-100 text-blue-700"
                        : "text-slate-500 hover:bg-slate-100"
                    }`}
                  >
                    <Mail className="h-4 w-4 inline mr-1" />
                    Email Import
                  </button>
                  <button
                    onClick={() => setCreatePFMode("excel")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      createPFMode === "excel"
                        ? "bg-purple-100 text-purple-700"
                        : "text-slate-500 hover:bg-slate-100"
                    }`}
                  >
                    <FileUp className="h-4 w-4 inline mr-1" />
                    Excel Import (Eurasia)
                  </button>
                  <button
                    onClick={() => setCreatePFMode("maersk")}
                    data-testid="create-pf-tab-maersk"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      createPFMode === "maersk"
                        ? "bg-sky-100 text-sky-700"
                        : "text-slate-500 hover:bg-slate-100"
                    }`}
                  >
                    <FileUp className="h-4 w-4 inline mr-1" />
                    Maersk Booking
                  </button>
                </div>

                {/* Maersk Booking Import Mode */}
                {createPFMode === "maersk" && (
                  <div className="space-y-4" data-testid="maersk-import-panel">
                    <div className="bg-sky-50 rounded-lg p-4 border border-sky-200">
                      <label className="block text-sm font-medium text-sky-900 mb-1">Template PF to clone</label>
                      <p className="text-xs text-sky-700 mb-2">Customer, email, notification list, carrier & service type are copied from this PF. Each Excel row becomes one new PF with its own containers, booking ref, ports, ETD/ETA & vessel.</p>
                      <input
                        type="text"
                        value={maerskTemplatePf}
                        onChange={(e) => setMaerskTemplatePf(e.target.value)}
                        placeholder="e.g. PF-000763-26"
                        data-testid="maersk-template-pf-input"
                        className="w-full border border-sky-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>

                    <div
                      onDrop={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        const f = e.dataTransfer.files?.[0];
                        if (f && (f.name.endsWith(".xlsx") || f.name.endsWith(".xls"))) { setMaerskFile(f); setMaerskResult(null); }
                        else toast.error("Please drop an Excel file (.xlsx or .xls)");
                      }}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                        maerskFile ? "border-green-400 bg-green-50" : "border-sky-300 hover:border-sky-400 bg-sky-50"
                      }`}
                    >
                      {maerskImporting ? (
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="h-10 w-10 text-sky-600 animate-spin" />
                          <p className="text-sky-600 font-medium">Creating Position Folders…</p>
                        </div>
                      ) : maerskFile ? (
                        <div className="space-y-2">
                          <CheckCircle className="h-10 w-10 text-green-500 mx-auto" />
                          <p className="font-medium text-slate-800" data-testid="maersk-file-name">{maerskFile.name}</p>
                          <p className="text-sm text-slate-500">{(maerskFile.size / 1024).toFixed(1)} KB</p>
                          <div className="flex gap-2 justify-center mt-3">
                            <Button variant="ghost" size="sm" onClick={() => { setMaerskFile(null); setMaerskResult(null); }} className="text-red-600">
                              Remove
                            </Button>
                            <Button onClick={handleImportMaersk} data-testid="maersk-import-btn" className="bg-sky-600 hover:bg-sky-700">
                              <FileUp className="h-4 w-4 mr-2" />
                              Create PFs from bookings
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <label className="cursor-pointer block">
                          <Upload className="h-10 w-10 text-sky-400 mx-auto mb-3" />
                          <p className="text-slate-700 font-medium">
                            <span className="text-sky-600 hover:underline">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-sm text-slate-500 mt-1">Maersk shipments export (.xlsx) — one PF per row on the "Shipments" sheet</p>
                          <input
                            type="file"
                            accept=".xlsx,.xls"
                            className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) { setMaerskFile(f); setMaerskResult(null); } }}
                            data-testid="maersk-import-input"
                          />
                        </label>
                      )}
                    </div>

                    {maerskResult && (
                      <div className="bg-white rounded-lg p-4 border border-slate-200" data-testid="maersk-import-result">
                        <p className="font-medium text-slate-800 text-sm mb-2">
                          Created {maerskResult.created_count} PF(s){maerskResult.skipped_count ? ` · ${maerskResult.skipped_count} skipped (already imported)` : ""}{maerskResult.error_count ? ` · ${maerskResult.error_count} failed` : ""}
                        </p>
                        <ul className="text-sm text-slate-600 space-y-1 max-h-48 overflow-y-auto">
                          {(maerskResult.created || []).map((c) => (
                            <li key={c.pf_number} className="flex justify-between">
                              <span className="font-mono text-emerald-700">{c.pf_number}</span>
                              <span className="text-slate-400">bkg {c.booking_number || "—"} · {c.containers} cntr</span>
                            </li>
                          ))}
                          {(maerskResult.skipped || []).map((s, idx) => (
                            <li key={`skip-${idx}`} className="flex justify-between text-amber-600">
                              <span>Row {s.row} — booking {s.booking_number}</span>
                              <span className="text-slate-400">exists: {s.existing_pf}</span>
                            </li>
                          ))}
                          {(maerskResult.errors || []).map((er, idx) => (
                            <li key={`err-${idx}`} className="text-rose-600">Row {er.row}: {er.error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Excel Import Mode */}
                {createPFMode === "excel" && (
                  <div className="space-y-4">
                    <div
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const file = e.dataTransfer.files?.[0];
                        if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
                          setImportFile(file);
                        } else {
                          toast.error("Please drop an Excel file (.xlsx or .xls)");
                        }
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                        importFile ? "border-green-400 bg-green-50" : "border-purple-300 hover:border-purple-400 bg-purple-50"
                      }`}
                    >
                      {importing ? (
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="h-10 w-10 text-purple-600 animate-spin" />
                          <p className="text-purple-600 font-medium">Importing and creating PFs...</p>
                        </div>
                      ) : importFile ? (
                        <div className="space-y-2">
                          <CheckCircle className="h-10 w-10 text-green-500 mx-auto" />
                          <p className="font-medium text-slate-800">{importFile.name}</p>
                          <p className="text-sm text-slate-500">{(importFile.size / 1024).toFixed(1)} KB</p>
                          <div className="flex gap-2 justify-center mt-3">
                            <Button variant="ghost" size="sm" onClick={() => setImportFile(null)} className="text-red-600">
                              Remove
                            </Button>
                            <Button onClick={handleImportExcel} className="bg-purple-600 hover:bg-purple-700">
                              <FileUp className="h-4 w-4 mr-2" />
                              Import & Create PFs
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <label className="cursor-pointer block">
                          <Upload className="h-10 w-10 text-purple-400 mx-auto mb-3" />
                          <p className="text-slate-700 font-medium">
                            <span className="text-purple-600 hover:underline">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-sm text-slate-500 mt-1">
                            Excel files (.xlsx, .xls) - Eurasia Container Load Report format
                          </p>
                          <input
                            type="file"
                            accept=".xlsx,.xls"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) setImportFile(file);
                            }}
                            data-testid="excel-import-input"
                          />
                        </label>
                      )}
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <h4 className="font-medium text-purple-800 text-sm mb-2">What gets imported:</h4>
                      <ul className="text-sm text-purple-700 space-y-1">
                        <li>• <strong>Master PF</strong> with vessel, container, MBL info</li>
                        <li>• <strong>All House BLs</strong> as sub-PFs (-1, -2, -3...)</li>
                        <li>• Shipper, Consignee, Cargo details</li>
                        <li>• Weights, volumes, incoterms</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* Email Drop Zone - only show if email mode or manual mode */}
                {(createPFMode === "email" || !createPFMode) && createPFMode !== "excel" && createPFMode !== "maersk" && (
                  <div
                    onDrop={handleEmailDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                      isDragOver
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-300 hover:border-slate-400 bg-slate-50"
                    }`}
                  >
                    {parsingEmail ? (
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-blue-600 font-medium">Parsing email with AI...</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-center gap-3 mb-2">
                          <Mail className="h-8 w-8 text-slate-400" />
                          <ArrowRight className="h-5 w-5 text-slate-300" />
                          <FolderOpen className="h-8 w-8 text-emerald-500" />
                        </div>
                        <p className="text-slate-700 font-medium">
                          Drop an email file to auto-populate
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          Supports .msg, .eml and .pdf files • AI extracts container, BL, ports, dates
                        </p>
                        <input
                          type="file"
                          accept=".msg,.eml,.pdf"
                          onChange={handleEmailDrop}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          data-testid="pf-email-upload-input"
                        />
                      </>
                    )}
                  </div>
                )}

                {/* Manual Form Fields - hide when in excel mode */}
                {createPFMode !== "excel" && createPFMode !== "maersk" && (
                  <>
                {/* CRM Quick Fill */}
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-700">Quick Fill from CRM</span>
                  </div>
                  <ContactAutocomplete
                    type="both"
                    placeholder="Search contacts or companies..."
                    onSelect={(item) => {
                      if (item) {
                        if (item._type === "company") {
                          setNewPFData(prev => ({ 
                            ...prev, 
                            customer_name: item.name,
                            customer_email: item.domain ? `info@${item.domain}` : prev.customer_email
                          }));
                          toast.success(`Filled with company: ${item.name}`);
                        } else {
                          setNewPFData(prev => ({ 
                            ...prev, 
                            customer_name: item.company_name || item.name,
                            customer_email: item.email || prev.customer_email
                          }));
                          toast.success(`Filled with contact: ${item.name}`);
                        }
                      }
                    }}
                  />
                </div>

                {/* Pantheon Company Selection - REQUIRED */}
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                  <PantheonCompanySelector
                    value={selectedCompany}
                    onChange={(company) => {
                      setSelectedCompany(company);
                      if (company) {
                        setNewPFData(prev => ({ 
                          ...prev, 
                          crm_company_id: company.type === 'group' ? null : company.id,
                          crm_group_id: company.type === 'group' ? company.id : null,
                          customer_name: company.name,
                        }));
                      } else {
                        setNewPFData(prev => ({ 
                          ...prev, 
                          crm_company_id: null,
                          crm_group_id: null,
                          customer_name: '',
                        }));
                      }
                    }}
                    placeholder="Search Pantheon companies..."
                    required={true}
                    showGroupOption={true}
                  />
                  
                  {/* Info Banner */}
                  <div className="mt-3 p-2.5 bg-emerald-100/50 rounded-lg border border-emerald-200">
                    <p className="text-xs text-emerald-700">
                      <strong>Required:</strong> Select a company from Pantheon list. If not found, you can register a new company 
                      (notification will be sent to accounting).
                    </p>
                  </div>

                  {/* Credit / SOA check banner */}
                  {creditChecking && (
                    <div className="mt-3 p-2.5 bg-slate-100 rounded-lg border border-slate-200 text-xs text-slate-600 flex items-center gap-2" data-testid="credit-check-loading">
                      <Loader2 className="h-3 w-3 animate-spin" /> Checking customer SOA…
                    </div>
                  )}
                  {creditCheck && !creditChecking && (
                    (() => {
                      const status = creditCheck.credit_status;
                      const isHardBlock = status === "over_limit" || status === "overdue";
                      const isSoftWarn = status === "overdue_no_agreement";
                      const isOk = creditCheck.approved && (status === "ok" || status === "no_agreement");
                      const tone = isHardBlock
                        ? "bg-red-50 border-red-200 text-red-800"
                        : isSoftWarn
                          ? "bg-amber-50 border-amber-200 text-amber-800"
                          : "bg-emerald-50 border-emerald-200 text-emerald-800";
                      const title = isHardBlock
                        ? "⛔ Credit hold — needs CEO approval"
                        : isSoftWarn
                          ? "⚠ Customer has overdue invoices"
                          : status === "no_agreement"
                            ? "✓ No open balance"
                            : "✓ Credit OK";
                      return (
                        <div className={`mt-3 p-3 rounded-lg border text-xs ${tone}`} data-testid={`credit-check-banner-${status}`}>
                          <div className="font-semibold text-sm">{title}</div>
                          <div className="mt-1">{creditCheck.message}</div>
                          {(creditCheck.overdue_amount > 0 || creditCheck.current_balance > 0) && (
                            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                              {creditCheck.credit_limit > 0 && (
                                <div><span className="opacity-70">Limit:</span> €{Number(creditCheck.credit_limit).toLocaleString()}</div>
                              )}
                              <div><span className="opacity-70">Balance:</span> €{Number(creditCheck.current_balance || 0).toLocaleString()}</div>
                              {creditCheck.overdue_amount > 0 && (
                                <div className="font-semibold"><span className="opacity-70 font-normal">Overdue:</span> €{Number(creditCheck.overdue_amount).toLocaleString()} ({creditCheck.overdue_invoices || 0} inv.)</div>
                              )}
                              {creditCheck.available_credit !== undefined && creditCheck.available_credit > 0 && (
                                <div><span className="opacity-70">Available:</span> €{Number(creditCheck.available_credit).toLocaleString()}</div>
                              )}
                            </div>
                          )}
                          {isOk && status === "no_agreement" && (
                            <div className="mt-1 opacity-70 italic">No credit agreement on file — proceed normally.</div>
                          )}
                        </div>
                      );
                    })()
                  )}
                </div>

                {/* Customer Details */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <User className="h-4 w-4 text-slate-500" />
                    Contact Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Customer Name - Auto-filled from Pantheon, readonly if selected */}
                    <div>
                      <Label className="text-xs text-slate-500">Company Name {!selectedCompany && '*'}</Label>
                      <Input
                        value={newPFData.customer_name}
                        onChange={(e) => !selectedCompany && setNewPFData(prev => ({ ...prev, customer_name: e.target.value }))}
                        placeholder={selectedCompany ? "Auto-filled from Pantheon" : "Select company above"}
                        className={`mt-1 ${selectedCompany ? 'bg-slate-50 text-slate-600' : ''}`}
                        disabled={!!selectedCompany}
                        data-testid="pf-customer-name-input"
                      />
                      {selectedCompany && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                          <Check className="h-3 w-3" />
                          Linked to Pantheon
                        </p>
                      )}
                    </div>
                    <div className="relative">
                      <Label className="text-xs text-slate-500">
                        Customer Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="email"
                        value={newPFData.customer_email}
                        onChange={(e) => {
                          setNewPFData(prev => ({ ...prev, customer_email: e.target.value }));
                          setShowEmailSuggestions(false);
                        }}
                        onFocus={() => suggestedEmails.length > 0 && setShowEmailSuggestions(true)}
                        placeholder="customer@company.com"
                        className="mt-1"
                        required
                        data-testid="pf-customer-email-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Required so milestone, arrival-notice & delivery emails reach the customer.
                        If unknown, add at least one Notification Email below.
                      </p>
                      {/* Email suggestions dropdown */}
                      {showEmailSuggestions && suggestedEmails.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                          <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50 border-b">
                            Emails from previous orders with this customer:
                          </div>
                          {suggestedEmails.map((email, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setNewPFData(prev => ({ ...prev, customer_email: email }));
                                setShowEmailSuggestions(false);
                                toast.success(`Email auto-filled: ${email}`);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2 transition-colors"
                            >
                              <Mail className="h-3 w-3 text-slate-400" />
                              {email}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Shipment Details */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Package className="h-4 w-4 text-slate-500" />
                    Shipment Details
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500">Description *</Label>
                      <Input
                        value={newPFData.description}
                        onChange={(e) => setNewPFData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="PRE-ALERT // CMA CGM EUGENE // Container details..."
                        className="mt-1"
                        data-testid="pf-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">Service Type</Label>
                        <select
                          value={newPFData.service_type}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, service_type: e.target.value }))}
                          className="w-full mt-1 rounded-lg border-slate-200 text-sm h-10 px-3"
                        >
                          <option value="FCL">FCL (Full Container)</option>
                          <option value="LCL">LCL (Less than Container)</option>
                          <option value="AIR">Air Freight</option>
                          <option value="TRUCKING">Trucking</option>
                          <option value="RAIL">Rail</option>
                          <option value="CUSTOMS">Customs Clearance</option>
                          <option value="MULTIMODAL">Multimodal</option>
                          <option value="WAREHOUSE">Warehouse</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Shipping Line</Label>
                        <CarrierSelect
                          value={newPFData.shipping_line}
                          onChange={(val) => setNewPFData(prev => ({ ...prev, shipping_line: val }))}
                          testId="create-pf-shipping-line-select"
                        />
                      </div>
                    </div>
                    
                    {/* Direction Toggle - Import/Export/Trucking */}
                    <div>
                      <Label className="text-xs text-slate-500 mb-2 block">Direction</Label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setNewPFData(prev => ({ ...prev, direction: "import" }))}
                          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                            newPFData.direction === "import"
                              ? "bg-blue-600 text-white shadow-md"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          <span className="flex items-center justify-center gap-2">
                            <ArrowDown className="h-4 w-4" />
                            Import
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewPFData(prev => ({ ...prev, direction: "export" }))}
                          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                            newPFData.direction === "export"
                              ? "bg-emerald-600 text-white shadow-md"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          <span className="flex items-center justify-center gap-2">
                            <ArrowUp className="h-4 w-4" />
                            Export
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewPFData(prev => ({ ...prev, direction: "cross_trade" }))}
                          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                            newPFData.direction === "cross_trade"
                              ? "bg-purple-600 text-white shadow-md"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          <span className="flex items-center justify-center gap-2">
                            <ArrowRight className="h-4 w-4" />
                            Cross Trade
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Trucking-only section ──────────────────────────────────
                    2026-05-27 — shown only when Service Type = TRUCKING.
                    Captures truck/trailer/driver/dispatcher contacts so ops
                    can reach the right person without digging through email. */}
                {newPFData.service_type === "TRUCKING" && (
                  <div className="bg-amber-50/60 rounded-xl p-4 sm:p-5 border border-amber-200" data-testid="pf-create-trucking-section">
                    <div className="flex items-center gap-2 mb-3">
                      <Truck className="h-4 w-4 text-amber-700" />
                      <h3 className="text-sm font-semibold text-slate-700">Trucking Details</h3>
                      <span className="text-xs text-slate-500">— filled when the truck is assigned</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-slate-500">Trucking Company</Label>
                        <Input
                          placeholder="e.g. XPO Transport d.o.o."
                          value={newPFData.trucking_details?.trucking_company || ""}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, trucking_details: { ...prev.trucking_details, trucking_company: e.target.value } }))}
                          data-testid="pf-create-trucking-company"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Truck (plate)</Label>
                        <Input
                          placeholder="e.g. LJ-AB-123"
                          value={newPFData.trucking_details?.truck_plate || ""}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, trucking_details: { ...prev.trucking_details, truck_plate: e.target.value } }))}
                          data-testid="pf-create-trucking-truck-plate"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Trailer (plate)</Label>
                        <Input
                          placeholder="e.g. LJ-CD-456"
                          value={newPFData.trucking_details?.trailer_plate || ""}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, trucking_details: { ...prev.trucking_details, trailer_plate: e.target.value } }))}
                          data-testid="pf-create-trucking-trailer-plate"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Driver Name</Label>
                        <Input
                          placeholder="Full name"
                          value={newPFData.trucking_details?.driver_name || ""}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, trucking_details: { ...prev.trucking_details, driver_name: e.target.value } }))}
                          data-testid="pf-create-trucking-driver-name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Driver Phone</Label>
                        <Input
                          placeholder="+386 …"
                          value={newPFData.trucking_details?.driver_phone || ""}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, trucking_details: { ...prev.trucking_details, driver_phone: e.target.value } }))}
                          data-testid="pf-create-trucking-driver-phone"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Dispatcher Name</Label>
                        <Input
                          placeholder="Full name"
                          value={newPFData.trucking_details?.dispatcher_name || ""}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, trucking_details: { ...prev.trucking_details, dispatcher_name: e.target.value } }))}
                          data-testid="pf-create-trucking-dispatcher-name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Dispatcher Phone</Label>
                        <Input
                          placeholder="+386 …"
                          value={newPFData.trucking_details?.dispatcher_phone || ""}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, trucking_details: { ...prev.trucking_details, dispatcher_phone: e.target.value } }))}
                          data-testid="pf-create-trucking-dispatcher-phone"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-slate-500">Dispatcher Email</Label>
                        <Input
                          type="email"
                          placeholder="dispatcher@company.com"
                          value={newPFData.trucking_details?.dispatcher_email || ""}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, trucking_details: { ...prev.trucking_details, dispatcher_email: e.target.value } }))}
                          data-testid="pf-create-trucking-dispatcher-email"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-slate-500">Notes</Label>
                        <Textarea
                          rows={2}
                          placeholder="Pickup window, loading reference, …"
                          value={newPFData.trucking_details?.notes || ""}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, trucking_details: { ...prev.trucking_details, notes: e.target.value } }))}
                          data-testid="pf-create-trucking-notes"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Container/BL Details - Air Freight shows AWB Details */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-500" />
                    {newPFData.service_type === "AIR" ? "AWB & Flight Details" : "Container & BL Details"}
                  </h3>
                  <div className="space-y-3">
                    {/* Booking # — first identifier ops gets from the carrier
                        when the booking is confirmed (legacy: email, carrier
                        system, etc.). Surfaces it before vessel/voyage which
                        usually arrive later. Skipped for AIR (separate flow). */}
                    {newPFData.service_type !== "AIR" && (
                      <div>
                        <Label className="text-xs text-slate-500">
                          Booking Number <span className="text-slate-400">(from carrier)</span>
                        </Label>
                        <Input
                          value={newPFData.booking_number}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, booking_number: e.target.value }))}
                          placeholder="e.g. ZIMUKOP1033149, MAEU269163428, CMA-A1B2C3..."
                          className="mt-1"
                          data-testid="new-pf-booking-number-input"
                        />
                      </div>
                    )}
                    {/* BL Structure — how the Bill of Lading is issued.
                        Direct MBL: shipper/consignee directly on the master BL (no house BL).
                        Indirect MBL: MBL issued to a forwarder with house BLs underneath.
                        HBL: this PF tracks a House Bill of Lading. */}
                    {newPFData.service_type !== "AIR" && (
                      <div>
                        <Label className="text-xs text-slate-500">BL Structure</Label>
                        <select
                          value={newPFData.bl_structure || ""}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, bl_structure: e.target.value }))}
                          className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                          data-testid="new-pf-bl-structure-select"
                        >
                          <option value="">Not specified</option>
                          <option value="Direct MBL">Direct MBL (shipper/consignee on master BL)</option>
                          <option value="Indirect MBL">Indirect MBL (MBL to forwarder + house BLs)</option>
                          <option value="HBL">HBL (House Bill of Lading)</option>
                        </select>
                      </div>
                    )}

                    {/* Vessel Info - show for FCL/LCL, Flight Info for AIR */}
                    {newPFData.service_type === "AIR" ? (
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-slate-500">Airline</Label>
                          <Input
                            value={newPFData.airline_name}
                            onChange={(e) => setNewPFData(prev => ({ ...prev, airline_name: e.target.value }))}
                            placeholder="Turkish Airlines, Emirates..."
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Flight Number</Label>
                          <Input
                            value={newPFData.flight_number}
                            onChange={(e) => setNewPFData(prev => ({ ...prev, flight_number: e.target.value }))}
                            placeholder="TK1234"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Pieces/Weight</Label>
                          <Input
                            value={newPFData.air_cargo_weight}
                            onChange={(e) => setNewPFData(prev => ({ ...prev, air_cargo_weight: e.target.value }))}
                            placeholder="5 pcs / 250 kg"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-slate-500">Vessel Name</Label>
                          <Input
                            value={newPFData.vessel_name}
                            onChange={(e) => setNewPFData(prev => ({ ...prev, vessel_name: e.target.value }))}
                            placeholder="CMA CGM MONTREAL"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Voyage</Label>
                          <Input
                            value={newPFData.voyage}
                            onChange={(e) => setNewPFData(prev => ({ ...prev, voyage: e.target.value }))}
                            placeholder="0IQ2QE1MA"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Seal Number</Label>
                          <Input
                            value={newPFData.seal_number}
                            onChange={(e) => setNewPFData(prev => ({ ...prev, seal_number: e.target.value }))}
                            placeholder="0457713"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    )}
                    {/* Container Numbers - hide for AIR */}
                    {newPFData.service_type !== "AIR" && (
                    <div>
                      <Label className="text-xs text-slate-500">Container Numbers (comma separated)</Label>
                      <Input
                        value={newPFData.containers}
                        onChange={(e) => setNewPFData(prev => ({ ...prev, containers: e.target.value }))}
                        placeholder="CSNU5701472, CCFS2KOP25166089, CCFS2KOP25170417"
                        className="mt-1"
                        data-testid="pf-containers-input"
                      />
                    </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">
                          {newPFData.service_type === "AIR" ? "MAWB Number" : "MBL Number"}
                        </Label>
                        <Input
                          value={newPFData.mbl_number}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, mbl_number: e.target.value }))}
                          placeholder={newPFData.service_type === "AIR" ? "176-12345678" : "SEL1996688"}
                          className="mt-1"
                        />
                        {newPFData.service_type === "AIR" && (
                          <p className="text-xs text-blue-600 mt-1">Format: 123-12345678 (first 3 digits = airline code)</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">
                          {newPFData.service_type === "AIR" ? "HAWB Numbers (comma separated)" : "HBL Numbers (comma separated)"}
                        </Label>
                        <Input
                          value={newPFData.hbl_numbers}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, hbl_numbers: e.target.value }))}
                          placeholder={newPFData.service_type === "AIR" ? "HAWB123456" : "GLVSKR0137626"}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Route Details */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-slate-500" />
                    Route & Schedule
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Origin Port</Label>
                      <SmartLocationInput
                        value={{
                          city: newPFData.origin_port,
                          country: newPFData.origin_country || "",
                          port_code: newPFData.origin_port_code || "",
                          port_name: newPFData.origin_port,
                        }}
                        onChange={(loc) => setNewPFData(prev => ({
                          ...prev,
                          origin_port: loc.city || loc.port_name || "",
                          origin_port_code: loc.port_code || "",
                          origin_country: loc.country || "",
                        }))}
                        showSeaport={true}
                        showAirport={newPFData.service_type === "AIR"}
                        placeholder="Search port..."
                        data-testid="pf-origin-port"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Destination Port</Label>
                      <SmartLocationInput
                        value={{
                          city: newPFData.destination_port,
                          country: newPFData.destination_country || "",
                          port_code: newPFData.destination_port_code || "",
                          port_name: newPFData.destination_port,
                        }}
                        onChange={(loc) => setNewPFData(prev => ({
                          ...prev,
                          destination_port: loc.city || loc.port_name || "",
                          destination_port_code: loc.port_code || "",
                          destination_country: loc.country || "",
                        }))}
                        showSeaport={true}
                        showAirport={newPFData.service_type === "AIR"}
                        placeholder="Search port..."
                        data-testid="pf-destination-port"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">ETD (Departure)</Label>
                      <Input
                        type="date"
                        value={newPFData.etd}
                        onChange={(e) => setNewPFData(prev => ({ ...prev, etd: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">ETA (Arrival)</Label>
                      <Input
                        type="date"
                        value={newPFData.eta}
                        onChange={(e) => setNewPFData(prev => ({ ...prev, eta: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Remarks */}
                <div>
                  <Label className="text-xs text-slate-500">Remarks</Label>
                  <textarea
                    value={newPFData.remarks}
                    onChange={(e) => setNewPFData(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Additional notes..."
                    rows={2}
                    className="w-full mt-1 rounded-lg border-slate-200 text-sm px-3 py-2"
                  />
                </div>

                {/* LCL Consolidation Options */}
                {newPFData.service_type === "LCL" && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      LCL Consolidation
                    </h3>
                    <div className="space-y-3">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newPFData.is_master}
                          onChange={(e) => setNewPFData(prev => ({ ...prev, is_master: e.target.checked }))}
                          className="rounded"
                        />
                        <span className="text-sm">This is a Master PF (container consolidation)</span>
                      </label>
                      <p className="text-xs text-purple-700">
                        Master PF: Container belongs to Freightlinks → Europacific. 
                        Sub-orders will be created for each actual shipper/consignee (HBL).
                      </p>
                      <div className="grid grid-cols-2 gap-4 mt-2">
                        <div>
                          <Label className="text-xs text-slate-500">Shipper (MBL)</Label>
                          <Input
                            value={newPFData.shipper}
                            onChange={(e) => setNewPFData(prev => ({ ...prev, shipper: e.target.value }))}
                            placeholder="Freightlinks"
                            className="mt-1 bg-white"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Consignee (MBL)</Label>
                          <Input
                            value={newPFData.consignee}
                            onChange={(e) => setNewPFData(prev => ({ ...prev, consignee: e.target.value }))}
                            placeholder="Europacific"
                            className="mt-1 bg-white"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tracking Integration */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <Ship className="h-4 w-4" />
                    Shipment Tracking
                  </h3>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newPFData.enable_tracking}
                        onChange={(e) => setNewPFData(prev => ({ ...prev, enable_tracking: e.target.checked }))}
                        className="rounded border-blue-300"
                        data-testid="pf-enable-tracking-checkbox"
                      />
                      <span className="text-sm font-medium">Enable automatic tracking</span>
                    </label>
                    <p className="text-xs text-blue-700">
                      Automatically add containers/BL numbers to the tracking watchlist for real-time status updates.
                    </p>
                    
                    {newPFData.enable_tracking && (
                      <div className="mt-3 pl-5 space-y-2 border-l-2 border-blue-200">
                        <p className="text-xs text-blue-600 font-medium mb-2">Track by:</p>
                        
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={newPFData.track_containers}
                            onChange={(e) => setNewPFData(prev => ({ ...prev, track_containers: e.target.checked }))}
                            className="rounded border-blue-300"
                            disabled={!newPFData.containers}
                          />
                          <span className={`text-sm ${!newPFData.containers ? 'text-slate-400' : ''}`}>
                            Container Numbers {newPFData.containers && `(${newPFData.containers.split(',').filter(c => c.trim()).length})`}
                          </span>
                        </label>
                        
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={newPFData.track_mbl}
                            onChange={(e) => setNewPFData(prev => ({ ...prev, track_mbl: e.target.checked }))}
                            className="rounded border-blue-300"
                            disabled={!newPFData.mbl_number}
                          />
                          <span className={`text-sm ${!newPFData.mbl_number ? 'text-slate-400' : ''}`}>
                            Master BL {newPFData.mbl_number && `(${newPFData.mbl_number})`}
                          </span>
                        </label>
                        
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={newPFData.track_hbl}
                            onChange={(e) => setNewPFData(prev => ({ ...prev, track_hbl: e.target.checked }))}
                            className="rounded border-blue-300"
                            disabled={!newPFData.hbl_numbers}
                          />
                          <span className={`text-sm ${!newPFData.hbl_numbers ? 'text-slate-400' : ''}`}>
                            House BL {newPFData.hbl_numbers && `(${newPFData.hbl_numbers.split(',').filter(h => h.trim()).length})`}
                          </span>
                        </label>
                        
                        {/* Tracking Sources Section */}
                        <div className="mt-4 pt-3 border-t border-blue-200">
                          <p className="text-xs text-blue-600 font-medium mb-2">Tracking Sources:</p>
                          <div className="grid grid-cols-2 gap-2">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={newPFData.tracking_fcl}
                                onChange={(e) => setNewPFData(prev => ({ ...prev, tracking_fcl: e.target.checked }))}
                                className="rounded border-blue-300"
                              />
                              <span className="text-sm">FCL Tracking</span>
                            </label>
                            
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={newPFData.tracking_lcl}
                                onChange={(e) => setNewPFData(prev => ({ ...prev, tracking_lcl: e.target.checked }))}
                                className="rounded border-blue-300"
                              />
                              <span className="text-sm">LCL Tracking</span>
                            </label>
                            
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={newPFData.tracking_port}
                                onChange={(e) => setNewPFData(prev => ({ ...prev, tracking_port: e.target.checked }))}
                                className="rounded border-blue-300"
                              />
                              <span className="text-sm">Port Tracking</span>
                            </label>
                            
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={newPFData.tracking_metrans}
                                onChange={(e) => setNewPFData(prev => ({ ...prev, tracking_metrans: e.target.checked }))}
                                className="rounded border-blue-300"
                              />
                              <span className="text-sm">Metrans Rail</span>
                            </label>
                          </div>
                        </div>
                        
                        <div className="mt-3">
                          <Label className="text-xs text-blue-700">Notification Emails (CC list)</Label>
                          <Input
                            value={newPFData.notification_emails}
                            onChange={(e) => setNewPFData(prev => ({ ...prev, notification_emails: e.target.value }))}
                            placeholder="notify@customer.com, ops@company.com"
                            className="mt-1 bg-white text-sm"
                            data-testid="pf-notification-emails-input"
                          />
                          <p className="text-xs text-blue-600 mt-1">
                            Receive email notifications when shipment status changes
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                  </>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => { setShowCreatePFModal(false); setCreatePFMode("manual"); setImportFile(null); }}
                  >
                    Cancel
                  </Button>
                  {createPFMode !== "excel" && createPFMode !== "maersk" && (
                    <Button
                      onClick={() => createNewPF()}
                      disabled={creatingPF}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="submit-create-pf-btn"
                    >
                      {creatingPF ? (
                        <>Creating...</>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Create Position Folder
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Duplicate PF Warning Modal */}
        {showDuplicateWarning && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="duplicate-warning-modal">
            <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
              <div className="flex items-center gap-3 border-b border-red-100 bg-red-50 px-5 py-4 rounded-t-xl">
                <AlertTriangle className="h-6 w-6 shrink-0 text-red-600" />
                <div>
                  <h3 className="text-lg font-semibold text-red-800">Possible duplicate Position Folder</h3>
                  <p className="text-sm text-red-600">{duplicatePFs.length} open PF{duplicatePFs.length > 1 ? "s" : ""} already use the same shipment reference. Check the original before opening a new one.</p>
                </div>
              </div>
              <div className="max-h-[50vh] overflow-y-auto px-5 py-4 space-y-3">
                {duplicatePFs.map((d) => (
                  <div key={d.id} className="rounded-lg border border-slate-200 p-3" data-testid={`duplicate-match-${d.pf_number}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-slate-800">{d.pf_number}</span>
                          {(d.matched_on || []).map((m, i) => (
                            <span key={i} className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">{m}</span>
                          ))}
                        </div>
                        <p className="mt-0.5 text-sm text-slate-600 truncate">{d.customer || "—"}</p>
                        <p className="text-xs text-slate-400">
                          Opened by {(d.created_by || "").split("@")[0]}{d.created_at ? ` · ${new Date(d.created_at).toLocaleDateString()}` : ""}
                        </p>
                        {d.description ? <p className="mt-1 text-xs text-slate-500 line-clamp-2">{d.description}</p> : null}
                      </div>
                      <button
                        onClick={() => window.open(`/position-folders?search=${encodeURIComponent(d.pf_number)}`, "_blank")}
                        className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-1"
                        data-testid={`duplicate-open-original-${d.pf_number}`}
                      >
                        <ExternalLink className="h-3 w-3" /> Open original
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-end gap-2 border-t border-slate-100 px-5 py-4">
                <Button variant="outline" onClick={() => setShowDuplicateWarning(false)} data-testid="duplicate-cancel-btn">
                  Don't open
                </Button>
                <Button
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  onClick={() => { setShowDuplicateWarning(false); createNewPF(true, "unsure"); }}
                  data-testid="duplicate-open-unsure-btn"
                >
                  Open anyway — not sure if duplicate
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => { setShowDuplicateWarning(false); createNewPF(true, "not_duplicate"); }}
                  data-testid="duplicate-open-notdup-btn"
                >
                  Open anyway — not a duplicate
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Merge PFs Modal */}
        {mergeFolder && (() => {
          const dupes = mergeFolder.possible_duplicate_of || [];
          const other = dupes.find((d) => d.id === mergeOtherId) || dupes[0];
          const keepIsCurrent = mergeKeepId === mergeFolder.id;
          return (
            <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50 p-4" data-testid="merge-modal">
              <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                    <AlertTriangle className="h-5 w-5 text-red-600" /> Merge Position Folders
                  </h3>
                  <button onClick={() => setMergeFolder(null)} className="text-slate-400 hover:text-slate-600" data-testid="merge-close"><X className="h-5 w-5" /></button>
                </div>
                <div className="space-y-4 px-5 py-4">
                  {dupes.length > 1 && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-600">Merge with</label>
                      <select value={mergeOtherId} onChange={(e) => setMergeOtherId(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="merge-other-select">
                        {dupes.map((d) => <option key={d.id} value={d.id}>{d.pf_number} ({(d.matched_on || []).join(", ")})</option>)}
                      </select>
                    </div>
                  )}
                  <p className="text-sm text-slate-500">Choose which Position Folder stays <b>live</b>. The other will be sent for recycling approval.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setMergeKeepId(mergeFolder.id)}
                      className={`rounded-lg border p-3 text-left ${keepIsCurrent ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500" : "border-slate-200"}`}
                      data-testid="merge-keep-current"
                    >
                      <div className="text-xs font-medium text-slate-400">Keep live</div>
                      <div className="font-semibold text-slate-800">{mergeFolder.pf_number}</div>
                      <div className="truncate text-xs text-slate-500">{mergeFolder.customer || "—"}</div>
                    </button>
                    <button
                      onClick={() => other && setMergeKeepId(other.id)}
                      className={`rounded-lg border p-3 text-left ${!keepIsCurrent ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500" : "border-slate-200"}`}
                      data-testid="merge-keep-other"
                    >
                      <div className="text-xs font-medium text-slate-400">Keep live</div>
                      <div className="font-semibold text-slate-800">{other?.pf_number || "—"}</div>
                      <div className="truncate text-xs text-slate-500">{(other?.matched_on || []).join(", ")}</div>
                    </button>
                  </div>
                  <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    <b>{keepIsCurrent ? (other?.pf_number || "the other PF") : mergeFolder.pf_number}</b> will be queued for recycling and needs admin approval before it's removed.
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-600">Reason</label>
                    <input value={mergeReason} onChange={(e) => setMergeReason(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="merge-reason" />
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
                  <button onClick={() => setMergeFolder(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                  <button onClick={confirmMerge} disabled={merging || !mergeOtherId}
                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50" data-testid="merge-confirm">
                    {merging ? "Merging…" : "Merge & send for recycling"}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}



        {/* Draft Detail Modal */}
        {selectedDraft && (
          <DraftDetailModal
            draft={selectedDraft}
            assignees={assignees}
            onClose={() => setSelectedDraft(null)}
            onConvert={handleConvertToPF}
          />
        )}

        {/* Build Export LCL Container Modal */}
        {showExportLclModal && (
          <BuildExportLCLModal
            token={token}
            onClose={() => setShowExportLclModal(false)}
            onCreated={(masterPf) => {
              setShowExportLclModal(false);
              fetchFolders();
              fetchStats();
              toast.success(`Master ${masterPf.pf_number} created with ${masterPf.sub_orders.length} sub-PFs`);
            }}
          />
        )}

        {/* Add existing PFs to an open consolidation master */}
        {addPFsMaster && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="add-pfs-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b bg-teal-700 text-white rounded-t-xl">
                <div>
                  <h3 className="font-semibold flex items-center gap-2"><PackagePlus className="h-5 w-5" /> Add PFs to {addPFsMaster.pf_number}</h3>
                  <p className="text-xs text-teal-100 mt-0.5">Attach more existing PFs to this open consolidation. They inherit the master's vessel & route.</p>
                </div>
                <button onClick={() => setAddPFsMaster(null)} className="text-white/80 hover:text-white" data-testid="add-pfs-close"><X className="h-5 w-5" /></button>
              </div>
              <div className="px-6 py-4 border-b flex gap-2">
                <Input
                  placeholder="Search PF#, customer, shipper, description…"
                  value={addPFsSearch}
                  onChange={(e) => setAddPFsSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") fetchAvailablePFs(addPFsSearch); }}
                  data-testid="add-pfs-search-input"
                />
                <Button onClick={() => fetchAvailablePFs(addPFsSearch)} variant="outline" data-testid="add-pfs-search-btn">Search</Button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-3 space-y-1">
                {addPFsLoading ? (
                  <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
                ) : addPFsAvailable.length === 0 ? (
                  <div className="text-center py-10 text-sm text-muted-foreground">No available PFs found.</div>
                ) : (
                  addPFsAvailable.map((pf) => {
                    const checked = addPFsSelected.includes(pf.id);
                    return (
                      <label
                        key={pf.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? "border-teal-500 bg-teal-50" : "border-gray-200 hover:bg-gray-50"}`}
                        data-testid={`add-pfs-option-${pf.pf_number}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={checked}
                          onChange={() =>
                            setAddPFsSelected((prev) => checked ? prev.filter((x) => x !== pf.id) : [...prev, pf.id])
                          }
                          data-testid={`add-pfs-checkbox-${pf.pf_number}`}
                        />
                        <div className="min-w-0">
                          <div className="font-medium text-sm">{pf.pf_number} <span className="text-muted-foreground font-normal">{pf.customer || ""}</span></div>
                          <div className="text-xs text-muted-foreground truncate">
                            {(pf.origin_port || "?")} → {(pf.destination_port || "?")}
                            {pf.etd ? ` · ETD ${pf.etd}` : ""}{pf.description ? ` · ${pf.description}` : ""}
                          </div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <span className="text-sm text-muted-foreground" data-testid="add-pfs-selected-count">{addPFsSelected.length} PF(s) selected</span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setAddPFsMaster(null)}>Cancel</Button>
                  <Button
                    className="bg-teal-700 hover:bg-teal-800 text-white"
                    disabled={addPFsSelected.length === 0 || attachingPFs}
                    onClick={handleAttachPFs}
                    data-testid="add-pfs-confirm-btn"
                  >
                    {attachingPFs ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PackagePlus className="h-4 w-4 mr-2" />}
                    Add {addPFsSelected.length || ""} PF(s)
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Inline Sub-PF cargo pieces editor (opened from master row expand) */}
        {editingCargoSubPf && (
          <SubPFCargoModal
            subPf={editingCargoSubPf}
            token={token}
            onClose={() => setEditingCargoSubPf(null)}
            onSaved={(data) => {
              // Refresh the cached sub-orders for this master so the inline summary updates
              const masterId = editingCargoSubPf.master_pf_id;
              if (masterId) {
                setSubOrdersCache(prev => ({
                  ...prev,
                  [masterId]: (prev[masterId] || []).map(s =>
                    s.id === editingCargoSubPf.id
                      ? { ...s, cargo_pieces: data.cargo_pieces, cargo_pieces_summary: data.summary }
                      : s
                  )
                }));
              }
              setEditingCargoSubPf(null);
            }}
          />
        )}

        {/* Folder Detail Modal */}
        {selectedFolder && (
          <FolderDetailModal
            folder={selectedFolder}
            token={token}
            subOrders={subOrders}
            navigate={navigate}
            onClose={() => {
              setSelectedFolder(null);
              setSubOrders([]);
            }}
            onUpdate={async () => {
              // Refresh the folder list
              fetchFolders();
              fetchStats();
              // Also refresh the selected folder data so the modal shows updated values
              if (selectedFolder?.id) {
                try {
                  const response = await axios.get(
                    `${API_URL}/position-folders/${selectedFolder.id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  if (response.data) {
                    setSelectedFolder(response.data);
                  }
                } catch (error) {
                  console.error("Failed to refresh folder details:", error);
                }
              }
            }}
            onAddSubOrder={() => openSubOrderModal(selectedFolder)}
            onDelete={(folder) => setPfToDelete(folder)}
          />
        )}

        {/* CRM Contact Confirmation Modal */}
        {showContactModal && pendingContact && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-4 text-white">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  New Contact Detected
                </h3>
                <p className="text-sm text-blue-100 mt-1">
                  This contact is not in your CRM. Would you like to add them?
                </p>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <Label className="text-xs text-slate-500">Contact Name</Label>
                  <Input
                    value={pendingContact.name || ''}
                    onChange={(e) => setPendingContact(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Smith"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <Input
                    value={pendingContact.email || ''}
                    onChange={(e) => setPendingContact(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@company.com"
                    className="mt-1"
                    disabled
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-slate-500">Company Name</Label>
                  <Input
                    value={pendingContact.company_name || ''}
                    onChange={(e) => setPendingContact(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="ACME Corporation"
                    className="mt-1"
                  />
                  {pendingContact.suggested_company && (
                    <p className="text-xs text-blue-600 mt-1">
                      💡 Suggested: {pendingContact.suggested_company} (matches email domain)
                    </p>
                  )}
                </div>
                
                <div>
                  <Label className="text-xs text-slate-500">Phone (optional)</Label>
                  <Input
                    value={pendingContact.phone || ''}
                    onChange={(e) => setPendingContact(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+386 1 234 5678"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 p-4 bg-slate-50 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowContactModal(false);
                    setPendingContact(null);
                  }}
                >
                  Skip
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={saveConfirmedContact}
                  disabled={savingContact}
                >
                  {savingContact ? "Saving..." : "Add to CRM"}
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Sub-Order Creation Modal */}
        {showSubOrderModal && subOrderParent && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            >
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <GitBranch className="h-5 w-5" />
                      Add Sub-Order
                    </h2>
                    <p className="text-purple-100 text-sm mt-1">
                      Under Master: {subOrderParent.pf_number}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowSubOrderModal(false)}
                    className="text-white hover:bg-white/20"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Shipper / Consignee - Required */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Shipper (HBL) *</Label>
                    <Input
                      value={subOrderData.shipper}
                      onChange={(e) => setSubOrderData(prev => ({ ...prev, shipper: e.target.value }))}
                      placeholder="Actual shipper"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Consignee (HBL) *</Label>
                    <Input
                      value={subOrderData.consignee}
                      onChange={(e) => setSubOrderData(prev => ({ ...prev, consignee: e.target.value }))}
                      placeholder="Actual consignee"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Customer & HBL */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Customer Name</Label>
                    <Input
                      value={subOrderData.customer_name}
                      onChange={(e) => setSubOrderData(prev => ({ ...prev, customer_name: e.target.value }))}
                      placeholder="Customer company"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">HBL Number</Label>
                    <Input
                      value={subOrderData.hbl_number}
                      onChange={(e) => setSubOrderData(prev => ({ ...prev, hbl_number: e.target.value }))}
                      placeholder="GLVSKR0137626"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Pantheon Company Link */}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <Label className="text-xs text-blue-700 font-medium flex items-center gap-1 mb-2">
                    <Building className="h-3 w-3" />
                    Link to Pantheon Company (Optional)
                  </Label>
                  <PantheonCompanySelector
                    value={subOrderPantheonCompany}
                    onChange={(company) => {
                      setSubOrderPantheonCompany(company);
                      if (company) {
                        setSubOrderData(prev => ({
                          ...prev,
                          pantheon_id: company.id,
                          pantheon_name: company.name,
                          customer_name: prev.customer_name || company.name,
                        }));
                      } else {
                        setSubOrderData(prev => ({
                          ...prev,
                          pantheon_id: "",
                          pantheon_name: "",
                        }));
                      }
                    }}
                    placeholder="Search Pantheon companies..."
                    className="w-full"
                  />
                  <p className="text-xs text-blue-600 mt-1">
                    Link to Pantheon for invoicing and CRM integration
                  </p>
                </div>

                {/* Description */}
                <div>
                  <Label className="text-xs text-slate-500">Description</Label>
                  <Input
                    value={subOrderData.description}
                    onChange={(e) => setSubOrderData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Shipment description"
                    className="mt-1"
                  />
                </div>

                {/* Cargo Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Commodity</Label>
                    <Input
                      value={subOrderData.commodity}
                      onChange={(e) => setSubOrderData(prev => ({ ...prev, commodity: e.target.value }))}
                      placeholder="e.g., Electronics"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Packages</Label>
                    <Input
                      value={subOrderData.packages}
                      onChange={(e) => setSubOrderData(prev => ({ ...prev, packages: e.target.value }))}
                      placeholder="e.g., 12 pallets"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Weight (kg)</Label>
                    <Input
                      value={subOrderData.weight}
                      onChange={(e) => setSubOrderData(prev => ({ ...prev, weight: e.target.value }))}
                      placeholder="e.g., 2075.68"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Volume (CBM)</Label>
                    <Input
                      value={subOrderData.volume}
                      onChange={(e) => setSubOrderData(prev => ({ ...prev, volume: e.target.value }))}
                      placeholder="e.g., 14.32"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Remarks */}
                <div>
                  <Label className="text-xs text-slate-500">Remarks</Label>
                  <textarea
                    value={subOrderData.remarks}
                    onChange={(e) => setSubOrderData(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Additional notes..."
                    rows={2}
                    className="w-full mt-1 rounded-lg border-slate-200 text-sm px-3 py-2"
                  />
                </div>

                {/* Info box */}
                <div className="p-3 bg-purple-50 rounded-lg text-sm">
                  <p className="text-purple-800">
                    <strong>Sub-order number:</strong> {subOrderParent.pf_number}-{(subOrderParent.sub_order_count || 0) + 1}
                  </p>
                  <p className="text-purple-600 text-xs mt-1">
                    Inherits container, MBL, ports, and ETA from master PF
                  </p>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowSubOrderModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={createSubOrder}
                    disabled={creatingSub}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {creatingSub ? (
                      <>Creating...</>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Sub-Order
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Add to Master PF Modal */}
        {showAddToMasterModal && (selectedPendingItem || selectedPendingItems.length > 0) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg m-4"
            >
              <div className="p-6 border-b flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold flex items-center gap-2">
                    <Layers className="h-5 w-5 text-purple-600" />
                    Add to Master PF
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {selectedPendingItems.length > 0 
                      ? `Link ${selectedPendingItems.length} shipment(s) to a Master Position Folder`
                      : "Link this shipment to an existing Master Position Folder"
                    }
                  </p>
                </div>
                <button 
                  onClick={() => { 
                    setShowAddToMasterModal(false); 
                    setSelectedPendingItem(null); 
                    setMasterPfSearch(""); 
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Selected pending item(s) info */}
                {selectedPendingItems.length > 0 ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-purple-800">
                      {selectedPendingItems.length} shipment(s) selected
                    </p>
                    <div className="mt-2 max-h-24 overflow-y-auto space-y-1">
                      {selectedPendingItems.map((item, idx) => (
                        <p key={idx} className="text-xs text-purple-600">
                          HBL: {item.hbl_number || item.data?.hbl_number || "N/A"} - {item.data?.shipper?.name || item.shipper_name || "Unknown"}
                        </p>
                      ))}
                    </div>
                  </div>
                ) : selectedPendingItem && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-orange-800">
                      HBL: {selectedPendingItem.hbl_number || selectedPendingItem.data?.hbl_number || "N/A"}
                    </p>
                    <p className="text-xs text-orange-600 mt-1">
                      {selectedPendingItem.data?.shipper?.name || selectedPendingItem.shipper_name || "Unknown Shipper"} → 
                      {selectedPendingItem.data?.consignee?.name || selectedPendingItem.consignee_name || "Unknown Consignee"}
                    </p>
                  </div>
                )}

                {/* Search bar for Master PFs */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Search Master PF:
                  </Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={masterPfSearch}
                      onChange={(e) => setMasterPfSearch(e.target.value)}
                      placeholder="Type PF number, customer, or MBL..."
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      data-testid="master-pf-search-input"
                    />
                    {masterPfSearch && (
                      <button
                        onClick={() => setMasterPfSearch("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Master PFs List */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Select a Master PF to link to:
                  </Label>
                  
                  {loadingMasterPFs ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                      <span className="ml-2 text-slate-500">Loading Master PFs...</span>
                    </div>
                  ) : masterPFsList.length === 0 ? (
                    <div className="text-center py-8 bg-slate-50 rounded-lg">
                      <Layers className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500">No open Master PFs found</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Create a new PF and mark it as Master first
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto space-y-2 border rounded-lg p-2">
                      {masterPFsList
                        .filter(pf => {
                          if (!masterPfSearch) return true;
                          const search = masterPfSearch.toLowerCase();
                          return (
                            (pf.pf_number || "").toLowerCase().includes(search) ||
                            (pf.customer || "").toLowerCase().includes(search) ||
                            (pf.mbl_number || "").toLowerCase().includes(search) ||
                            (pf.container_number || "").toLowerCase().includes(search)
                          );
                        })
                        .map((pf) => (
                        <button
                          key={pf.id}
                          onClick={() => handleLinkToMasterPF(pf.id)}
                          disabled={creatingFromPending}
                          className="w-full text-left p-3 rounded-lg border hover:border-purple-300 hover:bg-purple-50 transition-colors disabled:opacity-50"
                          data-testid={`select-master-pf-${pf.pf_number}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-mono font-bold text-purple-700">{pf.pf_number}</span>
                              <span className="ml-2 px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">
                                Master ({pf.sub_order_count || 0} sub)
                              </span>
                            </div>
                            {creatingFromPending ? (
                              <Loader2 className="h-4 w-4 text-purple-400 animate-spin" />
                            ) : (
                              <ArrowRight className="h-4 w-4 text-purple-400" />
                            )}
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{pf.customer}</p>
                          {pf.mbl_number && (
                            <p className="text-xs text-slate-400 mt-0.5">MBL: {pf.mbl_number}</p>
                          )}
                          {pf.container_number && (
                            <p className="text-xs text-slate-400">Container: {pf.container_number}</p>
                          )}
                        </button>
                      ))}
                      {masterPFsList.filter(pf => {
                        if (!masterPfSearch) return true;
                        const search = masterPfSearch.toLowerCase();
                        return (
                          (pf.pf_number || "").toLowerCase().includes(search) ||
                          (pf.customer || "").toLowerCase().includes(search) ||
                          (pf.mbl_number || "").toLowerCase().includes(search)
                        );
                      }).length === 0 && (
                        <div className="text-center py-4">
                          <p className="text-sm text-slate-500">No Master PFs match "{masterPfSearch}"</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => { 
                      setShowAddToMasterModal(false); 
                      setSelectedPendingItem(null); 
                      setMasterPfSearch(""); 
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>

      {/* Delete Pending Shipment Confirmation Dialog */}
      {pendingToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Delete Pending Shipment?</h3>
                <p className="text-sm text-slate-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <div className="space-y-1">
                <div className="font-mono font-bold text-lg">
                  {pendingToDelete.hbl_number || pendingToDelete.data?.hbl_number || "No HBL"}
                </div>
                {pendingToDelete.data?.shipper?.name && (
                  <div className="text-sm text-slate-600">
                    Shipper: {pendingToDelete.data.shipper.name}
                  </div>
                )}
                {pendingToDelete.data?.origin_port && (
                  <div className="text-sm text-slate-600">
                    Route: {pendingToDelete.data.origin_port} → {pendingToDelete.data.destination_port}
                  </div>
                )}
              </div>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Are you sure you want to permanently delete this pending shipment? 
              This will remove it from the Freightlinks queue.
            </p>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setPendingToDelete(null)}
                disabled={deletingPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeletePending}
                disabled={deletingPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {deletingPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Yes, Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Position Folder Confirmation Dialog (Admin Only) */}
      {pfToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl relative">
            {/* Close X button */}
            <button
              onClick={() => {
                setPfToDelete(null);
                setDeletionReason("");
              }}
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600"
              data-testid="close-delete-dialog"
            >
              <X className="h-5 w-5" />
            </button>
            
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-full ${isAdmin ? 'bg-red-100' : 'bg-orange-100'}`}>
                <AlertTriangle className={`h-6 w-6 ${isAdmin ? 'text-red-600' : 'text-orange-600'}`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {isAdmin ? "Delete Position Folder?" : "Request Deletion"}
                </h3>
                <p className="text-sm text-slate-500">
                  {isAdmin ? "Admin action - This cannot be undone" : "This will notify the administrator"}
                </p>
              </div>
            </div>
            
            <div className={`border rounded-lg p-4 mb-4 ${isAdmin ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
              <div className="space-y-2">
                <div className={`font-mono font-bold text-lg ${isAdmin ? 'text-red-800' : 'text-orange-800'}`}>
                  {pfToDelete.pf_number}
                </div>
                {(pfToDelete.customer_name || pfToDelete.customer) && (
                  <div className={`text-sm ${isAdmin ? 'text-red-700' : 'text-orange-700'}`}>
                    Customer: {pfToDelete.customer_name || pfToDelete.customer}
                  </div>
                )}
                {pfToDelete.shipper_name && (
                  <div className={`text-sm ${isAdmin ? 'text-red-700' : 'text-orange-700'}`}>
                    Shipper: {pfToDelete.shipper_name}
                  </div>
                )}
                {pfToDelete.consignee_name && (
                  <div className={`text-sm ${isAdmin ? 'text-red-700' : 'text-orange-700'}`}>
                    Consignee: {pfToDelete.consignee_name}
                  </div>
                )}
                {pfToDelete.pol && pfToDelete.pod && (
                  <div className={`text-sm ${isAdmin ? 'text-red-700' : 'text-orange-700'}`}>
                    Route: {pfToDelete.pol} → {pfToDelete.pod}
                  </div>
                )}
              </div>
            </div>
            
            {isAdmin ? (
              <p className="text-sm text-slate-600 mb-4">
                <strong>Options:</strong>
                <br/>• <strong>Recycle:</strong> Clear all data and make this PF number available for the next new PF. 
                Use this if the PF was created by mistake.
                <br/>• <strong>Move to Purge:</strong> Soft-delete to purge folder for 30 days, then permanently deleted.
              </p>
            ) : (
              <>
                <p className="text-sm text-slate-600 mb-3">
                  Your deletion request will be sent to the administrator for approval.
                </p>
                <div className="mb-4">
                  <Label className="text-sm text-slate-600">Reason for deletion (optional)</Label>
                  <Textarea
                    value={deletionReason}
                    onChange={(e) => setDeletionReason(e.target.value)}
                    placeholder="E.g., Duplicate entry, Created by mistake, etc."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </>
            )}
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setPfToDelete(null);
                  setDeletionReason("");
                }}
                disabled={deletingPf || requestingDeletion || recyclingPf}
              >
                Cancel
              </Button>
              {isAdmin ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleRecyclePF}
                    disabled={recyclingPf || deletingPf}
                    className="border-amber-500 text-amber-700 hover:bg-amber-50"
                    data-testid="recycle-pf-btn"
                  >
                    {recyclingPf ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Recycling...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Recycle (Reuse Number)
                      </>
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDeletePF}
                    disabled={deletingPf || recyclingPf}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {deletingPf ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Moving to Purge...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Move to Purge
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleRequestDeletion}
                  disabled={requestingDeletion}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {requestingDeletion ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Request Deletion
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadEmlModal({ onClose, onUpload }) {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const acceptFile = (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (name.endsWith(".eml") || name.endsWith(".msg")) {
      onUpload(file);
    } else {
      toast.error("Please drop a .eml or .msg file");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    // Same robust extractor as handleEmailDrop — Chrome on Windows
    // sometimes surfaces Outlook drags via dataTransfer.items even when
    // dataTransfer.files is empty.
    let file = null;
    let diagnosticTypes = [];
    const dt = e.dataTransfer;
    if (dt?.files && dt.files.length > 0) {
      file = dt.files[0];
    } else if (dt?.items && dt.items.length > 0) {
      for (let i = 0; i < dt.items.length; i++) {
        const item = dt.items[i];
        if (item && item.kind === "file" && typeof item.getAsFile === "function") {
          const f = item.getAsFile();
          if (f) { file = f; break; }
        }
      }
    }
    if (!file && dt) {
      try { diagnosticTypes = dt.types ? Array.from(dt.types) : []; } catch (_) {}
    }

    if (!file) {
      // Outlook desktop drag: drops the email as a proprietary clipboard
      // format that browsers don't translate into dataTransfer.files.
      const hint = diagnosticTypes.length
        ? ` (browser only exposed: ${diagnosticTypes.slice(0, 4).join(", ")})`
        : "";
      toast.error(
        "Couldn't read that drop — if you dragged from Outlook, save the email to your desktop first (drag it onto the desktop), then drop the .msg/.eml file here." + hint,
        { duration: 10000 }
      );
      return;
    }
    acceptFile(file);
  };

  const handleFileInput = (e) => {
    acceptFile(e.target.files?.[0]);
    e.target.value = "";  // allow re-picking the same file later
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Upload Email File</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
            dragOver ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          data-testid="upload-eml-dropzone"
        >
          <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="font-medium mb-2">Drag & drop .eml or .msg file here</p>
          <p className="text-sm text-muted-foreground mb-4">or click anywhere in this box to browse</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".eml,.msg"
            onChange={handleFileInput}
            className="hidden"
            data-testid="upload-eml-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            data-testid="upload-eml-browse-btn"
          >
            Browse Files
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-4 text-center">
          Email will be parsed and routed based on subject line triggers.<br />
          <strong>Tip:</strong> dragging directly from Outlook usually fails — save the email to your desktop first, then drag it here.
        </p>
      </div>
    </div>
  );
}

function DraftDetailModal({ draft, assignees, onClose, onConvert }) {
  const [customer, setCustomer] = useState("");
  const [assignee, setAssignee] = useState(assignees[0] || "");
  const [description, setDescription] = useState(draft.subject);
  const [converting, setConverting] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState([draft.from_email || ""]);
  const [newEmail, setNewEmail] = useState("");

  const addEmail = () => {
    if (newEmail && newEmail.includes("@") && !notificationEmails.includes(newEmail)) {
      setNotificationEmails([...notificationEmails, newEmail]);
      setNewEmail("");
    }
  };

  const removeEmail = (email) => {
    setNotificationEmails(notificationEmails.filter(e => e !== email));
  };

  const handleConvert = async () => {
    if (!customer.trim()) {
      toast.error("Please enter customer name");
      return;
    }
    setConverting(true);
    await onConvert(draft.id, customer, assignee, description, notificationEmails);
    setConverting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-slate-50">
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="h-4 w-4" />
              {draft.from_email}
            </div>
            <h2 className="font-bold text-lg">{draft.subject}</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Email Content */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Email Content
            </h3>
            <pre className="text-sm whitespace-pre-wrap font-sans">{draft.body}</pre>
          </div>

          {/* Attachments */}
          {draft.attachments?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Attachments ({draft.attachments.length})
              </h3>
              <div className="space-y-2">
                {draft.attachments.map((att, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <FileText className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">{att.filename}</span>
                    <span className="text-xs text-muted-foreground">
                      ({(att.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Convert Form */}
          <div className="border-t pt-4">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <ArrowRight className="h-4 w-4" />
              Convert to Position Folder
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={customer}
                  onChange={(e) => setCustomer(e.target.value)}
                  placeholder="Enter customer/company name"
                  data-testid="customer-input"
                />
              </div>
              <div>
                <Label>Assignee</Label>
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  data-testid="assignee-select"
                >
                  {assignees.map((a) => (
                    <option key={a} value={a}>
                      {a.split("@")[0].replace(".", " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Order description"
                />
              </div>
              
              {/* Notification Emails */}
              <div className="col-span-2">
                <Label>Notification Emails</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="Add email address"
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
                  />
                  <Button type="button" variant="outline" onClick={addEmail}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {notificationEmails.map((email, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      <Mail className="h-3 w-3" />
                      {email}
                      <button onClick={() => removeEmail(email)} className="hover:text-red-600">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  These addresses will receive notifications about this order
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleConvert} 
                disabled={converting}
                data-testid="confirm-convert-btn"
              >
                {converting ? "Creating..." : "Create Order & Notify Customer"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubPFCargoModal({ subPf, token, onClose, onSaved }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[110] p-4" data-testid="sub-pf-cargo-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <div className="text-xs text-slate-500">Sub-PF cargo pieces</div>
            <h3 className="text-lg font-semibold font-mono">{subPf.pf_number}</h3>
            <div className="text-xs text-slate-600">{subPf.customer || subPf.shipper}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="close-sub-pf-cargo-modal">✕</button>
        </div>
        <div className="p-5">
          <CargoPiecesEditor
            pfId={subPf.id}
            token={token}
            initialPieces={subPf.cargo_pieces || []}
            onSaved={(data) => {
              if (typeof onSaved === "function") onSaved(data);
            }}
          />
        </div>
      </div>
    </div>
  );
}

function BuildExportLCLModal({ token, onClose, onCreated }) {
  const [search, setSearch] = useState("");
  const [available, setAvailable] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    notification_emails: "",
    description: "",
    origin_port: "",
    destination_port: "",
    etd: "",
    eta: "",
    vessel_name: "",
    voyage: "",
    shipping_line: "",
    mbl_number: "",
    container_type: "40hc",
  });

  const loadAvailable = async (q = "") => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/position-folders/available-for-lcl`, {
        params: q ? { search: q } : {},
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailable(r.data.items || []);
    } catch {
      toast.error("Failed to load available PFs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAvailable(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelected = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const submit = async () => {
    const looksValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
    const notify = form.notification_emails.split(',').map(e => e.trim()).filter(Boolean);
    const hasEmail = looksValid(form.customer_email) || notify.some(looksValid);
    if (!form.customer_name || !form.description) {
      toast.error("Customer name + description are required");
      return;
    }
    if (!hasEmail) {
      toast.error("Provide a customer email or at least one notification email");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Select at least one PF to consolidate");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_URL}/position-folders/build-export-lcl`,
        {
          ...form,
          notification_emails: notify,
          sub_pf_ids: selectedIds,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCreated(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to build export LCL master");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="build-export-lcl-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-slate-900 text-white rounded-t-lg">
          <div className="flex items-center gap-3">
            <Boxes className="h-5 w-5 text-emerald-400" />
            <div>
              <h3 className="text-lg font-semibold">Build Export LCL Container</h3>
              <p className="text-xs text-slate-300">Group several existing export PFs under one master container. Original PF numbers are preserved.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="close-build-export-lcl-btn">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* LEFT: Master metadata */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-slate-700">Master container details</h4>
            <div>
              <Label className="text-xs">Customer / Consolidator <span className="text-red-500">*</span></Label>
              <Input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} placeholder="Europacific Group" data-testid="lcl-customer-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Customer Email <span className="text-red-500">*</span></Label>
                <Input type="email" value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} placeholder="ops@customer.com" data-testid="lcl-email-input" />
              </div>
              <div>
                <Label className="text-xs">Notification Emails (CC)</Label>
                <Input value={form.notification_emails} onChange={e => setForm({ ...form, notification_emails: e.target.value })} placeholder="ops@a.com, ops@b.com" data-testid="lcl-notify-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description <span className="text-red-500">*</span></Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="LCL consolidation Koper → Hamburg, week 18" data-testid="lcl-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Origin Port</Label>
                <Input value={form.origin_port} onChange={e => setForm({ ...form, origin_port: e.target.value })} placeholder="Koper" />
              </div>
              <div>
                <Label className="text-xs">Destination Port</Label>
                <Input value={form.destination_port} onChange={e => setForm({ ...form, destination_port: e.target.value })} placeholder="Hamburg" />
              </div>
              <div>
                <Label className="text-xs">ETD</Label>
                <Input type="date" value={form.etd} onChange={e => setForm({ ...form, etd: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">ETA</Label>
                <Input type="date" value={form.eta} onChange={e => setForm({ ...form, eta: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Vessel</Label>
                <Input value={form.vessel_name} onChange={e => setForm({ ...form, vessel_name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Voyage</Label>
                <Input value={form.voyage} onChange={e => setForm({ ...form, voyage: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Shipping Line</Label>
                <CarrierSelect value={form.shipping_line} onChange={val => setForm({ ...form, shipping_line: val })} testId="edit-pf-shipping-line-select" />
              </div>
              <div>
                <Label className="text-xs">MBL #</Label>
                <Input value={form.mbl_number} onChange={e => setForm({ ...form, mbl_number: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Target Container</Label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.container_type} onChange={e => setForm({ ...form, container_type: e.target.value })} data-testid="lcl-container-type-select">
                <option value="20st">20ft Standard (33.1 m³ / 28.2t)</option>
                <option value="40st">40ft Standard (67.5 m³ / 26.7t)</option>
                <option value="40hc">40ft High Cube (76.0 m³ / 26.5t)</option>
                <option value="45hc">45ft High Cube (85.7 m³ / 25.6t)</option>
              </select>
            </div>
          </div>
          {/* RIGHT: Sub-PF picker */}
          <div className="flex flex-col min-h-[400px]">
            <h4 className="font-semibold text-sm text-slate-700 mb-2">Select sub-PFs to consolidate</h4>
            <div className="flex gap-2 mb-2">
              <Input
                placeholder="Search PF#, customer, shipper, description..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadAvailable(search)}
                data-testid="lcl-sub-search-input"
              />
              <Button size="sm" variant="outline" onClick={() => loadAvailable(search)}>Search</Button>
            </div>
            <div className="border rounded-lg flex-1 overflow-y-auto max-h-[480px]">
              {loading ? (
                <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
              ) : available.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500">No available export PFs.</div>
              ) : (
                <div className="divide-y">
                  {available.map(pf => {
                    const checked = selectedIds.includes(pf.id);
                    return (
                      <label key={pf.id} className={`flex gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 ${checked ? 'bg-emerald-50' : ''}`} data-testid={`lcl-sub-row-${pf.pf_number}`}>
                        <input type="checkbox" className="mt-1" checked={checked} onChange={() => toggleSelected(pf.id)} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-mono font-semibold text-slate-900">{pf.pf_number}</span>
                            {pf.service_type && <Badge variant="outline" className="text-xs">{pf.service_type}</Badge>}
                            <span className="text-slate-700 truncate">{pf.customer || pf.shipper}</span>
                          </div>
                          <div className="text-xs text-slate-500 truncate">{pf.description}</div>
                          <div className="text-xs text-slate-400">
                            {pf.origin_port || '?'} → {pf.destination_port || '?'} {pf.etd ? `· ETD ${pf.etd.slice(0, 10)}` : ''}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="text-xs text-slate-600 mt-2">
              <span className="font-semibold">{selectedIds.length}</span> PF(s) selected
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t bg-slate-50">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || selectedIds.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lcl-submit-btn">
            {submitting ? "Building..." : `Build Master with ${selectedIds.length} sub-PF${selectedIds.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CargoPiecesEditor({ pfId, token, initialPieces = [], onSaved }) {
  const [pieces, setPieces] = useState(() =>
    (initialPieces || []).map((p, i) => ({ _key: i, ...p }))
  );
  const [saving, setSaving] = useState(false);
  const nextKey = useRef((initialPieces || []).length);

  const addLine = () => {
    setPieces(prev => [...prev, {
      _key: nextKey.current++,
      piece_type: "pallet", count: 1, weight_kg: 0, weight_mode: "per_piece",
      length_cm: 120, width_cm: 80, height_cm: 100, description: "", stackable: true,
    }]);
  };
  const update = (key, field, val) => setPieces(prev => prev.map(p => p._key === key ? { ...p, [field]: val } : p));
  const remove = (key) => setPieces(prev => prev.filter(p => p._key !== key));

  const totals = pieces.reduce((acc, p) => {
    const c = Number(p.count) || 0;
    const w = Number(p.weight_kg) || 0;
    const lw = p.weight_mode === "total" ? w : w * c;
    const vol = ((Number(p.length_cm) || 0) * (Number(p.width_cm) || 0) * (Number(p.height_cm) || 0) * c) / 1_000_000;
    acc.pieces += c;
    acc.weight += lw;
    acc.volume += vol;
    return acc;
  }, { pieces: 0, weight: 0, volume: 0 });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        cargo_pieces: pieces.map(({ _key, line_weight_kg, line_volume_m3, ...rest }) => ({
          ...rest,
          count: Number(rest.count) || 0,
          weight_kg: Number(rest.weight_kg) || 0,
          length_cm: Number(rest.length_cm) || 0,
          width_cm: Number(rest.width_cm) || 0,
          height_cm: Number(rest.height_cm) || 0,
        }))
      };
      const r = await axios.patch(`${API_URL}/position-folders/${pfId}/cargo-pieces`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cargo pieces saved");
      if (typeof onSaved === "function") onSaved(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-slate-50 space-y-3" data-testid="cargo-pieces-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Cargo Pieces (LCL detail)</div>
          <div className="text-xs text-slate-600">Add each cargo line: type, count, weight, dimensions. Used by the master container's stuffing calculator.</div>
        </div>
        <Button size="sm" variant="outline" onClick={addLine} data-testid="add-cargo-piece-btn">
          <Plus className="h-4 w-4 mr-1" /> Add line
        </Button>
      </div>
      {pieces.length === 0 && (
        <div className="text-center py-6 text-sm text-slate-500 bg-white rounded border border-dashed">No pieces yet — click "Add line" to start.</div>
      )}
      {pieces.map((p, idx) => (
        <div key={p._key} className="bg-white border rounded p-3 space-y-2" data-testid={`cargo-piece-row-${idx}`}>
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-3">
              <Label className="text-xs">Type</Label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={p.piece_type} onChange={e => update(p._key, 'piece_type', e.target.value)}>
                <option value="pallet">Pallet</option>
                <option value="box">Box / Carton</option>
                <option value="crate">Crate</option>
                <option value="bundle">Bundle</option>
                <option value="loose">Loose</option>
                <option value="drum">Drum / Barrel</option>
                <option value="ibc">IBC</option>
                <option value="big_bag">Big Bag</option>
                <option value="roll">Roll</option>
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Count</Label>
              <Input type="number" min={1} value={p.count} onChange={e => update(p._key, 'count', e.target.value)} className="text-sm" />
            </div>
            <div className="col-span-3">
              <Label className="text-xs">Weight (kg)</Label>
              <div className="flex gap-1">
                <Input type="number" min={0} step="0.1" value={p.weight_kg} onChange={e => update(p._key, 'weight_kg', e.target.value)} className="text-sm" />
                <select className="border rounded px-1 text-xs" value={p.weight_mode} onChange={e => update(p._key, 'weight_mode', e.target.value)} title="Weight per piece OR total for the line">
                  <option value="per_piece">/ pc</option>
                  <option value="total">total</option>
                </select>
              </div>
            </div>
            <div className="col-span-3">
              <Label className="text-xs">Dimensions L×W×H (cm)</Label>
              <div className="flex gap-1">
                <Input type="number" min={0} value={p.length_cm} onChange={e => update(p._key, 'length_cm', e.target.value)} placeholder="L" className="text-sm" />
                <Input type="number" min={0} value={p.width_cm} onChange={e => update(p._key, 'width_cm', e.target.value)} placeholder="W" className="text-sm" />
                <Input type="number" min={0} value={p.height_cm} onChange={e => update(p._key, 'height_cm', e.target.value)} placeholder="H" className="text-sm" />
              </div>
            </div>
            <div className="col-span-1 flex justify-end">
              <Button size="icon" variant="ghost" onClick={() => remove(p._key)} className="h-8 w-8 text-red-600 hover:text-red-700" data-testid={`remove-cargo-piece-${idx}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-9">
              <Input placeholder="Description / commodity (optional)" value={p.description || ""} onChange={e => update(p._key, 'description', e.target.value)} className="text-sm" />
            </div>
            <label className="col-span-3 flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={p.stackable !== false} onChange={e => update(p._key, 'stackable', e.target.checked)} />
              Stackable
            </label>
          </div>
        </div>
      ))}
      {pieces.length > 0 && (
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="text-xs text-slate-600">
            <span className="font-semibold text-slate-900">{totals.pieces}</span> pieces
            {' · '}
            <span className="font-semibold text-slate-900">{totals.weight.toFixed(2)}</span> kg
            {' · '}
            <span className="font-semibold text-slate-900">{totals.volume.toFixed(2)}</span> m³
          </div>
          <Button size="sm" onClick={save} disabled={saving} data-testid="save-cargo-pieces-btn">
            {saving ? "Saving..." : "Save pieces"}
          </Button>
        </div>
      )}
    </div>
  );
}

function StuffingTab({ folder, token }) {
  const [aggregate, setAggregate] = useState(null);
  const [loadingAgg, setLoadingAgg] = useState(false);
  const [containerType, setContainerType] = useState(folder.lcl_container_type || "40hc");
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [showLabels, setShowLabels] = useState(false);
  const [Viz, setViz] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const canvasContainerRef = useRef(null);

  const loadAggregate = async () => {
    setLoadingAgg(true);
    try {
      const r = await axios.get(`${API_URL}/position-folders/${folder.id}/aggregate-pieces`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAggregate(r.data);
    } catch {
      toast.error("Failed to load cargo pieces");
    } finally {
      setLoadingAgg(false);
    }
  };

  useEffect(() => { loadAggregate(); }, [folder.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load 3D viz only when user clicks Calculate (avoids heavy three.js on tab open)
  const ensureViz = async () => {
    if (Viz) return Viz;
    const mod = await import("../components/LoadVisualization3D");
    setViz(() => mod);
    return mod;
  };

  const calculate = async () => {
    if (!aggregate?.cargo_items?.length) {
      toast.error("Add cargo pieces to the sub-PFs first");
      return;
    }
    setCalculating(true);
    try {
      await ensureViz();
      const r = await axios.post(
        `${API_URL}/load-calculator/calculate`,
        { cargo_items: aggregate.cargo_items, container_types: [containerType], unit_system: "metric" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(r.data);
      if (r.data?.success) {
        toast.success(`Loaded ${r.data.total_items_loaded || 0} items`);
      } else {
        toast.warning(r.data?.summary?.error || "Calculation finished with warnings");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Calculation failed");
    } finally {
      setCalculating(false);
    }
  };

  const containerData = result?.containers?.[containerType];
  const Canvas = Viz?.LoadCanvas;

  return (
    <div className="space-y-4" data-testid="stuffing-tab">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-semibold text-emerald-900">Loading & Stuffing Calculator</div>
            <div className="text-xs text-emerald-800">Auto-aggregates cargo pieces from all sub-PFs of this Export LCL master.</div>
          </div>
          <div className="flex gap-2">
            <RolloverLCLButton folder={folder} token={token} />
            <Button size="sm" variant="outline" onClick={loadAggregate} disabled={loadingAgg}>
              <RefreshCw className={`h-3 w-3 mr-1 ${loadingAgg ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>
        {aggregate && (
          <div className="grid grid-cols-4 gap-2 text-xs">
            <div className="bg-white rounded p-2"><div className="text-slate-500">Sub-PFs</div><div className="text-lg font-bold">{aggregate.sub_pfs_count}</div></div>
            <div className="bg-white rounded p-2"><div className="text-slate-500">Pieces</div><div className="text-lg font-bold">{aggregate.summary?.total_pieces || 0}</div></div>
            <div className="bg-white rounded p-2"><div className="text-slate-500">Weight</div><div className="text-lg font-bold">{aggregate.summary?.total_weight_kg || 0} kg</div></div>
            <div className="bg-white rounded p-2"><div className="text-slate-500">Volume</div><div className="text-lg font-bold">{aggregate.summary?.total_volume_m3 || 0} m³</div></div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Target container</Label>
            <select className="w-full border rounded-lg px-3 py-2 text-sm" value={containerType} onChange={e => setContainerType(e.target.value)} data-testid="stuffing-container-select">
              <option value="20st">20ft Standard — 33.1 m³ / 28.2t</option>
              <option value="40st">40ft Standard — 67.5 m³ / 26.7t</option>
              <option value="40hc">40ft High Cube — 76.0 m³ / 26.5t</option>
              <option value="45hc">45ft High Cube — 85.7 m³ / 25.6t</option>
            </select>
          </div>
          <div className="border rounded-lg max-h-[260px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5">Source PF</th>
                  <th className="text-left px-2 py-1.5">Type</th>
                  <th className="text-right px-2 py-1.5">Qty</th>
                  <th className="text-right px-2 py-1.5">L×W×H (cm)</th>
                  <th className="text-right px-2 py-1.5">kg/pc</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(aggregate?.cargo_items || []).map(it => (
                  <tr key={it.id}>
                    <td className="px-2 py-1.5 font-mono">{it.source_pf_number}</td>
                    <td className="px-2 py-1.5">{it.original_piece_type || it.cargo_type}</td>
                    <td className="text-right px-2 py-1.5">{it.quantity}</td>
                    <td className="text-right px-2 py-1.5 font-mono">{it.length}×{it.width}×{it.height}</td>
                    <td className="text-right px-2 py-1.5">{it.weight}</td>
                  </tr>
                ))}
                {(!aggregate || aggregate.cargo_items.length === 0) && (
                  <tr><td colSpan={5} className="text-center py-4 text-slate-400">No pieces yet — open each sub-PF and add cargo pieces.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate} disabled={calculating || !aggregate?.cargo_items?.length} className="flex-1" data-testid="stuffing-calculate-btn">
              {calculating ? "Calculating..." : "Calculate stuffing"}
            </Button>
            <Button
              onClick={() => setShowSendModal(true)}
              disabled={!result?.success}
              variant="outline"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              data-testid="send-stuffing-manifest-btn"
              title={result?.success ? "Email PDF manifest with 3D preview" : "Calculate stuffing first"}
            >
              <Mail className="h-4 w-4 mr-1.5" /> Send Manifest
            </Button>
            <label className="flex items-center gap-1 text-xs text-slate-600">
              <input type="checkbox" checked={showLabels} onChange={e => setShowLabels(e.target.checked)} /> Labels
            </label>
          </div>
        </div>
        <div ref={canvasContainerRef} className="border rounded-lg bg-slate-900 min-h-[420px] relative">
          {result?.success && Canvas ? (
            <Canvas result={result} selectedContainer={containerType} showLabels={showLabels} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
              {calculating ? "Calculating…" : "Click 'Calculate stuffing' to see the 3D fit"}
            </div>
          )}
          {containerData && (
            <div className="absolute bottom-2 left-2 right-2 bg-black/60 backdrop-blur text-white text-xs px-3 py-2 rounded">
              <div className="grid grid-cols-3 gap-2">
                <div><span className="text-slate-300">Items:</span> {containerData.items_loaded || containerData.placed_items?.length || 0}/{containerData.total_items || aggregate?.summary?.total_pieces || 0}</div>
                <div><span className="text-slate-300">Vol util:</span> {containerData.volume_utilization || 0}%</div>
                <div><span className="text-slate-300">Wt util:</span> {containerData.weight_utilization || 0}%</div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showSendModal && (
        <SendStuffingManifestModal
          folder={folder}
          token={token}
          aggregate={aggregate}
          canvasContainerRef={canvasContainerRef}
          onClose={() => setShowSendModal(false)}
        />
      )}
    </div>
  );
}

function RolloverLCLButton({ folder, token }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    vessel_name: folder.vessel_name || "",
    voyage: folder.voyage || "",
    etd: (folder.etd || "").slice(0, 10),
    eta: (folder.eta || "").slice(0, 10),
    shipping_line: folder.shipping_line || "",
    mbl_number: folder.mbl_number || "",
    reason: "",
    notify_customers: true,
  });

  // Detect what changed vs original
  const changed = (
    form.vessel_name !== (folder.vessel_name || "") ||
    form.voyage !== (folder.voyage || "") ||
    form.etd !== (folder.etd || "").slice(0, 10) ||
    form.eta !== (folder.eta || "").slice(0, 10) ||
    form.shipping_line !== (folder.shipping_line || "") ||
    form.mbl_number !== (folder.mbl_number || "")
  );

  const submit = async () => {
    if (!changed) {
      toast.error("Change at least one field before saving");
      return;
    }
    setSubmitting(true);
    try {
      // Only send fields that actually changed
      const payload = { reason: form.reason, notify_customers: form.notify_customers };
      if (form.vessel_name !== (folder.vessel_name || "")) payload.vessel_name = form.vessel_name;
      if (form.voyage !== (folder.voyage || "")) payload.voyage = form.voyage;
      if (form.etd !== (folder.etd || "").slice(0, 10)) payload.etd = form.etd;
      if (form.eta !== (folder.eta || "").slice(0, 10)) payload.eta = form.eta;
      if (form.shipping_line !== (folder.shipping_line || "")) payload.shipping_line = form.shipping_line;
      if (form.mbl_number !== (folder.mbl_number || "")) payload.mbl_number = form.mbl_number;
      const r = await axios.post(
        `${API_URL}/position-folders/${folder.id}/rollover-lcl`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Rolled over: ${r.data.sub_pfs_updated} sub-PFs updated, ${r.data.customers_notified} customer(s) notified`);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to roll over");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-amber-400 text-amber-800 hover:bg-amber-50"
        data-testid="rollover-lcl-btn"
        title="Reschedule vessel/ETD/ETA — milestone update propagates to every sub-PF"
      >
        <CalendarClock className="h-3 w-3 mr-1" /> Roll-over booking
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[120] p-4" data-testid="rollover-lcl-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b bg-amber-500 text-white rounded-t-lg">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <CalendarClock className="h-5 w-5" /> Roll-over LCL booking
                </h3>
                <p className="text-xs text-amber-100">Master {folder.pf_number} — change vessel/ETD/ETA. A "Vessel rescheduled" milestone will be added to every sub-PF.</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-slate-50 border rounded p-3 text-xs">
                <div className="text-slate-500 mb-1">Current booking</div>
                <div className="grid grid-cols-2 gap-2 text-slate-800">
                  <div><span className="text-slate-500">Vessel:</span> <strong>{folder.vessel_name || '—'}</strong> {folder.voyage || ''}</div>
                  <div><span className="text-slate-500">Shipping line:</span> <strong>{folder.shipping_line || '—'}</strong></div>
                  <div><span className="text-slate-500">ETD:</span> <strong>{(folder.etd || '—').slice(0, 10)}</strong></div>
                  <div><span className="text-slate-500">ETA:</span> <strong>{(folder.eta || '—').slice(0, 10)}</strong></div>
                  <div className="col-span-2"><span className="text-slate-500">MBL:</span> <strong>{folder.mbl_number || '—'}</strong></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">New Vessel</Label>
                  <Input value={form.vessel_name} onChange={e => setForm({ ...form, vessel_name: e.target.value })} data-testid="rollover-vessel-input" />
                </div>
                <div>
                  <Label className="text-xs">New Voyage</Label>
                  <Input value={form.voyage} onChange={e => setForm({ ...form, voyage: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">New ETD</Label>
                  <Input type="date" value={form.etd} onChange={e => setForm({ ...form, etd: e.target.value })} data-testid="rollover-etd-input" />
                </div>
                <div>
                  <Label className="text-xs">New ETA</Label>
                  <Input type="date" value={form.eta} onChange={e => setForm({ ...form, eta: e.target.value })} data-testid="rollover-eta-input" />
                </div>
                <div>
                  <Label className="text-xs">Shipping Line</Label>
                  <CarrierSelect value={form.shipping_line} onChange={val => setForm({ ...form, shipping_line: val })} testId="rollover-shipping-line-select" />
                </div>
                <div>
                  <Label className="text-xs">MBL #</Label>
                  <Input value={form.mbl_number} onChange={e => setForm({ ...form, mbl_number: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Reason for roll-over (optional)</Label>
                <textarea
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g. Vessel cancelled by carrier · Port congestion in Koper · Customer delay"
                  value={form.reason}
                  onChange={e => setForm({ ...form, reason: e.target.value })}
                  data-testid="rollover-reason-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.notify_customers} onChange={e => setForm({ ...form, notify_customers: e.target.checked })} data-testid="rollover-notify-toggle" />
                Notify customer for every sub-PF (sends email to each sub-PF's recipients)
              </label>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t bg-slate-50 rounded-b-lg">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={submitting || !changed} className="bg-amber-600 hover:bg-amber-700" data-testid="rollover-submit-btn">
                {submitting ? "Rolling over..." : "Save & notify"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SendStuffingManifestModal({ folder, token, aggregate, canvasContainerRef, onClose }) {
  const [to, setTo] = useState(folder.customer_email || "");
  const [cc, setCc] = useState((folder.notification_emails || []).join(", "));
  const [subject, setSubject] = useState(
    `Stuffing Manifest — ${folder.pf_number} (${folder.origin_port || ''} → ${folder.destination_port || ''})`
  );
  const [note, setNote] = useState(
    "Please find attached the stuffing manifest for the upcoming Export LCL container.\n\n" +
    "Kindly confirm receipt and any handling notes for the warehouse."
  );
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [sending, setSending] = useState(false);

  const captureCanvas = () => {
    if (!canvasContainerRef?.current) return null;
    const canvas = canvasContainerRef.current.querySelector("canvas");
    if (!canvas) return null;
    try {
      return canvas.toDataURL("image/png");
    } catch {
      return null;
    }
  };

  const send = async () => {
    const looksValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
    const toList = to.split(",").map(e => e.trim()).filter(Boolean);
    const ccList = cc.split(",").map(e => e.trim()).filter(Boolean);
    if (toList.length === 0 || !toList.every(looksValid)) {
      toast.error("Provide at least one valid To: address");
      return;
    }
    if (ccList.some(e => !looksValid(e))) {
      toast.error("CC list contains an invalid address");
      return;
    }
    setSending(true);
    try {
      const screenshot_data_url = includeScreenshot ? captureCanvas() : null;
      const r = await axios.post(
        `${API_URL}/position-folders/${folder.id}/send-stuffing-manifest`,
        {
          to_emails: toList,
          cc_emails: ccList,
          subject,
          note,
          screenshot_data_url,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(r.data?.message || "Manifest sent");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send manifest");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[120] p-4" data-testid="send-stuffing-manifest-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-slate-900 text-white rounded-t-lg">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Mail className="h-5 w-5 text-emerald-400" /> Send Stuffing Manifest
            </h3>
            <p className="text-xs text-slate-300">
              Master {folder.pf_number} · {aggregate?.sub_pfs_count || 0} sub-PFs · {aggregate?.summary?.total_pieces || 0} pieces · {aggregate?.summary?.total_weight_kg || 0} kg
            </p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="close-send-stuffing-manifest">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label className="text-xs">To <span className="text-red-500">*</span></Label>
            <Input value={to} onChange={e => setTo(e.target.value)} placeholder="warehouse@cfs.com, ops@consignee.com" data-testid="manifest-to-input" />
            <p className="text-xs text-slate-500 mt-0.5">Comma-separated. Defaults to master's customer_email.</p>
          </div>
          <div>
            <Label className="text-xs">CC</Label>
            <Input value={cc} onChange={e => setCc(e.target.value)} placeholder="ops@a.com, ops@b.com" data-testid="manifest-cc-input" />
          </div>
          <div>
            <Label className="text-xs">Subject</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} data-testid="manifest-subject-input" />
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={5}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="manifest-note-input"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={includeScreenshot} onChange={e => setIncludeScreenshot(e.target.checked)} data-testid="manifest-screenshot-toggle" />
            Include 3D container fit screenshot in PDF
          </label>
          <div className="bg-slate-50 border rounded-lg p-3 text-xs text-slate-600">
            <div className="font-semibold text-slate-900 mb-1">PDF will include:</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Master {folder.pf_number} · MBL {folder.mbl_number || '—'} · Container {folder.lcl_container_type || '—'}</li>
              <li>Vessel {folder.vessel_name || '—'} {folder.voyage || ''} · ETD {folder.etd?.slice(0, 10) || '—'} · ETA {folder.eta?.slice(0, 10) || '—'}</li>
              <li>Cargo manifest grouped by sub-PF (type, qty, dims, weights, volume) with grand totals</li>
              {includeScreenshot && <li>3D container fit preview from current Stuffing view</li>}
            </ul>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t bg-slate-50 rounded-b-lg">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="manifest-send-btn">
            {sending ? "Sending..." : "Send manifest"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ManualTrinetStatusButton({ folder, token, onProcessed }) {
  const [open, setOpen] = useState(false);
  const [statusCode, setStatusCode] = useState("T02");
  const [mrn, setMrn] = useState("");
  const [note, setNote] = useState("");
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const STATUS_CHOICES = [
    { code: "import_dec_submitted", label: "Customs Clearance Started (IE415B / Uvozna deklaracija)" },
    { code: "T21", label: "T21 / U05 — Documentary Control (Obvestilo o kontroli)" },
    { code: "T14", label: "T14 — Goods Released (sprostitvi blaga)" },
    { code: "T13", label: "T13 / U04 — Declaration Rejected" },
    { code: "clearance_physical_check", label: "Physical Check Completed" },
    { code: "clearance_dox_check", label: "Document Check Completed" },
  ];

  const submit = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/trinet/manual-status`,
        {
          pf_number: folder.pf_number,
          status_code: statusCode,
          mrn: mrn.trim() || null,
          note: note.trim() || null,
          notify_customer: notifyCustomer,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      if (res.data?.duplicate) {
        toast.info(res.data.message || "Milestone already exists");
      } else {
        toast.success(`Added: ${res.data?.event_label}`);
      }
      if (typeof onProcessed === "function") onProcessed();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add status");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStatusCode("T02");
    setMrn("");
    setNote("");
    setNotifyCustomer(false);
    setResult(null);
  };

  return (
    <>
      <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-blue-900">Add TriNet status manually</div>
          <div className="text-xs text-blue-800">For statuses visible in Trinet UI but not sent as email (T02 Customs Started, T21 Documentary Control, etc.)</div>
        </div>
        <Button
          data-testid="manual-trinet-status-btn"
          variant="outline"
          size="sm"
          className="border-blue-400 text-blue-900 hover:bg-blue-100"
          onClick={() => setOpen(true)}
        >
          Add Trinet status
        </Button>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="manual-trinet-status-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">Add TriNet status manually</h3>
                <p className="text-xs text-muted-foreground">PF {folder.pf_number || folder.id}</p>
              </div>
              <button
                onClick={() => { setOpen(false); reset(); }}
                className="text-muted-foreground hover:text-foreground"
                data-testid="manual-trinet-status-close-btn"
              >
                ✕
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label className="text-sm">Status</Label>
                <select
                  value={statusCode}
                  onChange={(e) => setStatusCode(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  data-testid="manual-trinet-status-select"
                >
                  {STATUS_CHOICES.map((c) => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm">MRN (optional)</Label>
                <input
                  type="text"
                  value={mrn}
                  onChange={(e) => setMrn(e.target.value)}
                  placeholder="26SI00603640280680"
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  data-testid="manual-trinet-status-mrn-input"
                />
              </div>
              <div>
                <Label className="text-sm">Note (optional)</Label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Manually synced from Trinet UI"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="manual-trinet-status-note-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyCustomer}
                  onChange={(e) => setNotifyCustomer(e.target.checked)}
                  data-testid="manual-trinet-status-notify-checkbox"
                />
                <span>Send milestone email to customer</span>
              </label>

              {result && result.success && (
                <div className="rounded-lg p-3 text-xs bg-emerald-50 border border-emerald-200 text-emerald-900">
                  {result.duplicate ? (
                    <div>ℹ {result.message}</div>
                  ) : (
                    <>
                      <div className="font-semibold mb-1">✓ {result.event_label} added</div>
                      <div>tracking_status: <span className="font-mono">{result.tracking_status || "—"}</span></div>
                    </>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => { setOpen(false); reset(); }}
                  data-testid="manual-trinet-status-cancel-btn"
                >
                  Close
                </Button>
                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="manual-trinet-status-submit-btn"
                >
                  {submitting ? "Adding..." : "Add status"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function BackfillTlmEmailsButton({ token }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!window.confirm("Re-scan all historical TLM emails and backfill any missing customs milestones onto matching PFs? Safe — idempotent. May take ~30s.")) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/trinet/reprocess-tlm-emails`,
        { only_unprocessed: false, limit: 5000 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      toast.success(`Backfill done: ${res.data.processed} processed, ${res.data.skipped} skipped, ${res.data.errors} errors`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex items-center justify-between bg-violet-50 border border-violet-200 rounded-lg px-4 py-3">
      <div>
        <div className="text-sm font-semibold text-violet-900">Backfill TLM milestones (one-time)</div>
        <div className="text-xs text-violet-800">
          Re-runs the parser over all historical TLM emails. Useful after a deploy that adds new status detection.
          {result && (
            <span className="ml-2 font-mono">
              · Last run: {result.processed}/{result.scanned} processed, {result.errors} errors
            </span>
          )}
        </div>
      </div>
      <Button
        data-testid="backfill-tlm-emails-btn"
        variant="outline"
        size="sm"
        className="border-violet-400 text-violet-900 hover:bg-violet-100"
        onClick={run}
        disabled={running}
      >
        {running ? "Running..." : "Backfill now"}
      </Button>
    </div>
  );
}

function KV({ label, value, sub, mono, strong, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{label}</div>
      <div className={`mt-1 ${strong ? "text-base font-bold text-slate-900" : "text-sm text-slate-800"} ${mono ? "font-mono" : ""} break-words`}>{value}</div>
      {sub ? <div className="text-[11px] text-slate-400 mt-0.5 capitalize">{sub}</div> : null}
    </div>
  );
}

function ZziCustomsPanel({ folder, token, onUpdate, isCustomerView }) {
  const ci = folder?.customs_info || {};
  const duties = folder?.customs_duties || {};
  const status = folder?.tracking_status || "";
  const mrnList = (ci.mrn_list && ci.mrn_list.length ? ci.mrn_list : (ci.mrn ? [ci.mrn] : []));
  const lrnList = (ci.lrn_list && ci.lrn_list.length ? ci.lrn_list : (ci.lrn ? [ci.lrn] : []));

  const CUS_RE = /customs|clearance|declarat|inspection|control|kontrol|sprost|carin|\bmrn\b|ie4|\bt1\b|transit|prepust|dovoljenj|uvozn/i;
  const milestones = (folder?.milestones || [])
    .filter((m) => {
      const cat = (m.event_category || "").toLowerCase();
      const src = (m.source || "").toLowerCase();
      const key = `${m.event_type || ""} ${m.name || ""} ${m.name_sl || ""} ${m.title || ""} ${m.description || ""} ${m.label || ""} ${m.status || ""}`;
      return cat === "customs" || src === "ais2_xml" || src === "manual_trinet" || src === "trinet_auto" || CUS_RE.test(key);
    })
    .map((m) => ({ ...m, _date: m.actual_date || m.date || m.estimated_date || m.completed_at || m.timestamp || m.created_at || null }))
    .sort((a, b) => new Date(a._date || 0) - new Date(b._date || 0));

  const stateMap = {
    customs_cleared: { label: "Cleared — Goods Released", tone: "emerald", icon: Check },
    customs_inspection: { label: "Physical Inspection", tone: "amber", icon: AlertTriangle },
    customs_documentary_check: { label: "Documentary Control", tone: "amber", icon: ClipboardCheck },
    customs_inspection_result: { label: "Inspection Result Received", tone: "amber", icon: ClipboardCheck },
    customs_incomplete_declaration: { label: "Incomplete Declaration", tone: "amber", icon: AlertTriangle },
    customs_correction: { label: "Declaration Correction", tone: "amber", icon: AlertTriangle },
    customs_clearance: { label: "Customs Clearance In Progress", tone: "blue", icon: Loader2 },
    t1_transit: { label: "T1 Transit", tone: "blue", icon: CalendarClock },
    t1_in_progress: { label: "T1 In Progress", tone: "blue", icon: CalendarClock },
  };
  const st = stateMap[status] || (milestones.length
    ? { label: "Customs Activity Recorded", tone: "blue", icon: ShieldCheck }
    : { label: "No Customs Activity Yet", tone: "slate", icon: ShieldCheck });
  const toneClasses = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    slate: "bg-slate-50 border-slate-200 text-slate-600",
  };
  const StIcon = st.icon;
  const fmt = (d) => { if (!d) return "—"; const x = new Date(d); return isNaN(x) ? String(d) : x.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
  const fmtDate = (d) => { if (!d) return "—"; const x = new Date(d); return isNaN(x) ? String(d) : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); };
  const eur = (v) => (v == null || v === "" ? null : `${String(v).replace(/\s+/g, "")} EUR`);
  const hasDuties = duties && (duties.total || duties.customs || duties.vat || duties.other);
  const milestoneLabel = (m) => m.name || m.name_sl || m.title || m.label || m.description || (m.event_type || "").replace(/_/g, " ") || "Customs event";

  // ── Manual customs entry (staff): lets ops record a customs status
  // (e.g. Documentary Control) to buy time during clearance. Reuses the
  // existing POST /trinet/manual-status injection endpoint.
  const CUSTOMS_MANUAL_OPTIONS = [
    { value: "control_notice", label: "Documentary Control / Customs Check" },
    { value: "clearance_dox_check", label: "Document Check" },
    { value: "clearance_physical_check", label: "Physical Check" },
    { value: "clearance_started", label: "Customs Clearance Started" },
    { value: "import_dec_submitted", label: "Declaration Lodged" },
    { value: "control_result", label: "Control Result" },
    { value: "mrn_assigned", label: "MRN Assigned" },
    { value: "declaration_correction", label: "Declaration Correction" },
    { value: "clearance_finished", label: "Goods Released (Cleared)" },
  ];
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const [manualForm, setManualForm] = useState({
    status_code: "control_notice",
    mrn: mrnList[0] || "",
    note: "",
    notify_customer: false,
  });

  const submitManualEntry = async () => {
    if (!folder?.pf_number) {
      toast.error("Missing PF number");
      return;
    }
    setSavingManual(true);
    try {
      await axios.post(
        `${API_URL}/trinet/manual-status`,
        {
          pf_number: folder.pf_number,
          status_code: manualForm.status_code,
          mrn: manualForm.mrn?.trim() || null,
          note: manualForm.note?.trim() || null,
          notify_customer: !!manualForm.notify_customer,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Customs entry added");
      setShowManualEntry(false);
      setManualForm({ status_code: "control_notice", mrn: mrnList[0] || "", note: "", notify_customer: false });
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add customs entry");
    } finally {
      setSavingManual(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="zzi-customs-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-purple-100 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-purple-700" />
          </div>
          <div>
            <div className="text-base font-semibold text-slate-900">ZZI e-Customs (SI.AIS2)</div>
            <div className="text-xs text-slate-500">Auto-updated from ZZI IE-message / TLM notifications</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium ${toneClasses[st.tone]}`} data-testid="zzi-customs-status">
            <StIcon className={`h-4 w-4 ${status === "customs_clearance" ? "animate-spin" : ""}`} />
            {st.label}
          </div>
          {!isCustomerView && (
            <button
              onClick={() => setShowManualEntry((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-purple-200 bg-purple-50 text-purple-700 text-sm font-medium hover:bg-purple-100 transition-colors"
              data-testid="customs-add-manual-btn"
            >
              <Plus className="h-4 w-4" />
              Add manual entry
            </button>
          )}
        </div>
      </div>

      {!isCustomerView && showManualEntry && (
        <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 space-y-3" data-testid="customs-manual-entry-form">
          <div className="text-sm font-semibold text-slate-800">Add customs status manually</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">Status</label>
              <select
                value={manualForm.status_code}
                onChange={(e) => setManualForm((f) => ({ ...f, status_code: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="customs-manual-status-select"
              >
                {CUSTOMS_MANUAL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">MRN (optional)</label>
              <input
                type="text"
                value={manualForm.mrn}
                onChange={(e) => setManualForm((f) => ({ ...f, mrn: e.target.value }))}
                placeholder="e.g. 26SI00123456789A1"
                className="w-full h-10 px-3 rounded-lg border border-slate-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="customs-manual-mrn-input"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500">Note (optional)</label>
            <textarea
              value={manualForm.note}
              onChange={(e) => setManualForm((f) => ({ ...f, note: e.target.value }))}
              rows={2}
              placeholder="e.g. Documentary control in progress — awaiting release."
              className="w-full p-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="customs-manual-note-input"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={manualForm.notify_customer}
              onChange={(e) => setManualForm((f) => ({ ...f, notify_customer: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
              data-testid="customs-manual-notify-checkbox"
            />
            Notify customer by email
          </label>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowManualEntry(false)}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-100"
              data-testid="customs-manual-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={submitManualEntry}
              disabled={savingManual}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="customs-manual-save-btn"
            >
              {savingManual ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Save entry
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KV label="MRN" testid="zzi-mrn" value={mrnList.length ? mrnList.join(", ") : "—"} mono />
        <KV label="LRN" testid="zzi-lrn" value={lrnList.length ? lrnList.join(", ") : "—"} mono />
        <KV label="Declaration Type" value={ci.type || "—"} />
        <KV label="Last Update" value={fmt(ci.last_updated_at)} sub={ci.last_updated_from ? ci.last_updated_from.replace(/_/g, " ") : ""} />
      </div>

      {hasDuties ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="zzi-duties">
          <div className="text-sm font-semibold text-slate-800 mb-3">Assessed Duties</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {eur(duties.customs) ? <KV label="Customs" value={eur(duties.customs)} /> : null}
            {eur(duties.vat) ? <KV label="VAT" value={eur(duties.vat)} /> : null}
            {eur(duties.other) ? <KV label="Other" value={eur(duties.other)} /> : null}
            {eur(duties.total) ? <KV label="Total" value={eur(duties.total)} strong /> : null}
          </div>
        </div>
      ) : null}

      <div>
        <div className="text-sm font-semibold text-slate-800 mb-3">Customs Timeline</div>
        {milestones.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500" data-testid="zzi-timeline-empty">
            No customs milestones yet. They appear automatically when ZZI / TLM sends a status update.
          </div>
        ) : (
          <ol className="relative border-l-2 border-purple-100 ml-3 space-y-4" data-testid="zzi-timeline">
            {milestones.map((m, i) => {
              const done = m.completed !== false;
              return (
                <li key={m.id || i} className="ml-5" data-testid={`zzi-timeline-item-${i}`}>
                  <span className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ${done ? "bg-emerald-500" : "bg-slate-300"}`}>
                    <Check className="h-2.5 w-2.5 text-white" />
                  </span>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-slate-800">{milestoneLabel(m)}</div>
                      {(m.mrn || m.control_result_code) ? (
                        <div className="text-xs text-slate-500 mt-0.5">
                          {m.mrn ? <span className="font-mono">MRN {m.mrn}</span> : null}
                          {m.control_result_code ? <span className="ml-2">Control: {m.control_result_code}</span> : null}
                        </div>
                      ) : null}
                      {m.note ? (
                        <div className="text-xs text-slate-600 mt-0.5 italic">{m.note}</div>
                      ) : null}
                      {m.source === "manual_trinet" ? (
                        <span className="inline-block mt-1 text-[10px] uppercase tracking-wider text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5">Manual entry</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-400 whitespace-nowrap flex items-center gap-1">
                      <CalendarClock className="h-3 w-3" /> {fmtDate(m._date)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}


function ReprocessCustomsEmailButton({ folder, token, onProcessed }) {
  const [open, setOpen] = useState(false);
  const [emailBody, setEmailBody] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    if (!emailBody.trim()) {
      toast.error("Paste the customs email body first");
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/customs/parse-status-email`,
        { email_body: emailBody, email_subject: emailSubject, auto_update_pf: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      if (res.data?.success) {
        toast.success(`Parsed: ${res.data.parsed?.status_english || "OK"}`);
        if (typeof onProcessed === "function") onProcessed();
      } else {
        toast.error(res.data?.error || "Could not parse email");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse email");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setEmailBody("");
    setEmailSubject("");
    setResult(null);
  };

  return (
    <>
      <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-amber-900">Customs email fallback</div>
          <div className="text-xs text-amber-800">If a TLM/customs email didn't auto-process, paste it here to create the milestone.</div>
        </div>
        <Button
          data-testid="reprocess-customs-email-btn"
          variant="outline"
          size="sm"
          className="border-amber-400 text-amber-900 hover:bg-amber-100"
          onClick={() => setOpen(true)}
        >
          Reprocess customs email
        </Button>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="reprocess-customs-email-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">Reprocess customs email</h3>
                <p className="text-xs text-muted-foreground">PF {folder.pf_number || folder.id}</p>
              </div>
              <button
                onClick={() => { setOpen(false); reset(); }}
                className="text-muted-foreground hover:text-foreground"
                data-testid="reprocess-customs-email-close-btn"
              >
                ✕
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label className="text-sm">Email subject (optional)</Label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="e.g. SI.AIS2 - Obvestilo o sprostitvi blaga - MRN: 26SI..."
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="reprocess-customs-email-subject-input"
                />
              </div>
              <div>
                <Label className="text-sm">Email body <span className="text-red-500">*</span></Label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Paste the full body of the TLM customs email here..."
                  rows={12}
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  data-testid="reprocess-customs-email-body-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  The parser will extract MRN, status (cleared, transit, etc.), importer, container & duties — and add a milestone to the matched PF.
                </p>
              </div>

              {result && (
                <div className={`rounded-lg p-3 text-xs ${result.success ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-red-50 border border-red-200 text-red-900"}`}>
                  {result.success ? (
                    <>
                      <div className="font-semibold mb-1">{result.parsed?.status_english}</div>
                      <div>PF: <span className="font-mono">{result.parsed?.pf_number || "—"}</span></div>
                      <div>MRN: <span className="font-mono">{result.parsed?.mrn || "—"}</span></div>
                      {result.pf_update?.error && <div className="mt-1 text-amber-700">⚠ {result.pf_update.error}</div>}
                      {result.pf_update?.pf_id && <div className="mt-1 text-emerald-700">✓ PF updated: {result.pf_update.pf_number}</div>}
                    </>
                  ) : (
                    <div>{result.error || "Could not parse"}</div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => { setOpen(false); reset(); }}
                  data-testid="reprocess-customs-email-cancel-btn"
                >
                  Close
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !emailBody.trim()}
                  data-testid="reprocess-customs-email-submit-btn"
                >
                  {submitting ? "Parsing..." : "Parse & update PF"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PasteTlmStatusButton({ folder, token, onProcessed }) {
  const [open, setOpen] = useState(false);
  const [rawText, setRawText] = useState("");
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    if (!rawText.trim()) {
      toast.error("Paste the TLM customs status lines first");
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/trinet/paste-status`,
        { pf_number: folder.pf_number || folder.id, raw_text: rawText, notify_customer: notifyCustomer },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      toast.success(`${res.data.added} milestone(s) added, ${res.data.skipped} skipped, ${res.data.unmatched} unmatched`);
      if (res.data.added > 0 && typeof onProcessed === "function") onProcessed();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create milestones");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setRawText(""); setResult(null); setNotifyCustomer(false); };

  const statusColor = (s) =>
    s === "added" ? "text-emerald-700"
    : s === "unmatched" ? "text-red-600"
    : "text-amber-700";

  return (
    <>
      <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-emerald-900">Paste TLM customs statuses</div>
          <div className="text-xs text-emerald-800">
            Paste the "Opis" lines from TLM's data-exchange tab (Priprava deklaracije, Obvestilo o prejemu, Obvestilo o kontroli, Prepustitev blaga...) to create customs milestones.
          </div>
        </div>
        <Button
          data-testid="paste-tlm-status-btn"
          variant="outline"
          size="sm"
          className="border-emerald-400 text-emerald-900 hover:bg-emerald-100"
          onClick={() => setOpen(true)}
        >
          Paste statuses
        </Button>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="paste-tlm-status-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">Paste TLM customs statuses</h3>
                <p className="text-xs text-muted-foreground">PF {folder.pf_number || folder.id}</p>
              </div>
              <button
                onClick={() => { setOpen(false); reset(); }}
                className="text-muted-foreground hover:text-foreground"
                data-testid="paste-tlm-status-close-btn"
              >
                X
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label className="text-sm">Status lines (one per line) <span className="text-red-500">*</span></Label>
                <textarea
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder={"Priprava uvozne deklaracije\nObvestilo o prejemu uvozne deklaracije\nObvestilo o kontroli\nPrepustitev blaga"}
                  rows={10}
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  data-testid="paste-tlm-status-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Each recognised line becomes a milestone (Customs Draft Started, Customs Started, Customs Check/Control, Goods Released...). Duplicates are skipped automatically.
                </p>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={notifyCustomer}
                  onChange={(e) => setNotifyCustomer(e.target.checked)}
                  data-testid="paste-tlm-status-notify-checkbox"
                />
                Notify customer for each new milestone (leave off when backfilling)
              </label>

              {result && (
                <div className="rounded-lg p-3 text-xs bg-slate-50 border border-slate-200 space-y-1 max-h-48 overflow-y-auto" data-testid="paste-tlm-status-result">
                  <div className="font-semibold mb-1">
                    {result.added} added, {result.skipped} skipped, {result.unmatched} unmatched
                  </div>
                  {result.details?.map((d, i) => (
                    <div key={i} className={statusColor(d.status)}>
                      {d.label || d.matched || "(unmatched)"} <span className="text-muted-foreground">- {d.line}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => { setOpen(false); reset(); }}
                  data-testid="paste-tlm-status-cancel-btn"
                >
                  Close
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !rawText.trim()}
                  data-testid="paste-tlm-status-submit-btn"
                >
                  {submitting ? "Creating..." : "Create milestones"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function FolderDetailModal({ folder, token, subOrders = [], onClose, onUpdate, onAddSubOrder, navigate, onDelete }) {
  const { isAdmin, user, getEffectiveView, VIEW_MODES } = useAuth();
  // Customer view = read-only, limited tabs (no edit/staff controls)
  const isCustomerView = getEffectiveView() === VIEW_MODES.CUSTOMER;
  const CUSTOMER_ALLOWED_TABS = ["details", "documents", "milestones", "customs", "cargo", "email"];
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailTo, setEmailTo] = useState(
    [folder.customer_email, ...(folder.notification_emails || [])].filter(Boolean).join(", ")
  );
  const [emailCc, setEmailCc] = useState((folder.notification_emails || []).filter(Boolean).join(", "));
  const [sending, setSending] = useState(false);
  const [activeSection, setActiveSection] = useState("details");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatingTO, setCreatingTO] = useState(false);
  const [showTOModal, setShowTOModal] = useState(false);
  const [toFormData, setToFormData] = useState(null);
  const [showSpotModal, setShowSpotModal] = useState(false);
  const [spotFormData, setSpotFormData] = useState(null);
  const EMPTY_SPOT_CARGO = { packages: "", package_type: "", weight_kg: "", ldm: "", volume_cbm: "", dimensions: "", stackable: false, adr: false, adr_class: "", equipment_uid: "", equipment_name: "", commodity: "", temp_min: "", temp_max: "" };
  const [spotCargo, setSpotCargo] = useState(EMPTY_SPOT_CARGO);
  const [equipmentTypes, setEquipmentTypes] = useState(FALLBACK_EQUIPMENT);
  const [commodities, setCommodities] = useState([]);

  // Pre-fill the Spot Rate cargo section from the PF's saved cargo data where available.
  const prefillSpotCargo = () => {
    const firstNum = (v) => {
      if (v == null || v === "") return "";
      const m = String(v).replace(",", ".").match(/-?\d+(?:\.\d+)?/);
      return m ? m[0] : "";
    };
    const packagesRaw = folder.packages || folder.packing || "";
    const txt = `${packagesRaw} ${folder.cargo_type || ""} ${folder.package_type || ""}`.toUpperCase();
    let pkgType = "";
    if (/PLT|PAL|PALLET|\bEUR\b|EURO/.test(txt)) pkgType = "EUR pallets";
    else if (/CTN|CARTON/.test(txt)) pkgType = "Cartons";
    else if (/BOX/.test(txt)) pkgType = "Boxes";
    else if (/COLLI|CLL/.test(txt)) pkgType = "Colli";
    else if (/BAG/.test(txt)) pkgType = "Big bags";
    else if (/ROLL/.test(txt)) pkgType = "Rolls";
    const isAdr = !!(folder.adr || folder.dangerous_goods) ||
      /HAZARD|DANGER|ADR/.test((folder.cargo_type || "").toUpperCase());
    return {
      ...EMPTY_SPOT_CARGO,
      packages: firstNum(packagesRaw),
      package_type: pkgType,
      weight_kg: firstNum(folder.cargo_weight || folder.gross_weight_kg || folder.gross_weight),
      volume_cbm: firstNum(folder.cargo_volume || folder.volume_cbm || folder.volume),
      dimensions: folder.cargo_dimensions || folder.dimensions || "",
      adr: isAdr,
      adr_class: folder.adr_class || "",
    };
  };
  const [spotCompanies, setSpotCompanies] = useState([]);
  const [selectedSpotEmails, setSelectedSpotEmails] = useState([]);
  const [sendingSpot, setSendingSpot] = useState(false);
  const [spotResult, setSpotResult] = useState(null);
  
  // Edit PF Number state
  const [editingPfNumber, setEditingPfNumber] = useState(false);
  const [newPfNumber, setNewPfNumber] = useState(folder.pf_number || "");
  const [savingPfNumber, setSavingPfNumber] = useState(false);
  
  // Editable fields
  const [editData, setEditData] = useState({
    customer: folder.customer || folder.customer_name || "",
    customer_email: folder.customer_email || "",
    description: folder.description || folder.cargo_description || "",
    // Shipper & Consignee for sub-orders
    shipper: folder.shipper || "",
    consignee: folder.consignee || "",
    // CRM link
    crm_company_id: folder.crm_company_id || null,
    crm_group_id: folder.crm_group_id || null,
    linkedCompany: null,
    // Cargo details - check both new and legacy field names
    cargo_type: folder.cargo_type || "",
    cargo_weight: folder.cargo_weight || folder.gross_weight_kg || folder.gross_weight || "",
    cargo_dimensions: folder.cargo_dimensions || "",
    cargo_volume: folder.cargo_volume || folder.volume_cbm || folder.volume || "",
    container_type: folder.container_type || "",
    shipment_type: folder.shipment_type || (folder.is_sub_order ? "LCL" : "FCL"),
    commodity: folder.commodity || folder.cargo_description || "",
    packages: folder.packages || folder.packing || "",
    // HBL number - critical for tracking
    hbl_number: folder.hbl_number || "",
    // Notification emails
    notification_emails: folder.notification_emails || [],
  });
  const [newEmail, setNewEmail] = useState("");

  // Tracking Sources - FCL/LCL/HBL/Port/Metrans toggles
  const [trackingSources, setTrackingSources] = useState({
    fcl: folder.tracking_sources?.fcl ?? true,
    lcl: folder.tracking_sources?.lcl ?? false,
    hbl: folder.tracking_sources?.hbl ?? false,
    port: folder.tracking_sources?.port ?? true,
    metrans: folder.tracking_sources?.metrans ?? false,
  });
  const [selectedPort, setSelectedPort] = useState(folder.tracking_sources?.selected_port || "LUKA_KOPER");
  const [savingTracking, setSavingTracking] = useState(false);

  // Available ports for selection
  const availablePorts = [
    { code: "LUKA_KOPER", name: "Luka Koper", country: "Slovenia", flag: "🇸🇮" },
    { code: "RIJEKA_APM", name: "APM Rijeka", country: "Croatia", flag: "🇭🇷" },
    { code: "RIJEKA_AGCT", name: "AGCT Rijeka", country: "Croatia", flag: "🇭🇷" },
  ];

  // Edit container info state
  const [editingContainerInfo, setEditingContainerInfo] = useState(false);
  const [containerInfoEdit, setContainerInfoEdit] = useState({
    container_number: folder.container_number || "",
    containers: folder.containers || [],
    mbl_number: folder.mbl_number || "",
    hbl_numbers: folder.hbl_number || folder.hbl_numbers || [],  // Support both singular and plural
    booking_number: folder.booking_number || "",
  });
  const [savingContainerInfo, setSavingContainerInfo] = useState(false);

  // Multi-select sub-orders for bulk delete
  const [selectedSubOrders, setSelectedSubOrders] = useState([]);
  const [deletingSubOrders, setDeletingSubOrders] = useState(false);
  
  // Workflow mode (auto/pending) for master PFs
  const [workflowMode, setWorkflowMode] = useState(folder.workflow_mode || "auto");
  const [savingWorkflowMode, setSavingWorkflowMode] = useState(false);

  // Toggle sub-order selection
  const toggleSubOrderSelection = (subOrderId) => {
    setSelectedSubOrders(prev => 
      prev.includes(subOrderId) 
        ? prev.filter(id => id !== subOrderId)
        : [...prev, subOrderId]
    );
  };

  // Select all sub-orders
  const selectAllSubOrders = () => {
    if (selectedSubOrders.length === subOrders.length) {
      setSelectedSubOrders([]);
    } else {
      setSelectedSubOrders(subOrders.map(s => s.id));
    }
  };

  // Delete selected sub-orders
  const deleteSelectedSubOrders = async () => {
    if (selectedSubOrders.length === 0) {
      toast.error("No sub-orders selected");
      return;
    }
    
    if (!window.confirm(`Delete ${selectedSubOrders.length} selected sub-order(s)? They will be moved to the purge folder.`)) {
      return;
    }
    
    setDeletingSubOrders(true);
    try {
      const response = await axios.post(
        `${API_URL}/position-folders/bulk-soft-delete`,
        selectedSubOrders,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Deleted ${response.data.total_deleted} sub-order(s). They will be purged in 30 days.`);
      setSelectedSubOrders([]);
      onUpdate(); // Refresh parent
    } catch (error) {
      console.error("Delete sub-orders error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete sub-orders");
    } finally {
      setDeletingSubOrders(false);
    }
  };

  // Toggle workflow mode (auto/pending)
  const toggleWorkflowMode = async (newMode) => {
    setSavingWorkflowMode(true);
    try {
      const response = await axios.put(
        `${API_URL}/position-folders/${folder.id}/workflow-mode`,
        { mode: newMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setWorkflowMode(newMode);
      toast.success(response.data.message);
      onUpdate(); // Refresh parent
    } catch (error) {
      console.error("Toggle workflow mode error:", error);
      toast.error(error.response?.data?.detail || "Failed to change workflow mode");
    } finally {
      setSavingWorkflowMode(false);
    }
  };

  const saveTrackingSources = async () => {
    setSavingTracking(true);
    try {
      await axios.put(
        `${API_URL}/position-folders/${folder.id}`,
        { tracking_sources: { ...trackingSources, selected_port: selectedPort } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Tracking sources updated!");
      onUpdate();
    } catch (error) {
      toast.error("Failed to update tracking sources");
    } finally {
      setSavingTracking(false);
    }
  };

  // Update PF Number with customer notification
  const handleUpdatePfNumber = async () => {
    if (!newPfNumber.trim()) {
      toast.error("Please enter a new PF number");
      return;
    }
    
    setSavingPfNumber(true);
    try {
      const response = await axios.patch(
        `${API_URL}/position-folders/${folder.id}/number`,
        { new_pf_number: newPfNumber.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        response.data.customer_notified 
          ? `PF number updated to ${response.data.new_pf_number}. Customer notified at ${response.data.customer_email}`
          : `PF number updated to ${response.data.new_pf_number}`
      );
      
      // Update local folder reference
      folder.pf_number = response.data.new_pf_number;
      setEditingPfNumber(false);
      onUpdate(); // Refresh parent
    } catch (error) {
      console.error("Update PF number error:", error);
      toast.error(error.response?.data?.detail || "Failed to update PF number");
    } finally {
      setSavingPfNumber(false);
    }
  };

  // Spot Rate Request from this PF
  const fetchSpotCompanies = async (from, to, svc) => {
    try {
      const params = new URLSearchParams({ from_location: from || "Europe", to_location: to || "Europe" });
      if (svc && svc !== "all") params.set("service_type", svc);
      const res = await axios.get(`${API_URL}/trucking-companies/match?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = (res.data.companies || []).filter((c) => c.email);
      list.sort((a, b) => (b.route_matched ? 1 : 0) - (a.route_matched ? 1 : 0) || (b.route_count || 0) - (a.route_count || 0));
      setSpotCompanies(list);
    } catch {
      setSpotCompanies([]);
    }
  };

  const openSpotModal = async () => {
    const from = folder.origin_port || folder.pol || "";
    const to = folder.destination_port || folder.pod || "";
    setSpotResult(null);
    setSelectedSpotEmails([]);
    setSpotCargo(prefillSpotCargo());
    setSpotFormData({
      from_location: from,
      from_city: "",
      to_location: to,
      to_city: "",
      empty_location: "",
      service_type: folder.service_type || "FCL",
      pickup_date: (folder.etd || folder.departure_date || "").slice(0, 10),
      description: folder.cargo_description || folder.description || "",
      cc_emails: "",
      notes: "",
    });
    setShowSpotModal(true);
    // Load trucking companies from history for this lane
    fetchSpotCompanies(from, to);
    // Load Transporeon equipment/unit + commodity lists (replaces the fallback).
    try {
      const meta = await axios.get(`${API_URL}/transporeon/metadata`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const units = meta.data?.units || [];
      if (units.length) setEquipmentTypes(units);
      setCommodities((meta.data?.commodities || []).map((n) => ({ uid: n, name: n })));
    } catch { /* keep fallback list */ }
  };

  const toggleSpotEmail = (email) => {
    setSelectedSpotEmails((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const allSpotSelected = spotCompanies.length > 0 && selectedSpotEmails.length >= spotCompanies.length;
  const toggleSelectAllSpot = () => {
    if (allSpotSelected) setSelectedSpotEmails([]);
    else setSelectedSpotEmails(spotCompanies.map((c) => c.email));
  };

  const buildSpotCargo = () => {
    const num = (v) => (v === "" || v == null ? null : parseFloat(v));
    return {
      packages: num(spotCargo.packages),
      package_type: spotCargo.package_type || null,
      weight_kg: num(spotCargo.weight_kg),
      ldm: num(spotCargo.ldm),
      volume_cbm: num(spotCargo.volume_cbm),
      dimensions: spotCargo.dimensions || null,
      stackable: spotCargo.stackable,
      adr: spotCargo.adr,
      adr_class: spotCargo.adr ? (spotCargo.adr_class || null) : null,
      goods: spotFormData?.description || null,
      equipment_uid: spotCargo.equipment_uid || null,
      equipment_name: spotCargo.equipment_name || null,
      commodity: spotCargo.commodity || null,
      temp_min: spotCargo.temp_min !== "" ? parseFloat(spotCargo.temp_min) : null,
      temp_max: spotCargo.temp_max !== "" ? parseFloat(spotCargo.temp_max) : null,
    };
  };

  const sendSpotRequest = async () => {
    if (!spotFormData) return;
    setSendingSpot(true);
    try {
      const payload = {
        ...spotFormData,
        cargo: buildSpotCargo(),
        company_emails: selectedSpotEmails,
        cc_emails: spotFormData.cc_emails
          ? spotFormData.cc_emails.split(",").map((e) => e.trim()).filter(Boolean)
          : [],
      };
      const res = await axios.post(
        `${API_URL}/spot-requests/from-pf/${folder.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSpotResult(res.data);
      toast.success(`Spot request sent to ${res.data.emails_sent} trucking companies`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send spot request");
    } finally {
      setSendingSpot(false);
    }
  };

  const sendTransporeonSpot = async () => {
    if (!spotFormData) return;
    setSendingSpot(true);
    try {
      const weightMatch = (spotFormData.description || "").match(/(\d+(?:[.,]\d+)?)\s*(?:t|kg)/i);
      const weightFromDesc = weightMatch ? parseFloat(weightMatch[1].replace(",", ".")) : null;
      const cargo = buildSpotCargo();
      const payload = {
        from_location: spotFormData.from_location || spotFormData.from_city,
        to_location: spotFormData.to_location || spotFormData.to_city,
        service_type: spotFormData.service_type === "FCL" ? "FTL" : (spotFormData.service_type || "FTL"),
        pickup_date: spotFormData.pickup_date || null,
        cargo_description: spotFormData.description || null,
        weight_kg: cargo.weight_kg || weightFromDesc,
        notes: spotFormData.notes || null,
        pf_id: folder.id,
        pf_number: folder.pf_number,
        packages: cargo.packages,
        package_type: cargo.package_type,
        volume_cbm: cargo.volume_cbm,
        ldm: cargo.ldm,
        dimensions: cargo.dimensions,
        stackable: cargo.stackable,
        adr: cargo.adr,
        adr_class: cargo.adr_class,
        load_type: spotFormData.service_type === "LCL" || spotFormData.service_type === "LTL" ? "LTL" : "FTL",
        equipment_uid: cargo.equipment_uid,
        equipment_name: cargo.equipment_name,
        commodity: cargo.commodity,
        temp_min: cargo.temp_min,
        temp_max: cargo.temp_max,
      };
      const res = await axios.post(`${API_URL}/transporeon/spot-request`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Posted to Transporeon Marketplace (ref ${res.data.transport_number})`);
      setSpotResult({ ...(spotResult || {}), transporeon: res.data });
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to post to Transporeon";
      toast.error(msg);
    } finally {
      setSendingSpot(false);
    }
  };

  // Create Transport Order from this PF - open modal with pre-populated form
  const openTOModal = () => {
    // Pre-populate form with PF data
    const containers = folder.containers || [];
    const containerNumber = folder.container_number || (containers.length > 0 ? containers[0].number : "");
    
    setToFormData({
      trucking_company: {
        name: "",
        address: "",
        phone: "",
        email: "",
        vat_id: "",
        contact_person: "",
        dispatcher_tel: "",
      },
      loading_place: folder.origin_port || folder.pol ? `Port of ${folder.origin_port || folder.pol}` : "",
      unloading_place: folder.destination_port || folder.pod ? `Port of ${folder.destination_port || folder.pod}` : "",
      return_location: "",
      intermediate_stops: [],
      date_loading: folder.etd || folder.departure_date || "",
      date_unloading: folder.eta || folder.arrival_date || "",
      date_return: "",
      cargo_items: [{
        description: folder.cargo_description || folder.description || "",
        container_number: containerNumber,
        weight_kg: folder.gross_weight_kg || folder.gross_weight || "",
        pallets: folder.packages || "",
      }],
      price: "",
      currency: "EUR",
      customs_clearance: "CUSTOM CLEARED",
      remarks: folder.remarks || folder.notes || "",
      truck_number: "",
      trailer_number: "",
      driver_name: "",
      driver_phone: "",
      contact_person: localStorage.getItem("user_name") || "",
      contact_email: localStorage.getItem("user_email") || "",
      customer_name: folder.customer || folder.customer_name || "",
      customer_email: folder.customer_email || "",
      pf_number: folder.pf_number || "",
      pf_id: folder.id,
    });
    setShowTOModal(true);
  };

  const createTransportOrder = async () => {
    if (!toFormData) return;
    
    setCreatingTO(true);
    try {
      // Prepare payload with all form data
      const payload = {
        ...toFormData,
        price: parseFloat(toFormData.price) || 0,
        cargo_items: toFormData.cargo_items.map(item => ({
          ...item,
          weight_kg: item.weight_kg ? parseFloat(item.weight_kg) : null,
          pallets: item.pallets ? parseInt(item.pallets) : null,
        })),
        intermediate_stops: toFormData.intermediate_stops.map(stop => ({
          location: stop.location || "",
          purpose: stop.purpose || "customs",
          date: stop.date || "",
          notes: stop.notes || "",
        })),
      };
      
      const response = await axios.post(
        `${API_URL}/transport-orders/from-pf/${folder.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Transport Order ${response.data.order_number} created!`);
      setShowTOModal(false);
      setToFormData(null);
      // Navigate to Transport Orders page with the new order
      window.location.href = `/transport-orders?highlight=${response.data.order_id}`;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create Transport Order");
    } finally {
      setCreatingTO(false);
    }
  };
  
  // TO Form helper functions
  const addIntermediateStop = () => {
    setToFormData({
      ...toFormData,
      intermediate_stops: [
        ...toFormData.intermediate_stops,
        { location: "", purpose: "customs", date: "", notes: "" }
      ]
    });
  };

  const updateIntermediateStop = (index, field, value) => {
    const stops = [...toFormData.intermediate_stops];
    stops[index][field] = value;
    setToFormData({ ...toFormData, intermediate_stops: stops });
  };

  const removeIntermediateStop = (index) => {
    setToFormData({
      ...toFormData,
      intermediate_stops: toFormData.intermediate_stops.filter((_, i) => i !== index)
    });
  };

  const addCargoItem = () => {
    setToFormData({
      ...toFormData,
      cargo_items: [
        ...toFormData.cargo_items,
        { description: "", container_number: "", weight_kg: "", pallets: "" },
      ],
    });
  };

  const updateCargoItem = (index, field, value) => {
    const items = [...toFormData.cargo_items];
    items[index][field] = value;
    setToFormData({ ...toFormData, cargo_items: items });
  };

  const removeCargoItem = (index) => {
    setToFormData({
      ...toFormData,
      cargo_items: toFormData.cargo_items.filter((_, i) => i !== index),
    });
  };

  // Create Bill of Lading from this PF
  const [creatingBL, setCreatingBL] = useState(false);
  
  const createBillOfLading = async () => {
    setCreatingBL(true);
    try {
      console.log("Creating BL for folder:", folder.id);
      const response = await axios.post(
        `${API_URL}/bl/documents/from-pf/${folder.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("BL creation response:", response.data);
      toast.success(`Bill of Lading ${response.data.bl_number} created!`);
      // Close the modal first, then navigate
      onClose();
      // Navigate to BL page using React Router (correct route is /bl)
      navigate(`/bl?highlight=${response.data.bl_id}`);
    } catch (error) {
      console.error("BL creation error:", error.response?.status, error.response?.data, error.message);
      toast.error(error.response?.data?.detail || "Failed to create Bill of Lading");
    } finally {
      setCreatingBL(false);
    }
  };

  // Request BL Instructions from customer (Send & Track modal flow)
  const [requestingBLInstructions, setRequestingBLInstructions] = useState(false);
  const [showBLRequestModal, setShowBLRequestModal] = useState(false);
  const [sendingBLNudge, setSendingBLNudge] = useState(false);

  const openBLRequestModal = () => setShowBLRequestModal(true);

  const sendBLInstructionsNudge = async () => {
    setSendingBLNudge(true);
    try {
      const res = await axios.post(
        `${API_URL}/bl-instructions/${folder.id}/nudge`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.skipped) {
        toast.info("Already received — no reminder sent.");
      } else {
        toast.success(`Reminder sent to ${res.data.sent_to} (nudge #${res.data.nudge_count})`);
      }
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSendingBLNudge(false);
    }
  };
  
  // Mark BL Instructions as received
  const markBLInstructionsReceived = async () => {
    try {
      await axios.put(
        `${API_URL}/position-folders/${folder.id}`,
        { 
          bl_instructions_status: 'received',
          bl_instructions_received: true,
          bl_instructions_received_at: new Date().toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("BL Instructions marked as received!");
      onUpdate();
    } catch (error) {
      toast.error("Failed to update BL instructions status");
    }
  };

  // Send Satisfaction Survey to customer
  const [sendingSurvey, setSendingSurvey] = useState(false);
  
  const sendSatisfactionSurvey = async () => {
    if (!folder.customer_email) {
      toast.error("No customer email on this PF");
      return;
    }
    setSendingSurvey(true);
    try {
      await axios.post(
        `${API_URL}/service-satisfaction/send/${folder.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Satisfaction survey sent to customer!");
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send survey");
    } finally {
      setSendingSurvey(false);
    }
  };

  // Push PF to myCargoCFS Warehouse
  const [pushingToCFS, setPushingToCFS] = useState(false);
  
  const pushToCFS = async () => {
    setPushingToCFS(true);
    try {
      const response = await axios.post(
        `${API_URL}/wms/integration/push-from-europacific`,
        {
          pf_id: folder.id,
          pf_number: folder.pf_number,
          include_sub_orders: true, // Push all -1, -2, -3 subfiles
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      const subOrderCount = data.sub_orders?.length || 0;
      
      if (subOrderCount > 0) {
        toast.success(`Pushed to CFS: ${data.main_order.order_number} + ${subOrderCount} sub-orders`);
      } else {
        toast.success(`Pushed to CFS: ${data.main_order.order_number}`);
      }
      
      onUpdate(); // Refresh to show CFS link
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info("This PF is already linked to CFS");
      } else {
        toast.error(error.response?.data?.detail || "Failed to push to CFS");
      }
    } finally {
      setPushingToCFS(false);
    }
  };

  // Operations Workflow Steps based on direction
  const getWorkflowSteps = () => {
    const direction = folder.direction || 'import';
    const workflows = {
      import: [
        { id: "pf_created", label: "PF Created" },
        { id: "track_shipping", label: "Track Shipping" },
        { id: "arrival_notice_sent", label: "Arrival Notice Sent" },
        { id: "track_port", label: "Track Port" },
        { id: "track_trucking", label: "Track Trucking" },
        { id: "delivered", label: "Delivered" },
        { id: "pod_received", label: "POD Received" },
        { id: "invoiced", label: "Invoiced" },
      ],
      export: [
        { id: "pf_created", label: "PF Created" },
        { id: "track_trucking", label: "Track Trucking" },
        { id: "track_port", label: "Track Port" },
        { id: "pre_alert_sent", label: "Pre-Alert Sent" },
        { id: "track_shipping", label: "Track Shipping" },
        { id: "delivered", label: "Delivered" },
        { id: "pod_received", label: "POD Received" },
        { id: "invoiced", label: "Invoiced" },
      ],
      cross_trade: [
        { id: "pf_created", label: "PF Created" },
        { id: "track_origin_trucking", label: "Origin Trucking" },
        { id: "track_origin_port", label: "Origin Port" },
        { id: "pre_alert_sent", label: "Pre-Alert Sent" },
        { id: "track_shipping", label: "Track Shipping" },
        { id: "arrival_notice_sent", label: "Arrival Notice" },
        { id: "track_dest_port", label: "Dest Port" },
        { id: "track_dest_trucking", label: "Dest Trucking" },
        { id: "delivered", label: "Delivered" },
        { id: "pod_received", label: "POD Received" },
        { id: "invoiced", label: "Invoiced" },
      ],
    };
    return workflows[direction] || workflows.import;
  };

  const isStepCompleted = (stepId) => {
    return folder.operations_workflow?.steps?.[stepId]?.completed || false;
  };

  const updateWorkflowStep = async (stepId, completed) => {
    try {
      await axios.put(
        `${API_URL}/position-folders/${folder.id}/operations/step`,
        { step_id: stepId, completed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state
      if (!folder.operations_workflow) folder.operations_workflow = { steps: {} };
      if (!folder.operations_workflow.steps) folder.operations_workflow.steps = {};
      folder.operations_workflow.steps[stepId] = { completed };
      // Auto-update tracking_finished in local state when delivered/pod_received is completed
      if (completed && (stepId === "delivered" || stepId === "pod_received")) {
        folder.tracking_finished = true;
        toast.success("Tracking automatically stopped");
      }
      onUpdate();
    } catch (error) {
      toast.error("Failed to update step");
    }
  };

  // Schedule Inspections (all types)
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [schedulingInspection, setSchedulingInspection] = useState(false);
  const [inspectionData, setInspectionData] = useState({
    inspection_type: "customs_physical",
    scheduled_date: "",
    scheduled_time: "",
    officer_name: "",
    officer_phone: "",
    officer_email: "",
    reference_number: "",
    assigned_to: "",
    assigned_to_name: "",
    container_number: "",
    commodity: "",
    location: "Port of Koper",
    notes: "",
    set_reminder: true,
    reminder_minutes_before: 30
  });
  const [staffList, setStaffList] = useState([]);
  const [pfInspections, setPfInspections] = useState([]);

  // Arrival Notice Modal State
  const [showArrivalNoticeModal, setShowArrivalNoticeModal] = useState(false);
  const [sendingArrivalNotice, setSendingArrivalNotice] = useState(false);
  const [arrivalNoticeData, setArrivalNoticeData] = useState({
    notice_type: "simple", // "simple" or "rated"
    include_sub_orders: false,
    arrival_date: "",
    arrival_port: "KOPER",
    charges: ""  // Free text for rated notice
  });

  // Send Arrival Notice
  const sendArrivalNotice = async () => {
    setSendingArrivalNotice(true);
    try {
      const response = await axios.post(
        `${API_URL}/position-folders/${folder.id}/send-arrival-notice`,
        {
          notice_type: arrivalNoticeData.notice_type,
          include_sub_orders: arrivalNoticeData.include_sub_orders,
          arrival_date: arrivalNoticeData.arrival_date || folder.eta,
          arrival_port: arrivalNoticeData.arrival_port || folder.destination_port,
          charges: arrivalNoticeData.charges
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.sent > 0) {
        const recipientCount = response.data.results?.[0]?.recipients?.length || response.data.sent;
        toast.success(`Arrival notice sent to ${recipientCount} recipient(s) in one email`);
        setShowArrivalNoticeModal(false);
        setArrivalNoticeData({
          notice_type: "simple",
          include_sub_orders: false,
          arrival_date: "",
          arrival_port: "KOPER",
          charges: ""
        });
        onUpdate();
      } else if (response.data.skipped > 0) {
        toast.error("No customer email configured on this PF");
      } else {
        toast.error("Failed to send arrival notice");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send arrival notice");
    } finally {
      setSendingArrivalNotice(false);
    }
  };

  // Save container info changes
  const saveContainerInfo = async () => {
    setSavingContainerInfo(true);
    try {
      // Parse containers from string if it's a comma-separated string
      let containersToSave = containerInfoEdit.containers;
      if (typeof containerInfoEdit.containers === 'string') {
        containersToSave = containerInfoEdit.containers.split(',').map(c => c.trim()).filter(c => c);
      }
      
      // Parse HBL numbers similarly
      let hblsToSave = containerInfoEdit.hbl_numbers;
      if (typeof containerInfoEdit.hbl_numbers === 'string') {
        hblsToSave = containerInfoEdit.hbl_numbers.split(',').map(h => h.trim()).filter(h => h);
      }
      // For single HBL, use first one
      const hblNumber = Array.isArray(hblsToSave) && hblsToSave.length > 0 ? hblsToSave[0] : (hblsToSave || null);

      await axios.put(
        `${API_URL}/position-folders/${folder.id}`,
        {
          container_number: containerInfoEdit.container_number || null,
          containers: containersToSave,
          mbl_number: containerInfoEdit.mbl_number || null,
          hbl_number: hblNumber,  // Backend expects singular
          hbl_numbers: hblsToSave,  // Also send array for future compatibility
          booking_number: containerInfoEdit.booking_number || null,
          bl_structure: containerInfoEdit.bl_structure || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Container information updated");
      setEditingContainerInfo(false);
      onUpdate(); // Refresh parent
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update container info");
    } finally {
      setSavingContainerInfo(false);
    }
  };

  // Document Repository State
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedDocTag, setSelectedDocTag] = useState("BL");
  const [docDescription, setDocDescription] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  // ── Send-docs-to-customer (2026-05-15, Rok) ──────────────────────────
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [showSendDocsDialog, setShowSendDocsDialog] = useState(false);
  const [sendDocsRecipients, setSendDocsRecipients] = useState("");
  const [sendDocsSubject, setSendDocsSubject] = useState("");
  const [sendDocsMessage, setSendDocsMessage] = useState("");
  const [sendDocsBusy, setSendDocsBusy] = useState(false);
  // ── Share this PF with external partner(s) (2026-06, Rok) ───────────
  const [showSharePartnerDialog, setShowSharePartnerDialog] = useState(false);
  const [partnerList, setPartnerList] = useState([]);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [partnerBusyId, setPartnerBusyId] = useState(null);
  const [partnerSearch, setPartnerSearch] = useState("");

  const pfHasAccess = (p) =>
    (p.assigned_pf_ids || []).includes(folder.id) ||
    (p.assigned_pf_ids || []).includes(folder.pf_number);

  const loadPartners = async () => {
    const res = await axios.get(`${API_URL}/partner-portal/admin/partners`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setPartnerList((res.data.partners || []).filter((p) => p.approval_status === "approved"));
  };

  const openSharePartnerDialog = async () => {
    setShowSharePartnerDialog(true);
    setPartnerLoading(true);
    try {
      await loadPartners();
    } catch (e) {
      toast.error("Failed to load partner accounts");
    } finally {
      setPartnerLoading(false);
    }
  };

  const togglePartnerShare = async (p) => {
    const has = pfHasAccess(p);
    setPartnerBusyId(p.id);
    try {
      await axios.post(
        `${API_URL}/partner-portal/admin/partners/${p.id}/${has ? "unassign" : "assign"}`,
        { pf: folder.pf_number || folder.id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(has ? `Removed access for ${p.email}` : `Shared ${folder.pf_number} with ${p.email}`);
      await loadPartners();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setPartnerBusyId(null);
    }
  };

  const [sendDocsNotify, setSendDocsNotify] = useState(true);
  // ── Send editable customs forms to customer (2026-06-03, Rok) ────────
  const [showCustomsFormsDialog, setShowCustomsFormsDialog] = useState(false);
  const [cfStep, setCfStep] = useState("select"); // "select" | "review"
  const [cfAllForms, setCfAllForms] = useState([]); // [{slug,title}]
  const [cfSelectedSlugs, setCfSelectedSlugs] = useState([]);
  const [cfSourceDocIds, setCfSourceDocIds] = useState([]);
  const [cfForms, setCfForms] = useState([]); // prefilled forms for review
  const [cfRecipients, setCfRecipients] = useState("");
  const [cfMessage, setCfMessage] = useState("");
  const [cfAttachPdfs, setCfAttachPdfs] = useState(true);
  const [cfBusy, setCfBusy] = useState(false);
  // ── Request HS classification (Roket customs bot) ────────────────────
  const [showClassifyDialog, setShowClassifyDialog] = useState(false);
  const [clfDocIds, setClfDocIds] = useState([]);
  const [clfOrigin, setClfOrigin] = useState("");
  const [clfImportInto, setClfImportInto] = useState("EU");
  const [clfCargo, setClfCargo] = useState("");
  const [clfHs, setClfHs] = useState("");
  const [clfBusy, setClfBusy] = useState(false);


  const toggleDocSelected = (docId) => {
    setSelectedDocIds((prev) =>
      prev.includes(docId) ? prev.filter((x) => x !== docId) : [...prev, docId]
    );
  };

  const openSendDocsDialog = () => {
    if (selectedDocIds.length === 0) {
      toast.error("Select at least one document first");
      return;
    }
    const notify = (folder?.notification_emails || []).filter(Boolean).join(", ");
    setSendDocsRecipients(notify);
    setSendDocsSubject(`Documents for ${folder?.pf_number || ""}`);
    setSendDocsMessage("");
    setSendDocsNotify(true);
    setShowSendDocsDialog(true);
  };

  // 2026-05-27 (Rok): one-click "we received your documents" acknowledgment.
  // Different from openSendDocsDialog — this attaches NOTHING and just tells
  // the customer their docs are now in the system, so they stop chasing.
  const [docReceiptAckBusy, setDocReceiptAckBusy] = useState(false);
  const sendDocReceiptAck = async (force = false) => {
    if (!folder?.id) return;
    if (!folder?.notification_emails?.length && !folder?.customer_email) {
      toast.error("This PF has no customer or notification emails — add one before notifying.");
      return;
    }
    setDocReceiptAckBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/position-folders/${folder.id}/documents/notify-customer-received`,
        { force },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`✓ Acknowledgment sent to ${res.data.to}${res.data.cc?.length ? ` (+${res.data.cc.length} cc)` : ""}`);
      if (typeof onUpdate === "function") onUpdate();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || "Failed to send acknowledgment";
      if (status === 409) {
        // Already sent in the last 6h — offer to re-send
        if (window.confirm(`${detail}\n\nSend again anyway?`)) {
          return sendDocReceiptAck(true);
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setDocReceiptAckBusy(false);
    }
  };

  const sendDocsToCustomer = async () => {
    if (selectedDocIds.length === 0) {
      toast.error("Select at least one document first");
      return;
    }
    setSendDocsBusy(true);
    try {
      const recipients = sendDocsRecipients
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter((e) => e.includes("@"));
      const res = await axios.post(
        `${API_URL}/position-folders/${folder.id}/documents/send-to-customer`,
        {
          doc_ids:    selectedDocIds,
          recipients: recipients.length ? recipients : undefined,
          subject:    sendDocsSubject || undefined,
          message:    sendDocsMessage || undefined,
          notify:     sendDocsNotify,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `Sent ${res.data.attachments?.length || 0} document(s) to ${res.data.to}`
      );
      setShowSendDocsDialog(false);
      setSelectedDocIds([]);
      if (typeof onUpdate === "function") onUpdate();
    } catch (e) {
      console.error("Send docs failed:", e);
      toast.error(e.response?.data?.detail || "Failed to send documents");
    } finally {
      setSendDocsBusy(false);
    }
  };

  // ── Customs forms: open dialog, prefill, send ────────────────────────
  const openCustomsFormsDialog = async () => {
    setCfStep("select");
    setCfSelectedSlugs([]);
    setCfForms([]);
    setCfMessage("");
    setCfAttachPdfs(true);
    // Default source docs = commercial-invoice-like uploads, else any selected
    const invoiceDocs = documents
      .filter((d) => ["CI", "Invoice", "invoice", "PI", "PL", "PackingList", "packing_list", "proforma_invoice"].includes(d.tag))
      .map((d) => d.id);
    setCfSourceDocIds(invoiceDocs.length ? invoiceDocs : [...selectedDocIds]);
    const notify = (folder?.notification_emails || []).filter(Boolean).join(", ");
    setCfRecipients(notify || folder?.customer_email || "");
    setShowCustomsFormsDialog(true);
    try {
      const res = await axios.get(`${API_URL.replace(/\/$/, "")}/customs-fillable/forms`);
      setCfAllForms(res.data?.forms || []);
    } catch {
      setCfAllForms([]);
    }
  };

  const toggleCfSlug = (slug) =>
    setCfSelectedSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  const toggleCfDoc = (id) =>
    setCfSourceDocIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const prepareCustomsForms = async () => {
    if (cfSelectedSlugs.length === 0) {
      toast.error("Pick at least one form to send");
      return;
    }
    setCfBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/position-folders/${folder.id}/customs-forms/prefill`,
        { slugs: cfSelectedSlugs, doc_ids: cfSourceDocIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCfForms(res.data?.forms || []);
      if (res.data?.recipients?.length && !cfRecipients) {
        setCfRecipients(res.data.recipients.join(", "));
      }
      const filled = (res.data?.forms || []).reduce(
        (n, f) => n + f.fields.filter((x) => x.value).length, 0);
      toast.success(`Auto-filled ${filled} field(s) from ${res.data?.parsed_docs || 0} document(s)`);
      setCfStep("review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not prepare the forms");
    } finally {
      setCfBusy(false);
    }
  };

  const setCfFieldValue = (slug, fieldId, value) =>
    setCfForms((prev) =>
      prev.map((f) =>
        f.slug !== slug
          ? f
          : { ...f, fields: f.fields.map((x) => (x.id === fieldId ? { ...x, value } : x)) }
      )
    );

  const sendCustomsForms = async () => {
    setCfBusy(true);
    try {
      const prefill = {};
      cfForms.forEach((f) => {
        prefill[f.slug] = {};
        f.fields.forEach((x) => {
          if (x.value) prefill[f.slug][x.id] = x.value;
        });
      });
      const recipients = cfRecipients
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter((e) => e.includes("@"));
      const res = await axios.post(
        `${API_URL}/position-folders/${folder.id}/customs-forms/send`,
        {
          slugs: cfForms.map((f) => f.slug),
          prefill,
          recipients: recipients.length ? recipients : undefined,
          message: cfMessage || undefined,
          attach_pdfs: cfAttachPdfs,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Sent ${res.data.forms?.length || 0} form(s) to ${res.data.to}`);
      setShowCustomsFormsDialog(false);
      if (typeof onUpdate === "function") onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send customs forms");
    } finally {
      setCfBusy(false);
    }
  };

  // ── HS classification (Roket bot) ────────────────────────────────────
  const openClassifyDialog = () => {
    const invoiceDocs = documents
      .filter((d) => ["CI", "Invoice", "invoice", "PI", "proforma_invoice"].includes(d.tag))
      .map((d) => d.id);
    setClfDocIds(invoiceDocs.length ? invoiceDocs : [...selectedDocIds]);
    setClfOrigin("");
    setClfImportInto("EU");
    setClfCargo("");
    setClfHs("");
    setShowClassifyDialog(true);
  };

  const submitClassification = async () => {
    if (!clfCargo.trim() && clfDocIds.length === 0) {
      toast.error("Select an invoice to parse, or type the cargo name");
      return;
    }
    setClfBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/position-folders/${folder.id}/customs-forms/classify`,
        {
          doc_ids: clfDocIds,
          origin: clfOrigin || undefined,
          import_into: clfImportInto || "EU",
          cargo: clfCargo || undefined,
          hs_code: clfHs || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Classification request sent to Roket for "${res.data.cargo?.slice(0, 40)}"`);
      setShowClassifyDialog(false);
      if (typeof onUpdate === "function") onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send classification request");
    } finally {
      setClfBusy(false);
    }
  };


  const DOC_TAGS = {
    // Transport Documents - Sea
    BL: { label: "Bill of Lading (BL)", color: "bg-blue-100 text-blue-800", icon: "FileText", group: "transport" },
    bl: { label: "Bill of Lading (BL)", color: "bg-blue-100 text-blue-800", icon: "FileText", group: "transport" },
    OBL: { label: "Original Bill of Lading (OBL)", color: "bg-blue-100 text-blue-800", icon: "FileText", group: "transport" },
    SWB: { label: "Sea Waybill (SWB)", color: "bg-blue-100 text-blue-800", icon: "FileText", group: "transport" },
    sea_waybill: { label: "Sea Waybill (SWB)", color: "bg-blue-100 text-blue-800", icon: "FileText", group: "transport" },
    TelexRelease: { label: "Telex Release BL", color: "bg-blue-100 text-blue-800", icon: "FileText", group: "transport" },
    booking_confirmation: { label: "Booking Confirmation", color: "bg-blue-50 text-blue-700", icon: "FileText", group: "transport" },
    // Transport Documents - Air
    AWB: { label: "Air Waybill (AWB)", color: "bg-sky-100 text-sky-800", icon: "Plane", group: "transport" },
    awb: { label: "Air Waybill (AWB)", color: "bg-sky-100 text-sky-800", icon: "Plane", group: "transport" },
    MAWB: { label: "Master Air Waybill (MAWB)", color: "bg-sky-100 text-sky-800", icon: "Plane", group: "transport" },
    HAWB: { label: "House Air Waybill (HAWB)", color: "bg-sky-100 text-sky-800", icon: "Plane", group: "transport" },
    // Transport Documents - Road/Rail
    CMR: { label: "CMR (Road Transport)", color: "bg-orange-100 text-orange-800", icon: "Truck", group: "transport" },
    cmr: { label: "CMR (Road Transport)", color: "bg-orange-100 text-orange-800", icon: "Truck", group: "transport" },
    RailWaybill: { label: "Rail Waybill (CIM/SMGS)", color: "bg-orange-100 text-orange-800", icon: "Train", group: "transport" },
    // Commercial Documents
    CI: { label: "Commercial Invoice (CI)", color: "bg-green-100 text-green-800", icon: "Receipt", group: "commercial" },
    Invoice: { label: "Invoice", color: "bg-green-100 text-green-800", icon: "Receipt", group: "commercial" },
    invoice: { label: "Commercial Invoice", color: "bg-green-100 text-green-800", icon: "Receipt", group: "commercial" },
    proforma_invoice: { label: "Proforma Invoice", color: "bg-green-50 text-green-700", icon: "Receipt", group: "commercial" },
    PL: { label: "Packing List (PL)", color: "bg-amber-100 text-amber-800", icon: "Package", group: "commercial" },
    PackingList: { label: "Packing List", color: "bg-amber-100 text-amber-800", icon: "Package", group: "commercial" },
    packing_list: { label: "Packing List", color: "bg-amber-100 text-amber-800", icon: "Package", group: "commercial" },
    PI: { label: "Proforma Invoice (PI)", color: "bg-green-50 text-green-700", icon: "FileText", group: "commercial" },
    purchase_order: { label: "Purchase Order", color: "bg-green-50 text-green-700", icon: "FileText", group: "commercial" },
    sales_contract: { label: "Sales Contract", color: "bg-green-50 text-green-700", icon: "FileText", group: "commercial" },
    // Customs & Regulatory Documents
    CO: { label: "Certificate of Origin (CO/COO)", color: "bg-purple-100 text-purple-800", icon: "Award", group: "customs" },
    certificate_of_origin: { label: "Certificate of Origin", color: "bg-purple-100 text-purple-800", icon: "Award", group: "customs" },
    EX1: { label: "Export Declaration (EX/EX1)", color: "bg-purple-100 text-purple-800", icon: "FileOutput", group: "customs" },
    SAD: { label: "Single Admin Document (SAD)", color: "bg-purple-100 text-purple-800", icon: "FileText", group: "customs" },
    EUR1: { label: "EUR.1 Movement Certificate", color: "bg-purple-100 text-purple-800", icon: "Globe", group: "customs" },
    eur1: { label: "EUR.1 Movement Certificate", color: "bg-purple-100 text-purple-800", icon: "Globe", group: "customs" },
    atr: { label: "ATR Certificate", color: "bg-purple-100 text-purple-800", icon: "Globe", group: "customs" },
    customs_declaration: { label: "Customs Declaration / MRN", color: "bg-purple-100 text-purple-800", icon: "Shield", group: "customs" },
    MRN: { label: "Customs Declaration / MRN", color: "bg-purple-100 text-purple-800", icon: "Shield", group: "customs" },
    T1: { label: "T1 / Transit Document", color: "bg-purple-100 text-purple-800", icon: "FileText", group: "customs" },
    transit_document: { label: "T1 / Transit Document", color: "bg-purple-100 text-purple-800", icon: "FileText", group: "customs" },
    CustomsDoc: { label: "Customs Document", color: "bg-purple-100 text-purple-800", icon: "Shield", group: "customs" },
    phyto_certificate: { label: "Phytosanitary Certificate", color: "bg-purple-100 text-purple-800", icon: "Leaf", group: "customs" },
    veterinary_certificate: { label: "Veterinary Certificate", color: "bg-purple-100 text-purple-800", icon: "Shield", group: "customs" },
    // Port of Koper Documents (Luka Koper terminal dispositions — used
    // daily by Slovenian customs/forwarders to release containers from
    // the terminal. Aliases noted in DOC_TAG_ALIASES so legacy uploads
    // tagged "vhodna" / "izhodna" still map cleanly.)
    DVH: { label: "DVH — Entrance Disposition (Vhodna)", color: "bg-teal-100 text-teal-800", icon: "FileInput", group: "koper" },
    DIZ: { label: "DIZ — Exit Disposition (Izhodna)", color: "bg-teal-100 text-teal-800", icon: "FileOutput", group: "koper" },
    // Financial / Trade Documents
    LC: { label: "Letter of Credit (LC)", color: "bg-emerald-100 text-emerald-800", icon: "CreditCard", group: "financial" },
    Insurance: { label: "Insurance Certificate", color: "bg-emerald-100 text-emerald-800", icon: "Shield", group: "financial" },
    insurance: { label: "Insurance Certificate", color: "bg-emerald-100 text-emerald-800", icon: "Shield", group: "financial" },
    // Delivery & POD
    POD: { label: "Proof of Delivery (POD)", color: "bg-cyan-100 text-cyan-800", icon: "CheckCircle", group: "other" },
    pod: { label: "Proof of Delivery (POD)", color: "bg-cyan-100 text-cyan-800", icon: "CheckCircle", group: "other" },
    Delivery: { label: "Delivery Note / POD", color: "bg-cyan-100 text-cyan-800", icon: "Truck", group: "other" },
    // Other Documents
    MSDS: { label: "MSDS / Safety Data Sheet", color: "bg-red-100 text-red-800", icon: "AlertTriangle", group: "other" },
    DGDeclaration: { label: "Dangerous Goods Declaration", color: "bg-red-100 text-red-800", icon: "AlertTriangle", group: "other" },
    Inspection: { label: "Inspection Certificate", color: "bg-gray-100 text-gray-700", icon: "CheckCircle", group: "other" },
    inspection_report: { label: "Inspection Report", color: "bg-gray-100 text-gray-700", icon: "CheckCircle", group: "other" },
    Weight: { label: "Weight Certificate", color: "bg-gray-100 text-gray-700", icon: "Scale", group: "other" },
    correspondence: { label: "Correspondence", color: "bg-gray-100 text-gray-700", icon: "Mail", group: "other" },
    Other: { label: "Other", color: "bg-gray-100 text-gray-800", icon: "File", group: "other" },
    other: { label: "Other", color: "bg-gray-100 text-gray-800", icon: "File", group: "other" }
  };
  
  // Grouped document types for the selector UI
  const DOC_TAG_GROUPS = {
    transport: { label: "Transport Documents", items: ["BL", "OBL", "SWB", "TelexRelease", "AWB", "MAWB", "HAWB", "CMR", "RailWaybill"] },
    commercial: { label: "Commercial Documents", items: ["CI", "Invoice", "PL", "PackingList", "PI"] },
    customs: { label: "Customs & Regulatory", items: ["CO", "EX1", "SAD", "EUR1", "MRN", "T1", "CustomsDoc"] },
    koper: { label: "Port of Koper", items: ["DVH", "DIZ"] },
    financial: { label: "Financial / Trade", items: ["LC", "Insurance"] },
    other: { label: "Other Documents", items: ["MSDS", "DGDeclaration", "Inspection", "Weight", "Delivery", "Other"] }
  };

  // Free-text aliases the parser might surface — kept centralised so we
  // never have to update three places when a synonym pops up. Resolution
  // is case-insensitive; the resolver lives next to fetchDocuments.
  // eslint-disable-next-line no-unused-vars
  const DOC_TAG_ALIASES = {
    // Customs / MRN
    "mrn": "MRN",
    "customs declaration": "MRN",
    "customs decl": "MRN",
    "t1": "T1",
    "transit": "T1",
    "transit document": "T1",
    // Port of Koper — Slovenian aliases the staff actually type
    "dvh": "DVH",
    "vhodna": "DVH",
    "vhodna dispozicija": "DVH",
    "entrance disposition": "DVH",
    "diz": "DIZ",
    "izhodna": "DIZ",
    "izhodna dispozicija": "DIZ",
    "exit disposition": "DIZ",
  };

  // Fetch documents for PF
  const fetchDocuments = async () => {
    if (!folder?.id) return;
    setLoadingDocuments(true);
    try {
      const response = await axios.get(
        `${API_URL}/position-folders/${folder.id}/documents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Upload document
  const uploadDocument = async (file) => {
    if (!file || !folder?.id) return;
    
    setUploadingDocument(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("tag", selectedDocTag);
    formData.append("description", docDescription);
    
    try {
      await axios.post(
        `${API_URL}/position-folders/${folder.id}/documents`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      toast.success(`Document "${file.name}" uploaded`);
      setDocDescription("");
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploadingDocument(false);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      uploadDocument(files[0]);
    }
  };

  // Update document tag
  const updateDocumentTag = async (docId, newTag) => {
    try {
      await axios.put(
        `${API_URL}/position-folders/${folder.id}/documents/${docId}`,
        { tag: newTag },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchDocuments();
      toast.success("Document tag updated");
    } catch (error) {
      toast.error("Failed to update tag");
    }
  };

  // Delete document
  const deleteDocument = async (docId) => {
    if (!confirm("Delete this document?")) return;
    try {
      await axios.delete(
        `${API_URL}/position-folders/${folder.id}/documents/${docId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchDocuments();
      toast.success("Document deleted");
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  // Load documents when folder changes or section is documents
  useEffect(() => {
    if (folder?.id && activeSection === "documents") {
      fetchDocuments();
    }
  }, [folder?.id, activeSection]);

  // PF activity/communication history (arrival notices, pre-alerts, emails sent)
  const [pfHistory, setPfHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const fetchPfHistory = useCallback(async () => {
    if (!folder?.id) return;
    setLoadingHistory(true);
    try {
      const res = await axios.get(
        `${API_URL}/position-folders/${folder.id}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPfHistory(res.data?.history || []);
    } catch (e) {
      setPfHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [folder?.id, token]);

  useEffect(() => {
    if (folder?.id && activeSection === "history") {
      fetchPfHistory();
    }
  }, [folder?.id, activeSection, fetchPfHistory]);

  // 2026-05-16 — per-PF CO2 footprint (desktop modal parity with mobile)
  const [co2, setCo2] = useState(null);
  useEffect(() => {
    if (!folder?.id) { setCo2(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API_URL}/position-folders/${folder.id}/co2`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setCo2(r.data);
      } catch {
        if (!cancelled) setCo2(null);
      }
    })();
    return () => { cancelled = true; };
  }, [folder?.id, token]);

  const INSPECTION_TYPES = {
    customs_physical: {
      label: "Customs Physical Check",
      description: "Physical inspection by customs authorities",
      icon: "Shield",
      color: "#dc2626",
      requires_officer: true
    },
    cargo_damage: {
      label: "Cargo Damage Inspection",
      description: "Inspection for cargo damage claims",
      icon: "AlertTriangle",
      color: "#ea580c",
      requires_officer: false
    },
    veterinary: {
      label: "Veterinary Inspection",
      description: "Inspection for animal products and by-products",
      icon: "Stethoscope",
      color: "#16a34a",
      requires_officer: true
    },
    phytosanitary: {
      label: "Phytosanitary Inspection",
      description: "Inspection for plant products and plant health",
      icon: "Leaf",
      color: "#65a30d",
      requires_officer: true
    }
  };

  useEffect(() => {
    // Load staff list for assignment dropdown
    const loadStaff = async () => {
      try {
        const response = await axios.get(`${API_URL}/users/staff`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStaffList(Array.isArray(response.data) ? response.data : (response.data.users || []));
      } catch (error) {
        // Fallback: just allow manual email entry
      }
    };
    if (showInspectionModal) loadStaff();
  }, [showInspectionModal]);

  // Fetch inspections for selected PF
  const fetchPfInspections = async (pfId) => {
    try {
      const response = await axios.get(`${API_URL}/pf/${pfId}/inspections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPfInspections(response.data.inspections || []);
    } catch (error) {
      // Inspections may not exist yet
      setPfInspections([]);
    }
  };

  const scheduleInspection = async () => {
    if (!inspectionData.scheduled_date || !inspectionData.scheduled_time) {
      toast.error("Please select date and time");
      return;
    }
    if (!inspectionData.assigned_to) {
      toast.error("Please assign to a staff member");
      return;
    }

    setSchedulingInspection(true);
    try {
      const scheduledDateTime = `${inspectionData.scheduled_date}T${inspectionData.scheduled_time}:00`;
      const inspectionConfig = INSPECTION_TYPES[inspectionData.inspection_type];
      
      await axios.post(
        `${API_URL}/pf/${folder.id}/schedule-inspection`,
        {
          inspection_type: inspectionData.inspection_type,
          scheduled_date: scheduledDateTime,
          officer_name: inspectionData.officer_name || null,
          officer_phone: inspectionData.officer_phone || null,
          officer_email: inspectionData.officer_email || null,
          reference_number: inspectionData.reference_number || null,
          assigned_to: inspectionData.assigned_to,
          assigned_to_name: inspectionData.assigned_to_name || inspectionData.assigned_to,
          container_number: inspectionData.container_number || null,
          commodity: inspectionData.commodity || null,
          location: inspectionData.location,
          notes: inspectionData.notes || null,
          set_reminder: inspectionData.set_reminder,
          reminder_minutes_before: inspectionData.reminder_minutes_before
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${inspectionConfig.label} scheduled! Email sent to ${inspectionData.assigned_to}`);
      setShowInspectionModal(false);
      setInspectionData({
        inspection_type: "customs_physical",
        scheduled_date: "",
        scheduled_time: "",
        officer_name: "",
        officer_phone: "",
        officer_email: "",
        reference_number: "",
        assigned_to: "",
        assigned_to_name: "",
        container_number: "",
        commodity: "",
        location: "Port of Koper",
        notes: "",
        set_reminder: true,
        reminder_minutes_before: 30
      });
      // Refresh inspections list
      if (folder?.id) fetchPfInspections(folder.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule inspection");
    } finally {
      setSchedulingInspection(false);
    }
  };

  const addEmail = () => {
    if (newEmail && newEmail.includes("@") && !editData.notification_emails.includes(newEmail)) {
      setEditData({
        ...editData,
        notification_emails: [...editData.notification_emails, newEmail]
      });
      setNewEmail("");
    }
  };

  const removeEmail = (email) => {
    setEditData({
      ...editData,
      notification_emails: editData.notification_emails.filter(e => e !== email)
    });
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/position-folders/${folder.id}`,
        editData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      toast.success("Position folder updated");
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error("Please enter subject and body");
      return;
    }
    const toList = emailTo.split(",").map((e) => e.trim()).filter(Boolean);
    if (toList.length === 0) {
      toast.error("Please enter at least one recipient in 'To'");
      return;
    }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("subject", emailSubject);
      formData.append("body", emailBody);
      formData.append("to_emails", JSON.stringify(toList));
      formData.append("cc_emails", JSON.stringify(emailCc.split(",").map((e) => e.trim()).filter(Boolean)));
      
      await axios.post(
        `${API_URL}/position-folders/${folder.id}/send-email`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email sent!");
      setEmailSubject("");
      setEmailBody("");
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  // Customer view: reply to Europacific — routes to the assigned operator.
  const sendCustomerMessage = async () => {
    if (!emailBody.trim()) {
      toast.error("Please enter a message");
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/customer-portal/shipment/${folder.id}/contact-operator`,
        { subject: emailSubject, message: emailBody },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Message sent to your Europacific contact");
      setEmailSubject("");
      setEmailBody("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Compact Design */}
        <div className="p-4 border-b bg-slate-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <div className="flex items-center gap-2">
                  {editingPfNumber ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newPfNumber}
                        onChange={(e) => setNewPfNumber(e.target.value.toUpperCase())}
                        className="font-mono font-bold text-xl border rounded px-2 py-1 w-48"
                        placeholder="PF-XXXX-XX"
                        autoFocus
                        data-testid="edit-pf-number-input"
                      />
                      <button
                        onClick={handleUpdatePfNumber}
                        disabled={savingPfNumber}
                        className="p-1 rounded bg-green-100 text-green-700 hover:bg-green-200"
                        title="Save PF Number"
                        data-testid="save-pf-number-btn"
                      >
                        {savingPfNumber ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => { setEditingPfNumber(false); setNewPfNumber(folder.pf_number); }}
                        className="p-1 rounded bg-red-100 text-red-700 hover:bg-red-200"
                        title="Cancel"
                        data-testid="cancel-pf-number-btn"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="font-mono font-bold text-xl">{folder.pf_number}</span>
                      {!isCustomerView && (
                      <button
                        onClick={() => { setEditingPfNumber(true); setNewPfNumber(folder.pf_number); }}
                        className="p-1 rounded hover:bg-slate-200 text-slate-500"
                        title="Edit PF Number"
                        data-testid="edit-pf-number-btn"
                      >
                        <Edit className="h-3 w-3" />
                      </button>
                      )}
                    </>
                  )}
                  <span className={`px-2 py-0.5 text-xs rounded ${STATUS_COLORS[folder.status]}`}>
                    {STATUS_LABELS[folder.status] || folder.status}
                  </span>
                  {/* HBL Number - show prominently for sub-orders */}
                  {folder.hbl_number && (
                    <span className="px-2 py-1 text-sm font-mono bg-blue-100 text-blue-800 rounded border border-blue-200">
                      HBL: {folder.hbl_number}
                    </span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground flex items-center gap-4">
                  <span>{folder.customer || folder.customer_name}</span>
                  {/* Show weight/volume in header for quick reference */}
                  {(folder.gross_weight_kg || folder.volume_cbm) && (
                    <span className="text-xs text-slate-500">
                      {folder.gross_weight_kg && `${folder.gross_weight_kg} kg`}
                      {folder.gross_weight_kg && folder.volume_cbm && " | "}
                      {folder.volume_cbm && `${folder.volume_cbm} CBM`}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Compact Action Buttons */}
            <div className="flex items-center gap-2">
              {/* Primary Actions - Icons Only — hidden for customer (read-only) */}
              {!isCustomerView && (
              <div className="flex items-center bg-white border rounded-lg p-1 gap-1">
                <button
                  onClick={openTOModal}
                  disabled={creatingTO}
                  className="p-2 rounded hover:bg-orange-50 text-orange-600 disabled:opacity-50"
                  title="Create Transport Order"
                  data-testid="create-to-from-pf-btn"
                >
                  <Truck className="h-4 w-4" />
                </button>
                <button
                  onClick={openSpotModal}
                  className="p-2 rounded hover:bg-emerald-50 text-emerald-600 disabled:opacity-50"
                  title="Spot Rate Request"
                  data-testid="spot-rate-request-btn"
                >
                  <Send className="h-4 w-4" />
                </button>
                <button
                  onClick={createBillOfLading}
                  disabled={creatingBL}
                  className="p-2 rounded hover:bg-blue-50 text-blue-600 disabled:opacity-50"
                  title="Create Bill of Lading"
                  data-testid="create-bl-from-pf-btn"
                >
                  <FileText className="h-4 w-4" />
                </button>
                <button
                  onClick={openBLRequestModal}
                  disabled={requestingBLInstructions}
                  className="p-2 rounded hover:bg-green-50 text-green-600 disabled:opacity-50"
                  title="Send BL Instructions request to customer (email + secure link)"
                  data-testid="request-bl-instructions-btn"
                >
                  <Send className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    setInspectionData((prev) => ({
                      ...prev,
                      container_number: prev.container_number || (folder.containers || [])[0] || folder.container_number || "",
                      commodity: prev.commodity || folder.commodity || folder.cargo_description || "",
                    }));
                    setShowInspectionModal(true);
                  }}
                  className="p-2 rounded hover:bg-red-50 text-red-600"
                  title="Schedule Inspection"
                  data-testid="schedule-inspection-btn"
                >
                  <ClipboardCheck className="h-4 w-4" />
                </button>
                {/* Send VGM Declaration */}
                <button
                  onClick={async () => {
                    const recipient = window.prompt(
                      "Send VGM Declaration form to (email):",
                      folder.customer_email || ""
                    );
                    if (recipient === null) return;
                    try {
                      const r = await axios.post(
                        `${API_URL}/position-folders/${folder.id}/vgm/send`,
                        { recipient_email: recipient.trim() || null },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      toast.success(`VGM link sent to ${r.data?.public_url ? recipient : "customer"}`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Failed to send VGM link");
                    }
                  }}
                  className="p-2 rounded hover:bg-cyan-50 text-cyan-700"
                  title="Send VGM Declaration form to customer"
                  data-testid="send-vgm-btn"
                >
                  <Weight className="h-4 w-4" />
                </button>
                {/* Arrival Notice Dropdown */}
                <div className="relative group">
                  <button
                    className="p-2 rounded hover:bg-amber-50 text-amber-600"
                    title="Send Arrival Notice"
                    data-testid="arrival-notice-btn"
                  >
                    <Bell className="h-4 w-4" />
                  </button>
                  <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-1 min-w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <button
                      onClick={() => {
                        setArrivalNoticeData({ ...arrivalNoticeData, notice_type: "simple" });
                        setShowArrivalNoticeModal(true);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                      data-testid="simple-arrival-notice-btn"
                    >
                      <Mail className="h-4 w-4 text-slate-500" />
                      Simple Arrival Notice
                    </button>
                    <button
                      onClick={() => {
                        setArrivalNoticeData({ ...arrivalNoticeData, notice_type: "rated" });
                        setShowArrivalNoticeModal(true);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                      data-testid="rated-arrival-notice-btn"
                    >
                      <Mail className="h-4 w-4 text-green-500" />
                      Rated Arrival Notice
                      <span className="text-xs text-green-600 ml-auto">+ Charges</span>
                    </button>
                  </div>
                </div>
                {/* Push to CFS Warehouse */}
                {!folder.cfs_order_id && (
                  <button
                    onClick={pushToCFS}
                    disabled={pushingToCFS}
                    className="p-2 rounded hover:bg-indigo-50 text-indigo-600 disabled:opacity-50"
                    title="Push to myCargoCFS Warehouse"
                    data-testid="push-to-cfs-btn"
                  >
                    {pushingToCFS ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Package className="h-4 w-4" />
                    )}
                  </button>
                )}
                {folder.cfs_order_id && (
                  <div
                    className="flex items-center gap-1 px-2 py-1 rounded bg-green-50 text-green-700 text-xs font-medium"
                    title={`Pushed to CFS: ${folder.cfs_order_number || 'Order'}`}
                    data-testid="cfs-pushed-badge"
                  >
                    <CheckCircle className="h-3 w-3" />
                    <span>CFS: {folder.cfs_order_number || 'Sent'}</span>
                  </div>
                )}
                {/* Survey sent indicator - surveys are now sent automatically */}
                {folder.satisfaction_survey_sent && (
                  <div className="p-2 text-green-600" title="Survey sent automatically">
                    <Star className="h-4 w-4 fill-green-600" />
                  </div>
                )}
                {activeSection === "details" && !isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-2 rounded hover:bg-blue-100 text-blue-600 border border-blue-200"
                    title="Edit Details"
                    data-testid="edit-pf-btn"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                )}
                {/* Delete PF - Visible to all staff */}
                {onDelete && (
                  <button
                    onClick={() => onDelete(folder)}
                    className={`p-2 rounded border ${isAdmin 
                      ? 'hover:bg-red-100 text-red-600 border-red-200' 
                      : 'hover:bg-orange-100 text-orange-600 border-orange-200'}`}
                    title={isAdmin ? "Delete PF (Admin)" : "Request Deletion"}
                    data-testid="delete-pf-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              )}
              
              {/* Close Button - Always Visible */}
              <button 
                onClick={onClose}
                className="p-2 rounded-full hover:bg-slate-200 text-slate-500"
                title="Close"
                data-testid="close-modal-btn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {/* Service Scope Checkboxes - Operations Workflow — hidden for customer */}
          {!isCustomerView && (
          <div className="mt-3 flex flex-wrap gap-3 text-sm">
            {getWorkflowSteps().map((step, idx) => (
              <label key={step.id} className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isStepCompleted(step.id)}
                  onChange={(e) => updateWorkflowStep(step.id, e.target.checked)}
                  className={`w-4 h-4 rounded border-slate-300 focus:ring-blue-500 ${
                    step.id === 'invoiced' ? 'text-green-600' : 
                    step.id.includes('delivered') || step.id.includes('pod') ? 'text-purple-600' :
                    'text-blue-600'
                  }`}
                />
                <span className={`${isStepCompleted(step.id) ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
                  {step.label}
                </span>
              </label>
            ))}
          </div>
          )}
        </div>

        {/* BL Instructions Send & Track Modal */}
        {showBLRequestModal && (
          <BLInstructionsRequestModal
            folder={folder}
            onClose={() => setShowBLRequestModal(false)}
            onSent={() => onUpdate()}
          />
        )}

        {/* Schedule Inspection Modal - All Types */}
        {showInspectionModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b" style={{ backgroundColor: INSPECTION_TYPES[inspectionData.inspection_type]?.color + '10' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Schedule Inspection</h3>
                    <p className="text-sm text-slate-500">{folder.pf_number}</p>
                  </div>
                  <button onClick={() => setShowInspectionModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Inspection Type Selector */}
                <div>
                  <Label className="mb-2 block">Inspection Type *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(INSPECTION_TYPES).map(([key, config]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setInspectionData({...inspectionData, inspection_type: key})}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          inspectionData.inspection_type === key 
                            ? 'border-current bg-opacity-10' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        style={{ 
                          borderColor: inspectionData.inspection_type === key ? config.color : undefined,
                          backgroundColor: inspectionData.inspection_type === key ? config.color + '10' : undefined
                        }}
                      >
                        <div className="font-medium" style={{ color: config.color }}>{config.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{config.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Container + Commodity (editable / selectable) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Container</Label>
                    {(folder.containers || []).length > 0 ? (
                      <select
                        value={inspectionData.container_number}
                        onChange={(e) => setInspectionData({ ...inspectionData, container_number: e.target.value })}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500"
                        data-testid="inspection-container-select"
                      >
                        <option value="">— Select container —</option>
                        {(folder.containers || []).map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        value={inspectionData.container_number}
                        onChange={(e) => setInspectionData({ ...inspectionData, container_number: e.target.value })}
                        placeholder="Container number"
                        className="font-mono"
                        data-testid="inspection-container-input"
                      />
                    )}
                  </div>
                  <div>
                    <Label>Commodity</Label>
                    <Input
                      value={inspectionData.commodity}
                      onChange={(e) => setInspectionData({ ...inspectionData, commodity: e.target.value })}
                      placeholder="e.g. Cosmetics, Machinery"
                      data-testid="inspection-commodity-input"
                    />
                  </div>
                </div>

                {/* Pre-filled Info (read-only) */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                  {inspectionData.inspection_type === "customs_physical" && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">MRN:</span>
                      <span className="font-mono">{folder.mrn || "-"}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-500">Importer:</span>
                    <span>{folder.importer || folder.consignee || folder.customer || "-"}</span>
                  </div>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={inspectionData.scheduled_date}
                      onChange={(e) => setInspectionData({...inspectionData, scheduled_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Time *</Label>
                    <Input
                      type="time"
                      value={inspectionData.scheduled_time}
                      onChange={(e) => setInspectionData({...inspectionData, scheduled_time: e.target.value})}
                    />
                  </div>
                </div>

                {/* Location */}
                <div>
                  <Label>Location</Label>
                  <Input
                    value={inspectionData.location}
                    onChange={(e) => setInspectionData({...inspectionData, location: e.target.value})}
                    placeholder="Port of Koper"
                  />
                </div>

                {/* Officer Info - conditional based on type */}
                {INSPECTION_TYPES[inspectionData.inspection_type]?.requires_officer && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900">
                      {inspectionData.inspection_type === "customs_physical" ? "Customs Officer" : 
                       inspectionData.inspection_type === "veterinary" ? "Veterinary Inspector" : 
                       inspectionData.inspection_type === "phytosanitary" ? "Phytosanitary Inspector" : "Officer"} Details
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Officer Name</Label>
                        <Input
                          value={inspectionData.officer_name}
                          onChange={(e) => setInspectionData({...inspectionData, officer_name: e.target.value})}
                          placeholder="e.g., Sara Argenti"
                        />
                      </div>
                      <div>
                        <Label>Officer Phone</Label>
                        <Input
                          value={inspectionData.officer_phone}
                          onChange={(e) => setInspectionData({...inspectionData, officer_phone: e.target.value})}
                          placeholder="e.g., 05 297 60 12"
                        />
                      </div>
                    </div>
                    {(inspectionData.inspection_type === "veterinary" || inspectionData.inspection_type === "phytosanitary") && (
                      <div>
                        <Label>Reference Number (UVHVVR / FURS)</Label>
                        <Input
                          value={inspectionData.reference_number}
                          onChange={(e) => setInspectionData({...inspectionData, reference_number: e.target.value})}
                          placeholder="External reference number"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Assign To */}
                <div>
                  <Label>Assign To (Staff) *</Label>
                  <select
                    value={inspectionData.assigned_to}
                    onChange={(e) => {
                      const sel = staffList.find((s) => s.email === e.target.value);
                      setInspectionData({
                        ...inspectionData,
                        assigned_to: e.target.value,
                        assigned_to_name: sel ? (sel.full_name || sel.email) : "",
                      });
                    }}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    data-testid="inspection-assign-select"
                  >
                    <option value="">— Select staff member —</option>
                    {staffList.map((staff) => (
                      <option key={staff.email} value={staff.email}>
                        {staff.full_name || staff.email}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={inspectionData.notes}
                    onChange={(e) => setInspectionData({...inspectionData, notes: e.target.value})}
                    placeholder="Any special instructions, documents to bring, etc..."
                    rows={3}
                  />
                </div>

                {/* Reminder */}
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-medium text-blue-900">Set Reminder</p>
                    <p className="text-sm text-blue-700">Email notification before inspection</p>
                  </div>
                  <select
                    value={inspectionData.reminder_minutes_before}
                    onChange={(e) => setInspectionData({...inspectionData, reminder_minutes_before: parseInt(e.target.value)})}
                    className="border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value={15}>15 min before</option>
                    <option value={30}>30 min before</option>
                    <option value={60}>1 hour before</option>
                    <option value={120}>2 hours before</option>
                  </select>
                </div>
              </div>

              <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowInspectionModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={scheduleInspection}
                  disabled={schedulingInspection}
                  style={{ backgroundColor: INSPECTION_TYPES[inspectionData.inspection_type]?.color }}
                  className="hover:opacity-90"
                >
                  {schedulingInspection ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Scheduling...
                    </>
                  ) : (
                    <>
                      <Calendar className="h-4 w-4 mr-2" />
                      Schedule {INSPECTION_TYPES[inspectionData.inspection_type]?.label?.split(' ')[0]}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b px-4 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300" style={{ scrollbarWidth: 'thin' }}>
          {[
            { id: "details", label: "Details", icon: FileText },
            { id: "documents", label: "Documents", icon: FolderOpen },
            { id: "tasks", label: "Tasks", icon: ListTodo },
            { id: "tracking", label: "Tracking", icon: Radar },
            { id: "milestones", label: "Milestones", icon: Target },
            { id: "customs", label: "Customs", icon: ShieldCheck },
            { id: "cargo", label: "Cargo", icon: Package },
            ...(folder.is_master && folder.direction === "export" && (folder.service_type === "LCL" || folder.lcl_container_type)
              ? [{ id: "stuffing", label: "Stuffing", icon: Boxes }]
              : []),
            ...(folder.is_master || folder.sub_order_count > 0 || subOrders.length > 0 
              ? [{ id: "suborders", label: `Sub-Orders (${subOrders.length || folder.sub_order_count || 0})`, icon: GitBranch }] 
              : []),
            { id: "notifications", label: "Notify", icon: Mail },
            { id: "email", label: "Email", icon: Send },
            { id: "history", label: "History", icon: Clock },
          ].filter((tab) => !isCustomerView || CUSTOMER_ALLOWED_TABS.includes(tab.id)).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeSection === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeSection === "details" && (
            <div className="space-y-6">
              {isEditing ? (
                <>
                  {/* Shipper & Consignee - Show for sub-orders */}
                  {(folder.is_sub_order || folder.parent_pf_id || folder.master_pf_id) && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium">Shipper (HBL) <span className="text-red-500">*</span></Label>
                        <Input
                          value={editData.shipper || ""}
                          onChange={(e) => setEditData({...editData, shipper: e.target.value})}
                          placeholder="Actual shipper"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Consignee (HBL) <span className="text-red-500">*</span></Label>
                        <Input
                          value={editData.consignee || ""}
                          onChange={(e) => setEditData({...editData, consignee: e.target.value})}
                          placeholder="Actual consignee"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Customer Name</Label>
                      <Input
                        value={editData.customer}
                        onChange={(e) => setEditData({...editData, customer: e.target.value})}
                        data-testid="edit-customer-name-input"
                      />
                    </div>
                    <div>
                      <Label>Customer Email</Label>
                      <Input
                        type="email"
                        value={editData.customer_email}
                        onChange={(e) => setEditData({...editData, customer_email: e.target.value})}
                        placeholder="ops@customer.com"
                        data-testid="edit-customer-email-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Used for customer status emails. Pick a Pantheon company below to auto-fill.
                      </p>
                    </div>
                  </div>

                  {/* Opened-by + Operations owner (with reassign + audit log) */}
                  <PFAssignmentBlock folder={folder} onUpdate={onUpdate} />
                  
                  {/* Link to Pantheon Company/Group */}
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                    <Label className="text-xs text-slate-500 flex items-center gap-2 mb-2">
                      <Building className="h-3.5 w-3.5" />
                      Link to Pantheon Company / Group
                    </Label>
                    <PantheonCompanySelector
                      value={editData.linkedCompany || (folder.crm_company_id ? { id: folder.crm_company_id, name: folder.crm_company_name || folder.customer, type: 'company' } : folder.crm_group_id ? { id: folder.crm_group_id, name: folder.crm_group_name || '', type: 'group' } : null)}
                      onChange={(company) => {
                        setEditData(prev => ({ 
                          ...prev, 
                          linkedCompany: company,
                          crm_company_id: company?.type !== 'group' ? company?.id : null,
                          crm_group_id: company?.type === 'group' ? company?.id : null,
                          customer: company?.name || prev.customer,
                          customer_email: company?.email || prev.customer_email
                        }));
                      }}
                      placeholder="Search Pantheon companies or groups..."
                      required={false}
                      showGroupOption={true}
                    />
                    <p className="text-xs text-emerald-600 mt-2">
                      Link this PF to a Pantheon company or group for better organization
                    </p>
                  </div>
                  
                  <div>
                    <Label>Description</Label>
                    <textarea
                      className="w-full h-32 p-3 border rounded-lg"
                      value={editData.description}
                      onChange={(e) => setEditData({...editData, description: e.target.value})}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsEditing(false)} data-testid="cancel-edit-btn">Cancel</Button>
                    <Button onClick={saveChanges} disabled={saving} data-testid="save-changes-btn">
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-muted-foreground">Customer</Label>
                      <div className="font-medium">{folder.customer || folder.customer_name}</div>
                      {folder.customer_email && (
                        <div className="text-sm text-muted-foreground">{folder.customer_email}</div>
                      )}
                      {/* Show linked company/group badge */}
                      {(folder.crm_company_id || folder.crm_group_id) && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs flex items-center gap-1">
                            {folder.crm_group_id ? <Users className="h-3 w-3" /> : <Building className="h-3 w-3" />}
                            Linked to {folder.crm_group_id ? 'Group' : 'Pantheon'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-muted-foreground">Document Date</Label>
                      <div className="font-medium">{folder.document_date}</div>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-muted-foreground">Charged Amount</Label>
                      <div className="font-medium">€{folder.charged_amount?.toLocaleString() || 0}</div>
                    </div>
                  </div>

                  {/* Shipment basics: POL/POD + references */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      ["POL (Origin)", folder.origin_port || folder.pol],
                      ["POD (Destination)", folder.destination_port || folder.pod],
                      ["Booking No.", folder.booking_number],
                      ["MBL No.", folder.mbl_number],
                      ["Vessel / Voyage", [folder.vessel_name, folder.voyage].filter(Boolean).join(" / ")],
                      ["ETD", folder.etd ? new Date(folder.etd).toLocaleDateString() : ""],
                      ["ETA", folder.eta ? new Date(folder.eta).toLocaleDateString() : ""],
                      ["Carrier", folder.shipping_line || folder.carrier],
                    ].map(([label, val], i) => (
                      <div key={i} className="p-3 bg-slate-50 rounded-lg" data-testid={`pf-basic-${i}`}>
                        <Label className="text-xs text-muted-foreground">{label}</Label>
                        <div className="font-medium text-sm">{val || "—"}</div>
                      </div>
                    ))}
                  </div>

                  {/* Change of Destination tracker */}
                  <CODTracker folder={folder} isCustomerView={isCustomerView} onUpdate={onUpdate} />

                  {/* Opened-by + Operations owner (with reassign + audit log) */}
                  {!isCustomerView && <PFAssignmentBlock folder={folder} onUpdate={onUpdate} />}

                  <div>
                    <Label className="text-xs text-muted-foreground">Description</Label>
                    <div className="p-4 bg-slate-50 rounded-lg mt-1">
                      {folder.description}
                    </div>
                  </div>

                  {folder.source_email_body && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Original Email</Label>
                      <pre className="p-4 bg-slate-50 rounded-lg mt-1 text-sm whitespace-pre-wrap font-sans max-h-48 overflow-y-auto">
                        {folder.source_email_body}
                      </pre>
                    </div>
                  )}

                  {folder.attachments?.length > 0 && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Attachments</Label>
                      <div className="space-y-2 mt-2">
                        {folder.attachments.map((att, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                            <Paperclip className="h-4 w-4 text-blue-500" />
                            <span className="text-sm">{att.filename}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 2026-05-21 — DVH (vhodna dispozicija) quick toggle.
                      Lets staff close the DVH reminder loop from anywhere
                      via one of three paths: flip the toggle, type the
                      number, or upload the DVH.pdf in the Documents tab. */}
                  {!isCustomerView && <DvhQuickToggle folder={folder} token={token} onUpdate={onUpdate} />}

                  {/* DIZ (izhodna dispozicija) quick toggle — same as DVH
                      but for export-side PFs after IN/F at Koper. Rendered
                      universally; the backend only enrolls the follow-up
                      when direction == "export", so the toggle is a no-op
                      for unrelated PFs. */}
                  {!isCustomerView && <DizQuickToggle folder={folder} token={token} onUpdate={onUpdate} />}

                  {/* 2026-05-16 — CO2 footprint card (parity with mobile views) */}
                  <div
                    className="bg-white rounded-xl p-4 border shadow-sm"
                    data-testid="pf-detail-co2-block"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Leaf className="w-4 h-4 text-green-600" />
                        Carbon footprint
                      </h3>
                      {co2 && co2.method === "estimated" && (
                        <span className="text-[10px] uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                          GLEC · est.
                        </span>
                      )}
                    </div>
                    {!co2 ? (
                      <p className="text-sm text-slate-400">Loading…</p>
                    ) : co2.co2_kg == null ? (
                      <p className="text-xs text-slate-500">
                        {co2.reason || "Insufficient data to estimate emissions."}
                      </p>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold text-green-700">
                            {co2.co2_kg.toLocaleString()}
                          </span>
                          <span className="text-sm text-slate-500">kg CO₂e</span>
                          {co2.co2_tonnes != null && (
                            <span className="text-xs text-slate-400">
                              ≈ {co2.co2_tonnes} t
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-2 grid grid-cols-2 gap-1">
                          <span>Mode: <b className="text-slate-700 capitalize">{co2.mode}</b></span>
                          <span>Distance: <b className="text-slate-700">{co2.distance_km?.toLocaleString()} km</b></span>
                          <span>Weight: <b className="text-slate-700">{co2.weight_tonnes} t</b></span>
                          <span>Factor: <b className="text-slate-700">{co2.factor_g_per_tonne_km} g/t·km</b></span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* 2026-05-16 — AWB tracker moved to the Tracking tab
                      (per user request — natural home next to BL/MBL inputs). */}

                  {/* BL Instructions Status */}
                  {(folder.bl_instructions_status || folder.bl_instructions_received) && (
                    <div className={`mt-4 p-4 rounded-lg border ${
                      folder.bl_instructions_status === 'received' || folder.bl_instructions_received
                        ? 'border-green-200 bg-green-50' 
                        : 'border-amber-200 bg-amber-50'
                    }`}>
                      <Label className={`text-xs flex items-center gap-1 ${
                        folder.bl_instructions_status === 'received' || folder.bl_instructions_received
                          ? 'text-green-700'
                          : 'text-amber-700'
                      }`}>
                        <FileText className="h-3 w-3" />
                        BL Instructions
                      </Label>
                      
                      {folder.bl_instructions_status === 'received' || folder.bl_instructions_received ? (
                        <div className="flex items-center gap-2 mt-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-700 font-medium">
                            Instructions received from customer
                          </span>
                          {folder.bl_instructions_received_at && (
                            <span className="text-xs text-green-600">
                              ({new Date(folder.bl_instructions_received_at).toLocaleDateString()})
                            </span>
                          )}
                        </div>
                      ) : folder.bl_instructions_status === 'requested' && (
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-amber-600" />
                            <span className="text-sm text-amber-700 font-medium">
                              Waiting for customer response
                            </span>
                          </div>
                          {folder.bl_instructions_requested_at && (
                            <p className="text-xs text-amber-600">
                              Requested on {new Date(folder.bl_instructions_requested_at).toLocaleDateString()}
                              {folder.bl_instructions_recipients?.to && (
                                <> → <span className="font-mono">{folder.bl_instructions_recipients.to}</span></>
                              )}
                              {folder.bl_instructions_nudge_count > 0 && (
                                <> · {folder.bl_instructions_nudge_count} reminder{folder.bl_instructions_nudge_count === 1 ? '' : 's'} sent</>
                              )}
                            </p>
                          )}
                          {Array.isArray(folder.bl_instructions_recipients?.cc) && folder.bl_instructions_recipients.cc.length > 0 && (
                            <p className="text-[11px] text-amber-600/80">
                              CC: {folder.bl_instructions_recipients.cc.join(", ")}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-amber-300 text-amber-700 hover:bg-amber-100"
                              onClick={sendBLInstructionsNudge}
                              disabled={sendingBLNudge}
                              data-testid="bl-send-reminder-btn"
                            >
                              <Send className="h-4 w-4 mr-2" />
                              {sendingBLNudge ? "Sending reminder…" : "Send Reminder"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-300 text-blue-700 hover:bg-blue-100"
                              onClick={openBLRequestModal}
                              data-testid="bl-resend-modal-btn"
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Resend with overrides
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-green-300 text-green-700 hover:bg-green-100"
                              onClick={markBLInstructionsReceived}
                              data-testid="bl-mark-received-btn"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark as Received
                            </Button>
                            {folder.bl_instructions_nudges_disabled ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-slate-300 text-slate-600 hover:bg-slate-100"
                                onClick={async () => {
                                  try {
                                    await axios.post(
                                      `${API_URL}/bl-instructions/${folder.id}/nudges/enable`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success("Auto-reminders re-enabled");
                                    onUpdate();
                                  } catch (e) {
                                    toast.error("Couldn't re-enable reminders");
                                  }
                                }}
                                data-testid="bl-enable-nudges-btn"
                                title="Resume automatic reminder emails for this PF"
                              >
                                <BellRing className="h-4 w-4 mr-2" />
                                Resume reminders
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-700 hover:bg-red-100"
                                onClick={async () => {
                                  if (!window.confirm("Stop automatic reminder emails for this PF? The shipment will still show as 'awaiting BL instructions' — only the email nudging stops.")) return;
                                  try {
                                    await axios.post(
                                      `${API_URL}/bl-instructions/${folder.id}/nudges/disable`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success("Auto-reminders stopped for this PF");
                                    onUpdate();
                                  } catch (e) {
                                    toast.error("Couldn't stop reminders");
                                  }
                                }}
                                data-testid="bl-disable-nudges-btn"
                                title="Stop automatic reminder emails for this PF"
                              >
                                <BellOff className="h-4 w-4 mr-2" />
                                Stop reminders
                              </Button>
                            )}
                          </div>
                          {folder.bl_instructions_nudges_disabled && (
                            <p className="text-[11px] text-slate-500 italic mt-1">
                              Auto-reminders are paused for this PF. Click "Resume reminders" to turn them back on.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeSection === "documents" && (
            <div className="space-y-4">
              {/* Upload Area with Drag & Drop */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                  isDragOver 
                    ? "border-blue-500 bg-blue-50" 
                    : "border-slate-300 hover:border-slate-400"
                }`}
              >
                <Upload className={`h-8 w-8 mx-auto mb-2 ${isDragOver ? "text-blue-500" : "text-slate-400"}`} />
                <p className="text-sm font-medium mb-1">
                  Drag & drop documents here
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  or click to select files
                </p>
                
                {/* Tag Selection - Grouped by Category */}
                <div className="mb-3">
                  <select
                    value={selectedDocTag}
                    onChange={(e) => setSelectedDocTag(e.target.value)}
                    className="w-full max-w-xs mx-auto px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <optgroup label="Transport Documents - Sea">
                      <option value="BL">Bill of Lading (BL)</option>
                      <option value="OBL">Original Bill of Lading (OBL)</option>
                      <option value="SWB">Sea Waybill (SWB)</option>
                      <option value="TelexRelease">Telex Release BL</option>
                    </optgroup>
                    <optgroup label="Transport Documents - Air">
                      <option value="AWB">Air Waybill (AWB)</option>
                      <option value="MAWB">Master Air Waybill (MAWB)</option>
                      <option value="HAWB">House Air Waybill (HAWB)</option>
                    </optgroup>
                    <optgroup label="Transport Documents - Road/Rail">
                      <option value="CMR">CMR (Road Transport)</option>
                      <option value="RailWaybill">Rail Waybill (CIM/SMGS)</option>
                    </optgroup>
                    <optgroup label="Commercial Documents">
                      <option value="CI">Commercial Invoice (CI)</option>
                      <option value="PL">Packing List (PL)</option>
                      <option value="PI">Proforma Invoice (PI)</option>
                    </optgroup>
                    <optgroup label="Customs & Regulatory">
                      <option value="CO">Certificate of Origin (CO/COO)</option>
                      <option value="EX1">Export Declaration (EX/EX1)</option>
                      <option value="SAD">Single Admin Document (SAD)</option>
                      <option value="EUR1">EUR.1 Movement Certificate</option>
                      <option value="MRN">Customs Declaration / MRN</option>
                      <option value="T1">T1 / Transit Document</option>
                      <option value="CustomsDoc">Other Customs Document</option>
                    </optgroup>
                    <optgroup label="Port of Koper">
                      <option value="DVH">DVH — Entrance Disposition (Vhodna)</option>
                      <option value="DIZ">DIZ — Exit Disposition (Izhodna)</option>
                    </optgroup>
                    <optgroup label="Financial / Trade">
                      <option value="LC">Letter of Credit (LC)</option>
                      <option value="Insurance">Insurance Certificate</option>
                    </optgroup>
                    <optgroup label="Other Documents">
                      <option value="MSDS">MSDS / Safety Data Sheet</option>
                      <option value="DGDeclaration">Dangerous Goods Declaration</option>
                      <option value="Inspection">Inspection Certificate</option>
                      <option value="Weight">Weight Certificate</option>
                      <option value="Delivery">Delivery Note / POD</option>
                      <option value="Other">Other</option>
                    </optgroup>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Selected: <span className={`px-1.5 py-0.5 rounded ${DOC_TAGS[selectedDocTag]?.color || "bg-gray-100"}`}>
                      {DOC_TAGS[selectedDocTag]?.label || selectedDocTag}
                    </span>
                  </p>
                </div>
                
                <input
                  type="file"
                  onChange={(e) => e.target.files?.[0] && uploadDocument(e.target.files[0])}
                  className="hidden"
                  id="doc-upload"
                />
                <label
                  htmlFor="doc-upload"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 text-sm"
                >
                  {uploadingDocument ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Select File
                    </>
                  )}
                </label>
              </div>
              
              {/* Documents List */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" />
                    Documents ({documents.length})
                  </h3>
                  <div className="flex items-center gap-2">
                    {documents.length > 0 && (
                      <button
                        onClick={() => sendDocReceiptAck(false)}
                        disabled={docReceiptAckBusy}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
                          docReceiptAckBusy
                            ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                            : "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
                        }`}
                        data-testid="pf-doc-receipt-ack-btn"
                        title="Email the customer to confirm we received their documents and uploaded them into the system. No attachments — just an acknowledgment."
                      >
                        {docReceiptAckBusy ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Check className="h-3.5 w-3.5" />
                        )}
                        Notify: docs received
                      </button>
                    )}
                    {documents.length > 0 && (
                      <button
                        onClick={openSendDocsDialog}
                        disabled={selectedDocIds.length === 0}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
                          selectedDocIds.length === 0
                            ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                            : "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
                        }`}
                        data-testid="pf-doc-send-to-customer-btn"
                        title={
                          selectedDocIds.length === 0
                            ? "Tick the checkbox on one or more documents first"
                            : `Send ${selectedDocIds.length} doc(s) to customer`
                        }
                      >
                        <Send className="h-3.5 w-3.5" />
                        {selectedDocIds.length === 0
                          ? "Send to customer"
                          : `Send ${selectedDocIds.length} to customer`}
                      </button>
                    )}
                    <button
                      onClick={openCustomsFormsDialog}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border bg-amber-500 text-white border-amber-500 hover:bg-amber-600 transition-colors"
                      data-testid="pf-send-customs-forms-btn"
                      title="Send pre-filled, editable customs forms to the customer"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Send customs forms
                    </button>
                    <button
                      onClick={openClassifyDialog}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border bg-purple-600 text-white border-purple-600 hover:bg-purple-700 transition-colors"
                      data-testid="pf-request-classify-btn"
                      title="Ask the Roket customs bot to classify the cargo (HS code)"
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      Request HS classification
                    </button>
                    <button
                      onClick={openSharePartnerDialog}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border bg-[#003366] text-white border-[#003366] hover:bg-[#00294f] transition-colors"
                      data-testid="pf-share-partner-btn"
                      title="Give an external partner access to this PF's documents in the Partner Repository"
                    >
                      <Users className="h-3.5 w-3.5" />
                      Share with partner
                    </button>
                  </div>
                </div>
                
                {loadingDocuments ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : documents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No documents yet</p>
                    <p className="text-xs">Upload BLs, Invoices, CMRs, or other documents</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Group documents by tag */}
                    {Object.keys(DOC_TAGS).map((tagKey) => {
                      const tagDocs = documents.filter(d => d.tag === tagKey);
                      if (tagDocs.length === 0) return null;
                      
                      return (
                        <div key={tagKey} className="bg-slate-50 rounded-lg p-3">
                          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium mb-2 ${DOC_TAGS[tagKey]?.color || "bg-gray-100"}`}>
                            <FileText className="h-3 w-3" />
                            {DOC_TAGS[tagKey]?.label || tagKey} ({tagDocs.length})
                          </div>
                          <div className="space-y-1">
                            {tagDocs.map((doc) => (
                              <div
                                key={doc.id}
                                className={`flex items-center justify-between rounded p-2 group hover:shadow-sm transition-shadow ${
                                  selectedDocIds.includes(doc.id)
                                    ? "bg-blue-50 ring-1 ring-blue-300"
                                    : "bg-white"
                                }`}
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <input
                                    type="checkbox"
                                    checked={selectedDocIds.includes(doc.id)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      toggleDocSelected(doc.id);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                                    data-testid={`pf-doc-select-${doc.id}`}
                                    title="Select to send to customer"
                                  />
                                  <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">{doc.filename}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {(doc.file_size / 1024).toFixed(1)} KB • {new Date(doc.uploaded_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {/* View/Preview Button - for PDFs */}
                                  {doc.filename.toLowerCase().endsWith('.pdf') && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const response = await axios.get(
                                            `${API_URL}/position-folders/${folder.id}/documents/${doc.id}/download`,
                                            {
                                              headers: { Authorization: `Bearer ${token}` },
                                              responseType: 'blob'
                                            }
                                          );
                                          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                                          window.open(url, '_blank');
                                        } catch (error) {
                                          console.error("View error:", error);
                                          toast.error("Failed to open document");
                                        }
                                      }}
                                      className="p-1 hover:bg-green-50 rounded text-green-600"
                                      title="View PDF"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                  )}
                                  {/* Change Tag Dropdown */}
                                  <select
                                    value={doc.tag}
                                    onChange={(e) => updateDocumentTag(doc.id, e.target.value)}
                                    className="text-xs border rounded px-1 py-0.5"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {Object.entries(DOC_TAGS).map(([k, t]) => (
                                      <option key={k} value={k}>{t.label}</option>
                                    ))}
                                  </select>
                                  {/* Download */}
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const response = await axios.get(
                                          `${API_URL}/position-folders/${folder.id}/documents/${doc.id}/download`,
                                          {
                                            headers: { Authorization: `Bearer ${token}` },
                                            responseType: 'blob'
                                          }
                                        );
                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', doc.filename);
                                        document.body.appendChild(link);
                                        link.click();
                                        link.remove();
                                        window.URL.revokeObjectURL(url);
                                      } catch (error) {
                                        console.error("Download error:", error);
                                        toast.error("Failed to download document");
                                      }
                                    }}
                                    className="p-1 hover:bg-blue-50 rounded text-blue-600"
                                    title="Download"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                  {/* Delete */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteDocument(doc.id);
                                    }}
                                    className="p-1 hover:bg-red-50 rounded text-red-600"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Send Docs to Customer Dialog (2026-05-15) ────────── */}
              {showSendDocsDialog && (
                <div
                  className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                  onClick={() => !sendDocsBusy && setShowSendDocsDialog(false)}
                  data-testid="pf-doc-send-dialog"
                >
                  <div
                    className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold flex items-center gap-2">
                        <Send className="h-4 w-4 text-blue-600" />
                        Send {selectedDocIds.length} document(s) to customer
                      </h3>
                      <button
                        onClick={() => !sendDocsBusy && setShowSendDocsDialog(false)}
                        className="p-1 rounded hover:bg-slate-100"
                        data-testid="pf-doc-send-close"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">Recipients (comma-separated)</Label>
                        <Input
                          value={sendDocsRecipients}
                          onChange={(e) => setSendDocsRecipients(e.target.value)}
                          placeholder="customer@example.com, ops@example.com"
                          data-testid="pf-doc-send-recipients"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">
                          Defaults to the PF's Notify list. CC: sender + operations@europacific.com.
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs">Subject</Label>
                        <Input
                          value={sendDocsSubject}
                          onChange={(e) => setSendDocsSubject(e.target.value)}
                          data-testid="pf-doc-send-subject"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Message (optional, prepended to body)</Label>
                        <textarea
                          value={sendDocsMessage}
                          onChange={(e) => setSendDocsMessage(e.target.value)}
                          rows={3}
                          className="w-full border rounded p-2 text-sm"
                          placeholder="e.g. Please find attached the requested BL and invoice."
                          data-testid="pf-doc-send-message"
                        />
                      </div>
                      <label className="flex items-start gap-2 p-2 rounded bg-emerald-50 border border-emerald-200 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={sendDocsNotify}
                          onChange={(e) => setSendDocsNotify(e.target.checked)}
                          className="h-4 w-4 mt-0.5 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                          data-testid="pf-doc-send-notify"
                        />
                        <div className="text-xs">
                          <p className="font-medium text-emerald-900">Log on PF timeline</p>
                          <p className="text-emerald-700">
                            Records a <code>doc_send</code> event on this PF so the operator history shows the email + attached filenames.
                          </p>
                        </div>
                      </label>

                      <div className="rounded border border-slate-200 bg-slate-50 p-2 max-h-32 overflow-auto text-xs">
                        <p className="font-medium mb-1 text-slate-700">Attachments:</p>
                        {documents
                          .filter((d) => selectedDocIds.includes(d.id))
                          .map((d) => (
                            <div key={d.id} className="flex items-center gap-1.5 py-0.5">
                              <FileText className="h-3 w-3 text-slate-400" />
                              <span className="truncate">{d.filename}</span>
                            </div>
                          ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-4">
                      <button
                        onClick={() => !sendDocsBusy && setShowSendDocsDialog(false)}
                        disabled={sendDocsBusy}
                        className="px-3 py-1.5 text-sm rounded border hover:bg-slate-50"
                        data-testid="pf-doc-send-cancel"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={sendDocsToCustomer}
                        disabled={sendDocsBusy || selectedDocIds.length === 0}
                        className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                        data-testid="pf-doc-send-submit"
                      >
                        {sendDocsBusy ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Sending…
                          </>
                        ) : (
                          <>
                            <Send className="h-3.5 w-3.5" />
                            Send email
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Share with partner dialog ─────────────────────────── */}
              {showSharePartnerDialog && (
                <div
                  className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                  onClick={() => setShowSharePartnerDialog(false)}
                  data-testid="pf-share-partner-dialog"
                >
                  <div
                    className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 max-h-[85vh] overflow-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-base font-semibold flex items-center gap-2">
                        <Users className="h-4 w-4 text-[#003366]" />
                        Share {folder.pf_number} with partners
                      </h3>
                      <button onClick={() => setShowSharePartnerDialog(false)} data-testid="pf-share-partner-close">
                        <X className="h-5 w-5 text-slate-400" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mb-3">
                      Toggle access for any approved partner. Shared partners see this PF's documents in their Partner Repository and can upload files. You can share with several partners.
                    </p>
                    <input
                      value={partnerSearch}
                      onChange={(e) => setPartnerSearch(e.target.value)}
                      placeholder="Search partner by email or company…"
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm mb-3"
                      data-testid="pf-share-partner-search"
                    />
                    {partnerLoading ? (
                      <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading partners…
                      </div>
                    ) : partnerList.length === 0 ? (
                      <div className="text-center py-8 text-sm text-slate-500">
                        No approved partner accounts yet.<br />
                        Create one under <span className="font-medium">Partner Approvals</span>.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {partnerList
                          .filter((p) => {
                            if (!partnerSearch) return true;
                            const q = partnerSearch.toLowerCase();
                            return (
                              (p.email || "").toLowerCase().includes(q) ||
                              (p.company_name || "").toLowerCase().includes(q) ||
                              (p.full_name || "").toLowerCase().includes(q)
                            );
                          })
                          .map((p) => {
                            const has = pfHasAccess(p);
                            return (
                              <div
                                key={p.id}
                                className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2"
                                data-testid={`pf-share-partner-row-${p.id}`}
                              >
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-slate-800 truncate">{p.email}</div>
                                  <div className="text-xs text-slate-400 truncate">
                                    {p.full_name}{p.company_name ? ` · ${p.company_name}` : ""} · {p.assigned_count} folders
                                  </div>
                                </div>
                                <button
                                  onClick={() => togglePartnerShare(p)}
                                  disabled={partnerBusyId === p.id}
                                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
                                    has
                                      ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                                      : "bg-[#003366] text-white border-[#003366] hover:bg-[#00294f]"
                                  }`}
                                  data-testid={`pf-share-partner-toggle-${p.id}`}
                                >
                                  {partnerBusyId === p.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : has ? (
                                    <X className="h-3.5 w-3.5" />
                                  ) : (
                                    <Check className="h-3.5 w-3.5" />
                                  )}
                                  {has ? "Remove" : "Share"}
                                </button>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Send customs forms dialog ─────────────────────────── */}
              {showCustomsFormsDialog && (
                <div
                  className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                  onClick={() => !cfBusy && setShowCustomsFormsDialog(false)}
                  data-testid="pf-customs-forms-dialog"
                >
                  <div
                    className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-5 max-h-[90vh] overflow-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold flex items-center gap-2">
                        <FileText className="h-4 w-4 text-amber-600" />
                        {cfStep === "select"
                          ? "Send customs forms to customer"
                          : "Review & send pre-filled forms"}
                      </h3>
                      <button
                        onClick={() => !cfBusy && setShowCustomsFormsDialog(false)}
                        className="p-1 rounded hover:bg-slate-100"
                        data-testid="pf-customs-forms-close"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {cfStep === "select" && (
                      <div className="space-y-4">
                        <div>
                          <Label className="text-xs font-semibold">1. Forms to send</Label>
                          <div className="grid sm:grid-cols-2 gap-2 mt-1.5">
                            {cfAllForms.map((f) => (
                              <label
                                key={f.slug}
                                className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm ${
                                  cfSelectedSlugs.includes(f.slug)
                                    ? "border-amber-400 bg-amber-50"
                                    : "border-slate-200 hover:bg-slate-50"
                                }`}
                                data-testid={`cf-form-option-${f.slug}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={cfSelectedSlugs.includes(f.slug)}
                                  onChange={() => toggleCfSlug(f.slug)}
                                  className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                />
                                <span className="truncate">{f.title}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs font-semibold">
                            2. Documents to parse for auto-fill
                          </Label>
                          <p className="text-[11px] text-slate-500 mb-1.5">
                            We read these (invoice, BL…) and pre-fill the forms automatically.
                          </p>
                          {documents.length === 0 ? (
                            <p className="text-xs text-slate-400">No documents uploaded yet.</p>
                          ) : (
                            <div className="space-y-1 max-h-40 overflow-auto">
                              {documents.map((d) => (
                                <label
                                  key={d.id}
                                  className={`flex items-center gap-2 p-1.5 rounded border cursor-pointer text-xs ${
                                    cfSourceDocIds.includes(d.id)
                                      ? "border-blue-300 bg-blue-50"
                                      : "border-slate-200 hover:bg-slate-50"
                                  }`}
                                  data-testid={`cf-doc-option-${d.id}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={cfSourceDocIds.includes(d.id)}
                                    onChange={() => toggleCfDoc(d.id)}
                                    className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <FileText className="h-3 w-3 text-slate-400" />
                                  <span className="truncate flex-1">{d.filename}</span>
                                  <span className="text-[10px] text-slate-400">
                                    {DOC_TAGS[d.tag]?.label || d.tag}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-end gap-2 pt-1">
                          <button
                            onClick={() => !cfBusy && setShowCustomsFormsDialog(false)}
                            disabled={cfBusy}
                            className="px-3 py-1.5 text-sm rounded border hover:bg-slate-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={prepareCustomsForms}
                            disabled={cfBusy || cfSelectedSlugs.length === 0}
                            className="px-3 py-1.5 text-sm rounded bg-amber-500 text-white hover:bg-amber-600 disabled:bg-slate-300 inline-flex items-center gap-1.5"
                            data-testid="cf-prepare-btn"
                          >
                            {cfBusy ? (
                              <>
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Auto-filling…
                              </>
                            ) : (
                              <>Prepare &amp; auto-fill →</>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {cfStep === "review" && (
                      <div className="space-y-4">
                        <div>
                          <Label className="text-xs">Recipients (comma-separated)</Label>
                          <Input
                            value={cfRecipients}
                            onChange={(e) => setCfRecipients(e.target.value)}
                            placeholder="customer@example.com"
                            data-testid="cf-recipients"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Message (optional)</Label>
                          <textarea
                            value={cfMessage}
                            onChange={(e) => setCfMessage(e.target.value)}
                            rows={2}
                            className="w-full border rounded p-2 text-sm"
                            placeholder="e.g. Please complete and sign the attached customs forms."
                            data-testid="cf-message"
                          />
                        </div>

                        {cfForms.map((f) => {
                          const filled = f.fields.filter((x) => x.value);
                          return (
                            <div key={f.slug} className="border rounded-lg p-3">
                              <p className="text-sm font-semibold mb-2">
                                {f.title}{" "}
                                <span className="text-xs font-normal text-slate-400">
                                  ({filled.length} pre-filled)
                                </span>
                              </p>
                              {filled.length === 0 ? (
                                <p className="text-xs text-slate-400">
                                  No fields auto-filled — the customer will complete it.
                                </p>
                              ) : (
                                <div className="space-y-1.5 max-h-44 overflow-auto">
                                  {filled.map((x) => (
                                    <div key={x.id} className="flex items-center gap-2">
                                      <span
                                        className="text-[11px] text-slate-500 w-40 truncate flex-shrink-0"
                                        title={x.label}
                                      >
                                        {x.label || x.id}
                                      </span>
                                      <Input
                                        value={x.value}
                                        onChange={(e) => setCfFieldValue(f.slug, x.id, e.target.value)}
                                        className="h-7 text-xs"
                                        data-testid={`cf-field-${f.slug}-${x.id}`}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={cfAttachPdfs}
                            onChange={(e) => setCfAttachPdfs(e.target.checked)}
                            className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                            data-testid="cf-attach-pdfs"
                          />
                          Also attach the pre-filled PDFs to the email
                        </label>

                        <div className="flex items-center justify-between gap-2 pt-1">
                          <button
                            onClick={() => setCfStep("select")}
                            disabled={cfBusy}
                            className="px-3 py-1.5 text-sm rounded border hover:bg-slate-50"
                          >
                            ← Back
                          </button>
                          <button
                            onClick={sendCustomsForms}
                            disabled={cfBusy}
                            className="px-3 py-1.5 text-sm rounded bg-amber-500 text-white hover:bg-amber-600 disabled:bg-slate-300 inline-flex items-center gap-1.5"
                            data-testid="cf-send-btn"
                          >
                            {cfBusy ? (
                              <>
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…
                              </>
                            ) : (
                              <>
                                <Send className="h-3.5 w-3.5" /> Send to customer
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Request HS classification dialog ──────────────────── */}
              {showClassifyDialog && (
                <div
                  className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                  onClick={() => !clfBusy && setShowClassifyDialog(false)}
                  data-testid="pf-classify-dialog"
                >
                  <div
                    className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-purple-600" />
                        Request HS classification
                      </h3>
                      <button
                        onClick={() => !clfBusy && setShowClassifyDialog(false)}
                        className="p-1 rounded hover:bg-slate-100"
                        data-testid="pf-classify-close"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    <p className="text-[11px] text-slate-500 mb-3">
                      We email the Roket customs bot. Leave fields blank to auto-detect
                      them from the selected invoice. The result goes to
                      customs@europacific.com (CC the PF notify list).
                    </p>

                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">Invoice to parse</Label>
                        {documents.length === 0 ? (
                          <p className="text-xs text-slate-400">No documents uploaded.</p>
                        ) : (
                          <div className="space-y-1 max-h-32 overflow-auto">
                            {documents.map((d) => (
                              <label
                                key={d.id}
                                className={`flex items-center gap-2 p-1.5 rounded border cursor-pointer text-xs ${
                                  clfDocIds.includes(d.id)
                                    ? "border-purple-300 bg-purple-50"
                                    : "border-slate-200 hover:bg-slate-50"
                                }`}
                                data-testid={`clf-doc-option-${d.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={clfDocIds.includes(d.id)}
                                  onChange={() =>
                                    setClfDocIds((prev) =>
                                      prev.includes(d.id)
                                        ? prev.filter((x) => x !== d.id)
                                        : [...prev, d.id]
                                    )
                                  }
                                  className="h-3.5 w-3.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                                />
                                <FileText className="h-3 w-3 text-slate-400" />
                                <span className="truncate flex-1">{d.filename}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Origin (country)</Label>
                          <Input
                            value={clfOrigin}
                            onChange={(e) => setClfOrigin(e.target.value)}
                            placeholder="auto from invoice"
                            data-testid="clf-origin"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Import into</Label>
                          <Input
                            value={clfImportInto}
                            onChange={(e) => setClfImportInto(e.target.value)}
                            placeholder="EU"
                            data-testid="clf-import-into"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Cargo name</Label>
                        <Input
                          value={clfCargo}
                          onChange={(e) => setClfCargo(e.target.value)}
                          placeholder="auto from invoice"
                          data-testid="clf-cargo"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">HS code (if known)</Label>
                        <Input
                          value={clfHs}
                          onChange={(e) => setClfHs(e.target.value)}
                          placeholder="optional"
                          data-testid="clf-hs"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-4">
                      <button
                        onClick={() => !clfBusy && setShowClassifyDialog(false)}
                        disabled={clfBusy}
                        className="px-3 py-1.5 text-sm rounded border hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={submitClassification}
                        disabled={clfBusy}
                        className="px-3 py-1.5 text-sm rounded bg-purple-600 text-white hover:bg-purple-700 disabled:bg-slate-300 inline-flex items-center gap-1.5"
                        data-testid="clf-submit-btn"
                      >
                        {clfBusy ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…
                          </>
                        ) : (
                          <>
                            <ShieldCheck className="h-3.5 w-3.5" /> Send request
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

          {activeSection === "tasks" && (
            <PFTasksSection 
              folder={folder}
              token={token}
              onUpdate={onUpdate}
            />
          )}

          {activeSection === "tracking" && (
            <div className="space-y-6">
              {/* 2026-05-16 — AWB tracker (only for AIR shipments).
                  Moved from Details tab to here per user request. */}
              {String(folder.service_type || "").toUpperCase() === "AIR" &&
                (folder.mbl_number || folder.awb_number || folder.hawb_number) && (
                  <AwbTracker
                    mawb={folder.mbl_number || folder.awb_number || folder.hawb_number}
                    pfNumber={folder.pf_number}
                    token={token}
                    contactEmail={folder.customer_email || ""}
                    contactPhone={folder.customer_phone || ""}
                  />
                )}
              {/* Container Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Container Information
                  </h3>
                  {!editingContainerInfo ? (
                    <button
                      onClick={() => {
                        setContainerInfoEdit({
                          container_number: folder.container_number || "",
                          containers: folder.containers || [],
                          mbl_number: folder.mbl_number || "",
                          hbl_numbers: folder.hbl_numbers || [],
                          booking_number: folder.booking_number || "",
                          bl_structure: folder.bl_structure || "",
                        });
                        setEditingContainerInfo(true);
                      }}
                      className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                      title="Edit Container Information"
                      data-testid="edit-container-info-btn"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingContainerInfo(false)}
                        className="px-2 py-1 text-xs rounded border hover:bg-slate-100"
                        disabled={savingContainerInfo}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveContainerInfo}
                        disabled={savingContainerInfo}
                        className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        {savingContainerInfo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                        Save
                      </button>
                    </div>
                  )}
                </div>

                {!editingContainerInfo ? (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Container(s):</span>
                      <span className="ml-2 font-mono font-medium">
                        {folder.containers?.join(', ') || folder.container_number || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">MBL:</span>
                      <span className="ml-2 font-mono">{folder.mbl_number || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Booking #:</span>
                      <span className="ml-2 font-mono" data-testid="pf-detail-booking-number">{folder.booking_number || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">HBL:</span>
                      <span className="ml-2 font-mono">{folder.hbl_number || folder.hbl_numbers?.join(', ') || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Service Type:</span>
                      <span className="ml-2 font-medium">{folder.service_type || 'FCL'}</span>
                    </div>
                    {folder.bl_structure && (
                      <div>
                        <span className="text-slate-500">BL Structure:</span>
                        <span className="ml-2 font-medium" data-testid="pf-detail-bl-structure">{folder.bl_structure}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <label className="text-slate-500 block mb-1">Container Number(s)</label>
                      <Input
                        value={Array.isArray(containerInfoEdit.containers) 
                          ? containerInfoEdit.containers.join(', ') 
                          : containerInfoEdit.containers || containerInfoEdit.container_number}
                        onChange={(e) => setContainerInfoEdit({
                          ...containerInfoEdit, 
                          containers: e.target.value,
                          container_number: e.target.value.split(',')[0]?.trim() || ""
                        })}
                        placeholder="ABCD1234567, EFGH7654321"
                        className="font-mono text-sm"
                        data-testid="edit-container-number-input"
                      />
                      <p className="text-xs text-slate-400 mt-1">Separate multiple containers with commas</p>
                    </div>
                    <div>
                      <label className="text-slate-500 block mb-1">Booking Number <span className="text-slate-400 font-normal">(from carrier)</span></label>
                      <Input
                        value={containerInfoEdit.booking_number || ""}
                        onChange={(e) => setContainerInfoEdit({...containerInfoEdit, booking_number: e.target.value})}
                        placeholder="e.g. ZIMUKOP1033149, MAEU269163428"
                        className="font-mono text-sm"
                        data-testid="edit-booking-number-input"
                      />
                    </div>
                    <div>
                      <label className="text-slate-500 block mb-1">MBL Number</label>
                      <Input
                        value={containerInfoEdit.mbl_number}
                        onChange={(e) => setContainerInfoEdit({...containerInfoEdit, mbl_number: e.target.value})}
                        placeholder="Master Bill of Lading"
                        className="font-mono text-sm"
                        data-testid="edit-mbl-input"
                      />
                    </div>
                    <div>
                      <label className="text-slate-500 block mb-1">HBL Number(s)</label>
                      <Input
                        value={Array.isArray(containerInfoEdit.hbl_numbers) 
                          ? containerInfoEdit.hbl_numbers.join(', ') 
                          : containerInfoEdit.hbl_numbers}
                        onChange={(e) => setContainerInfoEdit({...containerInfoEdit, hbl_numbers: e.target.value})}
                        placeholder="HBLKOP001, HBLKOP002"
                        className="font-mono text-sm"
                        data-testid="edit-hbl-input"
                      />
                      <p className="text-xs text-slate-400 mt-1">Separate multiple HBLs with commas</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Service Type:</span>
                      <span className="ml-2 font-medium">{folder.service_type || 'FCL'}</span>
                    </div>
                    <div>
                      <label className="text-slate-500 block mb-1">BL Structure</label>
                      <select
                        value={containerInfoEdit.bl_structure || ""}
                        onChange={(e) => setContainerInfoEdit({ ...containerInfoEdit, bl_structure: e.target.value })}
                        className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                        data-testid="edit-bl-structure-select"
                      >
                        <option value="">Not specified</option>
                        <option value="Direct MBL">Direct MBL</option>
                        <option value="Indirect MBL">Indirect MBL</option>
                        <option value="HBL">HBL</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* ═══ Trucking Details (read-only) — mirrors the Create modal for TRUCKING PFs ═══ */}
              {String(folder.service_type || "").toUpperCase() === "TRUCKING" && folder.trucking_details && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200" data-testid="pf-detail-trucking-details">
                  <h3 className="font-bold text-amber-900 flex items-center gap-2 mb-3">
                    <Truck className="h-5 w-5 text-amber-600" />
                    Trucking Details
                  </h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    {[
                      ["Trucking Company", folder.trucking_details.trucking_company],
                      ["Truck Plate", folder.trucking_details.truck_plate],
                      ["Trailer Plate", folder.trucking_details.trailer_plate],
                      ["Driver", folder.trucking_details.driver_name],
                      ["Driver Phone", folder.trucking_details.driver_phone],
                      ["Dispatcher", folder.trucking_details.dispatcher_name],
                      ["Dispatcher Phone", folder.trucking_details.dispatcher_phone],
                      ["Dispatcher Email", folder.trucking_details.dispatcher_email],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <span className="text-slate-500">{label}:</span>
                        <span className="ml-2 font-medium text-slate-800">{val || '—'}</span>
                      </div>
                    ))}
                  </div>
                  {folder.trucking_details.notes && (
                    <div className="mt-2 text-sm">
                      <span className="text-slate-500">Notes:</span>
                      <span className="ml-2 text-slate-800 whitespace-pre-wrap">{folder.trucking_details.notes}</span>
                    </div>
                  )}
                </div>
              )}


              {/* ═══ Tracking Mode toggle — joint (single BL) vs separate (per-container BL) ═══ */}
              {(folder.containers && folder.containers.length >= 2) && (
                <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200" data-testid="tracking-mode-panel">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-amber-900 flex items-center gap-2">
                        <Layers className="h-5 w-5 text-amber-600" />
                        Tracking Mode
                      </h3>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Use separate tracking when containers travel on different vessels / BLs.
                      </p>
                    </div>
                    <span className="text-[10px] text-amber-700 uppercase font-semibold">
                      {(folder.tracking_mode || "joint") === "separate" ? `${(folder.containers_tracking || []).length} threads` : "1 thread"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await axios.put(`${API_URL}/position-folders/${folder.pf_number || folder.id}/tracking-mode`,
                            { tracking_mode: "joint" },
                            { headers: { Authorization: `Bearer ${token}` } });
                          toast.success("Tracking mode → joint (single BL)");
                          onUpdate?.();
                        } catch (e) {
                          const status = e.response?.status;
                          const detail = e.response?.data?.detail;
                          const msg = (typeof detail === "string" && detail) || (status ? `HTTP ${status}` : "Network error");
                          toast.error(`Tracking mode change failed — ${msg}. Backend may need a redeploy.`);
                        }
                      }}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        (folder.tracking_mode || "joint") === "joint"
                          ? "bg-white border-amber-500 ring-2 ring-amber-200"
                          : "bg-white/60 border-amber-200 hover:border-amber-300"
                      }`}
                      data-testid="tracking-mode-joint"
                    >
                      <div className="flex items-center gap-2">
                        <Package className={`h-4 w-4 ${(folder.tracking_mode || "joint") === "joint" ? "text-amber-700" : "text-slate-400"}`} />
                        <span className="font-semibold text-sm">Single BL — joint</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1">Both containers on the same vessel/voyage. One tracking thread, one customer email per milestone.</p>
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await axios.put(`${API_URL}/position-folders/${folder.pf_number || folder.id}/tracking-mode`,
                            { tracking_mode: "separate" },
                            { headers: { Authorization: `Bearer ${token}` } });
                          toast.success("Tracking mode → separate (per container)");
                          onUpdate?.();
                        } catch (e) {
                          const status = e.response?.status;
                          const detail = e.response?.data?.detail;
                          const msg = (typeof detail === "string" && detail) || (status ? `HTTP ${status}` : "Network error");
                          toast.error(`Tracking mode change failed — ${msg}. Backend may need a redeploy.`);
                        }
                      }}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        folder.tracking_mode === "separate"
                          ? "bg-white border-amber-500 ring-2 ring-amber-200"
                          : "bg-white/60 border-amber-200 hover:border-amber-300"
                      }`}
                      data-testid="tracking-mode-separate"
                    >
                      <div className="flex items-center gap-2">
                        <Layers className={`h-4 w-4 ${folder.tracking_mode === "separate" ? "text-amber-700" : "text-slate-400"}`} />
                        <span className="font-semibold text-sm">Multiple BLs — per container</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1">Containers on different vessels. One tracking thread per container, separate milestone emails.</p>
                    </button>
                  </div>

                  {/* Per-container cards when separate mode is on */}
                  {folder.tracking_mode === "separate" && (folder.containers_tracking || []).length > 0 && (
                    <div className="mt-3 space-y-2" data-testid="per-container-cards">
                      {(folder.containers_tracking || []).map((row, idx) => (
                        <div key={row.container_no || idx} className="bg-white rounded-lg p-3 border border-amber-200" data-testid={`container-card-${row.container_no}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Ship className="h-4 w-4 text-amber-600" />
                            <span className="font-mono font-semibold text-sm">{row.container_no}</span>
                            <Badge variant="outline" className="text-[10px]">{row.tracking_status || "pending"}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px] uppercase text-slate-500">Vessel</Label>
                              <Input
                                defaultValue={row.vessel_name || ""}
                                onBlur={async (e) => {
                                  if (e.target.value === (row.vessel_name || "")) return;
                                  try {
                                    await axios.put(`${API_URL}/position-folders/${folder.pf_number || folder.id}/container-tracking/${row.container_no}`,
                                      { vessel_name: e.target.value },
                                      { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success(`${row.container_no}: vessel updated`);
                                    onUpdate?.();
                                  } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
                                }}
                                className="h-8 text-xs"
                                placeholder="e.g. CMA CGM CARMEN"
                                data-testid={`container-vessel-${row.container_no}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase text-slate-500">Voyage</Label>
                              <Input
                                defaultValue={row.voyage || ""}
                                onBlur={async (e) => {
                                  if (e.target.value === (row.voyage || "")) return;
                                  try {
                                    await axios.put(`${API_URL}/position-folders/${folder.pf_number || folder.id}/container-tracking/${row.container_no}`,
                                      { voyage: e.target.value },
                                      { headers: { Authorization: `Bearer ${token}` } });
                                    onUpdate?.();
                                  } catch (err) { toast.error("Save failed"); }
                                }}
                                className="h-8 text-xs"
                                placeholder="123W"
                                data-testid={`container-voyage-${row.container_no}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase text-slate-500">MBL</Label>
                              <Input
                                defaultValue={row.mbl_number || ""}
                                onBlur={async (e) => {
                                  if (e.target.value === (row.mbl_number || "")) return;
                                  try {
                                    await axios.put(`${API_URL}/position-folders/${folder.pf_number || folder.id}/container-tracking/${row.container_no}`,
                                      { mbl_number: e.target.value },
                                      { headers: { Authorization: `Bearer ${token}` } });
                                    onUpdate?.();
                                  } catch (err) { toast.error("Save failed"); }
                                }}
                                className="h-8 text-xs font-mono"
                                placeholder="MAEU…"
                                data-testid={`container-mbl-${row.container_no}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase text-slate-500">HBL</Label>
                              <Input
                                defaultValue={row.hbl_number || ""}
                                onBlur={async (e) => {
                                  if (e.target.value === (row.hbl_number || "")) return;
                                  try {
                                    await axios.put(`${API_URL}/position-folders/${folder.pf_number || folder.id}/container-tracking/${row.container_no}`,
                                      { hbl_number: e.target.value },
                                      { headers: { Authorization: `Bearer ${token}` } });
                                    onUpdate?.();
                                  } catch (err) { toast.error("Save failed"); }
                                }}
                                className="h-8 text-xs font-mono"
                                placeholder="HBL…"
                                data-testid={`container-hbl-${row.container_no}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase text-slate-500">ETD</Label>
                              <Input
                                type="date"
                                defaultValue={row.etd ? String(row.etd).slice(0, 10) : ""}
                                onBlur={async (e) => {
                                  try {
                                    await axios.put(`${API_URL}/position-folders/${folder.pf_number || folder.id}/container-tracking/${row.container_no}`,
                                      { etd: e.target.value || null },
                                      { headers: { Authorization: `Bearer ${token}` } });
                                    onUpdate?.();
                                  } catch (err) { toast.error("Save failed"); }
                                }}
                                className="h-8 text-xs"
                                data-testid={`container-etd-${row.container_no}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase text-slate-500">ETA</Label>
                              <Input
                                type="date"
                                defaultValue={row.eta ? String(row.eta).slice(0, 10) : ""}
                                onBlur={async (e) => {
                                  try {
                                    await axios.put(`${API_URL}/position-folders/${folder.pf_number || folder.id}/container-tracking/${row.container_no}`,
                                      { eta: e.target.value || null },
                                      { headers: { Authorization: `Bearer ${token}` } });
                                    onUpdate?.();
                                  } catch (err) { toast.error("Save failed"); }
                                }}
                                className="h-8 text-xs"
                                data-testid={`container-eta-${row.container_no}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Tracking Sources Toggles - Enhanced UI */}
              <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-blue-900 flex items-center gap-2">
                      <Radar className="h-5 w-5 text-blue-600" />
                      Tracking Sources
                    </h3>
                    <p className="text-sm text-blue-600 mt-0.5">
                      Enable tracking services for this shipment
                    </p>
                  </div>
                  {(trackingSources.fcl || trackingSources.port || trackingSources.metrans) && (
                    <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full flex items-center gap-1">
                      <CheckCircle className="h-3.5 w-3.5" />
                      Tracking Active
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* FCL Tracking */}
                  <label className={`relative flex items-center justify-between p-4 bg-white rounded-xl border-2 cursor-pointer transition-all ${
                    trackingSources.fcl 
                      ? 'border-blue-400 bg-blue-50/50 ring-2 ring-blue-100' 
                      : 'border-slate-200 hover:border-blue-300'
                  }`}
                    data-testid="tracking-toggle-fcl">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-lg ${trackingSources.fcl ? 'bg-blue-100' : 'bg-slate-100'}`}>
                        <Ship className={`h-5 w-5 ${trackingSources.fcl ? 'text-blue-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">FCL Tracking</div>
                        <div className="text-xs text-slate-500">SeaRates Container API</div>
                        {trackingSources.fcl && folder.tracking_status?.fcl_last_update && (
                          <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Last updated: {new Date(folder.tracking_status.fcl_last_update).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={trackingSources.fcl}
                      onChange={(e) => setTrackingSources({...trackingSources, fcl: e.target.checked})}
                      className="w-5 h-5 text-blue-600 rounded-md border-slate-300 focus:ring-blue-500"
                    />
                    {trackingSources.fcl && (
                      <div className="absolute top-1.5 right-1.5">
                        <span className="w-2 h-2 bg-green-500 rounded-full block animate-pulse" />
                      </div>
                    )}
                  </label>

                  {/* LCL Tracking */}
                  <label className={`relative flex items-center justify-between p-4 bg-white rounded-xl border-2 cursor-pointer transition-all ${
                    trackingSources.lcl 
                      ? 'border-purple-400 bg-purple-50/50 ring-2 ring-purple-100' 
                      : 'border-slate-200 hover:border-purple-300'
                  }`}
                    data-testid="tracking-toggle-lcl">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-lg ${trackingSources.lcl ? 'bg-purple-100' : 'bg-slate-100'}`}>
                        <Package className={`h-5 w-5 ${trackingSources.lcl ? 'text-purple-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">LCL Tracking</div>
                        <div className="text-xs text-slate-500">Less than container load</div>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={trackingSources.lcl}
                      onChange={(e) => setTrackingSources({...trackingSources, lcl: e.target.checked})}
                      className="w-5 h-5 text-purple-600 rounded-md border-slate-300 focus:ring-purple-500"
                    />
                    {trackingSources.lcl && (
                      <div className="absolute top-1.5 right-1.5">
                        <span className="w-2 h-2 bg-green-500 rounded-full block animate-pulse" />
                      </div>
                    )}
                  </label>

                  {/* HBL Tracking */}
                  <label className={`relative flex items-center justify-between p-4 bg-white rounded-xl border-2 cursor-pointer transition-all ${
                    trackingSources.hbl 
                      ? 'border-emerald-400 bg-emerald-50/50 ring-2 ring-emerald-100' 
                      : 'border-slate-200 hover:border-emerald-300'
                  }`}
                    data-testid="tracking-toggle-hbl">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-lg ${trackingSources.hbl ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                        <FileText className={`h-5 w-5 ${trackingSources.hbl ? 'text-emerald-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">HBL Tracking</div>
                        <div className="text-xs text-slate-500">House Bill of Lading</div>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={trackingSources.hbl}
                      onChange={(e) => setTrackingSources({...trackingSources, hbl: e.target.checked})}
                      className="w-5 h-5 text-emerald-600 rounded-md border-slate-300 focus:ring-emerald-500"
                    />
                    {trackingSources.hbl && (
                      <div className="absolute top-1.5 right-1.5">
                        <span className="w-2 h-2 bg-green-500 rounded-full block animate-pulse" />
                      </div>
                    )}
                  </label>

                  {/* Port Tracking - With Port Selection */}
                  <div className={`relative p-4 bg-white rounded-xl border-2 transition-all ${
                    trackingSources.port 
                      ? 'border-orange-400 bg-orange-50/50 ring-2 ring-orange-100' 
                      : 'border-slate-200'
                  }`}>
                    <label className="flex items-center justify-between cursor-pointer"
                      data-testid="tracking-toggle-port">
                      <div className="flex items-center gap-3">
                        <div className={`p-2.5 rounded-lg ${trackingSources.port ? 'bg-orange-100' : 'bg-slate-100'}`}>
                          <Building2 className={`h-5 w-5 ${trackingSources.port ? 'text-orange-600' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">Port Tracking</div>
                          <div className="text-xs text-slate-500">Track containers at port terminals</div>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={trackingSources.port}
                        onChange={(e) => setTrackingSources({...trackingSources, port: e.target.checked})}
                        className="w-5 h-5 text-orange-600 rounded-md border-slate-300 focus:ring-orange-500"
                      />
                    </label>
                    
                    {/* Port Selection - Shows when port tracking enabled */}
                    {trackingSources.port && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <p className="text-xs text-orange-700 font-medium mb-2">Select Port Terminal:</p>
                        <div className="grid grid-cols-3 gap-2">
                          {availablePorts.map((port) => (
                            <button
                              key={port.code}
                              type="button"
                              onClick={() => setSelectedPort(port.code)}
                              className={`p-2 rounded-lg border-2 text-left transition-all ${
                                selectedPort === port.code
                                  ? 'border-orange-500 bg-orange-100'
                                  : 'border-slate-200 bg-white hover:border-orange-300'
                              }`}
                              data-testid={`port-select-${port.code}`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{port.flag}</span>
                                <div>
                                  <p className={`text-xs font-semibold ${selectedPort === port.code ? 'text-orange-700' : 'text-slate-700'}`}>
                                    {port.name}
                                  </p>
                                  <p className="text-[10px] text-slate-400">{port.country}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                        {folder.tracking_status?.port_status && (
                          <div className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                            <Anchor className="h-3 w-3" />
                            {folder.tracking_status.port_status}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {trackingSources.port && (
                      <div className="absolute top-1.5 right-1.5">
                        <span className="w-2 h-2 bg-green-500 rounded-full block animate-pulse" />
                      </div>
                    )}
                  </div>

                  {/* Metrans Tracking - Full Width */}
                  <label className={`relative flex items-center justify-between p-4 bg-white rounded-xl border-2 cursor-pointer transition-all md:col-span-2 ${
                    trackingSources.metrans 
                      ? 'border-red-400 bg-red-50/50 ring-2 ring-red-100' 
                      : 'border-slate-200 hover:border-red-300'
                  }`}
                    data-testid="tracking-toggle-metrans">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-lg ${trackingSources.metrans ? 'bg-red-100' : 'bg-slate-100'}`}>
                        <Truck className={`h-5 w-5 ${trackingSources.metrans ? 'text-red-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">Metrans Rail</div>
                        <div className="text-xs text-slate-500">Rail transport tracking via Metrans portal</div>
                        {trackingSources.metrans && folder.tracking_status?.metrans_status && (
                          <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                            <Navigation className="h-3 w-3" />
                            {folder.tracking_status.metrans_status}
                          </div>
                        )}
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={trackingSources.metrans}
                      onChange={(e) => setTrackingSources({...trackingSources, metrans: e.target.checked})}
                      className="w-5 h-5 text-red-600 rounded-md border-slate-300 focus:ring-red-500"
                    />
                    {trackingSources.metrans && (
                      <div className="absolute top-1.5 right-1.5">
                        <span className="w-2 h-2 bg-green-500 rounded-full block animate-pulse" />
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Active Tracking Summary - Enhanced */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Active Tracking Summary
                </h4>
                <div className="flex flex-wrap gap-2">
                  {trackingSources.fcl && (
                    <span className="px-3 py-1.5 bg-blue-100 text-blue-800 text-sm rounded-lg flex items-center gap-1.5 font-medium border border-blue-200">
                      <Ship className="h-4 w-4" /> FCL
                    </span>
                  )}
                  {trackingSources.lcl && (
                    <span className="px-3 py-1.5 bg-purple-100 text-purple-800 text-sm rounded-lg flex items-center gap-1.5 font-medium border border-purple-200">
                      <Package className="h-4 w-4" /> LCL
                    </span>
                  )}
                  {trackingSources.hbl && (
                    <span className="px-3 py-1.5 bg-emerald-100 text-emerald-800 text-sm rounded-lg flex items-center gap-1.5 font-medium border border-emerald-200">
                      <FileText className="h-4 w-4" /> HBL
                    </span>
                  )}
                  {trackingSources.port && (
                    <span className="px-3 py-1.5 bg-orange-100 text-orange-800 text-sm rounded-lg flex items-center gap-1.5 font-medium border border-orange-200">
                      <Building2 className="h-4 w-4" /> Port
                    </span>
                  )}
                  {trackingSources.metrans && (
                    <span className="px-3 py-1.5 bg-red-100 text-red-800 text-sm rounded-lg flex items-center gap-1.5 font-medium border border-red-200">
                      <Truck className="h-4 w-4" /> Metrans
                    </span>
                  )}
                  {!trackingSources.fcl && !trackingSources.lcl && !trackingSources.hbl && !trackingSources.port && !trackingSources.metrans && (
                    <span className="text-slate-500 text-sm italic">No tracking sources enabled</span>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button 
                  onClick={saveTrackingSources} 
                  disabled={savingTracking}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-tracking-sources-btn"
                >
                  {savingTracking ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Tracking Settings
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {activeSection === "milestones" && (
            <div className="space-y-4">
              <ManualTrinetStatusButton folder={folder} token={token} onProcessed={onUpdate} />
              <PasteTlmStatusButton folder={folder} token={token} onProcessed={onUpdate} />
              <ReprocessCustomsEmailButton folder={folder} token={token} onProcessed={onUpdate} />
              {isAdmin && <BackfillTlmEmailsButton token={token} />}
              <PFMilestones 
                pfId={folder.id} 
                pfNumber={folder.pf_number}
                containers={folder.containers || []}
                trackingMode={folder.tracking_mode}
              />
              <InspectionReportsPanel
                pfId={folder.id}
                pfNumber={folder.pf_number}
                token={token}
                onUpdate={onUpdate}
              />
              <PFComments
                pfId={folder.id}
                pfNumber={folder.pf_number}
                containers={folder.containers || []}
                onNotify={onUpdate}
              />
            </div>
          )}

          {activeSection === "customs" && (
            <ZziCustomsPanel folder={folder} token={token} onUpdate={onUpdate} isCustomerView={isCustomerView} />
          )}

          {activeSection === "stuffing" && (
            <StuffingTab folder={folder} token={token} />
          )}

          {activeSection === "cargo" && (
            <div className="space-y-6">
              {(folder.is_sub_pf || folder.master_pf_id || folder.direction === "export") && (
                <CargoPiecesEditor
                  pfId={folder.id}
                  token={token}
                  initialPieces={folder.cargo_pieces || []}
                  onSaved={() => { if (typeof onUpdate === "function") onUpdate(); }}
                />
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Shipment Type</Label>
                  <select
                    className="w-full border rounded-lg px-3 py-2"
                    value={editData.shipment_type}
                    onChange={(e) => setEditData({...editData, shipment_type: e.target.value})}
                  >
                    <option value="FCL">FCL (Full Container Load)</option>
                    <option value="LCL">LCL (Less than Container Load)</option>
                    <option value="AIR">Air Freight</option>
                    <option value="ROAD">Road Transport</option>
                    <option value="RAIL">Rail</option>
                  </select>
                </div>
                <div>
                  <Label>Container Type</Label>
                  <select
                    className="w-full border rounded-lg px-3 py-2"
                    value={editData.container_type}
                    onChange={(e) => setEditData({...editData, container_type: e.target.value})}
                  >
                    <option value="">Select...</option>
                    <option value="20GP">20' GP</option>
                    <option value="40GP">40' GP</option>
                    <option value="40HC">40' HC</option>
                    <option value="45HC">45' HC</option>
                    <option value="20RF">20' Reefer</option>
                    <option value="40RF">40' Reefer</option>
                    <option value="40RHC">40' HC Reefer</option>
                    <option value="20OT">20' Open Top</option>
                    <option value="40OT">40' Open Top</option>
                    <option value="20FR">20' Flat Rack</option>
                    <option value="40FR">40' Flat Rack</option>
                    <option value="20PL">20' Platform</option>
                    <option value="40PL">40' Platform</option>
                    <option value="TANK">ISO Tank</option>
                    <option value="LCL">LCL (Loose)</option>
                  </select>
                </div>
                <div>
                  <Label>Cargo Type</Label>
                  <Input
                    value={editData.cargo_type}
                    onChange={(e) => setEditData({...editData, cargo_type: e.target.value})}
                    placeholder="e.g., General, Hazardous, Perishable"
                  />
                </div>
                {/* HBL Number - Critical for tracking */}
                {folder.is_sub_order && (
                  <div className="col-span-2">
                    <Label className="text-blue-700 font-medium">House B/L Number (HBL)</Label>
                    <Input
                      value={editData.hbl_number}
                      onChange={(e) => setEditData({...editData, hbl_number: e.target.value})}
                      placeholder="e.g., AKOP26030014"
                      className="border-blue-200 focus:border-blue-400"
                    />
                  </div>
                )}
                <div>
                  <Label>Commodity</Label>
                  <Input
                    value={editData.commodity}
                    onChange={(e) => setEditData({...editData, commodity: e.target.value})}
                    placeholder="e.g., Electronics, Machinery"
                  />
                </div>
                <div>
                  <Label>Weight (kg)</Label>
                  <Input
                    value={editData.cargo_weight}
                    onChange={(e) => setEditData({...editData, cargo_weight: e.target.value})}
                    placeholder="e.g., 15000"
                  />
                </div>
                <div>
                  <Label>Volume (CBM)</Label>
                  <Input
                    value={editData.cargo_volume}
                    onChange={(e) => setEditData({...editData, cargo_volume: e.target.value})}
                    placeholder="e.g., 33"
                  />
                </div>
                <div>
                  <Label>Dimensions (L x W x H cm)</Label>
                  <Input
                    value={editData.cargo_dimensions}
                    onChange={(e) => setEditData({...editData, cargo_dimensions: e.target.value})}
                    placeholder="e.g., 120 x 80 x 100"
                  />
                </div>
                <div>
                  <Label>Packages</Label>
                  <Input
                    value={editData.packages}
                    onChange={(e) => setEditData({...editData, packages: e.target.value})}
                    placeholder="e.g., 25 pallets"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={saveChanges} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? "Saving..." : "Save Cargo Details"}
                </Button>
              </div>
            </div>
          )}

          {activeSection === "suborders" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Sub-Orders</h3>
                  <p className="text-sm text-muted-foreground">
                    Individual HBL shipments within this LCL consolidation
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Delete Selected Button - only show if sub-orders selected */}
                  {selectedSubOrders.length > 0 && isAdmin && (
                    <Button 
                      onClick={deleteSelectedSubOrders} 
                      variant="destructive"
                      disabled={deletingSubOrders}
                      data-testid="delete-selected-suborders-btn"
                    >
                      {deletingSubOrders ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      Delete {selectedSubOrders.length} Selected
                    </Button>
                  )}
                  {onAddSubOrder && (
                    <Button onClick={onAddSubOrder} className="bg-purple-600 hover:bg-purple-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Sub-Order
                    </Button>
                  )}
                </div>
              </div>

              {/* Workflow Mode Toggle for Master PFs */}
              {(folder.is_master || folder.sub_order_count > 0) && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${workflowMode === 'auto' ? 'bg-green-100' : 'bg-amber-100'}`}>
                        {workflowMode === 'auto' ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                          <Clock className="h-5 w-5 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800">Email Flow Mode</h4>
                        <p className="text-sm text-slate-600">
                          {workflowMode === 'auto' 
                            ? 'Automated emails (arrival notices, etc.) will trigger normally' 
                            : 'Automated emails are paused - still working on data/PF changes'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-medium ${workflowMode === 'pending' ? 'text-amber-700' : 'text-slate-400'}`}>
                        Pending
                      </span>
                      <button
                        onClick={() => toggleWorkflowMode(workflowMode === 'auto' ? 'pending' : 'auto')}
                        disabled={savingWorkflowMode}
                        className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          workflowMode === 'auto' ? 'bg-green-500' : 'bg-amber-500'
                        } ${savingWorkflowMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        data-testid="workflow-mode-toggle"
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                            workflowMode === 'auto' ? 'translate-x-8' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className={`text-sm font-medium ${workflowMode === 'auto' ? 'text-green-700' : 'text-slate-400'}`}>
                        Auto
                      </span>
                      {savingWorkflowMode && (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Master PF Info */}
              {folder.is_master && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="h-5 w-5 text-purple-600" />
                    <span className="font-semibold text-purple-900">Master PF (LCL Consolidation)</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-purple-600">MBL Shipper:</span>
                      <span className="ml-2 font-medium">{folder.shipper || 'Not set'}</span>
                    </div>
                    <div>
                      <span className="text-purple-600">MBL Consignee:</span>
                      <span className="ml-2 font-medium">{folder.consignee || 'Not set'}</span>
                    </div>
                    <div>
                      <span className="text-purple-600">MBL Number:</span>
                      <span className="ml-2 font-medium">{folder.mbl_number || 'Not set'}</span>
                    </div>
                    <div>
                      <span className="text-purple-600">Container:</span>
                      <span className="ml-2 font-medium font-mono">{folder.container_number || folder.containers?.join(', ') || 'Not set'}</span>
                    </div>
                    <div>
                      <span className="text-purple-600">Vessel:</span>
                      <span className="ml-2 font-medium">{folder.vessel_name || 'Not set'}</span>
                    </div>
                    <div>
                      <span className="text-purple-600">ETD:</span>
                      <span className="ml-2 font-medium">{folder.etd ? new Date(folder.etd).toLocaleDateString() : 'Not set'}</span>
                    </div>
                    <div>
                      <span className="text-purple-600">ETA:</span>
                      <span className="ml-2 font-medium">{folder.eta ? new Date(folder.eta).toLocaleDateString() : 'Not set'}</span>
                    </div>
                    <div>
                      <span className="text-purple-600">Carrier:</span>
                      <span className="ml-2 font-medium">{folder.carrier || 'Not set'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Sub-orders list with checkboxes */}
              {subOrders.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <GitBranch className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <h4 className="font-medium text-slate-600">No sub-orders yet</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add sub-orders for each HBL shipment in this container
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Select All Header */}
                  {isAdmin && subOrders.length > 1 && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg">
                      <input
                        type="checkbox"
                        checked={selectedSubOrders.length === subOrders.length}
                        onChange={selectAllSubOrders}
                        className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        data-testid="select-all-suborders"
                      />
                      <span className="text-sm font-medium text-slate-700">
                        {selectedSubOrders.length === subOrders.length 
                          ? `Deselect All (${subOrders.length})` 
                          : `Select All (${subOrders.length})`}
                      </span>
                    </div>
                  )}
                  
                  {subOrders.map((sub) => (
                    <div 
                      key={sub.id} 
                      className={`p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow ${
                        selectedSubOrders.includes(sub.id) ? 'ring-2 ring-purple-500 border-purple-300' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Checkbox for selection */}
                        {isAdmin && (
                          <div className="pt-1">
                            <input
                              type="checkbox"
                              checked={selectedSubOrders.includes(sub.id)}
                              onChange={() => toggleSubOrderSelection(sub.id)}
                              className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                              data-testid={`select-suborder-${sub.pf_number}`}
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-mono font-bold text-purple-700">{sub.pf_number}</span>
                            {sub.hbl_number && (
                              <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700 font-mono">
                                HBL: {sub.hbl_number}
                              </span>
                            )}
                            <span className={`px-2 py-0.5 text-xs rounded ${STATUS_COLORS[sub.status]}`}>
                              {STATUS_LABELS[sub.status] || sub.status}
                            </span>
                            {sub.incoterm && (
                              <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-700">
                                {sub.incoterm}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-green-600 font-medium">Shipper:</span>
                              <span>{sub.shipper_name || sub.shipper || "-"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-blue-600 font-medium">Consignee:</span>
                              <span>{sub.consignee_name || sub.consignee || sub.customer || "-"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">Origin:</span>
                              <span>{sub.origin_port || "-"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">Destination:</span>
                              <span>{sub.destination_port || "-"}</span>
                            </div>
                            {(sub.gross_weight_kg || sub.weight) && (
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">Weight:</span>
                                <span>{sub.gross_weight_kg || sub.weight} kg</span>
                              </div>
                            )}
                            {sub.volume_cbm && (
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">Volume:</span>
                                <span>{sub.volume_cbm} CBM</span>
                              </div>
                            )}
                            {sub.packing && (
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">Packing:</span>
                                <span>{sub.packing}</span>
                              </div>
                            )}
                            {sub.routing && (
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">Routing:</span>
                                <span>{sub.routing}</span>
                              </div>
                            )}
                          </div>
                          {(sub.cargo_description || sub.description || sub.commodity) && (
                            <p className="text-sm text-muted-foreground mt-2 bg-slate-50 p-2 rounded">
                              <span className="font-medium">Cargo:</span> {sub.cargo_description || sub.description || sub.commodity}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeSection === "notifications" && (
            <div className="space-y-6">
              <div>
                <Label>Notification Email Addresses</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  These addresses will receive updates about this position folder
                </p>
                <div className="flex gap-2 mb-4">
                  <Input
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="Add email address"
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
                  />
                  <Button type="button" onClick={addEmail}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {editData.notification_emails.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground bg-slate-50 rounded-lg">
                      <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No notification emails configured</p>
                    </div>
                  ) : (
                    editData.notification_emails.map((email, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-blue-500" />
                          <span>{email}</span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeEmail(email)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={saveChanges} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? "Saving..." : "Save Email List"}
                </Button>
              </div>
            </div>
          )}

          {activeSection === "email" && (
            isCustomerView ? (
            <div className="space-y-4" data-testid="customer-email-tab">
              <div className="p-3 bg-blue-50 rounded-lg text-sm flex items-start gap-2">
                <Mail className="h-4 w-4 text-blue-600 mt-0.5" />
                <div>
                  <span className="font-medium">Message Europacific</span>
                  <p className="text-slate-600 text-xs mt-0.5">
                    Your message goes straight to the operator handling {folder.pf_number}. They usually reply by email.
                  </p>
                </div>
              </div>
              <div>
                <Label>Subject (optional)</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder={`Question about ${folder.pf_number}`}
                  data-testid="customer-email-subject"
                />
              </div>
              <div>
                <Label>Message</Label>
                <textarea
                  className="w-full h-48 p-3 border rounded-lg"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Type your message to our team..."
                  data-testid="customer-email-body"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={sendCustomerMessage} disabled={sending || !emailBody.trim()} data-testid="customer-email-send-btn">
                  <Send className="h-4 w-4 mr-2" />
                  {sending ? "Sending..." : "Send to Europacific"}
                </Button>
                {emailBody && (
                  <Button variant="outline" onClick={() => { setEmailSubject(""); setEmailBody(""); }}>
                    Clear
                  </Button>
                )}
              </div>
            </div>
            ) : (
            <div className="space-y-4">
              {(folder.customer_email || editData.notification_emails.length > 0) ? (
                <>
                  <div className="p-3 bg-blue-50 rounded-lg space-y-2">
                    <div>
                      <Label className="text-xs text-slate-600 mb-1 block">To (comma-separated)</Label>
                      <Input
                        value={emailTo}
                        onChange={(e) => setEmailTo(e.target.value)}
                        placeholder="recipient@example.com, another@example.com"
                        className="bg-white text-sm"
                        data-testid="email-to-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 mb-1 block">Cc (comma-separated)</Label>
                      <Input
                        value={emailCc}
                        onChange={(e) => setEmailCc(e.target.value)}
                        placeholder="cc@example.com"
                        className="bg-white text-sm"
                        data-testid="email-cc-input"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">Prefilled with notify emails — edit, add or remove as needed.</p>
                    </div>
                  </div>
                  
                  {/* Email Template Selector */}
                  <div className="bg-slate-50 rounded-lg p-3 border">
                    <Label className="text-xs text-slate-600 mb-2 block">Quick Templates</Label>
                    <Select onValueChange={(templateId) => {
                      const template = EMAIL_TEMPLATES.find(t => t.id === templateId);
                      if (template) {
                        const { subject, body } = fillTemplate(template, folder, user?.email?.split('@')[0]?.replace('.', ' '));
                        setEmailSubject(subject);
                        setEmailBody(body);
                        toast.success(`Template "${template.name}" loaded`);
                      }
                    }}>
                      <SelectTrigger className="w-full bg-white">
                        <SelectValue placeholder="Select an email template..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-80">
                        {TEMPLATE_CATEGORIES.map(cat => (
                          <SelectGroup key={cat.id}>
                            <SelectLabel className="text-xs font-semibold text-slate-500 bg-slate-100">
                              {cat.name}
                            </SelectLabel>
                            {EMAIL_TEMPLATES.filter(t => t.category === cat.id).map(template => (
                              <SelectItem key={template.id} value={template.id}>
                                {template.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Subject</Label>
                    <Input
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Enter email subject"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Will be prefixed with "RE: {folder.pf_number} - "
                    </p>
                  </div>
                  <div>
                    <Label>Message</Label>
                    <textarea
                      className="w-full h-48 p-3 border rounded-lg"
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="Enter your message..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={sendEmail} disabled={sending}>
                      <Send className="h-4 w-4 mr-2" />
                      {sending ? "Sending..." : "Send Email"}
                    </Button>
                    {emailBody && (
                      <Button variant="outline" onClick={() => { setEmailSubject(""); setEmailBody(""); }}>
                        Clear
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No customer email on this folder</p>
                </div>
              )}
            </div>
            )
          )}

          {activeSection === "history" && (
            <div className="space-y-3" data-testid="pf-history-section">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-500" />
                  Sent through the system
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchPfHistory}
                  disabled={loadingHistory}
                  data-testid="pf-history-refresh-btn"
                >
                  <RefreshCw className={`h-4 w-4 ${loadingHistory ? "animate-spin" : ""}`} />
                </Button>
              </div>

              {loadingHistory && pfHistory.length === 0 && (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading history…
                </div>
              )}

              {pfHistory.map((event, idx) => {
                const isEmail = event.channel === "email";
                const isCreated = event.type === "created" || event.type === "pf_created";
                let Icon = FileText;
                let tone = "bg-slate-100 text-slate-600";
                if (isCreated) { Icon = CheckCircle; tone = "bg-green-100 text-green-600"; }
                else if (event.type === "email_sent") { Icon = Send; tone = "bg-blue-100 text-blue-600"; }
                else if (event.type === "arrival_notice_sent") { Icon = Bell; tone = "bg-amber-100 text-amber-700"; }
                else if (event.type === "pre_alert_sent") { Icon = BellRing; tone = "bg-orange-100 text-orange-700"; }
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100"
                    data-testid={`pf-history-item-${event.type}`}
                  >
                    <div className={`p-1.5 rounded ${tone}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{event.label}</span>
                        {isEmail && (
                          <span className="text-[10px] uppercase tracking-wide bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">
                            Email
                          </span>
                        )}
                      </div>
                      {event.subject && (
                        <div className="text-sm text-slate-700 mt-0.5 truncate">{event.subject}</div>
                      )}
                      {event.recipients?.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-0.5 break-all">
                          To: {event.recipients.join(", ")}
                        </div>
                      )}
                      {event.detail && (
                        <div className="text-xs text-muted-foreground mt-0.5">{event.detail}</div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">
                        {event.by ? `by ${event.by} • ` : ""}
                        {event.timestamp ? new Date(event.timestamp).toLocaleString() : ""}
                      </div>
                    </div>
                  </div>
                );
              })}

              {!loadingHistory && pfHistory.length === 0 && (
                <div className="text-center py-8 text-muted-foreground" data-testid="pf-history-empty">
                  Nothing sent through the system yet for this PF.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Arrival Notice Modal */}
      {showArrivalNoticeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Bell className="h-5 w-5 text-amber-600" />
                {arrivalNoticeData.notice_type === "rated" ? "Rated Arrival Notice" : "Arrival Notice"}
              </h3>
              <button
                onClick={() => setShowArrivalNoticeModal(false)}
                className="p-1 rounded hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* PF Info */}
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="font-mono font-bold">{folder.pf_number}</div>
                <div className="text-sm text-muted-foreground">
                  {(() => {
                    const allEmails = [];
                    if (folder.customer_email) allEmails.push(folder.customer_email);
                    if (folder.notification_emails?.length) {
                      folder.notification_emails.forEach(e => {
                        if (e && !allEmails.includes(e)) allEmails.push(e);
                      });
                    }
                    return allEmails.length > 0 
                      ? allEmails.join(", ") 
                      : "No customer email set";
                  })()}
                </div>
              </div>

              {/* Notice Type Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setArrivalNoticeData({ ...arrivalNoticeData, notice_type: "simple" })}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    arrivalNoticeData.notice_type === "simple"
                      ? "bg-amber-100 border-amber-300 text-amber-800"
                      : "hover:bg-slate-50"
                  }`}
                >
                  Simple Notice
                </button>
                <button
                  onClick={() => setArrivalNoticeData({ ...arrivalNoticeData, notice_type: "rated" })}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    arrivalNoticeData.notice_type === "rated"
                      ? "bg-green-100 border-green-300 text-green-800"
                      : "hover:bg-slate-50"
                  }`}
                >
                  Rated Notice (+ Charges)
                </button>
              </div>

              {/* Arrival Details */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Arrival Date</label>
                  <input
                    type="date"
                    value={arrivalNoticeData.arrival_date || folder.eta?.split("T")[0] || ""}
                    onChange={(e) => setArrivalNoticeData({ ...arrivalNoticeData, arrival_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Arrival Port</label>
                  <input
                    type="text"
                    value={arrivalNoticeData.arrival_port || folder.destination_port || "KOPER"}
                    onChange={(e) => setArrivalNoticeData({ ...arrivalNoticeData, arrival_port: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Charges Section (for Rated Notice) */}
              {arrivalNoticeData.notice_type === "rated" && (
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Local Charges / Costing
                    <span className="text-xs text-muted-foreground ml-2">(Free text)</span>
                  </label>
                  <textarea
                    value={arrivalNoticeData.charges}
                    onChange={(e) => setArrivalNoticeData({ ...arrivalNoticeData, charges: e.target.value })}
                    placeholder={`Service                      Amount\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\nOcean Freight                €500.00\nTerminal Handling            €120.00\nCustoms Clearance            €80.00\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\nTOTAL                        €700.00`}
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono h-40"
                  />
                </div>
              )}

              {/* Include Sub-Orders (for Master PFs) */}
              {(folder.is_master || folder.sub_order_count > 0) && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={arrivalNoticeData.include_sub_orders}
                    onChange={(e) => setArrivalNoticeData({ ...arrivalNoticeData, include_sub_orders: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm">
                    Also send to all {folder.sub_order_count || 0} sub-order customers
                  </span>
                </label>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowArrivalNoticeModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={sendArrivalNotice}
                disabled={sendingArrivalNotice || (!folder.customer_email && (!folder.notification_emails || folder.notification_emails.length === 0))}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {sendingArrivalNotice ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Arrival Notice
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Spot Rate Request Modal */}
      {showSpotModal && spotFormData && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" data-testid="spot-rate-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 bg-white">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Send className="h-5 w-5 text-emerald-600" />
                Spot Rate Request {folder.pf_number ? `· ${folder.pf_number}` : ""}
              </h3>
              <button onClick={() => setShowSpotModal(false)} className="text-slate-400 hover:text-slate-700" data-testid="spot-modal-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            {spotResult ? (
              <div className="p-6 space-y-4">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800">
                  Request sent to <b>{spotResult.emails_sent}</b> trucking companies. They can also submit online.
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Online submit link (share with any carrier)</label>
                  <div className="flex gap-2 mt-1">
                    <input readOnly value={spotResult.submit_link} className="flex-1 border rounded-lg px-3 py-2 text-sm bg-slate-50" data-testid="spot-submit-link" />
                    <Button type="button" variant="outline" onClick={() => { navigator.clipboard.writeText(spotResult.submit_link); toast.success("Link copied"); }}>Copy</Button>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={() => setShowSpotModal(false)}>Done</Button>
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500">From</label>
                    <SmartLocationInput
                      value={{ city: spotFormData.from_location }}
                      onChange={(loc) => { const v = loc.city || loc.port_name || ""; setSpotFormData((p) => ({ ...p, from_location: v })); fetchSpotCompanies(v, spotFormData.to_location, spotFormData.service_type); }}
                      placeholder="Search city or port..."
                      showSeaport={true}
                      data-testid="spot-from"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">To</label>
                    <SmartLocationInput
                      value={{ city: spotFormData.to_location }}
                      onChange={(loc) => { const v = loc.city || loc.port_name || ""; setSpotFormData((p) => ({ ...p, to_location: v })); fetchSpotCompanies(spotFormData.from_location, v, spotFormData.service_type); }}
                      placeholder="Search city or port..."
                      showSeaport={true}
                      data-testid="spot-to"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Empty return (optional)</label>
                    <input value={spotFormData.empty_location} onChange={(e) => setSpotFormData({ ...spotFormData, empty_location: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="spot-empty" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Service type</label>
                    <select value={spotFormData.service_type} onChange={(e) => setSpotFormData({ ...spotFormData, service_type: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="spot-svc">
                      {["FCL", "LCL", "FTL", "LTL", "Reefer", "OOG", "Hazmat"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Pickup date</label>
                    <input type="date" value={spotFormData.pickup_date} onChange={(e) => setSpotFormData({ ...spotFormData, pickup_date: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="spot-pu-date" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">CC emails (comma-separated)</label>
                    <input value={spotFormData.cc_emails} onChange={(e) => setSpotFormData({ ...spotFormData, cc_emails: e.target.value })} placeholder="ops@..., manager@..." className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="spot-cc" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Description</label>
                  <textarea value={spotFormData.description} onChange={(e) => setSpotFormData({ ...spotFormData, description: e.target.value })} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="spot-desc" />
                </div>

                {/* Cargo details — carriers need these to quote accurately */}
                <div className="border border-teal-200 bg-teal-50/50 rounded-lg p-3 space-y-3" data-testid="spot-cargo-section">
                  <div className="text-xs font-semibold uppercase tracking-wide text-teal-700">Cargo details</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-500">Packages</label>
                      <input type="number" value={spotCargo.packages} onChange={(e) => setSpotCargo({ ...spotCargo, packages: e.target.value })} placeholder="e.g. 12" className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="spot-cargo-packages" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">Package type</label>
                      <select value={spotCargo.package_type} onChange={(e) => setSpotCargo({ ...spotCargo, package_type: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="spot-cargo-pkgtype">
                        {["", "EUR pallets", "Block pallets", "Colli", "Boxes", "Cartons", "Big bags", "Rolls", "Loose"].map((o) => <option key={o} value={o}>{o || "—"}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">Gross weight (kg)</label>
                      <input type="number" step="0.1" value={spotCargo.weight_kg} onChange={(e) => setSpotCargo({ ...spotCargo, weight_kg: e.target.value })} placeholder="e.g. 12000" className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="spot-cargo-weight" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">Loading meters (LDM)</label>
                      <input type="number" step="0.1" value={spotCargo.ldm} onChange={(e) => setSpotCargo({ ...spotCargo, ldm: e.target.value })} placeholder="e.g. 13.6" className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="spot-cargo-ldm" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">Volume (cbm)</label>
                      <input type="number" step="0.1" value={spotCargo.volume_cbm} onChange={(e) => setSpotCargo({ ...spotCargo, volume_cbm: e.target.value })} placeholder="e.g. 33" className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="spot-cargo-cbm" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">Dimensions (L×W×H)</label>
                      <input value={spotCargo.dimensions} onChange={(e) => setSpotCargo({ ...spotCargo, dimensions: e.target.value })} placeholder="120×80×160 cm" className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="spot-cargo-dims" />
                    </div>
                  </div>
                  {equipmentTypes.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-1">
                        <label className="text-xs font-medium text-slate-500">Container / Truck / Unit type</label>
                        <EquipmentSelect
                          options={equipmentTypes}
                          value={spotCargo.equipment_uid}
                          onChange={(uid, name) => setSpotCargo({ ...spotCargo, equipment_uid: uid, equipment_name: name })}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500">Type of goods</label>
                        <EquipmentSelect
                          options={commodities}
                          value={spotCargo.commodity}
                          onChange={(uid) => setSpotCargo({ ...spotCargo, commodity: uid })}
                          placeholder="Select commodity"
                          icon={PackageOpen}
                          testIdPrefix="commodity"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs font-medium text-slate-500">Temp. min (°C)</label>
                          <input type="number" value={spotCargo.temp_min} onChange={(e) => setSpotCargo({ ...spotCargo, temp_min: e.target.value })} placeholder="2" className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="spot-cargo-temp-min" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-500">Temp. max (°C)</label>
                          <input type="number" value={spotCargo.temp_max} onChange={(e) => setSpotCargo({ ...spotCargo, temp_max: e.target.value })} placeholder="8" className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="spot-cargo-temp-max" />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input type="checkbox" checked={spotCargo.stackable} onChange={(e) => setSpotCargo({ ...spotCargo, stackable: e.target.checked })} data-testid="spot-cargo-stackable" />
                      Stackable
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input type="checkbox" checked={spotCargo.adr} onChange={(e) => setSpotCargo({ ...spotCargo, adr: e.target.checked })} data-testid="spot-cargo-adr" />
                      ADR / Dangerous goods
                    </label>
                    {spotCargo.adr && (
                      <input value={spotCargo.adr_class} onChange={(e) => setSpotCargo({ ...spotCargo, adr_class: e.target.value })} placeholder="ADR class (e.g. 3, UN1203)" className="border rounded-lg px-3 py-1.5 text-sm bg-white flex-1 min-w-[160px]" data-testid="spot-cargo-adr-class" />
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-slate-500">Select trucking companies ({selectedSpotEmails.length} selected)</label>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">{spotCompanies.length} available</span>
                      {spotCompanies.length > 0 && (
                        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 cursor-pointer" data-testid="spot-select-all-label">
                          <input type="checkbox" checked={allSpotSelected} onChange={toggleSelectAllSpot} data-testid="spot-select-all" />
                          {allSpotSelected ? "Deselect all" : "Select all"}
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="border rounded-lg max-h-52 overflow-y-auto divide-y">
                    {spotCompanies.length === 0 && <div className="p-3 text-sm text-slate-400">No trucking companies with emails found.</div>}
                    {spotCompanies.map((c) => {
                      const isSel = selectedSpotEmails.includes(c.email);
                      return (
                      <label key={c.email} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer border-l-4 ${isSel ? "bg-orange-50 border-orange-500" : "border-transparent hover:bg-slate-50"}`} data-testid={`spot-company-${c.email}`}>
                        <input type="checkbox" className="h-4 w-4 accent-orange-600 shrink-0" checked={isSel} onChange={() => toggleSpotEmail(c.email)} />
                        <span className="flex-1">{c.name} <span className="text-xs text-slate-400">({c.email})</span></span>
                        {isSel && <span className="text-[10px] font-semibold uppercase text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">Selected</span>}
                        {c.route_matched && <span className="text-[10px] font-semibold uppercase text-green-700 bg-green-100 px-1.5 py-0.5 rounded" title={c.match_reason || "Serves this route"}>Recommended</span>}
                        {c.route_count ? <span className="text-xs text-slate-400">{c.route_count} orders</span> : null}
                      </label>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowSpotModal(false)}>Cancel</Button>
                  <Button variant="outline" onClick={sendTransporeonSpot} disabled={sendingSpot || !(spotFormData?.from_location || spotFormData?.from_city) || !(spotFormData?.to_location || spotFormData?.to_city)} data-testid="spot-transporeon-btn" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                    {sendingSpot ? "Working..." : "Transporeon"}
                  </Button>
                  <Button onClick={async () => { await sendTransporeonSpot(); if (selectedSpotEmails.length > 0) await sendSpotRequest(); }} disabled={sendingSpot || !(spotFormData?.from_location || spotFormData?.from_city) || !(spotFormData?.to_location || spotFormData?.to_city)} data-testid="spot-transporeon-vendors-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    {sendingSpot ? "Working..." : `Transporeon + Vendors (${selectedSpotEmails.length})`}
                  </Button>
                  <Button variant="outline" onClick={sendSpotRequest} disabled={sendingSpot || selectedSpotEmails.length === 0} data-testid="spot-send-btn">
                    {sendingSpot ? "Sending..." : `Vendors – email (${selectedSpotEmails.length})`}
                  </Button>
                </div>

                {spotResult?.transporeon?.transport_number && (
                  <div className="mt-3 border-t pt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-orange-700">Transporeon Marketplace — carrier quotes</span>
                    </div>
                    <TransporeonQuotesPanel
                      apiUrl={API_URL}
                      token={token}
                      transportNumber={spotResult.transporeon.transport_number}
                      autoLoad={true}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Transport Order Modal */}
      {showTOModal && toFormData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Truck className="h-5 w-5 text-orange-600" />
                  Create Transport Order
                </h2>
                <p className="text-sm text-muted-foreground">From {folder.pf_number}</p>
              </div>
              <button
                onClick={() => { setShowTOModal(false); setToFormData(null); }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); createTransportOrder(); }} className="p-6 space-y-6">
              {/* Trucking Company */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2 text-slate-900">
                  <Truck className="h-4 w-4" />
                  Trucking Company
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name *</Label>
                    <Input
                      required
                      value={toFormData.trucking_company.name}
                      onChange={(e) => setToFormData({
                        ...toFormData,
                        trucking_company: { ...toFormData.trucking_company, name: e.target.value }
                      })}
                      placeholder="Trucking company name"
                    />
                  </div>
                  <div>
                    <Label>Contact Person</Label>
                    <Input
                      value={toFormData.trucking_company.contact_person}
                      onChange={(e) => setToFormData({
                        ...toFormData,
                        trucking_company: { ...toFormData.trucking_company, contact_person: e.target.value }
                      })}
                      placeholder="Name"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Address *</Label>
                    <Input
                      required
                      value={toFormData.trucking_company.address}
                      onChange={(e) => setToFormData({
                        ...toFormData,
                        trucking_company: { ...toFormData.trucking_company, address: e.target.value }
                      })}
                      placeholder="Street, City, Country"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={toFormData.trucking_company.phone}
                      onChange={(e) => setToFormData({
                        ...toFormData,
                        trucking_company: { ...toFormData.trucking_company, phone: e.target.value }
                      })}
                      placeholder="+48 xxx xxx xxx"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={toFormData.trucking_company.email}
                      onChange={(e) => setToFormData({
                        ...toFormData,
                        trucking_company: { ...toFormData.trucking_company, email: e.target.value }
                      })}
                      placeholder="email@company.com"
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle & Driver */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2 text-slate-900">
                  <Truck className="h-4 w-4" />
                  Vehicle & Driver (Optional)
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Truck Number</Label>
                    <Input
                      value={toFormData.truck_number}
                      onChange={(e) => setToFormData({ ...toFormData, truck_number: e.target.value })}
                      placeholder="LJ XX-XXX"
                    />
                  </div>
                  <div>
                    <Label>Trailer Number</Label>
                    <Input
                      value={toFormData.trailer_number}
                      onChange={(e) => setToFormData({ ...toFormData, trailer_number: e.target.value })}
                      placeholder="LJ YY-YYY"
                    />
                  </div>
                  <div>
                    <Label>Driver Name</Label>
                    <Input
                      value={toFormData.driver_name}
                      onChange={(e) => setToFormData({ ...toFormData, driver_name: e.target.value })}
                      placeholder="Driver's full name"
                    />
                  </div>
                  <div>
                    <Label>Driver Phone</Label>
                    <Input
                      value={toFormData.driver_phone}
                      onChange={(e) => setToFormData({ ...toFormData, driver_phone: e.target.value })}
                      placeholder="+48 666 123 456"
                    />
                  </div>
                </div>
              </div>

              {/* Route Details */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2 text-slate-900">
                  <Navigation className="h-4 w-4" />
                  Route Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Loading Place *</Label>
                    <textarea
                      required
                      className="w-full px-3 py-2 border rounded-md text-sm"
                      rows={3}
                      value={toFormData.loading_place}
                      onChange={(e) => setToFormData({ ...toFormData, loading_place: e.target.value })}
                      placeholder="LUKA KOPER&#10;SI-6000 KOPER"
                    />
                  </div>
                  <div>
                    <Label>Unloading Place (Delivery) *</Label>
                    <textarea
                      required
                      className="w-full px-3 py-2 border rounded-md text-sm"
                      rows={3}
                      value={toFormData.unloading_place}
                      onChange={(e) => setToFormData({ ...toFormData, unloading_place: e.target.value })}
                      placeholder="Company Name&#10;Address, City, Country"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Empty Container Return Location</Label>
                    <textarea
                      className="w-full px-3 py-2 border rounded-md text-sm border-amber-300 bg-amber-50/30"
                      rows={2}
                      value={toFormData.return_location}
                      onChange={(e) => setToFormData({ ...toFormData, return_location: e.target.value })}
                      placeholder="Where should the empty container be returned?"
                    />
                  </div>
                  <div>
                    <Label>Loading Date *</Label>
                    <Input
                      type="datetime-local"
                      required
                      value={toFormData.date_loading}
                      onChange={(e) => setToFormData({ ...toFormData, date_loading: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Unloading Date *</Label>
                    <Input
                      type="datetime-local"
                      required
                      value={toFormData.date_unloading}
                      onChange={(e) => setToFormData({ ...toFormData, date_unloading: e.target.value })}
                    />
                  </div>
                </div>

                {/* Intermediate Stops */}
                <div className="mt-4 pt-4 border-t border-dashed">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium">Intermediate Stops (Optional)</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addIntermediateStop}>
                      <Plus className="h-3 w-3 mr-1" />
                      Add Stop
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Add stops between loading → delivery or delivery → return (e.g., customs, cross-dock)
                  </p>
                  {toFormData.intermediate_stops.map((stop, index) => (
                    <div key={index} className="grid grid-cols-5 gap-2 items-end mb-2 p-2 bg-gray-50 rounded">
                      <div className="col-span-2">
                        <Label className="text-xs">Location</Label>
                        <Input
                          value={stop.location}
                          onChange={(e) => updateIntermediateStop(index, "location", e.target.value)}
                          placeholder="Stop address"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Purpose</Label>
                        <select
                          className="w-full h-10 px-3 border rounded-md text-sm"
                          value={stop.purpose}
                          onChange={(e) => updateIntermediateStop(index, "purpose", e.target.value)}
                        >
                          <option value="customs">Customs</option>
                          <option value="loading">Partial Loading</option>
                          <option value="unloading">Partial Unloading</option>
                          <option value="service">Service/Inspection</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Date/Time</Label>
                        <Input
                          type="datetime-local"
                          value={stop.date}
                          onChange={(e) => updateIntermediateStop(index, "date", e.target.value)}
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-red-500"
                          onClick={() => removeIntermediateStop(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cargo Details */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2 text-slate-900">
                    <Package className="h-4 w-4" />
                    Cargo Details
                  </h3>
                  <Button type="button" variant="outline" size="sm" onClick={addCargoItem}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Item
                  </Button>
                </div>
                {toFormData.cargo_items.map((item, index) => (
                  <div key={index} className="grid grid-cols-5 gap-2 items-end p-2 bg-slate-50 rounded">
                    <div>
                      <Label className="text-xs">Container #</Label>
                      <Input
                        value={item.container_number}
                        onChange={(e) => updateCargoItem(index, "container_number", e.target.value)}
                        placeholder="TLLU4322906"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => updateCargoItem(index, "description", e.target.value)}
                        placeholder="Cargo description"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Weight (kg)</Label>
                      <Input
                        type="number"
                        value={item.weight_kg}
                        onChange={(e) => updateCargoItem(index, "weight_kg", e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">Pallets</Label>
                        <Input
                          type="number"
                          value={item.pallets}
                          onChange={(e) => updateCargoItem(index, "pallets", e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      {toFormData.cargo_items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-red-500 mt-4"
                          onClick={() => removeCargoItem(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pricing & Customer */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2 text-slate-900">
                  <FileText className="h-4 w-4" />
                  Pricing & Customer
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Price *</Label>
                    <Input
                      type="number"
                      required
                      value={toFormData.price}
                      onChange={(e) => setToFormData({ ...toFormData, price: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-md text-sm"
                      value={toFormData.currency}
                      onChange={(e) => setToFormData({ ...toFormData, currency: e.target.value })}
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="PLN">PLN</option>
                      <option value="HUF">HUF</option>
                    </select>
                  </div>
                  <div>
                    <Label>Customs Status</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-md text-sm"
                      value={toFormData.customs_clearance}
                      onChange={(e) => setToFormData({ ...toFormData, customs_clearance: e.target.value })}
                    >
                      <option value="CUSTOM CLEARED">Custom Cleared</option>
                      <option value="T1">T1 Transit</option>
                      <option value="UNDER CUSTOMS">Under Customs</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Customer Name</Label>
                    <Input
                      value={toFormData.customer_name}
                      onChange={(e) => setToFormData({ ...toFormData, customer_name: e.target.value })}
                      placeholder="Customer company name"
                    />
                  </div>
                  <div>
                    <Label>Customer Email</Label>
                    <Input
                      type="email"
                      value={toFormData.customer_email}
                      onChange={(e) => setToFormData({ ...toFormData, customer_email: e.target.value })}
                      placeholder="customer@email.com"
                    />
                  </div>
                </div>
                <div>
                  <Label>Remarks</Label>
                  <textarea
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    rows={2}
                    value={toFormData.remarks}
                    onChange={(e) => setToFormData({ ...toFormData, remarks: e.target.value })}
                    placeholder="Special instructions or notes..."
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowTOModal(false); setToFormData(null); }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creatingTO}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  {creatingTO ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Truck className="h-4 w-4 mr-2" />
                      Create Transport Order
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
